
import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
import {
    Box,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    Paper,
    IconButton,
    Divider,
    Stack,
    Snackbar,
    Alert,
    TextField,
} from "@mui/material";

import BackToScenariosButton from "./utilities/BackToScenario";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import DeleteIcon from "@mui/icons-material/Delete";
import GridOffIcon from '@mui/icons-material/GridOff';
import GridOnIcon from '@mui/icons-material/GridOn';

import FloorLogo from "./three/FloorLogo";
import UI3DModelButtons from "./three/UI3DModelButtons";
import FirstPersonView from "./three/FirstPersonView";
import ModelList from "./utilities/ModelList";
import GridWalls from "./three/GridWalls";
import { ObjectHighlighter } from "./utilities/ObjectHighlighter";
import WallBoards from "./three/WallBoards";
import { useSelector } from "react-redux";

const GRID_SIZE = 50;

const UNITY_Y_OFFSET = 180;

const MODEL_LIBRARY = [
    { name: "whiteboard", visible: true },
    { name: "CC CC0 1", visible: false },
    { name: "arrow", visible: true },

    { name: "envp_bin_4", visible: true },
    { name: "envp_bin_4_lids", visible: true },
    { name: "envp_bin_lid", visible: true },

    { name: "envp_furnitures_chair", visible: true },
    { name: "envp_furnitures_office_chair", visible: true },

    { name: "envp_furnitures_sofa_corner_3_2", visible: true },
    { name: "envp_furnitures_sofa_corner_3_3", visible: true },
    { name: "envp_furnitures_sofa_line_2", visible: true },
    { name: "envp_furnitures_sofa_line_3", visible: true },
    { name: "envp_furnitures_sofa_single", visible: true },
    { name: "envp_furnitures_sofa_single_corner", visible: true },

    { name: "envp_furnitures_table_140_80", visible: true },
    { name: "envp_furnitures_table_160_80", visible: true },
    { name: "envp_furnitures_table_250_120", visible: true },
    { name: "envp_furnitures_table_400_120", visible: true },

    { name: "envp_furnitures_table_small_100x100x100", visible: true },
    { name: "envp_furnitures_table_small_100x100x75", visible: true },
    { name: "envp_furnitures_table_small_125x100x100", visible: true },
    { name: "envp_furnitures_table_small_125x100x75", visible: true },
    { name: "envp_furnitures_table_small_75x75x100", visible: true },
    { name: "envp_furnitures_table_small_75x75x75", visible: true },

    { name: "envp_modular_door_frame", visible: true },
    { name: "env_staircase_stairs", visible: true },
    { name: "env_wall_column_05", visible: true },
    { name: "env_wall_creator_x2_5", visible: true },

    { name: "exe_cd_essential_items", visible: true },

    { name: "exe_cooking_bins", visible: true },
    { name: "exe_cooking_cookbase", visible: true },
    { name: "exe_cooking_cook_station", visible: true },
    { name: "exe_cooking_countertop_2", visible: true },
    { name: "exe_cooking_countertop_4", visible: true },
    { name: "exe_cooking_countertop_drawer", visible: true },
    { name: "exe_cooking_device_blender", visible: true },
    { name: "exe_cooking_device_mixer", visible: true },
    { name: "exe_cooking_device_mixer_blender", visible: true },
    { name: "exe_cooking_drawers_ingrediens", visible: true },
    { name: "exe_cooking_fridge_dairy", visible: true },
    { name: "exe_cooking_fridge_fruits", visible: true },
    { name: "exe_cooking_fridge_meat", visible: true },
    { name: "exe_cooking_fridge_vegetables", visible: true },
    { name: "exe_cooking_fridges_all", visible: true },
    { name: "exe_cooking_mech_cooker", visible: true },
    { name: "exe_cooking_mech_furnance", visible: true },
    { name: "exe_cooking_sink", visible: true },
    { name: "exe_cooking_sink_hanged", visible: true },
    { name: "exe_cooking_utensils", visible: true },

    { name: "exe_creator_door_office_double", visible: true },
    { name: "exe_creator_door_wooden_1", visible: true },

    { name: "exe_printing_3d_printing_station", visible: true },
    { name: "exe_printing_cabinet_filament_abs_ptg", visible: true },
    { name: "exe_printing_cabinet_filament_pla_tpu", visible: true },
    { name: "exe_printing_cleaning_set", visible: true },
    { name: "exe_printing_examples", visible: true },

    { name: "exe_wd_22bolt_22nut_impactdriver", visible: true },
    { name: "exe_wd_22nut_impactdriver", visible: true },
    { name: "exe_wd_combined_exercises", visible: true },
    { name: "exe_wd_drill_26plug_26screw_impactdriver", visible: true },
    { name: "exe_wd_drill_dowel_hammer", visible: true },
    { name: "exe_wd_foaming", visible: true },
    { name: "exe_wd_garage_gate_installation", visible: true },
    { name: "exe_wd_garage_gate_supply", visible: true },
    { name: "exe_wd_interior_door_installation", visible: true },
    { name: "exe_wd_interior_door_supply", visible: true },
    { name: "exe_wd_leveler", visible: true },
    { name: "exe_wd_outer_door_installation", visible: true },
    { name: "exe_wd_outer_door_supply", visible: true },
    { name: "exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill", visible: true },
    { name: "exe_wd_roof_window_installation", visible: true },
    { name: "exe_wd_roof_window_supply", visible: true },
    { name: "exe_wd_strut", visible: true },
    { name: "exe_wd_technical_door_installation", visible: true },
    { name: "exe_wd_technical_door_supply", visible: true },
    { name: "exe_wd_tool_rack_1", visible: true },
    { name: "exe_wd_tool_rack_boxes", visible: true },
    { name: "exe_wd_vertical_window_installation", visible: true },
    { name: "exe_wd_vertical_window_supply", visible: true },
    { name: "exe_wd_wedge_hammer", visible: true },

    { name: "elevator_1x2.1x", visible: true },
];

export default function MapEditor({ selectedScenario, changeScenarioRef }) {
    const mountRef = useRef(null);
    const viewportRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const controlsRef = useRef(null);
    const firstPersonActiveRef = useRef(false);
    const planeRef = useRef(null);
    const objectsRef = useRef([]);
    const modelTemplates = useRef({});
    const selectedObjectRef = useRef(null);
    const uiClickHandlerRef = useRef(null);
    const rotationDirRef = useRef(0);
    const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
    const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
    const [gridVersion, setGridVersion] = useState(0);
    const [height, setHeight] = useState(0);
    const [boardText, setBoardText] = useState("");
    const [boardMode, setBoardMode] = useState("move");

    const highlighterRef = useRef(null);

    const assetsTokenRef = useRef(null);
    const bootstrapPromiseRef = useRef(null);

    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [selectedObject, setSelectedObject] = useState(null);
    const [gridWalls, setGridWalls] = useState(true);



    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertSeverity, setAlertSeverity] = useState("success");

    const [modelsReadyVersion, setModelsReadyVersion] = useState(0);

    // const [MODEL_LIBRARY, setModelLibrary] = useState([]);


    const round3 = (num) => Math.round(num * 1000) / 1000;
    const radToDeg = (rad) => rad * (180 / Math.PI);
    const degToRad = (deg) => (deg * Math.PI) / 180;
    const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


    const { accessToken: token } = useSelector((state) => state.auth);

    async function assetsBootstrap() {
        const res = await fetch(
            // `${process.env.REACT_APP_API_URL_VR_EDITOR}/assets-vr/bootstrap?env=webe`,
            `https://beta.vccsystem.eu:3002/assets-vr/bootstrap?env=webe`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        console.log("assets bootstrap response:", res);
        if (!res.ok) {
            throw new Error("Bootstrap failed");
        }

        const data = await res.json();



        assetsTokenRef.current = data.token;
    }



    useEffect(() => {
        const init = async () => {
            if (!bootstrapPromiseRef.current) bootstrapPromiseRef.current = assetsBootstrap()
                .catch(error => { bootstrapPromiseRef.current = null; throw error; });
            await bootstrapPromiseRef.current;

            const arrowConfig = MODEL_LIBRARY.find(
                m => m.name === "arrow"
            );

            if (!arrowConfig) return;
            try {
                console.log("arrowConfig.name", arrowConfig.name)
                const tpl = await loadModel(arrowConfig.name);
                modelTemplates.current["arrow"] = tpl;
                setModelsReadyVersion(v => v + 1);
            } catch (error) {
                console.error("Nie udało się załadować arrow:", error);
                setAlertMessage(`Nie udało się załadować strzałek: ${error.message}`);
                setAlertSeverity("warning");
                setAlertOpen(true);
            }
        };
        init().catch(error => {
            setAlertMessage(`Błąd ładowania zasobów: ${error.message}`);
            setAlertSeverity("error");
            setAlertOpen(true);
        });
    }, [MODEL_LIBRARY]);


    // -------------------- GRID UPDATE --------------------

    useEffect(() => {
        if (!selectedScenario) return;
        setBoardSize({
            dimX: selectedScenario.dimensionX || 18,
            dimZ: selectedScenario.dimensionZ || 18
        });
        setGridVersion(v => v + 1);
    }, [selectedScenario?.id]);

    const modelCache = useRef({});
    const modelPromises = useRef({});

    const loadModel = async (name) => {
        if (name !== "whiteboard" && !assetsTokenRef.current) {
            if (!bootstrapPromiseRef.current) bootstrapPromiseRef.current = assetsBootstrap()
                .catch(error => { bootstrapPromiseRef.current = null; throw error; });
            await bootstrapPromiseRef.current;
        }
        return loadModelAsset(name);
    };
    const loadModelAsset = (name) => {
        if (name === "whiteboard") {
            if (!modelPromises.current[name]) {
                modelPromises.current[name] = new GLTFLoader().loadAsync("/whiteboard/scene.gltf")
                    .then(gltf => {
                        modelTemplates.current[name] = gltf.scene;
                        return gltf.scene;
                    }).catch(error => { delete modelPromises.current[name]; throw error; });
            }
            return modelPromises.current[name];
        }

        if (modelCache.current[name]) {
            return Promise.resolve(modelCache.current[name]);
        }

        if (modelPromises.current[name]) {
            return modelPromises.current[name];
        }


        const promise = new Promise((resolve, reject) => {

            console.log("Loading model:", name);


            const baseUrl =
                `https://assets.micree.eu/webe/${encodeURIComponent(name)}`;


            const manager = new THREE.LoadingManager();


            // manager.setURLModifier((url) => {

            //     if (/^(data:|blob:)/i.test(url)) return url;
            //     const resolved = new URL(url, `${baseUrl}/`);
            //     if (resolved.origin === new URL(baseUrl).origin) {
            //         const tokenQuery = String(assetsTokenRef.current || "").replace(/^[?&]/, "");
            //         new URLSearchParams(tokenQuery).forEach((value, key) => {
            //             resolved.searchParams.set(key, value);
            //         });
            //     }

            //     console.log('resolved.href', resolved.href)

            //     return resolved.href;
            // });

            manager.setURLModifier((url) => {
                if (/^(data:|blob:)/i.test(url)) {
                    return url;
                }

                const resolved = new URL(url, `${baseUrl}/`);

                if (resolved.origin === new URL(baseUrl).origin) {
                    let tokenQuery = String(assetsTokenRef.current || "");

                    tokenQuery = tokenQuery.replace(/^[?&]/, "");

                    if (tokenQuery) {
                        resolved.search = resolved.search
                            ? `${resolved.search}&${tokenQuery}`
                            : `?${tokenQuery}`;
                    }
                }



                return resolved.href;
            });


            const loader = new GLTFLoader(manager);


            const url =
                `${baseUrl}/${encodeURIComponent(name)}.gltf`;


            console.log("GLTF URL:", url);


            const onLoaded = (gltf) => {
                if (!gltf.scene) {
                    onError(new Error("Model nie zawiera sceny"));
                    return;
                }
                modelCache.current[name] = gltf.scene;
                modelTemplates.current[name] = gltf.scene;
                delete modelPromises.current[name];
                resolve(gltf.scene);
            };
            const onError = (error) => {
                delete modelPromises.current[name];
                console.error("GLTF failed:", name, error);
                reject(new Error(
                    `Model ${name}: ${error.message || String(error)} (URL: ${url})`
                ));
            };

            if (name === "arrow") {
                (async () => {
                    const response = await fetch(manager.resolveURL(url), { cache: "no-store" });
                    if (!response.ok) throw new Error(`arrow.gltf: HTTP ${response.status}`);
                    const json = await response.json();
                    if (!json || typeof json !== "object") throw new Error("arrow.gltf: nieprawidłowy JSON");
                    if (!Array.isArray(json.buffers) || json.buffers.length === 0) {
                        const views = json.bufferViews;
                        if (!Array.isArray(views) || !views.length || views.some(view =>
                            view.buffer !== 0 || !Number.isInteger(view.byteLength) || view.byteLength < 0 ||
                            !Number.isInteger(view.byteOffset ?? 0) || (view.byteOffset ?? 0) < 0
                        )) throw new Error("arrow.gltf: nieobsługiwana struktura bufferViews");
                        const requiredBytes = Math.max(...views.map(view => (view.byteOffset || 0) + view.byteLength));
                        if (requiredBytes !== 19392) throw new Error("arrow.gltf: inna geometria niż oczekiwany scene.bin");
                        json.buffers = [{ uri: "scene.bin", byteLength: 19392 }];
                    }
                    return new Promise((resolveParsed, rejectParsed) => {
                        loader.parse(JSON.stringify(json), `${baseUrl}/`, resolveParsed, rejectParsed);
                    });
                })().then(onLoaded).catch(onError);
            } else {
                loader.load(url, onLoaded, undefined, onError);
            }

        });


        modelPromises.current[name] = promise;

        return promise;
    };

    const getObjectBox = (obj) => {
        obj.updateWorldMatrix(true, true);
        return new THREE.Box3().setFromObject(obj);
    };
    const syncPosition = (obj) => {
        obj.userData.gridX = obj.position.x / GRID_SIZE;
        obj.userData.gridY = (obj.position.y - (obj.userData.heightOffset || 0)) / GRID_SIZE;
        obj.userData.gridZ = -boardSizeRef.current.dimZ - obj.position.z / GRID_SIZE;
        if (selectedObjectRef.current === obj) setHeight(round3(obj.userData.gridY));
    };
    const overlaps = (a, b) => ["x", "y", "z"].every(axis =>
        Math.min(a.max[axis], b.max[axis]) - Math.max(a.min[axis], b.min[axis]) > 0.001
    );
    const overlapsXZ = (a, b) => ["x", "z"].every(axis =>
        Math.min(a.max[axis], b.max[axis]) - Math.max(a.min[axis], b.min[axis]) > 0.001
    );

    const moveOnSurfaces = (obj, worldX, worldZ) => {
        highlighterRef.current?.clear?.();
        const previous = obj.position.clone();
        const oldBox = getObjectBox(obj);
        if (oldBox.isEmpty()) return false;
        const offset = previous.y - oldBox.min.y;
        obj.position.x = worldX;
        obj.position.z = worldZ;
        const candidateBox = getObjectBox(obj);
        let supportY = 0;
        for (const other of objectsRef.current) {
            if (other === obj || !other.userData?.isModel) continue;
            const box = getObjectBox(other);
            if (!box.isEmpty() && overlapsXZ(candidateBox, box)) {
                supportY = Math.max(supportY, box.max.y);
            }
        }
        obj.position.y = offset + supportY;
        if (checkCollision(obj)) {
            obj.position.copy(previous);
            obj.updateWorldMatrix(true, true);
            return false;
        }
        obj.userData.heightOffset = offset;
        finishPlacement(obj);
        return true;
    };
    const finishPlacement = (obj) => {
        syncPosition(obj);
        obj.updateWorldMatrix(true, true);
        removeRadialMenu();
        highlighterRef.current?.update?.();
    };
    const changeHeight = (value) => {
        const obj = selectedObjectRef.current;
        if (!obj || obj.userData.mode === "write" || !Number.isFinite(value)) return;
        highlighterRef.current?.clear?.();
        const box = getObjectBox(obj);
        if (box.isEmpty()) return;
        const oldY = obj.position.y;
        obj.userData.heightOffset = oldY - box.min.y;
        let delta = Math.max(0, value) * GRID_SIZE - box.min.y;
        const obstacles = objectsRef.current.filter(other => other !== obj && other.userData.isModel);
        sceneRef.current?.children.filter(other => other.userData?.isWall)
            .forEach(wall => obstacles.push(wall));
        for (const other of obstacles) {
            const obstacle = getObjectBox(other);
            if (obstacle.isEmpty() || !overlapsXZ(box, obstacle)) continue;

            if (delta < 0 && obstacle.max.y <= box.min.y + 0.001) {
                delta = Math.max(delta, Math.min(0, obstacle.max.y - box.min.y));
            } else if (delta > 0 && obstacle.min.y >= box.max.y - 0.001) {
                delta = Math.min(delta, Math.max(0, obstacle.min.y - box.max.y));
            }
        }
        obj.position.y += delta;
        if (checkCollision(obj)) obj.position.y = oldY;
        finishPlacement(obj);
    };
    const placeOnSupport = () => {
        const obj = selectedObjectRef.current;
        if (!obj || obj.userData.mode === "write") return;
        changeHeight(0);
    };
    const paintBoard = (model, text) => {
        const surface = model.getObjectByName("Object001_0");
        const data = surface?.userData?.boardSurface;
        if (!data) return;
        model.userData.boardText = text;
        const { canvas, ctx, texture } = data;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#111111";
        ctx.font = "40px Arial";
        ctx.textBaseline = "top";
        let y = 32;
        for (const paragraph of text.split("\n")) {
            let line = "";
            for (const char of paragraph) {
                if (ctx.measureText(line + char).width > canvas.width - 64) {
                    ctx.fillText(line, 32, y); y += 52; line = "";
                }
                line += char;
            }
            ctx.fillText(line, 32, y); y += 52;
        }
        texture.needsUpdate = true;
    };
    const initBoard = (model, text = "") => {
        const surface = model.getObjectByName("Object001_0");
        if (!surface?.isMesh) throw new Error("Whiteboard: brak siatki Object001_0");
        const canvas = document.createElement("canvas");
        canvas.width = 1024; canvas.height = 512;
        const ctx = canvas.getContext("2d");
        const texture = new THREE.CanvasTexture(canvas);
        texture.flipY = true;
        texture.wrapS = THREE.RepeatWrapping;
        texture.repeat.x = -1;
        texture.offset.x = 1;
        texture.colorSpace = THREE.SRGBColorSpace;
        const makeMaterial = original => {
            const material = original.clone();
            material.map = texture;
            material.color.set(0xffffff);
            material.needsUpdate = true;
            return material;
        };
        surface.material = Array.isArray(surface.material)
            ? surface.material.map(makeMaterial) : makeMaterial(surface.material);
        Object.defineProperty(surface.userData, "boardSurface", {
            value: { canvas, ctx, texture }, configurable: true, enumerable: false
        });
        model.userData.isWritableBoard = true;
        model.userData.mode = "move";
        paintBoard(model, text);
    };
    const selectObject = (obj) => {
        selectedObjectRef.current = obj;
        setSelectedObject(obj);
        setHeight(round3(obj.userData.gridY || 0));
        setBoardText(obj.userData.boardText || "");
        setBoardMode(obj.userData.mode || "move");
    };
    const toggleBoard = (obj) => {
        obj.userData.mode = obj.userData.mode === "write" ? "move" : "write";
        obj.userData.isMovable = obj.userData.mode !== "write";
        rotationDirRef.current = 0;
        setBoardMode(obj.userData.mode);
        highlighterRef.current?.clear?.();
        createRadialMenu(obj);
    };

    const checkCollision = (obj) => {
        if (!sceneRef.current) return false;

        const objBox = getObjectBox(obj);

        const walls = sceneRef.current.children.filter(o => o.userData?.isWall);

        for (const wall of walls) {
            const wallBox = new THREE.Box3().setFromObject(wall);
            if (overlaps(objBox, wallBox)) return true;
        }

        for (const other of objectsRef.current) {
            if (!other.userData?.isModel) continue;
            if (other === obj) continue;

            const otherBox = getObjectBox(other);
            if (overlaps(objBox, otherBox)) return true;
        }

        return false;
    };

    // -------------------- INIT SCENE --------------------
    useEffect(() => {
        if (!mountRef.current) return;

        const { dimX, dimZ } = boardSizeRef.current;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf0f0f0);
        sceneRef.current = scene;

        const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

        const camera = new THREE.PerspectiveCamera(
            45,
            window.innerWidth / window.innerHeight,
            1,
            10000
        );
        camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
        camera.lookAt(worldOffset.x, 0, worldOffset.z);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        // renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        Object.assign(renderer.domElement.style, {
            display: "block", width: "100%", height: "100%", touchAction: "none",
        });
        mountRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        const viewport = viewportRef.current;
        let lastWidth = 0;
        let lastHeight = 0;
        const resizeScene = () => {
            if (!viewport || !mountRef.current) return;

            const top = Math.max(0, viewport.getBoundingClientRect().top + window.scrollY);
            const availableHeight = Math.max(1, Math.floor(
                (window.visualViewport?.height ?? window.innerHeight) - top
            ));
            const cssHeight = `${availableHeight}px`;
            if (viewport.style.height !== cssHeight) viewport.style.height = cssHeight;
            const width = Math.max(1, Math.floor(mountRef.current.clientWidth));
            const height = Math.max(1, Math.floor(mountRef.current.clientHeight));
            if (width === lastWidth && height === lastHeight) return;
            lastWidth = width;
            lastHeight = height;
            renderer.setSize(width, height, false);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
        };
        resizeScene();
        const resizeObserver = new ResizeObserver(resizeScene);
        resizeObserver.observe(viewport);
        if (viewport.parentElement) resizeObserver.observe(viewport.parentElement);
        document.querySelectorAll(".MuiAppBar-root").forEach(bar => resizeObserver.observe(bar));
        window.addEventListener("resize", resizeScene);
        window.visualViewport?.addEventListener("resize", resizeScene);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.maxPolarAngle = Math.PI / 2.05;
        controls.target.set(worldOffset.x, 0, worldOffset.z);
        controls.update();
        controlsRef.current = controls;

        scene.add(new THREE.AmbientLight(0xffffff, 1.5));
        const dirLight = new THREE.DirectionalLight(0xffffff, 2);
        dirLight.position.set(300, 400, 200);
        scene.add(dirLight);

        const raycaster = new THREE.Raycaster();
        const pointer = new THREE.Vector2();
        let isDragging = false;
        const grabOffset = new THREE.Vector3();
        const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        // let highlight = null;

        // const highlighter = new ObjectHighlighter(scene, {
        //     color: 0x4da3ff,
        //     tintStrength: 0.35,
        //     auraScale: 1.06,
        //     auraOpacity: 0.25
        // });

        highlighterRef.current = new ObjectHighlighter(scene, {
            color: 0x4da3ff,
            tintStrength: 0.35,
            auraScale: 1.06,
            auraOpacity: 0.25
        });


        const updatePointer = (e) => {
            if (!rendererRef.current) return;
            const rect = rendererRef.current.domElement.getBoundingClientRect();
            pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        };

        const highlightObject = (obj) => {
            // highlighter.highlightObject(obj);
            highlighterRef.current?.highlightObject?.(obj);
        };

        const clearHighlight = () => {
            // highlighter.clear();
            highlighterRef.current?.clear?.();
        };

        const onDown = (e) => {
            if (firstPersonActiveRef.current) return;
            if (e.button !== 0) return;
            if (uiClickHandlerRef.current?.(e)) return;

            updatePointer(e);
            raycaster.setFromCamera(pointer, camera);

            const intersects = raycaster.intersectObjects(
                sceneRef.current.children,
                true
            );

            for (const i of intersects) {
                if (i.object.userData?.isRadialButton) {
                    i.object.userData.onClick();
                    return;
                }
            }

            const modelHits = raycaster.intersectObjects(objectsRef.current.filter(o => o.userData.isModel), true);


            let hitModel = modelHits[0]?.object;
            while (hitModel && !objectsRef.current.includes(hitModel)) hitModel = hitModel.parent;

            if (!hitModel) {
                selectedObjectRef.current = null;
                setSelectedObject(null);
                clearHighlight();
                removeRadialMenu();
                return;
            }

            removeRadialMenu();

            selectObject(hitModel);
            if (!hitModel.userData.isWritableBoard) highlightObject(hitModel);
            else clearHighlight();

            createRadialMenu(hitModel);

            if (hitModel.userData.mode === "write") return;
            isDragging = true;
            const grabPoint = new THREE.Vector3();
            dragPlane.constant = -hitModel.position.y;
            grabOffset.set(0, 0, 0);
            if (raycaster.ray.intersectPlane(dragPlane, grabPoint)) grabOffset.copy(hitModel.position).sub(grabPoint);
            renderer.domElement.setPointerCapture(e.pointerId);
            controls.enabled = false;
        };




        // Przesuwanie płynne

        const onMove = (e) => {
            if (firstPersonActiveRef.current) { isDragging = false; return; }
            if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

            const obj = selectedObjectRef.current;

            if (obj.userData.radialMenu) {
                sceneRef.current.remove(obj.userData.radialMenu);
                obj.userData.radialMenu = null;
            }

            updatePointer(e);
            raycaster.setFromCamera(pointer, camera);

            const p = new THREE.Vector3();
            if (!raycaster.ray.intersectPlane(dragPlane, p)) return;
            const { dimX, dimZ } = boardSizeRef.current;

            const maxZ = dimZ;

            let worldX = p.x + grabOffset.x;
            let worldZ = p.z + grabOffset.z;

            const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
            const maxDepth = maxZ * GRID_SIZE;

            worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
            worldZ = Math.max(-maxDepth, Math.min(0, worldZ));



            if (!moveOnSurfaces(obj, worldX, worldZ)) return;


            // highlighter?.update?.();
            highlighterRef.current?.update?.();
        };


        const onUp = () => {
            rotationDirRef.current = 0;
            isDragging = false;
            controls.enabled = !firstPersonActiveRef.current;
        };

        renderer.domElement.addEventListener("pointerdown", onDown);
        renderer.domElement.addEventListener("pointermove", onMove);
        renderer.domElement.addEventListener("pointerup", onUp);

        renderer.domElement.addEventListener("pointercancel", onUp);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("blur", onUp);
        let frame;
        const animate = () => {
            frame = requestAnimationFrame(animate);


            // highlighter?.update?.();
            highlighterRef.current?.update?.();
            if (!firstPersonActiveRef.current && selectedObjectRef.current && rotationDirRef.current) {
                rotateSelected(0.02 * rotationDirRef.current);
                // highlight?.update?.();
            }

            if (!firstPersonActiveRef.current) controls.update();
            renderer.render(scene, camera);
        };

        animate();

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            window.removeEventListener("resize", resizeScene);
            window.visualViewport?.removeEventListener("resize", resizeScene);
            renderer.domElement.removeEventListener("pointerdown", onDown);
            renderer.domElement.removeEventListener("pointermove", onMove);
            renderer.domElement.removeEventListener("pointerup", onUp);
            renderer.domElement.removeEventListener("pointercancel", onUp);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("blur", onUp);
            highlighterRef.current?.clear?.();
            controls.dispose();
            renderer.dispose();
            renderer.domElement.remove();
        };
    }, []);

    // -------------------- GRID + PLANE --------------------
    const createRectGrid = (width, height, gridSize, color = 0x808080) => {
        const material = new THREE.LineBasicMaterial({ color });
        const geometry = new THREE.BufferGeometry();
        const vertices = [];

        const halfWidth = width / 2;

        for (let i = 0; i <= width; i++) {
            const x = (i - halfWidth) * gridSize;
            vertices.push(x, 0, 0, x, 0, -height * gridSize);
        }

        for (let i = 0; i <= height; i++) {
            const z = -i * gridSize;
            vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
        }

        geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
        const grid = new THREE.LineSegments(geometry, material);
        grid.userData.isGrid = true;
        return grid;
    };

    useEffect(() => {
        if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

        const dimX = selectedScenario?.dimensionX || 18;
        const dimZ = selectedScenario?.dimensionZ || 18;
        boardSizeRef.current.dimX = dimX;
        boardSizeRef.current.dimZ = dimZ;

        removeRadialMenu();
        highlighterRef.current?.clear?.();
        // usuń poprzedni grid i plane
        sceneRef.current.children
            .filter((o) => o.userData?.isGrid)
            .forEach((o) => sceneRef.current.remove(o));

        if (planeRef.current) {
            sceneRef.current.remove(planeRef.current);
            planeRef.current.geometry.dispose();
        }

        objectsRef.current
            .filter((o) => o.userData?.isModel)
            .forEach((o) => sceneRef.current.remove(o));

        const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

        const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
        sceneRef.current.add(grid);

        const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
        planeGeo.rotateX(-Math.PI / 2);
        const plane = new THREE.Mesh(
            planeGeo,
            new THREE.MeshBasicMaterial({ visible: false })
        );
        plane.position.set(worldOffset.x, 0, worldOffset.z);
        sceneRef.current.add(plane);
        planeRef.current = plane;
        objectsRef.current = [plane];

        // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
        cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
        cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
        controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
        controlsRef.current.update();

        selectedObjectRef.current = null;
        setSelectedObject(null);
    }, [selectedScenario?.id]);



    useEffect(() => {
        if (!sceneRef.current || !selectedScenario?.entities) return;

        let cancelled = false;
        const loadedInstances = [];

        const loadEntities = async () => {
            const { dimZ } = boardSizeRef.current;

            const worldOffsetZ = -(dimZ) * GRID_SIZE;
            const worldOffsetX = 0;

            const UNITY_Y_OFFSET = 180;


            // grupujemy encje po modelu
            const entitiesByModel = selectedScenario.entities.reduce((acc, e) => {
                if (!acc[e.name]) acc[e.name] = [];
                acc[e.name].push(e);
                return acc;
            }, {});


            const loadModelEntities = async (modelName, entities) => {

                try {
                    console.log("Loading model group:", modelName);


                    const tpl = await loadModel(modelName);

                    if (!tpl || cancelled) return;


                    modelTemplates.current[modelName] = tpl;


                    for (const e of entities) {

                        if (cancelled) return;


                        const model = SkeletonUtils.clone(tpl);

                        model.name = e.name;
                        model.scale.setScalar(modelName === "whiteboard" ? 7 : 50);
                        model.userData.isModel = true;


                        const box = new THREE.Box3()
                            .setFromObject(model);

                        model.userData.heightOffset = -box.min.y;


                        const gridX = e.posX ?? 0;
                        const gridZ = e.posZ ?? 0;

                        model.userData.gridX = gridX;
                        model.userData.gridZ = gridZ;


                        const worldX = gridX * GRID_SIZE + worldOffsetX;
                        const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;


                        model.position.set(
                            worldX,
                            model.userData.heightOffset,
                            worldZ
                        );


                        const baseQuat = tpl.quaternion.clone();


                        const userQuat = new THREE.Quaternion()
                            .setFromEuler(
                                new THREE.Euler(
                                    degToRad(e.rotX || 0),
                                    degToRad(UNITY_Y_OFFSET - (e.rotY || 0)),
                                    degToRad(e.rotZ || 0),
                                    "YXZ"
                                )
                            );


                        model.quaternion
                            .copy(baseQuat)
                            .multiply(userQuat);


                        model.position.y = 0;
                        model.userData.heightOffset = -getObjectBox(model).min.y;
                        model.position.y = model.userData.heightOffset + (Number(e.posY) || 0) * GRID_SIZE;
                        syncPosition(model);
                        if (modelName === "whiteboard") initBoard(model, e.boardText || "");
                        sceneRef.current.add(model);

                        objectsRef.current.push(model);
                        loadedInstances.push(model);


                        console.log(
                            "Added:",
                            model.name
                        );
                    }


                } catch (err) {
                    console.error(
                        "Failed model group:",
                        modelName,
                        err
                    );
                }
            };


            Object.entries(entitiesByModel)
                .forEach(([name, entities]) => {
                    loadModelEntities(name, entities);
                });

        };

        loadEntities();

        return () => {
            cancelled = true;
            loadedInstances.forEach(model => model.removeFromParent());
            objectsRef.current = objectsRef.current.filter(model => !loadedInstances.includes(model));
            if (loadedInstances.includes(selectedObjectRef.current)) {
                removeRadialMenu();
                highlighterRef.current?.clear?.();
                selectedObjectRef.current = null;
                setSelectedObject(null);
            }
        };

    }, [selectedScenario]);

    // useEffect(() => {
    //     if (!selectedScenario?.entities) return;

    //     const uniqueModels = [...new Set(
    //         selectedScenario.entities.map(e => e.name)
    //     )];

    //     uniqueModels.forEach(name => {
    //         const config = MODEL_LIBRARY.find(m => m.name === name);

    //         if (config) {
    //             loadModel(name, config.path).then(tpl => {
    //                 modelTemplates.current[name] = tpl;

    //                 setModelsReadyVersion(v => v + 1);
    //             });
    //         }
    //     });
    // }, [selectedScenario]);

    // -------------------- ADD / DELETE --------------------
    const addModel = async (name) => {
        const config = MODEL_LIBRARY.find(m => m.name === name);
        if (!config) return;

        // const tpl = modelTemplates.current[name];

        console.log("config2121:", config);

        const sceneTpl = await loadModel(name);
        if (!sceneTpl) return;


        const model = SkeletonUtils.clone(sceneTpl);
        model.name = name;
        model.scale.setScalar(name === "whiteboard" ? 7 : 50);
        modelTemplates.current[name] = sceneTpl;
        model.userData.isModel = true;

        const box = new THREE.Box3().setFromObject(model);
        model.userData.heightOffset = -box.min.y;

        // model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
        // model.userData.gridZ = 0;

        // Centrowanie
        model.userData.gridX = 0
        model.userData.gridZ = -Math.floor((selectedScenario?.dimensionZ || 18) / 2);

        const worldX = model.userData.gridX * GRID_SIZE;
        const worldZ = model.userData.gridZ * GRID_SIZE;
        model.position.set(worldX, model.userData.heightOffset, worldZ);

        const baseQuat = sceneTpl.quaternion.clone();
        const userQuat = new THREE.Quaternion().setFromEuler(
            new THREE.Euler(0, degToRad(UNITY_Y_OFFSET), 0, "YXZ")
        );

        model.quaternion.copy(baseQuat).multiply(userQuat);

        model.position.y = 0;
        model.userData.heightOffset = -getObjectBox(model).min.y;
        model.position.y = model.userData.heightOffset;
        syncPosition(model);
        if (name === "whiteboard") initBoard(model);
        sceneRef.current.add(model);
        objectsRef.current.push(model);

        if (selectedObjectRef.current) {
            removeRadialMenu(selectedObjectRef.current);
        }

        selectObject(model);
        createRadialMenu(model);
    };


    const deleteSelected = () => {
        if (!selectedObjectRef.current) return;
        removeRadialMenu();
        highlighterRef.current?.clear?.();
        sceneRef.current.remove(selectedObjectRef.current);
        objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
        selectedObjectRef.current = null;
        setSelectedObject(null);
    };

    const duplicateSelected = () => {
        const selected = selectedObjectRef.current;
        if (!selected) return;

        highlighterRef.current?.clear?.();
        highlighterRef.current?.unhighlight?.(selected);

        selected.traverse((child) => {
            if (child.isMesh && child.userData?.originalColor) {
                child.material.color.copy(child.userData.originalColor);
            }
        });

        removeRadialMenu();
        const copy = SkeletonUtils.clone(selected);

        copy.position.x += 10;
        copy.position.z += 10;

        copy.userData = { ...selected.userData, radialMenu: null };
        syncPosition(copy);
        if (copy.userData.isWritableBoard) initBoard(copy, selected.userData.boardText || "");

        sceneRef.current.add(copy);

        objectsRef.current.push(copy);

        selectObject(copy);
        createRadialMenu(copy);
    };


    const saveScenario = async () => {
        if (!selectedScenario) return;

        const entities = objectsRef.current
            .filter(o => o.userData.isModel)
            .map(o => {
                const tpl = modelTemplates.current[o.name];
                const baseQuat = tpl?.quaternion.clone() || new THREE.Quaternion();
                const baseInv = baseQuat.clone().invert();

                const userQuat = o.quaternion.clone().premultiply(baseInv);

                const euler = new THREE.Euler().setFromQuaternion(userQuat, "YXZ");

                const rotX = normalizeDeg(radToDeg(euler.x));
                const rotY = normalizeDeg(UNITY_Y_OFFSET - normalizeDeg(radToDeg(euler.y))); // flip Y do VR
                const rotZ = normalizeDeg(radToDeg(euler.z));

                return {
                    name: o.name,
                    GUID: crypto.randomUUID(),
                    posX: round3(o.position.x / GRID_SIZE),
                    posY: (o.position.y - (o.userData.heightOffset || 0)) / GRID_SIZE,
                    posZ: round3(-boardSizeRef.current.dimZ - o.position.z / GRID_SIZE),
                    ...(o.userData.isWritableBoard ? { boardText: o.userData.boardText || "" } : {}),
                    rotX,
                    rotY,
                    rotZ,
                };
            });

        // console.log('entities to save:', entities);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        stationName: selectedScenario.stationName,
                        GUID: crypto.randomUUID(),
                        dimensionX: selectedScenario.dimensionX,
                        dimensionY: selectedScenario.dimensionY,
                        entities,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Nie udało się zapisać stacji");
            }

            setAlertMessage("Stacja zapisana pomyślnie!");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (error) {
            console.error(error);
            setAlertMessage(`Błąd: ${error.message}`);
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };


    const rotateSelected = (angle) => {
        if (firstPersonActiveRef.current) return;
        const obj = selectedObjectRef.current;
        if (!obj || obj.userData.mode === "write") return;
        const previous = obj.quaternion.clone();
        obj.rotation.y += angle;
        if (checkCollision(obj)) obj.quaternion.copy(previous);
        highlighterRef.current?.update?.();
    };
    const rotate90Right = () => rotateSelected(-Math.PI / 2);
    const rotate90Left = () => rotateSelected(Math.PI / 2);

    const textureLoader = new THREE.TextureLoader();


    const createRadialMenu = (object) => {
        if (!sceneRef.current) return;

        if (object.userData.radialMenu) {
            sceneRef.current.remove(object.userData.radialMenu);
            object.userData.radialMenu = null;
        }

        const radius = 120;
        const group = new THREE.Group();
        group.userData.isRadialMenu = true;

        const actions = [
            { fn: rotate90Left, icon: "/icons/rotateleft.png" },
            { fn: rotate90Right, icon: "/icons/rotateright.png" },
            { fn: deleteSelected, icon: "/icons/delete.png" },
            { fn: duplicateSelected, icon: "/icons/copy.png" }
        ];

        // ======= DODANIE PRZYCISKU PISZ DLA TABLICY =======
        if (object.userData.isWritableBoard) {
            actions.push({
                fn: () => toggleBoard(object),
                icon: "/icons/edit.svg",
                isWriteButton: true
            });
        }

        actions.forEach((action, i) => {
            const angle = (i / actions.length) * Math.PI * 2;

            const geometry = new THREE.CircleGeometry(20, 32);
            const material = new THREE.MeshBasicMaterial({
                color: action.isWriteButton
                    ? object.userData.mode === "write"
                        ? 0xffff00
                        : 0x6cc6d9
                    : 0x6cc6d9,
                depthTest: false
            });

            const mesh = new THREE.Mesh(geometry, material);
            mesh.rotation.x = -Math.PI / 2;
            mesh.position.set(
                Math.cos(angle) * radius,
                1,
                Math.sin(angle) * radius
            );

            const texture = textureLoader.load(action.icon);
            const iconGeometry = new THREE.PlaneGeometry(30, 30);
            const iconMaterial = new THREE.MeshBasicMaterial({
                map: texture,
                transparent: true,
                depthTest: false,
                side: THREE.DoubleSide
            });
            const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);
            iconMesh.rotation.x = Math.PI;
            iconMesh.rotation.z = -Math.PI / 2;
            iconMesh.position.set(0, 0.1, 0);
            mesh.add(iconMesh);

            mesh.userData.onClick = action.fn;
            mesh.userData.isRadialButton = true;
            if (action.isWriteButton) mesh.userData.isWriteButton = true;

            group.add(mesh);
            object.userData.radialMenu = group;
        });

        sceneRef.current.add(group);
        group.position.copy(object.position);
        group.position.y = 1;
    };





    const removeRadialMenu = () => {
        if (!sceneRef.current) return;

        const menus = sceneRef.current.children.filter(
            o => o.userData?.isRadialMenu
        );

        menus.forEach(menu => {
            sceneRef.current.remove(menu);
            menu.traverse(child => {
                child.geometry?.dispose();
                child.material?.map?.dispose();
                child.material?.dispose();
            });
        });
        objectsRef.current.forEach(obj => { obj.userData.radialMenu = null; });
    };




    // -------------------- KEYBOARD --------------------

    useEffect(() => {
        // Przesuwanie płynne
        const moveGrid = (dx, dz) => {
            if (!selectedObjectRef.current) return;

            const obj = selectedObjectRef.current;
            const { dimX, dimZ } = boardSizeRef.current;

            const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
            const maxDepth = dimZ * GRID_SIZE;

            let worldX = obj.position.x + dx;
            let worldZ = obj.position.z + dz;

            worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
            worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

            if (!moveOnSurfaces(obj, worldX, worldZ)) return;

            removeRadialMenu();
        };



        const onKeyDown = (e) => {
            if (firstPersonActiveRef.current) return;
            if (
                e.target.tagName === "INPUT" ||
                e.target.tagName === "TEXTAREA" ||
                e.target.isContentEditable
            ) return;

            // Dodajemy blokadę ruchu w trybie write (TABLICA)
            const obj = selectedObjectRef.current;
            if (!obj) return;

            if (obj.userData.mode === "write") {
                return;
            }

            switch (e.key.toLowerCase()) {
                case "q":
                case "e":
                    e.preventDefault();
                    changeHeight((obj.position.y - (obj.userData.heightOffset || 0)) / GRID_SIZE + (e.key.toLowerCase() === "e" ? 0.1 : -0.1));
                    break;

                case "w":
                    e.preventDefault();
                    moveGrid(0, 5);
                    break;
                case "s":
                    e.preventDefault();
                    moveGrid(0, -5);
                    break;
                case "a":
                    e.preventDefault();
                    moveGrid(5, 0);
                    break;
                case "d":
                    e.preventDefault();
                    moveGrid(-5, 0);
                    break;

                case "arrowleft":
                    e.preventDefault();
                    rotate90Left();
                    break;
                case "arrowright":
                    e.preventDefault();
                    rotate90Right();
                    break;

                case "delete":
                case "backspace":
                    e.preventDefault();
                    deleteSelected();
                    break;

                case "escape":
                    selectedObjectRef.current = null;
                    setSelectedObject(null);
                    break;

                default:
                    break;
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);


    // -------------------- RENDER --------------------
    return (
        <Box ref={viewportRef} sx={{
            position: "relative", width: "100%", height: 0,
            minWidth: 0, minHeight: 0, overflow: "hidden", flexShrink: 0
        }}>
            <Paper sx={{ position: "absolute", top: 18, left: 16, width: 280, p: 2, zIndex: 10 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
                    <BackToScenariosButton />
                </Box>
                <Divider sx={{ my: 1 }} />
                {/* <Typography variant="subtitle2">Modele</Typography> */}
                <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
                {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
                    {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
                        <ListItem key={m.name} disablePadding>
                            <ListItemButton onMouseDown={() => addModel(m.name)}>
                                <ListItemText primary={m.name} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List> */}

                {selectedObject && (
                    <>
                        {selectedObject.userData.isWritableBoard && <>
                            <Button fullWidth onClick={() => toggleBoard(selectedObject)}>
                                {boardMode === "write" ? "Zakończ pisanie" : "Pisz na tablicy"}
                            </Button>
                            {boardMode === "write" && <TextField
                                label="Tekst tablicy" multiline minRows={3} fullWidth value={boardText}
                                onChange={event => {
                                    setBoardText(event.target.value);
                                    paintBoard(selectedObject, event.target.value);
                                }}
                            />}
                        </>}
                        <Stack direction="row" sx={{ justifyContent: 'center' }}>
                            <IconButton
                                onClick={rotate90Left}
                            >
                                <Rotate90DegreesCcwIcon />
                            </IconButton>
                            <IconButton
                                onMouseDown={() => (rotationDirRef.current = 1)}
                                onMouseUp={() => (rotationDirRef.current = 0)}
                            >
                                <RotateLeftIcon />
                            </IconButton>
                            <IconButton
                                onMouseDown={() => (rotationDirRef.current = -1)}
                                onMouseUp={() => (rotationDirRef.current = 0)}
                            >
                                <RotateRightIcon />
                            </IconButton>
                            <IconButton
                                onClick={rotate90Right}
                            >
                                <Rotate90DegreesCwIcon />
                            </IconButton>
                            <IconButton color="error" onClick={deleteSelected}>
                                <DeleteIcon />
                            </IconButton>
                            {/* <Button onClick={logRotation}>
                                log
                            </Button> */}
                        </Stack>
                    </>
                )}

                <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
                    Zapisz stację
                </Button>
            </Paper>

            <div ref={mountRef} style={{ position: "absolute", inset: 0, overflow: "hidden" }} />

            {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
            {gridWalls && <GridWalls
                scene={sceneRef.current}
                dimX={boardSizeRef.current.dimX}
                dimZ={boardSizeRef.current.dimZ}
                GRID_SIZE={GRID_SIZE}
                cameraRef={cameraRef}
            />}

            {sceneRef.current && cameraRef.current && (
                <>
                    <FirstPersonView
                        key={selectedScenario?.id ?? "no-scenario"}
                        cameraRef={cameraRef}
                        rendererRef={rendererRef}
                        controlsRef={controlsRef}
                        boardSizeRef={boardSizeRef}
                        activeRef={firstPersonActiveRef}
                        scenarioKey={selectedScenario?.id}
                        gridSize={GRID_SIZE}
                    />
                    <FloorLogo
                        key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
                        scene={sceneRef.current}
                        svgPath="/micree.svg"
                        GRID_SIZE={GRID_SIZE}
                        boardSizeRef={boardSizeRef.current}
                    />
                    <UI3DModelButtons
                        rendererRef={rendererRef}
                        key={`ui-${boardSize.dimX}x${boardSize.dimZ}-${modelsReadyVersion}`}
                        scene={sceneRef.current}
                        camera={cameraRef.current}
                        modelTemplates={modelTemplates.current}
                        objectsRef={objectsRef.current}
                        onClickHandlerRef={uiClickHandlerRef}
                        gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
                        gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
                        changeScenarioRef={changeScenarioRef}
                        boardSizeRef={boardSizeRef.current}
                        GRID_SIZE={GRID_SIZE}
                    />
                </>
            )}

            <Typography
                variant="h6"
                sx={{
                    mt: 1,
                    position: "absolute",
                    top: 10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "10px",
                }}
            >
                Wybrana stacja:
            </Typography>
            <Typography
                variant="h6"
                sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 30, left: "50%", transform: "translateX(-50%)" }}
            >
                {/* {selectedScenario?.existingSource} */}
                {
                    selectedScenario?.stationName
                        ? selectedScenario.stationName
                        : selectedScenario?.id !== undefined
                            ? `Stacja ${selectedScenario.id}`
                            : "nie wybrano"
                }
            </Typography>

            <IconButton
                onClick={() => setGridWalls(prev => !prev)}
                sx={{ position: 'absolute', bottom: 20, right: 20 }}
            >
                {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

            </IconButton>

            <Snackbar
                open={alertOpen}
                autoHideDuration={4000}
                onClose={() => setAlertOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{ mt: 8 }}
            >
                <Alert
                    onClose={() => setAlertOpen(false)}
                    severity={alertSeverity}
                    sx={{ width: "100%" }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>


            {/* <WallBoards
                scene={sceneRef.current}
                cameraRef={cameraRef}
                rendererRef={rendererRef}
            //   wallsRef={}
            /> */}





        </Box>
    );
}








// import React, { useEffect, useRef, useState } from "react";
// import * as THREE from "three";
// import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// import {
//     Box,
//     Button,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Typography,
//     Paper,
//     IconButton,
//     Divider,
//     Stack,
//     Snackbar,
//     Alert,
//     TextField,
// } from "@mui/material";

// import BackToScenariosButton from "./utilities/BackToScenario";
// import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// import RotateRightIcon from "@mui/icons-material/RotateRight";
// import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// import DeleteIcon from "@mui/icons-material/Delete";
// import GridOffIcon from '@mui/icons-material/GridOff';
// import GridOnIcon from '@mui/icons-material/GridOn';

// import FloorLogo from "./three/FloorLogo";
// import UI3DModelButtons from "./three/UI3DModelButtons";
// import ModelList from "./utilities/ModelList";
// import GridWalls from "./three/GridWalls";
// import { ObjectHighlighter } from "./utilities/ObjectHighlighter";
// import WallBoards from "./three/WallBoards";
// import { useSelector } from "react-redux";

// const GRID_SIZE = 50;

// const UNITY_Y_OFFSET = 180;

// const MODEL_LIBRARY = [
//     { name: "whiteboard", visible: true },
//     { name: "CC CC0 1", visible: false },
//     { name: "arrow", visible: true },

//     { name: "envp_bin_4", visible: true },
//     { name: "envp_bin_4_lids", visible: true },
//     { name: "envp_bin_lid", visible: true },

//     { name: "envp_furnitures_chair", visible: true },
//     { name: "envp_furnitures_office_chair", visible: true },

//     { name: "envp_furnitures_sofa_corner_3_2", visible: true },
//     { name: "envp_furnitures_sofa_corner_3_3", visible: true },
//     { name: "envp_furnitures_sofa_line_2", visible: true },
//     { name: "envp_furnitures_sofa_line_3", visible: true },
//     { name: "envp_furnitures_sofa_single", visible: true },
//     { name: "envp_furnitures_sofa_single_corner", visible: true },

//     { name: "envp_furnitures_table_140_80", visible: true },
//     { name: "envp_furnitures_table_160_80", visible: true },
//     { name: "envp_furnitures_table_250_120", visible: true },
//     { name: "envp_furnitures_table_400_120", visible: true },

//     { name: "envp_furnitures_table_small_100x100x100", visible: true },
//     { name: "envp_furnitures_table_small_100x100x75", visible: true },
//     { name: "envp_furnitures_table_small_125x100x100", visible: true },
//     { name: "envp_furnitures_table_small_125x100x75", visible: true },
//     { name: "envp_furnitures_table_small_75x75x100", visible: true },
//     { name: "envp_furnitures_table_small_75x75x75", visible: true },

//     { name: "envp_modular_door_frame", visible: true },
//     { name: "env_staircase_stairs", visible: true },
//     { name: "env_wall_column_05", visible: true },
//     { name: "env_wall_creator_x2_5", visible: true },

//     { name: "exe_cd_essential_items", visible: true },

//     { name: "exe_cooking_bins", visible: true },
//     { name: "exe_cooking_cookbase", visible: true },
//     { name: "exe_cooking_cook_station", visible: true },
//     { name: "exe_cooking_countertop_2", visible: true },
//     { name: "exe_cooking_countertop_4", visible: true },
//     { name: "exe_cooking_countertop_drawer", visible: true },
//     { name: "exe_cooking_device_blender", visible: true },
//     { name: "exe_cooking_device_mixer", visible: true },
//     { name: "exe_cooking_device_mixer_blender", visible: true },
//     { name: "exe_cooking_drawers_ingrediens", visible: true },
//     { name: "exe_cooking_fridge_dairy", visible: true },
//     { name: "exe_cooking_fridge_fruits", visible: true },
//     { name: "exe_cooking_fridge_meat", visible: true },
//     { name: "exe_cooking_fridge_vegetables", visible: true },
//     { name: "exe_cooking_fridges_all", visible: true },
//     { name: "exe_cooking_mech_cooker", visible: true },
//     { name: "exe_cooking_mech_furnance", visible: true },
//     { name: "exe_cooking_sink", visible: true },
//     { name: "exe_cooking_sink_hanged", visible: true },
//     { name: "exe_cooking_utensils", visible: true },

//     { name: "exe_creator_door_office_double", visible: true },
//     { name: "exe_creator_door_wooden_1", visible: true },

//     { name: "exe_printing_3d_printing_station", visible: true },
//     { name: "exe_printing_cabinet_filament_abs_ptg", visible: true },
//     { name: "exe_printing_cabinet_filament_pla_tpu", visible: true },
//     { name: "exe_printing_cleaning_set", visible: true },
//     { name: "exe_printing_examples", visible: true },

//     { name: "exe_wd_22bolt_22nut_impactdriver", visible: true },
//     { name: "exe_wd_22nut_impactdriver", visible: true },
//     { name: "exe_wd_combined_exercises", visible: true },
//     { name: "exe_wd_drill_26plug_26screw_impactdriver", visible: true },
//     { name: "exe_wd_drill_dowel_hammer", visible: true },
//     { name: "exe_wd_foaming", visible: true },
//     { name: "exe_wd_garage_gate_installation", visible: true },
//     { name: "exe_wd_garage_gate_supply", visible: true },
//     { name: "exe_wd_interior_door_installation", visible: true },
//     { name: "exe_wd_interior_door_supply", visible: true },
//     { name: "exe_wd_leveler", visible: true },
//     { name: "exe_wd_outer_door_installation", visible: true },
//     { name: "exe_wd_outer_door_supply", visible: true },
//     { name: "exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill", visible: true },
//     { name: "exe_wd_roof_window_installation", visible: true },
//     { name: "exe_wd_roof_window_supply", visible: true },
//     { name: "exe_wd_strut", visible: true },
//     { name: "exe_wd_technical_door_installation", visible: true },
//     { name: "exe_wd_technical_door_supply", visible: true },
//     { name: "exe_wd_tool_rack_1", visible: true },
//     { name: "exe_wd_tool_rack_boxes", visible: true },
//     { name: "exe_wd_vertical_window_installation", visible: true },
//     { name: "exe_wd_vertical_window_supply", visible: true },
//     { name: "exe_wd_wedge_hammer", visible: true },

//     { name: "elevator_1x2.1x", visible: true },
// ];

// export default function MapEditor({ selectedScenario, changeScenarioRef }) {
//     const mountRef = useRef(null);
//     const sceneRef = useRef(null);
//     const cameraRef = useRef(null);
//     const rendererRef = useRef(null);
//     const controlsRef = useRef(null);
//     const planeRef = useRef(null);
//     const objectsRef = useRef([]);
//     const modelTemplates = useRef({});
//     const selectedObjectRef = useRef(null);
//     const uiClickHandlerRef = useRef(null);
//     const rotationDirRef = useRef(0);
//     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
//     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
//     const [gridVersion, setGridVersion] = useState(0);
//     const [height, setHeight] = useState(0);
//     const [boardText, setBoardText] = useState("");
//     const [boardMode, setBoardMode] = useState("move");

//     const highlighterRef = useRef(null);

//     const assetsTokenRef = useRef(null);
//     const bootstrapPromiseRef = useRef(null);

//     const [modelsLoaded, setModelsLoaded] = useState(false);
//     const [selectedObject, setSelectedObject] = useState(null);
//     const [gridWalls, setGridWalls] = useState(true);



//     const [alertOpen, setAlertOpen] = useState(false);
//     const [alertMessage, setAlertMessage] = useState("");
//     const [alertSeverity, setAlertSeverity] = useState("success");

//     const [modelsReadyVersion, setModelsReadyVersion] = useState(0);

//     // const [MODEL_LIBRARY, setModelLibrary] = useState([]);


//     const round3 = (num) => Math.round(num * 1000) / 1000;
//     const radToDeg = (rad) => rad * (180 / Math.PI);
//     const degToRad = (deg) => (deg * Math.PI) / 180;
//     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


//     const { accessToken: token } = useSelector((state) => state.auth);

//     async function assetsBootstrap() {
//         const res = await fetch(
//             // `${process.env.REACT_APP_API_URL_VR_EDITOR}/assets-vr/bootstrap?env=webe`,
//             // `https://beta.vccsystem.eu:3002/assets-vr/bootstrap?env=webe`,
//             `${process.env.REACT_APP_API_URL_VR_EDITOR}/assets-vr/bootstrap?env=webe`,
//             {
//                 headers: {
//                     Authorization: `Bearer ${token}`,
//                 },
//             }
//         );
//         console.log("assets bootstrap response:", res);
//         if (!res.ok) {
//             throw new Error("Bootstrap failed");
//         }

//         const data = await res.json();

//         console.log("assets bootstrap:", data);

//         assetsTokenRef.current = data.token;
//     }



//     useEffect(() => {
//         const init = async () => {
//             if (!bootstrapPromiseRef.current) bootstrapPromiseRef.current = assetsBootstrap()
//                 .catch(error => { bootstrapPromiseRef.current = null; throw error; });
//             await bootstrapPromiseRef.current;

//             const arrowConfig = MODEL_LIBRARY.find(
//                 m => m.name === "arrow"
//             );

//             if (!arrowConfig) return;
//             try {
//                 console.log("arrowConfig.name",arrowConfig.name)
//                 const tpl = await loadModel(arrowConfig.name);
//                 modelTemplates.current["arrow"] = tpl;
//                 setModelsReadyVersion(v => v + 1);
//             } catch (error) {
//                 console.error("Nie udało się załadować arrow:", error);
//                 setAlertMessage(`Nie udało się załadować strzałek: ${error.message}`);
//                 setAlertSeverity("warning");
//                 setAlertOpen(true);
//             }
//         };
//         init().catch(error => {
//             setAlertMessage(`Błąd ładowania zasobów: ${error.message}`);
//             setAlertSeverity("error");
//             setAlertOpen(true);
//         });
//     }, [MODEL_LIBRARY]);


//     // -------------------- GRID UPDATE --------------------

//     useEffect(() => {
//         if (!selectedScenario) return;
//         setBoardSize({
//             dimX: selectedScenario.dimensionX || 18,
//             dimZ: selectedScenario.dimensionZ || 18
//         });
//         setGridVersion(v => v + 1);
//     }, [selectedScenario?.id]);

//     const modelCache = useRef({});
//     const modelPromises = useRef({});

//     const loadModel = async (name) => {
//         if (name !== "whiteboard" && !assetsTokenRef.current) {
//             if (!bootstrapPromiseRef.current) bootstrapPromiseRef.current = assetsBootstrap()
//                 .catch(error => { bootstrapPromiseRef.current = null; throw error; });
//             await bootstrapPromiseRef.current;
//         }
//         return loadModelAsset(name);
//     };
//     const loadModelAsset = (name) => {
//         if (name === "whiteboard") {
//             if (!modelPromises.current[name]) {
//                 modelPromises.current[name] = new GLTFLoader().loadAsync("/whiteboard/scene.gltf")
//                     .then(gltf => {
//                         modelTemplates.current[name] = gltf.scene;
//                         return gltf.scene;
//                     }).catch(error => { delete modelPromises.current[name]; throw error; });
//             }
//             return modelPromises.current[name];
//         }

//         if (modelCache.current[name]) {
//             return Promise.resolve(modelCache.current[name]);
//         }

//         if (modelPromises.current[name]) {
//             return modelPromises.current[name];
//         }


//         const promise = new Promise((resolve, reject) => {

//             console.log("Loading model:", name);


//             const baseUrl =
//                 `https://assets.micree.eu/webe/${encodeURIComponent(name)}`;


//             const manager = new THREE.LoadingManager();


//             // manager.setURLModifier((url) => {

//             //     if (/^(data:|blob:)/i.test(url)) return url;
//             //     const resolved = new URL(url, `${baseUrl}/`);
//             //     if (resolved.origin === new URL(baseUrl).origin) {
//             //         const tokenQuery = String(assetsTokenRef.current || "").replace(/^[?&]/, "");
//             //         new URLSearchParams(tokenQuery).forEach((value, key) => {
//             //             resolved.searchParams.set(key, value);
//             //         });
//             //     }

//             //     console.log('resolved.href', resolved.href)

//             //     return resolved.href;
//             // });

//             manager.setURLModifier((url) => {
//     if (/^(data:|blob:)/i.test(url)) {
//         return url;
//     }

//     const resolved = new URL(url, `${baseUrl}/`);

//     if (resolved.origin === new URL(baseUrl).origin) {
//         let tokenQuery = String(assetsTokenRef.current || "");

//         tokenQuery = tokenQuery.replace(/^[?&]/, "");

//         if (tokenQuery) {
//             resolved.search = resolved.search
//                 ? `${resolved.search}&${tokenQuery}`
//                 : `?${tokenQuery}`;
//         }
//     }

//     console.log("assetsTokenRef.current:", assetsTokenRef.current);

//     return resolved.href;
// });


//             const loader = new GLTFLoader(manager);


//             const url =
//                 `${baseUrl}/${encodeURIComponent(name)}.gltf`;


//             console.log("GLTF URL:", url);


//             loader.load(
//                 url,

//                 (gltf) => {

//                     console.log("Loaded model:", name);

//                     modelCache.current[name] = gltf.scene;

//                     delete modelPromises.current[name];

//                     resolve(gltf.scene);
//                 },

//                 undefined,

//                 (err) => {

//                     console.error(
//                         "GLTF failed:",
//                         name,
//                         err
//                     );

//                     delete modelPromises.current[name];

//                     reject(new Error(`Model ${name}: ${err.message || String(err)} (URL: ${url})`));

//                 }
//             );

//         });


//         modelPromises.current[name] = promise;

//         return promise;
//     };

//     const getObjectBox = (obj) => {
//         obj.updateWorldMatrix(true, true);
//         return new THREE.Box3().setFromObject(obj);
//     };
//     const syncPosition = (obj) => {
//         obj.userData.gridX = obj.position.x / GRID_SIZE;
//         obj.userData.gridY = (obj.position.y - (obj.userData.heightOffset || 0)) / GRID_SIZE;
//         obj.userData.gridZ = -boardSizeRef.current.dimZ - obj.position.z / GRID_SIZE;
//         if (selectedObjectRef.current === obj) setHeight(round3(obj.userData.gridY));
//     };
//     const overlaps = (a, b) => ["x", "y", "z"].every(axis =>
//         Math.min(a.max[axis], b.max[axis]) - Math.max(a.min[axis], b.min[axis]) > 0.001
//     );
//     const overlapsXZ = (a, b) => ["x", "z"].every(axis =>
//         Math.min(a.max[axis], b.max[axis]) - Math.max(a.min[axis], b.min[axis]) > 0.001
//     );
//     // Wspólne pozycjonowanie dla przeciągania i WASD.
//     const moveOnSurfaces = (obj, worldX, worldZ) => {
//         highlighterRef.current?.clear?.();
//         const previous = obj.position.clone();
//         const oldBox = getObjectBox(obj);
//         if (oldBox.isEmpty()) return false;
//         const offset = previous.y - oldBox.min.y;
//         obj.position.x = worldX;
//         obj.position.z = worldZ;
//         const candidateBox = getObjectBox(obj);
//         let supportY = 0;
//         for (const other of objectsRef.current) {
//             if (other === obj || !other.userData?.isModel) continue;
//             const box = getObjectBox(other);
//             if (!box.isEmpty() && overlapsXZ(candidateBox, box)) {
//                 supportY = Math.max(supportY, box.max.y);
//             }
//         }
//         obj.position.y = offset + supportY;
//         // Ściany pozostają przeszkodami; nie służą jako podstawa.
//         if (checkCollision(obj)) {
//             obj.position.copy(previous);
//             obj.updateWorldMatrix(true, true);
//             return false;
//         }
//         obj.userData.heightOffset = offset;
//         finishPlacement(obj);
//         return true;
//     };
//     const finishPlacement = (obj) => {
//         syncPosition(obj);
//         obj.updateWorldMatrix(true, true);
//         removeRadialMenu();
//         highlighterRef.current?.update?.();
//     };
//     const changeHeight = (value) => {
//         const obj = selectedObjectRef.current;
//         if (!obj || obj.userData.mode === "write" || !Number.isFinite(value)) return;
//         highlighterRef.current?.clear?.();
//         const box = getObjectBox(obj);
//         if (box.isEmpty()) return;
//         const oldY = obj.position.y;
//         // Rzeczywisty dół modelu: działa także dla przesuniętego pivotu.
//         obj.userData.heightOffset = oldY - box.min.y;
//         let delta = Math.max(0, value) * GRID_SIZE - box.min.y;
//         const obstacles = objectsRef.current.filter(other => other !== obj && other.userData.isModel);
//         sceneRef.current?.children.filter(other => other.userData?.isWall)
//             .forEach(wall => obstacles.push(wall));
//         for (const other of obstacles) {
//             const obstacle = getObjectBox(other);
//             if (obstacle.isEmpty() || !overlapsXZ(box, obstacle)) continue;
//             // Zatrzymaj na powierzchni, nawet gdy krok Y ją przekracza.
//             if (delta < 0 && obstacle.max.y <= box.min.y + 0.001) {
//                 delta = Math.max(delta, Math.min(0, obstacle.max.y - box.min.y));
//             } else if (delta > 0 && obstacle.min.y >= box.max.y - 0.001) {
//                 delta = Math.min(delta, Math.max(0, obstacle.min.y - box.max.y));
//             }
//         }
//         obj.position.y += delta;
//         if (checkCollision(obj)) obj.position.y = oldY;
//         finishPlacement(obj);
//     };
//     const placeOnSupport = () => {
//         const obj = selectedObjectRef.current;
//         if (!obj || obj.userData.mode === "write") return;
//         // changeHeight wykonuje ruch w dół do pierwszej przeszkody lub podłogi.
//         changeHeight(0);
//     };
//     const paintBoard = (model, text) => {
//         const surface = model.getObjectByName("Object001_0");
//         const data = surface?.userData?.boardSurface;
//         if (!data) return;
//         model.userData.boardText = text;
//         const { canvas, ctx, texture } = data;
//         ctx.fillStyle = "#ffffff";
//         ctx.fillRect(0, 0, canvas.width, canvas.height);
//         ctx.fillStyle = "#111111";
//         ctx.font = "40px Arial";
//         ctx.textBaseline = "top";
//         let y = 32;
//         for (const paragraph of text.split("\n")) {
//             let line = "";
//             for (const char of paragraph) {
//                 if (ctx.measureText(line + char).width > canvas.width - 64) {
//                     ctx.fillText(line, 32, y); y += 52; line = "";
//                 }
//                 line += char;
//             }
//             ctx.fillText(line, 32, y); y += 52;
//         }
//         texture.needsUpdate = true;
//     };
//     const initBoard = (model, text = "") => {
//         const surface = model.getObjectByName("Object001_0");
//         if (!surface?.isMesh) throw new Error("Whiteboard: brak siatki Object001_0");
//         const canvas = document.createElement("canvas");
//         canvas.width = 1024; canvas.height = 1024;
//         const ctx = canvas.getContext("2d");
//         const texture = new THREE.CanvasTexture(canvas);
//         texture.flipY = false;
//         texture.colorSpace = THREE.SRGBColorSpace;
//         const makeMaterial = original => {
//             const material = original.clone();
//             material.map = texture;
//             material.color.set(0xffffff);
//             material.needsUpdate = true;
//             return material;
//         };
//         surface.material = Array.isArray(surface.material)
//             ? surface.material.map(makeMaterial) : makeMaterial(surface.material);
//         Object.defineProperty(surface.userData, "boardSurface", {
//             value: { canvas, ctx, texture }, configurable: true, enumerable: false
//         });
//         model.userData.isWritableBoard = true;
//         model.userData.mode = "move";
//         paintBoard(model, text);
//     };
//     const selectObject = (obj) => {
//         selectedObjectRef.current = obj;
//         setSelectedObject(obj);
//         setHeight(round3(obj.userData.gridY || 0));
//         setBoardText(obj.userData.boardText || "");
//         setBoardMode(obj.userData.mode || "move");
//     };
//     const toggleBoard = (obj) => {
//         obj.userData.mode = obj.userData.mode === "write" ? "move" : "write";
//         obj.userData.isMovable = obj.userData.mode !== "write";
//         rotationDirRef.current = 0;
//         setBoardMode(obj.userData.mode);
//         highlighterRef.current?.clear?.();
//         createRadialMenu(obj);
//     };

//     const checkCollision = (obj) => {
//         if (!sceneRef.current) return false;

//         const objBox = getObjectBox(obj);

//         const walls = sceneRef.current.children.filter(o => o.userData?.isWall);

//         for (const wall of walls) {
//             const wallBox = new THREE.Box3().setFromObject(wall);
//             if (overlaps(objBox, wallBox)) return true;
//         }

//         for (const other of objectsRef.current) {
//             if (!other.userData?.isModel) continue;
//             if (other === obj) continue;

//             const otherBox = getObjectBox(other);
//             if (overlaps(objBox, otherBox)) return true;
//         }

//         return false;
//     };

//     // -------------------- INIT SCENE --------------------
//     useEffect(() => {
//         if (!mountRef.current) return;

//         const { dimX, dimZ } = boardSizeRef.current;

//         const scene = new THREE.Scene();
//         scene.background = new THREE.Color(0xf0f0f0);
//         sceneRef.current = scene;

//         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

//         const camera = new THREE.PerspectiveCamera(
//             45,
//             window.innerWidth / window.innerHeight,
//             1,
//             10000
//         );
//         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
//         camera.lookAt(worldOffset.x, 0, worldOffset.z);
//         cameraRef.current = camera;

//         const renderer = new THREE.WebGLRenderer({ antialias: true });
//         renderer.setSize(window.innerWidth, window.innerHeight);
//         renderer.setPixelRatio(window.devicePixelRatio);
//         mountRef.current.appendChild(renderer.domElement);
//         rendererRef.current = renderer;

//         const controls = new OrbitControls(camera, renderer.domElement);
//         controls.enableDamping = true;
//         controls.maxPolarAngle = Math.PI / 2.05;
//         controls.target.set(worldOffset.x, 0, worldOffset.z);
//         controls.update();
//         controlsRef.current = controls;

//         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
//         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
//         dirLight.position.set(300, 400, 200);
//         scene.add(dirLight);

//         const raycaster = new THREE.Raycaster();
//         const pointer = new THREE.Vector2();
//         let isDragging = false;
//         const grabOffset = new THREE.Vector3();
//         const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
//         // let highlight = null;

//         // const highlighter = new ObjectHighlighter(scene, {
//         //     color: 0x4da3ff,
//         //     tintStrength: 0.35,
//         //     auraScale: 1.06,
//         //     auraOpacity: 0.25
//         // });

//         highlighterRef.current = new ObjectHighlighter(scene, {
//             color: 0x4da3ff,
//             tintStrength: 0.35,
//             auraScale: 1.06,
//             auraOpacity: 0.25
//         });


//         const updatePointer = (e) => {
//             if (!rendererRef.current) return;
//             const rect = rendererRef.current.domElement.getBoundingClientRect();
//             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
//             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
//         };

//         const highlightObject = (obj) => {
//             // highlighter.highlightObject(obj);
//             highlighterRef.current?.highlightObject?.(obj);
//         };

//         const clearHighlight = () => {
//             // highlighter.clear();
//             highlighterRef.current?.clear?.();
//         };

//         const onDown = (e) => {
//             if (e.button !== 0) return;
//             if (uiClickHandlerRef.current?.(e)) return;

//             updatePointer(e);
//             raycaster.setFromCamera(pointer, camera);

//             const intersects = raycaster.intersectObjects(
//                 sceneRef.current.children,
//                 true
//             );

//             for (const i of intersects) {
//                 if (i.object.userData?.isRadialButton) {
//                     i.object.userData.onClick();
//                     return;
//                 }
//             }

//             const modelHits = raycaster.intersectObjects(objectsRef.current.filter(o => o.userData.isModel), true);


//             let hitModel = modelHits[0]?.object;
//             while (hitModel && !objectsRef.current.includes(hitModel)) hitModel = hitModel.parent;

//             if (!hitModel) {
//                 selectedObjectRef.current = null;
//                 setSelectedObject(null);
//                 clearHighlight();
//                 removeRadialMenu();
//                 return;
//             }

//             removeRadialMenu();

//             selectObject(hitModel);
//             if (!hitModel.userData.isWritableBoard) highlightObject(hitModel);
//             else clearHighlight();

//             createRadialMenu(hitModel);

//             if (hitModel.userData.mode === "write") return;
//             isDragging = true;
//             const grabPoint = new THREE.Vector3();
//             dragPlane.constant = -hitModel.position.y;
//             grabOffset.set(0, 0, 0);
//             if (raycaster.ray.intersectPlane(dragPlane, grabPoint)) grabOffset.copy(hitModel.position).sub(grabPoint);
//             renderer.domElement.setPointerCapture(e.pointerId);
//             controls.enabled = false;
//         };




//         // Przesuwanie płynne

//         const onMove = (e) => {
//             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

//             const obj = selectedObjectRef.current;

//             if (obj.userData.radialMenu) {
//                 sceneRef.current.remove(obj.userData.radialMenu);
//                 obj.userData.radialMenu = null;
//             }

//             updatePointer(e);
//             raycaster.setFromCamera(pointer, camera);

//             const p = new THREE.Vector3();
//             if (!raycaster.ray.intersectPlane(dragPlane, p)) return;
//             const { dimX, dimZ } = boardSizeRef.current;

//             const maxZ = dimZ;

//             let worldX = p.x + grabOffset.x;
//             let worldZ = p.z + grabOffset.z;

//             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
//             const maxDepth = maxZ * GRID_SIZE;

//             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
//             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));



//             if (!moveOnSurfaces(obj, worldX, worldZ)) return;


//             // highlighter?.update?.();
//             highlighterRef.current?.update?.();
//         };


//         const onUp = () => {
//             rotationDirRef.current = 0;
//             isDragging = false;
//             controls.enabled = true;
//         };

//         renderer.domElement.addEventListener("pointerdown", onDown);
//         renderer.domElement.addEventListener("pointermove", onMove);
//         renderer.domElement.addEventListener("pointerup", onUp);

//         renderer.domElement.addEventListener("pointercancel", onUp);
//         window.addEventListener("pointerup", onUp);
//         window.addEventListener("blur", onUp);
//         let frame;
//         const animate = () => {
//             frame = requestAnimationFrame(animate);


//             // highlighter?.update?.();
//             highlighterRef.current?.update?.();
//             if (selectedObjectRef.current && rotationDirRef.current) {
//                 rotateSelected(0.02 * rotationDirRef.current);
//                 // highlight?.update?.();
//             }

//             controls.update();
//             renderer.render(scene, camera);
//         };

//         animate();

//         return () => {
//             cancelAnimationFrame(frame);
//             renderer.domElement.removeEventListener("pointerdown", onDown);
//             renderer.domElement.removeEventListener("pointermove", onMove);
//             renderer.domElement.removeEventListener("pointerup", onUp);
//             renderer.domElement.removeEventListener("pointercancel", onUp);
//             window.removeEventListener("pointerup", onUp);
//             window.removeEventListener("blur", onUp);
//             highlighterRef.current?.clear?.();
//             controls.dispose();
//             renderer.dispose();
//             renderer.domElement.remove();
//         };
//     }, []);

//     // -------------------- GRID + PLANE --------------------
//     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
//         const material = new THREE.LineBasicMaterial({ color });
//         const geometry = new THREE.BufferGeometry();
//         const vertices = [];

//         const halfWidth = width / 2;

//         for (let i = 0; i <= width; i++) {
//             const x = (i - halfWidth) * gridSize;
//             vertices.push(x, 0, 0, x, 0, -height * gridSize);
//         }

//         for (let i = 0; i <= height; i++) {
//             const z = -i * gridSize;
//             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
//         }

//         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
//         const grid = new THREE.LineSegments(geometry, material);
//         grid.userData.isGrid = true;
//         return grid;
//     };

//     useEffect(() => {
//         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

//         const dimX = selectedScenario?.dimensionX || 18;
//         const dimZ = selectedScenario?.dimensionZ || 18;
//         boardSizeRef.current.dimX = dimX;
//         boardSizeRef.current.dimZ = dimZ;

//         removeRadialMenu();
//         highlighterRef.current?.clear?.();
//         // usuń poprzedni grid i plane
//         sceneRef.current.children
//             .filter((o) => o.userData?.isGrid)
//             .forEach((o) => sceneRef.current.remove(o));

//         if (planeRef.current) {
//             sceneRef.current.remove(planeRef.current);
//             planeRef.current.geometry.dispose();
//         }

//         objectsRef.current
//             .filter((o) => o.userData?.isModel)
//             .forEach((o) => sceneRef.current.remove(o));

//         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

//         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
//         sceneRef.current.add(grid);

//         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
//         planeGeo.rotateX(-Math.PI / 2);
//         const plane = new THREE.Mesh(
//             planeGeo,
//             new THREE.MeshBasicMaterial({ visible: false })
//         );
//         plane.position.set(worldOffset.x, 0, worldOffset.z);
//         sceneRef.current.add(plane);
//         planeRef.current = plane;
//         objectsRef.current = [plane];

//         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
//         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
//         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
//         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
//         controlsRef.current.update();

//         selectedObjectRef.current = null;
//         setSelectedObject(null);
//     }, [selectedScenario?.id]);



//     useEffect(() => {
//         if (!sceneRef.current || !selectedScenario?.entities) return;

//         let cancelled = false;
//         const loadedInstances = [];

//         const loadEntities = async () => {
//             const { dimZ } = boardSizeRef.current;

//             const worldOffsetZ = -(dimZ) * GRID_SIZE;
//             const worldOffsetX = 0;

//             const UNITY_Y_OFFSET = 180;


//             // grupujemy encje po modelu
//             const entitiesByModel = selectedScenario.entities.reduce((acc, e) => {
//                 if (!acc[e.name]) acc[e.name] = [];
//                 acc[e.name].push(e);
//                 return acc;
//             }, {});


//             const loadModelEntities = async (modelName, entities) => {

//                 try {
//                     console.log("Loading model group:", modelName);


//                     const tpl = await loadModel(modelName);

//                     if (!tpl || cancelled) return;


//                     modelTemplates.current[modelName] = tpl;


//                     // tutaj od razu dodajemy wszystkie instancje tego modelu
//                     for (const e of entities) {

//                         if (cancelled) return;


//                         const model = SkeletonUtils.clone(tpl);

//                         model.name = e.name;
//                         model.scale.setScalar(modelName === "whiteboard" ? 7 : 50);
//                         model.userData.isModel = true;


//                         const box = new THREE.Box3()
//                             .setFromObject(model);

//                         model.userData.heightOffset = -box.min.y;


//                         const gridX = e.posX ?? 0;
//                         const gridZ = e.posZ ?? 0;

//                         model.userData.gridX = gridX;
//                         model.userData.gridZ = gridZ;


//                         const worldX = gridX * GRID_SIZE + worldOffsetX;
//                         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;


//                         model.position.set(
//                             worldX,
//                             model.userData.heightOffset,
//                             worldZ
//                         );


//                         const baseQuat = tpl.quaternion.clone();


//                         const userQuat = new THREE.Quaternion()
//                             .setFromEuler(
//                                 new THREE.Euler(
//                                     degToRad(e.rotX || 0),
//                                     degToRad(UNITY_Y_OFFSET - (e.rotY || 0)),
//                                     degToRad(e.rotZ || 0),
//                                     "YXZ"
//                                 )
//                             );


//                         model.quaternion
//                             .copy(baseQuat)
//                             .multiply(userQuat);


//                         model.position.y = 0;
//                         model.userData.heightOffset = -getObjectBox(model).min.y;
//                         model.position.y = model.userData.heightOffset + (Number(e.posY) || 0) * GRID_SIZE;
//                         syncPosition(model);
//                         if (modelName === "whiteboard") initBoard(model, e.boardText || "");
//                         sceneRef.current.add(model);

//                         objectsRef.current.push(model);
//                         loadedInstances.push(model);


//                         console.log(
//                             "Added:",
//                             model.name
//                         );
//                     }


//                 } catch (err) {
//                     console.error(
//                         "Failed model group:",
//                         modelName,
//                         err
//                     );
//                 }
//             };


//             Object.entries(entitiesByModel)
//                 .forEach(([name, entities]) => {
//                     loadModelEntities(name, entities);
//                 });

//         };

//         loadEntities();

//         return () => {
//             cancelled = true;
//             loadedInstances.forEach(model => model.removeFromParent());
//             objectsRef.current = objectsRef.current.filter(model => !loadedInstances.includes(model));
//             if (loadedInstances.includes(selectedObjectRef.current)) {
//                 removeRadialMenu();
//                 highlighterRef.current?.clear?.();
//                 selectedObjectRef.current = null;
//                 setSelectedObject(null);
//             }
//         };

//     }, [selectedScenario]);

//     // useEffect(() => {
//     //     if (!selectedScenario?.entities) return;

//     //     const uniqueModels = [...new Set(
//     //         selectedScenario.entities.map(e => e.name)
//     //     )];

//     //     uniqueModels.forEach(name => {
//     //         const config = MODEL_LIBRARY.find(m => m.name === name);

//     //         if (config) {
//     //             loadModel(name, config.path).then(tpl => {
//     //                 modelTemplates.current[name] = tpl;

//     //                 setModelsReadyVersion(v => v + 1);
//     //             });
//     //         }
//     //     });
//     // }, [selectedScenario]);

//     // -------------------- ADD / DELETE --------------------
//     const addModel = async (name) => {
//         const config = MODEL_LIBRARY.find(m => m.name === name);
//         if (!config) return;

//         // const tpl = modelTemplates.current[name];

//         console.log("config2121:", config);

//         const sceneTpl = await loadModel(name);
//         if (!sceneTpl) return;


//         const model = SkeletonUtils.clone(sceneTpl);
//         model.name = name;
//         model.scale.setScalar(name === "whiteboard" ? 7 : 50);
//         modelTemplates.current[name] = sceneTpl;
//         model.userData.isModel = true;

//         const box = new THREE.Box3().setFromObject(model);
//         model.userData.heightOffset = -box.min.y;

//         // model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
//         // model.userData.gridZ = 0;

//         // Centrowanie
//         model.userData.gridX = 0
//         model.userData.gridZ = -Math.floor((selectedScenario?.dimensionZ || 18) / 2);

//         const worldX = model.userData.gridX * GRID_SIZE;
//         const worldZ = model.userData.gridZ * GRID_SIZE;
//         model.position.set(worldX, model.userData.heightOffset, worldZ);

//         const baseQuat = sceneTpl.quaternion.clone();
//         const userQuat = new THREE.Quaternion().setFromEuler(
//             new THREE.Euler(0, degToRad(UNITY_Y_OFFSET), 0, "YXZ")
//         );

//         model.quaternion.copy(baseQuat).multiply(userQuat);

//         model.position.y = 0;
//         model.userData.heightOffset = -getObjectBox(model).min.y;
//         model.position.y = model.userData.heightOffset;
//         syncPosition(model);
//         if (name === "whiteboard") initBoard(model);
//         sceneRef.current.add(model);
//         objectsRef.current.push(model);

//         if (selectedObjectRef.current) {
//             removeRadialMenu(selectedObjectRef.current);
//         }

//         selectObject(model);
//         createRadialMenu(model);
//     };


//     const deleteSelected = () => {
//         if (!selectedObjectRef.current) return;
//         removeRadialMenu();
//         highlighterRef.current?.clear?.();
//         sceneRef.current.remove(selectedObjectRef.current);
//         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
//         selectedObjectRef.current = null;
//         setSelectedObject(null);
//     };

//     const duplicateSelected = () => {
//         const selected = selectedObjectRef.current;
//         if (!selected) return;

//         highlighterRef.current?.clear?.();
//         highlighterRef.current?.unhighlight?.(selected);

//         selected.traverse((child) => {
//             if (child.isMesh && child.userData?.originalColor) {
//                 child.material.color.copy(child.userData.originalColor);
//             }
//         });

//         removeRadialMenu();
//         const copy = SkeletonUtils.clone(selected);

//         copy.position.x += 10;
//         copy.position.z += 10;

//         copy.userData = { ...selected.userData, radialMenu: null };
//         syncPosition(copy);
//         if (copy.userData.isWritableBoard) initBoard(copy, selected.userData.boardText || "");

//         sceneRef.current.add(copy);

//         objectsRef.current.push(copy);

//         selectObject(copy);
//         createRadialMenu(copy);
//     };


//     const saveScenario = async () => {
//         if (!selectedScenario) return;

//         const entities = objectsRef.current
//             .filter(o => o.userData.isModel)
//             .map(o => {
//                 const tpl = modelTemplates.current[o.name];
//                 const baseQuat = tpl?.quaternion.clone() || new THREE.Quaternion();
//                 const baseInv = baseQuat.clone().invert();

//                 const userQuat = o.quaternion.clone().premultiply(baseInv);

//                 const euler = new THREE.Euler().setFromQuaternion(userQuat, "YXZ");

//                 const rotX = normalizeDeg(radToDeg(euler.x));
//                 const rotY = normalizeDeg(UNITY_Y_OFFSET - normalizeDeg(radToDeg(euler.y))); // flip Y do VR
//                 const rotZ = normalizeDeg(radToDeg(euler.z));

//                 return {
//                     name: o.name,
//                     GUID: crypto.randomUUID(),
//                     posX: round3(o.position.x / GRID_SIZE),
//                     posY: (o.position.y - (o.userData.heightOffset || 0)) / GRID_SIZE,
//                     posZ: round3(-boardSizeRef.current.dimZ - o.position.z / GRID_SIZE),
//                     ...(o.userData.isWritableBoard ? { boardText: o.userData.boardText || "" } : {}),
//                     rotX,
//                     rotY,
//                     rotZ,
//                 };
//             });

//         // console.log('entities to save:', entities);

//         try {
//             const response = await fetch(
//                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
//                 {
//                     method: "PATCH",
//                     headers: { "Content-Type": "application/json" },
//                     body: JSON.stringify({
//                         stationName: selectedScenario.stationName,
//                         GUID: crypto.randomUUID(),
//                         dimensionX: selectedScenario.dimensionX,
//                         dimensionY: selectedScenario.dimensionY,
//                         entities,
//                     }),
//                 }
//             );

//             if (!response.ok) {
//                 const errorData = await response.json();
//                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
//             }

//             setAlertMessage("Stacja zapisana pomyślnie!");
//             setAlertSeverity("success");
//             setAlertOpen(true);
//         } catch (error) {
//             console.error(error);
//             setAlertMessage(`Błąd: ${error.message}`);
//             setAlertSeverity("error");
//             setAlertOpen(true);
//         }
//     };


//     const rotateSelected = (angle) => {
//         const obj = selectedObjectRef.current;
//         if (!obj || obj.userData.mode === "write") return;
//         const previous = obj.quaternion.clone();
//         obj.rotation.y += angle;
//         if (checkCollision(obj)) obj.quaternion.copy(previous);
//         highlighterRef.current?.update?.();
//     };
//     const rotate90Right = () => rotateSelected(-Math.PI / 2);
//     const rotate90Left = () => rotateSelected(Math.PI / 2);

//     const textureLoader = new THREE.TextureLoader();


//     const createRadialMenu = (object) => {
//         if (!sceneRef.current) return;

//         // USUWANIE STAREGO MENU
//         if (object.userData.radialMenu) {
//             sceneRef.current.remove(object.userData.radialMenu);
//             object.userData.radialMenu = null;
//         }

//         const radius = 120;
//         const group = new THREE.Group();
//         group.userData.isRadialMenu = true;

//         const actions = [
//             { fn: rotate90Left, icon: "/icons/rotateleft.png" },
//             { fn: rotate90Right, icon: "/icons/rotateright.png" },
//             { fn: deleteSelected, icon: "/icons/delete.png" },
//             { fn: duplicateSelected, icon: "/icons/copy.png" }
//         ];

//         if (object.userData.isWritableBoard) {
//             actions.push({
//                 fn: () => toggleBoard(object),
//                 icon: "/icons/pisz.png",
//                 isWriteButton: true
//             });
//         }

//         actions.forEach((action, i) => {
//             const angle = (i / actions.length) * Math.PI * 2;

//             const geometry = new THREE.CircleGeometry(20, 32);
//             const material = new THREE.MeshBasicMaterial({
//                 color: action.isWriteButton
//                     ? object.userData.mode === "write"
//                         ? 0xffff00
//                         : 0x6cc6d9
//                     : 0x6cc6d9,
//                 depthTest: false
//             });

//             const mesh = new THREE.Mesh(geometry, material);
//             mesh.rotation.x = -Math.PI / 2;
//             mesh.position.set(
//                 Math.cos(angle) * radius,
//                 1,
//                 Math.sin(angle) * radius
//             );

//             const texture = textureLoader.load(action.icon);
//             const iconGeometry = new THREE.PlaneGeometry(30, 30);
//             const iconMaterial = new THREE.MeshBasicMaterial({
//                 map: texture,
//                 transparent: true,
//                 depthTest: false,
//                 side: THREE.DoubleSide
//             });
//             const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);
//             iconMesh.rotation.x = Math.PI;
//             iconMesh.rotation.z = -Math.PI / 2;
//             iconMesh.position.set(0, 0.1, 0);
//             mesh.add(iconMesh);

//             mesh.userData.onClick = action.fn;
//             mesh.userData.isRadialButton = true;
//             if (action.isWriteButton) mesh.userData.isWriteButton = true;

//             group.add(mesh);
//             object.userData.radialMenu = group;
//         });

//         sceneRef.current.add(group);
//         group.position.copy(object.position);
//         group.position.y = 1;
//     };





//     const removeRadialMenu = () => {
//         if (!sceneRef.current) return;

//         const menus = sceneRef.current.children.filter(
//             o => o.userData?.isRadialMenu
//         );

//         menus.forEach(menu => {
//             sceneRef.current.remove(menu);
//             menu.traverse(child => {
//                 child.geometry?.dispose();
//                 child.material?.map?.dispose();
//                 child.material?.dispose();
//             });
//         });
//         objectsRef.current.forEach(obj => { obj.userData.radialMenu = null; });
//     };




//     // -------------------- KEYBOARD --------------------

//     useEffect(() => {
//         // Przesuwanie płynne
//         const moveGrid = (dx, dz) => {
//             if (!selectedObjectRef.current) return;

//             const obj = selectedObjectRef.current;
//             const { dimX, dimZ } = boardSizeRef.current;

//             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
//             const maxDepth = dimZ * GRID_SIZE;

//             let worldX = obj.position.x + dx;
//             let worldZ = obj.position.z + dz;

//             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
//             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

//             if (!moveOnSurfaces(obj, worldX, worldZ)) return;

//             removeRadialMenu();
//         };



//         const onKeyDown = (e) => {
//             if (
//                 e.target.tagName === "INPUT" ||
//                 e.target.tagName === "TEXTAREA" ||
//                 e.target.isContentEditable
//             ) return;

//             // Dodajemy blokadę ruchu w trybie write (TABLICA)
//             const obj = selectedObjectRef.current;
//             if (!obj) return;

//             if (obj.userData.mode === "write") {
//                 return;
//             }

//             switch (e.key.toLowerCase()) {
//                 case "q":
//                 case "e":
//                     e.preventDefault();
//                     changeHeight((obj.position.y - (obj.userData.heightOffset || 0)) / GRID_SIZE + (e.key.toLowerCase() === "e" ? 0.1 : -0.1));
//                     break;

//                 case "w":
//                     e.preventDefault();
//                     moveGrid(0, 5);
//                     break;
//                 case "s":
//                     e.preventDefault();
//                     moveGrid(0, -5);
//                     break;
//                 case "a":
//                     e.preventDefault();
//                     moveGrid(5, 0);
//                     break;
//                 case "d":
//                     e.preventDefault();
//                     moveGrid(-5, 0);
//                     break;

//                 case "arrowleft":
//                     e.preventDefault();
//                     rotate90Left();
//                     break;
//                 case "arrowright":
//                     e.preventDefault();
//                     rotate90Right();
//                     break;

//                 case "delete":
//                 case "backspace":
//                     e.preventDefault();
//                     deleteSelected();
//                     break;

//                 case "escape":
//                     selectedObjectRef.current = null;
//                     setSelectedObject(null);
//                     break;

//                 default:
//                     break;
//             }
//         };

//         window.addEventListener("keydown", onKeyDown);
//         return () => window.removeEventListener("keydown", onKeyDown);
//     }, []);


//     // -------------------- RENDER --------------------
//     return (
//         <>
//             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
//                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
//                     <BackToScenariosButton />
//                 </Box>
//                 <Divider sx={{ my: 1 }} />
//                 {/* <Typography variant="subtitle2">Modele</Typography> */}
//                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
//                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
//                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
//                         <ListItem key={m.name} disablePadding>
//                             <ListItemButton onMouseDown={() => addModel(m.name)}>
//                                 <ListItemText primary={m.name} />
//                             </ListItemButton>
//                         </ListItem>
//                     ))}
//                 </List> */}

//                 {selectedObject && (
//                     <>
//                         {selectedObject.userData.isWritableBoard && <>
//                             <Button fullWidth onClick={() => toggleBoard(selectedObject)}>
//                                 {boardMode === "write" ? "Zakończ pisanie" : "Pisz na tablicy"}
//                             </Button>
//                             {boardMode === "write" && <TextField
//                                 label="Tekst tablicy" multiline minRows={3} fullWidth value={boardText}
//                                 onChange={event => {
//                                     setBoardText(event.target.value);
//                                     paintBoard(selectedObject, event.target.value);
//                                 }}
//                             />}
//                         </>}
//                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
//                             <IconButton
//                                 onClick={rotate90Left}
//                             >
//                                 <Rotate90DegreesCcwIcon />
//                             </IconButton>
//                             <IconButton
//                                 onMouseDown={() => (rotationDirRef.current = 1)}
//                                 onMouseUp={() => (rotationDirRef.current = 0)}
//                             >
//                                 <RotateLeftIcon />
//                             </IconButton>
//                             <IconButton
//                                 onMouseDown={() => (rotationDirRef.current = -1)}
//                                 onMouseUp={() => (rotationDirRef.current = 0)}
//                             >
//                                 <RotateRightIcon />
//                             </IconButton>
//                             <IconButton
//                                 onClick={rotate90Right}
//                             >
//                                 <Rotate90DegreesCwIcon />
//                             </IconButton>
//                             <IconButton color="error" onClick={deleteSelected}>
//                                 <DeleteIcon />
//                             </IconButton>
//                             {/* <Button onClick={logRotation}>
//                                 log
//                             </Button> */}
//                         </Stack>
//                     </>
//                 )}

//                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
//                     Zapisz stację
//                 </Button>
//             </Paper>

//             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

//             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
//             {gridWalls && <GridWalls
//                 scene={sceneRef.current}
//                 dimX={boardSizeRef.current.dimX}
//                 dimZ={boardSizeRef.current.dimZ}
//                 GRID_SIZE={GRID_SIZE}
//                 cameraRef={cameraRef}
//             />}

//             {sceneRef.current && cameraRef.current && (
//                 <>
//                     <FloorLogo
//                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
//                         scene={sceneRef.current}
//                         svgPath="/micree.svg"
//                         GRID_SIZE={GRID_SIZE}
//                         boardSizeRef={boardSizeRef.current}
//                     />
//                     <UI3DModelButtons
//                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}-${modelsReadyVersion}`}
//                         scene={sceneRef.current}
//                         camera={cameraRef.current}
//                         modelTemplates={modelTemplates.current}
//                         objectsRef={objectsRef.current}
//                         onClickHandlerRef={uiClickHandlerRef}
//                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
//                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
//                         changeScenarioRef={changeScenarioRef}
//                         boardSizeRef={boardSizeRef.current}
//                         GRID_SIZE={GRID_SIZE}
//                     />
//                 </>
//             )}

//             <Typography
//                 variant="h6"
//                 sx={{
//                     mt: 1,
//                     position: "absolute",
//                     top: 70,
//                     left: "50%",
//                     transform: "translateX(-50%)",
//                     fontSize: "10px",
//                 }}
//             >
//                 Wybrana stacja:
//             </Typography>
//             <Typography
//                 variant="h6"
//                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
//             >
//                 {/* {selectedScenario?.existingSource} */}
//                 {
//                     selectedScenario?.stationName
//                         ? selectedScenario.stationName
//                         : selectedScenario?.id !== undefined
//                             ? `Stacja ${selectedScenario.id}`
//                             : "nie wybrano"
//                 }
//             </Typography>

//             <IconButton
//                 onClick={() => setGridWalls(prev => !prev)}
//                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
//             >
//                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

//             </IconButton>

//             <Snackbar
//                 open={alertOpen}
//                 autoHideDuration={4000}
//                 onClose={() => setAlertOpen(false)}
//                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
//                 sx={{ mt: 8 }}
//             >
//                 <Alert
//                     onClose={() => setAlertOpen(false)}
//                     severity={alertSeverity}
//                     sx={{ width: "100%" }}
//                 >
//                     {alertMessage}
//                 </Alert>
//             </Snackbar>


//             {/* <WallBoards
//                 scene={sceneRef.current}
//                 cameraRef={cameraRef}
//                 rendererRef={rendererRef}
//             //   wallsRef={}
//             /> */}





//         </>
//     );
// }




// // import React, { useEffect, useRef, useState } from "react";
// // import * as THREE from "three";
// // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// // import {
// //     Box,
// //     Button,
// //     List,
// //     ListItem,
// //     ListItemButton,
// //     ListItemText,
// //     Typography,
// //     Paper,
// //     IconButton,
// //     Divider,
// //     Stack,
// //     Snackbar,
// //     Alert,
// //     TextField,
// // } from "@mui/material";

// // import BackToScenariosButton from "./utilities/BackToScenario";
// // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// // import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// // import DeleteIcon from "@mui/icons-material/Delete";
// // import GridOffIcon from '@mui/icons-material/GridOff';
// // import GridOnIcon from '@mui/icons-material/GridOn';

// // import FloorLogo from "./three/FloorLogo";
// // import UI3DModelButtons from "./three/UI3DModelButtons";
// // import ModelList from "./utilities/ModelList";
// // import GridWalls from "./three/GridWalls";
// // import { ObjectHighlighter } from "./utilities/ObjectHighlighter";
// // import WallBoards from "./three/WallBoards";
// // import { useSelector } from "react-redux";

// // const GRID_SIZE = 50;

// // const UNITY_Y_OFFSET = 180;

// // const MODEL_LIBRARY = [
// //     { name: "whiteboard", visible: true },
// //     { name: "CC CC0 1", visible: false },
// //     { name: "arrow", visible: true },

// //     { name: "envp_bin_4", visible: true },
// //     { name: "envp_bin_4_lids", visible: true },
// //     { name: "envp_bin_lid", visible: true },

// //     { name: "envp_furnitures_chair", visible: true },
// //     { name: "envp_furnitures_office_chair", visible: true },

// //     { name: "envp_furnitures_sofa_corner_3_2", visible: true },
// //     { name: "envp_furnitures_sofa_corner_3_3", visible: true },
// //     { name: "envp_furnitures_sofa_line_2", visible: true },
// //     { name: "envp_furnitures_sofa_line_3", visible: true },
// //     { name: "envp_furnitures_sofa_single", visible: true },
// //     { name: "envp_furnitures_sofa_single_corner", visible: true },

// //     { name: "envp_furnitures_table_140_80", visible: true },
// //     { name: "envp_furnitures_table_160_80", visible: true },
// //     { name: "envp_furnitures_table_250_120", visible: true },
// //     { name: "envp_furnitures_table_400_120", visible: true },

// //     { name: "envp_furnitures_table_small_100x100x100", visible: true },
// //     { name: "envp_furnitures_table_small_100x100x75", visible: true },
// //     { name: "envp_furnitures_table_small_125x100x100", visible: true },
// //     { name: "envp_furnitures_table_small_125x100x75", visible: true },
// //     { name: "envp_furnitures_table_small_75x75x100", visible: true },
// //     { name: "envp_furnitures_table_small_75x75x75", visible: true },

// //     { name: "envp_modular_door_frame", visible: true },
// //     { name: "env_staircase_stairs", visible: true },
// //     { name: "env_wall_column_05", visible: true },
// //     { name: "env_wall_creator_x2_5", visible: true },

// //     { name: "exe_cd_essential_items", visible: true },

// //     { name: "exe_cooking_bins", visible: true },
// //     { name: "exe_cooking_cookbase", visible: true },
// //     { name: "exe_cooking_cook_station", visible: true },
// //     { name: "exe_cooking_countertop_2", visible: true },
// //     { name: "exe_cooking_countertop_4", visible: true },
// //     { name: "exe_cooking_countertop_drawer", visible: true },
// //     { name: "exe_cooking_device_blender", visible: true },
// //     { name: "exe_cooking_device_mixer", visible: true },
// //     { name: "exe_cooking_device_mixer_blender", visible: true },
// //     { name: "exe_cooking_drawers_ingrediens", visible: true },
// //     { name: "exe_cooking_fridge_dairy", visible: true },
// //     { name: "exe_cooking_fridge_fruits", visible: true },
// //     { name: "exe_cooking_fridge_meat", visible: true },
// //     { name: "exe_cooking_fridge_vegetables", visible: true },
// //     { name: "exe_cooking_fridges_all", visible: true },
// //     { name: "exe_cooking_mech_cooker", visible: true },
// //     { name: "exe_cooking_mech_furnance", visible: true },
// //     { name: "exe_cooking_sink", visible: true },
// //     { name: "exe_cooking_sink_hanged", visible: true },
// //     { name: "exe_cooking_utensils", visible: true },

// //     { name: "exe_creator_door_office_double", visible: true },
// //     { name: "exe_creator_door_wooden_1", visible: true },

// //     { name: "exe_printing_3d_printing_station", visible: true },
// //     { name: "exe_printing_cabinet_filament_abs_ptg", visible: true },
// //     { name: "exe_printing_cabinet_filament_pla_tpu", visible: true },
// //     { name: "exe_printing_cleaning_set", visible: true },
// //     { name: "exe_printing_examples", visible: true },

// //     { name: "exe_wd_22bolt_22nut_impactdriver", visible: true },
// //     { name: "exe_wd_22nut_impactdriver", visible: true },
// //     { name: "exe_wd_combined_exercises", visible: true },
// //     { name: "exe_wd_drill_26plug_26screw_impactdriver", visible: true },
// //     { name: "exe_wd_drill_dowel_hammer", visible: true },
// //     { name: "exe_wd_foaming", visible: true },
// //     { name: "exe_wd_garage_gate_installation", visible: true },
// //     { name: "exe_wd_garage_gate_supply", visible: true },
// //     { name: "exe_wd_interior_door_installation", visible: true },
// //     { name: "exe_wd_interior_door_supply", visible: true },
// //     { name: "exe_wd_leveler", visible: true },
// //     { name: "exe_wd_outer_door_installation", visible: true },
// //     { name: "exe_wd_outer_door_supply", visible: true },
// //     { name: "exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill", visible: true },
// //     { name: "exe_wd_roof_window_installation", visible: true },
// //     { name: "exe_wd_roof_window_supply", visible: true },
// //     { name: "exe_wd_strut", visible: true },
// //     { name: "exe_wd_technical_door_installation", visible: true },
// //     { name: "exe_wd_technical_door_supply", visible: true },
// //     { name: "exe_wd_tool_rack_1", visible: true },
// //     { name: "exe_wd_tool_rack_boxes", visible: true },
// //     { name: "exe_wd_vertical_window_installation", visible: true },
// //     { name: "exe_wd_vertical_window_supply", visible: true },
// //     { name: "exe_wd_wedge_hammer", visible: true },

// //     { name: "elevator_1x2.1x", visible: true },
// // ];

// // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// //     const mountRef = useRef(null);
// //     const sceneRef = useRef(null);
// //     const cameraRef = useRef(null);
// //     const rendererRef = useRef(null);
// //     const controlsRef = useRef(null);
// //     const planeRef = useRef(null);
// //     const objectsRef = useRef([]);
// //     const modelTemplates = useRef({});
// //     const selectedObjectRef = useRef(null);
// //     const uiClickHandlerRef = useRef(null);
// //     const rotationDirRef = useRef(0);
// //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// //     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
// //     const [gridVersion, setGridVersion] = useState(0);
// //     const [height, setHeight] = useState(0);
// //     const [boardText, setBoardText] = useState("");
// //     const [boardMode, setBoardMode] = useState("move");

// //     const highlighterRef = useRef(null);

// //     const assetsTokenRef = useRef(null);
// //     const bootstrapPromiseRef = useRef(null);

// //     const [modelsLoaded, setModelsLoaded] = useState(false);
// //     const [selectedObject, setSelectedObject] = useState(null);
// //     const [gridWalls, setGridWalls] = useState(true);



// //     const [alertOpen, setAlertOpen] = useState(false);
// //     const [alertMessage, setAlertMessage] = useState("");
// //     const [alertSeverity, setAlertSeverity] = useState("success");

// //     const [modelsReadyVersion, setModelsReadyVersion] = useState(0);

// //     // const [MODEL_LIBRARY, setModelLibrary] = useState([]);


// //     const round3 = (num) => Math.round(num * 1000) / 1000;
// //     const radToDeg = (rad) => rad * (180 / Math.PI);
// //     const degToRad = (deg) => (deg * Math.PI) / 180;
// //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


// //     const { accessToken: token } = useSelector((state) => state.auth);

// //     async function assetsBootstrap() {
// //         const res = await fetch(
// //             // `${process.env.REACT_APP_API_URL_VR_EDITOR}/assets-vr/bootstrap?env=webe`,
// //             `https://beta.vccsystem.eu:3002/assets-vr/bootstrap?env=webe`,
// //             {
// //                 headers: {
// //                     Authorization: `Bearer ${token}`,
// //                 },
// //             }
// //         );
// //         console.log("assets bootstrap response:", res);
// //         if (!res.ok) {
// //             throw new Error("Bootstrap failed");
// //         }

// //         const data = await res.json();

// //         console.log("assets bootstrap:", data);

// //         assetsTokenRef.current = data.token;
// //     }



// //     useEffect(() => {
// //         const init = async () => {
// //             if (!bootstrapPromiseRef.current) bootstrapPromiseRef.current = assetsBootstrap()
// //                 .catch(error => { bootstrapPromiseRef.current = null; throw error; });
// //             await bootstrapPromiseRef.current;

// //             const arrowConfig = MODEL_LIBRARY.find(
// //                 m => m.name === "arrow"
// //             );

// //             if (!arrowConfig) return;
// //             try {
// //                 console.log("arrowConfig.name",arrowConfig.name)
// //                 const tpl = await loadModel(arrowConfig.name);
// //                 modelTemplates.current["arrow"] = tpl;
// //                 setModelsReadyVersion(v => v + 1);
// //             } catch (error) {
// //                 console.error("Nie udało się załadować arrow:", error);
// //                 setAlertMessage(`Nie udało się załadować strzałek: ${error.message}`);
// //                 setAlertSeverity("warning");
// //                 setAlertOpen(true);
// //             }
// //         };
// //         init().catch(error => {
// //             setAlertMessage(`Błąd ładowania zasobów: ${error.message}`);
// //             setAlertSeverity("error");
// //             setAlertOpen(true);
// //         });
// //     }, [MODEL_LIBRARY]);


// //     // -------------------- GRID UPDATE --------------------

// //     useEffect(() => {
// //         if (!selectedScenario) return;
// //         setBoardSize({
// //             dimX: selectedScenario.dimensionX || 18,
// //             dimZ: selectedScenario.dimensionZ || 18
// //         });
// //         setGridVersion(v => v + 1);
// //     }, [selectedScenario?.id]);

// //     const modelCache = useRef({});
// //     const modelPromises = useRef({});

// //     const loadModel = async (name) => {
// //         if (name !== "whiteboard" && !assetsTokenRef.current) {
// //             if (!bootstrapPromiseRef.current) bootstrapPromiseRef.current = assetsBootstrap()
// //                 .catch(error => { bootstrapPromiseRef.current = null; throw error; });
// //             await bootstrapPromiseRef.current;
// //         }
// //         return loadModelAsset(name);
// //     };
// //     const loadModelAsset = (name) => {
// //         if (name === "whiteboard") {
// //             if (!modelPromises.current[name]) {
// //                 modelPromises.current[name] = new GLTFLoader().loadAsync("/whiteboard/scene.gltf")
// //                     .then(gltf => {
// //                         modelTemplates.current[name] = gltf.scene;
// //                         return gltf.scene;
// //                     }).catch(error => { delete modelPromises.current[name]; throw error; });
// //             }
// //             return modelPromises.current[name];
// //         }

// //         if (modelCache.current[name]) {
// //             return Promise.resolve(modelCache.current[name]);
// //         }

// //         if (modelPromises.current[name]) {
// //             return modelPromises.current[name];
// //         }


// //         const promise = new Promise((resolve, reject) => {

// //             console.log("Loading model:", name);


// //             const baseUrl =
// //                 `https://assets.micree.eu/webe/${encodeURIComponent(name)}`;


// //             const manager = new THREE.LoadingManager();


// //             // manager.setURLModifier((url) => {

// //             //     if (/^(data:|blob:)/i.test(url)) return url;
// //             //     const resolved = new URL(url, `${baseUrl}/`);
// //             //     if (resolved.origin === new URL(baseUrl).origin) {
// //             //         const tokenQuery = String(assetsTokenRef.current || "").replace(/^[?&]/, "");
// //             //         new URLSearchParams(tokenQuery).forEach((value, key) => {
// //             //             resolved.searchParams.set(key, value);
// //             //         });
// //             //     }

// //             //     console.log('resolved.href', resolved.href)

// //             //     return resolved.href;
// //             // });

// //             manager.setURLModifier((url) => {
// //     if (/^(data:|blob:)/i.test(url)) {
// //         return url;
// //     }

// //     const resolved = new URL(url, `${baseUrl}/`);

// //     if (resolved.origin === new URL(baseUrl).origin) {
// //         let tokenQuery = String(assetsTokenRef.current || "");

// //         tokenQuery = tokenQuery.replace(/^[?&]/, "");

// //         if (tokenQuery) {
// //             resolved.search = resolved.search
// //                 ? `${resolved.search}&${tokenQuery}`
// //                 : `?${tokenQuery}`;
// //         }
// //     }

// //     console.log("assetsTokenRef.current:", assetsTokenRef.current);

// //     return resolved.href;
// // });


// //             const loader = new GLTFLoader(manager);


// //             const url =
// //                 `${baseUrl}/${encodeURIComponent(name)}.gltf`;


// //             console.log("GLTF URL:", url);


// //             loader.load(
// //                 url,

// //                 (gltf) => {

// //                     console.log("Loaded model:", name);

// //                     modelCache.current[name] = gltf.scene;

// //                     delete modelPromises.current[name];

// //                     resolve(gltf.scene);
// //                 },

// //                 undefined,

// //                 (err) => {

// //                     console.error(
// //                         "GLTF failed:",
// //                         name,
// //                         err
// //                     );

// //                     delete modelPromises.current[name];

// //                     reject(new Error(`Model ${name}: ${err.message || String(err)} (URL: ${url})`));

// //                 }
// //             );

// //         });


// //         modelPromises.current[name] = promise;

// //         return promise;
// //     };

// //     const getObjectBox = (obj) => {
// //         obj.updateWorldMatrix(true, true);
// //         return new THREE.Box3().setFromObject(obj);
// //     };
// //     const syncPosition = (obj) => {
// //         obj.userData.gridX = obj.position.x / GRID_SIZE;
// //         obj.userData.gridY = (obj.position.y - (obj.userData.heightOffset || 0)) / GRID_SIZE;
// //         obj.userData.gridZ = -boardSizeRef.current.dimZ - obj.position.z / GRID_SIZE;
// //         if (selectedObjectRef.current === obj) setHeight(round3(obj.userData.gridY));
// //     };
// //     const overlaps = (a, b) => ["x", "y", "z"].every(axis =>
// //         Math.min(a.max[axis], b.max[axis]) - Math.max(a.min[axis], b.min[axis]) > 0.001
// //     );
// //     const changeHeight = (value) => {
// //         const obj = selectedObjectRef.current;
// //         if (!obj || obj.userData.mode === "write" || !Number.isFinite(value)) return;
// //         const oldY = obj.position.y;
// //         obj.position.y = (obj.userData.heightOffset || 0) + Math.max(0, value) * GRID_SIZE;
// //         if (checkCollision(obj)) obj.position.y = oldY;
// //         syncPosition(obj);
// //         highlighterRef.current?.update?.();
// //     };
// //     const placeOnSupport = () => {
// //         const obj = selectedObjectRef.current;
// //         if (!obj || obj.userData.mode === "write") return;
// //         const box = getObjectBox(obj);
// //         const supports = objectsRef.current.filter(other => other !== obj && other.userData.isModel)
// //             .map(getObjectBox).filter(b =>
// //                 Math.min(box.max.x, b.max.x) - Math.max(box.min.x, b.min.x) > 0.001 &&
// //                 Math.min(box.max.z, b.max.z) - Math.max(box.min.z, b.min.z) > 0.001 &&
// //                 b.max.y <= box.min.y + 0.001);
// //         const top = Math.max(0, ...supports.map(b => b.max.y));
// //         const previous = obj.position.y;
// //         obj.position.y += top - box.min.y;
// //         if (checkCollision(obj)) obj.position.y = previous;
// //         syncPosition(obj);
// //         highlighterRef.current?.update?.();
// //     };
// //     const paintBoard = (model, text) => {
// //         const surface = model.getObjectByName("Object001_0");
// //         const data = surface?.userData?.boardSurface;
// //         if (!data) return;
// //         model.userData.boardText = text;
// //         const { canvas, ctx, texture } = data;
// //         ctx.fillStyle = "#ffffff";
// //         ctx.fillRect(0, 0, canvas.width, canvas.height);
// //         ctx.fillStyle = "#111111";
// //         ctx.font = "40px Arial";
// //         ctx.textBaseline = "top";
// //         let y = 32;
// //         for (const paragraph of text.split("\n")) {
// //             let line = "";
// //             for (const char of paragraph) {
// //                 if (ctx.measureText(line + char).width > canvas.width - 64) {
// //                     ctx.fillText(line, 32, y); y += 52; line = "";
// //                 }
// //                 line += char;
// //             }
// //             ctx.fillText(line, 32, y); y += 52;
// //         }
// //         texture.needsUpdate = true;
// //     };
// //     const initBoard = (model, text = "") => {
// //         const surface = model.getObjectByName("Object001_0");
// //         if (!surface?.isMesh) throw new Error("Whiteboard: brak siatki Object001_0");
// //         const canvas = document.createElement("canvas");
// //         canvas.width = 1024; canvas.height = 1024;
// //         const ctx = canvas.getContext("2d");
// //         const texture = new THREE.CanvasTexture(canvas);
// //         texture.flipY = false;
// //         texture.colorSpace = THREE.SRGBColorSpace;
// //         const makeMaterial = original => {
// //             const material = original.clone();
// //             material.map = texture;
// //             material.color.set(0xffffff);
// //             material.needsUpdate = true;
// //             return material;
// //         };
// //         surface.material = Array.isArray(surface.material)
// //             ? surface.material.map(makeMaterial) : makeMaterial(surface.material);
// //         Object.defineProperty(surface.userData, "boardSurface", {
// //             value: { canvas, ctx, texture }, configurable: true, enumerable: false
// //         });
// //         model.userData.isWritableBoard = true;
// //         model.userData.mode = "move";
// //         paintBoard(model, text);
// //     };
// //     const selectObject = (obj) => {
// //         selectedObjectRef.current = obj;
// //         setSelectedObject(obj);
// //         setHeight(round3(obj.userData.gridY || 0));
// //         setBoardText(obj.userData.boardText || "");
// //         setBoardMode(obj.userData.mode || "move");
// //     };
// //     const toggleBoard = (obj) => {
// //         obj.userData.mode = obj.userData.mode === "write" ? "move" : "write";
// //         obj.userData.isMovable = obj.userData.mode !== "write";
// //         rotationDirRef.current = 0;
// //         setBoardMode(obj.userData.mode);
// //         highlighterRef.current?.clear?.();
// //         createRadialMenu(obj);
// //     };

// //     const checkCollision = (obj) => {
// //         if (!sceneRef.current) return false;

// //         const objBox = getObjectBox(obj);

// //         const walls = sceneRef.current.children.filter(o => o.userData?.isWall);

// //         for (const wall of walls) {
// //             const wallBox = new THREE.Box3().setFromObject(wall);
// //             if (overlaps(objBox, wallBox)) return true;
// //         }

// //         for (const other of objectsRef.current) {
// //             if (!other.userData?.isModel) continue;
// //             if (other === obj) continue;

// //             const otherBox = getObjectBox(other);
// //             if (overlaps(objBox, otherBox)) return true;
// //         }

// //         return false;
// //     };

// //     // -------------------- INIT SCENE --------------------
// //     useEffect(() => {
// //         if (!mountRef.current) return;

// //         const { dimX, dimZ } = boardSizeRef.current;

// //         const scene = new THREE.Scene();
// //         scene.background = new THREE.Color(0xf0f0f0);
// //         sceneRef.current = scene;

// //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// //         const camera = new THREE.PerspectiveCamera(
// //             45,
// //             window.innerWidth / window.innerHeight,
// //             1,
// //             10000
// //         );
// //         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// //         camera.lookAt(worldOffset.x, 0, worldOffset.z);
// //         cameraRef.current = camera;

// //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// //         renderer.setSize(window.innerWidth, window.innerHeight);
// //         renderer.setPixelRatio(window.devicePixelRatio);
// //         mountRef.current.appendChild(renderer.domElement);
// //         rendererRef.current = renderer;

// //         const controls = new OrbitControls(camera, renderer.domElement);
// //         controls.enableDamping = true;
// //         controls.maxPolarAngle = Math.PI / 2.05;
// //         controls.target.set(worldOffset.x, 0, worldOffset.z);
// //         controls.update();
// //         controlsRef.current = controls;

// //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// //         dirLight.position.set(300, 400, 200);
// //         scene.add(dirLight);

// //         const raycaster = new THREE.Raycaster();
// //         const pointer = new THREE.Vector2();
// //         let isDragging = false;
// //         const grabOffset = new THREE.Vector3();
// //         // let highlight = null;

// //         // const highlighter = new ObjectHighlighter(scene, {
// //         //     color: 0x4da3ff,
// //         //     tintStrength: 0.35,
// //         //     auraScale: 1.06,
// //         //     auraOpacity: 0.25
// //         // });

// //         highlighterRef.current = new ObjectHighlighter(scene, {
// //             color: 0x4da3ff,
// //             tintStrength: 0.35,
// //             auraScale: 1.06,
// //             auraOpacity: 0.25
// //         });


// //         const updatePointer = (e) => {
// //             if (!rendererRef.current) return;
// //             const rect = rendererRef.current.domElement.getBoundingClientRect();
// //             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
// //             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
// //         };

// //         const highlightObject = (obj) => {
// //             // highlighter.highlightObject(obj);
// //             highlighterRef.current?.highlightObject?.(obj);
// //         };

// //         const clearHighlight = () => {
// //             // highlighter.clear();
// //             highlighterRef.current?.clear?.();
// //         };

// //         const onDown = (e) => {
// //             if (e.button !== 0) return;
// //             if (uiClickHandlerRef.current?.(e)) return;

// //             updatePointer(e);
// //             raycaster.setFromCamera(pointer, camera);

// //             const intersects = raycaster.intersectObjects(
// //                 sceneRef.current.children,
// //                 true
// //             );

// //             for (const i of intersects) {
// //                 if (i.object.userData?.isRadialButton) {
// //                     i.object.userData.onClick();
// //                     return;
// //                 }
// //             }

// //             const modelHits = raycaster.intersectObjects(objectsRef.current.filter(o => o.userData.isModel), true);


// //             let hitModel = modelHits[0]?.object;
// //             while (hitModel && !objectsRef.current.includes(hitModel)) hitModel = hitModel.parent;

// //             if (!hitModel) {
// //                 selectedObjectRef.current = null;
// //                 setSelectedObject(null);
// //                 clearHighlight();
// //                 removeRadialMenu();
// //                 return;
// //             }

// //             removeRadialMenu();

// //             selectObject(hitModel);
// //             if (!hitModel.userData.isWritableBoard) highlightObject(hitModel);
// //             else clearHighlight();

// //             createRadialMenu(hitModel);

// //             if (hitModel.userData.mode === "write") return;
// //             isDragging = true;
// //             const grabPoint = new THREE.Vector3();
// //             const grabPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -hitModel.position.y);
// //             grabOffset.set(0, 0, 0);
// //             if (raycaster.ray.intersectPlane(grabPlane, grabPoint)) grabOffset.copy(hitModel.position).sub(grabPoint);
// //             renderer.domElement.setPointerCapture(e.pointerId);
// //             controls.enabled = false;
// //         };




// //         // Przesuwanie płynne

// //         const onMove = (e) => {
// //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// //             const obj = selectedObjectRef.current;

// //             if (obj.userData.radialMenu) {
// //                 sceneRef.current.remove(obj.userData.radialMenu);
// //                 obj.userData.radialMenu = null;
// //             }

// //             updatePointer(e);
// //             raycaster.setFromCamera(pointer, camera);

// //             const p = new THREE.Vector3();
// //             const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -obj.position.y);
// //             if (!raycaster.ray.intersectPlane(dragPlane, p)) return;
// //             const { dimX, dimZ } = boardSizeRef.current;

// //             const maxZ = dimZ;

// //             let worldX = p.x + grabOffset.x;
// //             let worldZ = p.z + grabOffset.z;

// //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// //             const maxDepth = maxZ * GRID_SIZE;

// //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));



// //             const prevPosition = obj.position.clone();

// //             obj.position.set(
// //                 worldX,
// //                 obj.position.y,
// //                 worldZ
// //             );

// //             if (checkCollision(obj)) {
// //                 obj.position.copy(prevPosition);
// //                 return;
// //             }

// //             const gridX = worldX / GRID_SIZE;
// //             const rawGridZ = worldZ / GRID_SIZE;
// //             const gridZ = -maxZ - rawGridZ;

// //             obj.userData.gridX = gridX;
// //             obj.userData.gridZ = gridZ;
// //             syncPosition(obj);


// //             // highlighter?.update?.();
// //             highlighterRef.current?.update?.();
// //         };


// //         const onUp = () => {
// //             rotationDirRef.current = 0;
// //             isDragging = false;
// //             controls.enabled = true;
// //         };

// //         renderer.domElement.addEventListener("pointerdown", onDown);
// //         renderer.domElement.addEventListener("pointermove", onMove);
// //         renderer.domElement.addEventListener("pointerup", onUp);

// //         renderer.domElement.addEventListener("pointercancel", onUp);
// //         window.addEventListener("pointerup", onUp);
// //         window.addEventListener("blur", onUp);
// //         let frame;
// //         const animate = () => {
// //             frame = requestAnimationFrame(animate);


// //             // highlighter?.update?.();
// //             highlighterRef.current?.update?.();
// //             if (selectedObjectRef.current && rotationDirRef.current) {
// //                 rotateSelected(0.02 * rotationDirRef.current);
// //                 // highlight?.update?.();
// //             }

// //             controls.update();
// //             renderer.render(scene, camera);
// //         };

// //         animate();

// //         return () => {
// //             cancelAnimationFrame(frame);
// //             renderer.domElement.removeEventListener("pointerdown", onDown);
// //             renderer.domElement.removeEventListener("pointermove", onMove);
// //             renderer.domElement.removeEventListener("pointerup", onUp);
// //             renderer.domElement.removeEventListener("pointercancel", onUp);
// //             window.removeEventListener("pointerup", onUp);
// //             window.removeEventListener("blur", onUp);
// //             highlighterRef.current?.clear?.();
// //             controls.dispose();
// //             renderer.dispose();
// //             renderer.domElement.remove();
// //         };
// //     }, []);

// //     // -------------------- GRID + PLANE --------------------
// //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// //         const material = new THREE.LineBasicMaterial({ color });
// //         const geometry = new THREE.BufferGeometry();
// //         const vertices = [];

// //         const halfWidth = width / 2;

// //         for (let i = 0; i <= width; i++) {
// //             const x = (i - halfWidth) * gridSize;
// //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// //         }

// //         for (let i = 0; i <= height; i++) {
// //             const z = -i * gridSize;
// //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// //         }

// //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// //         const grid = new THREE.LineSegments(geometry, material);
// //         grid.userData.isGrid = true;
// //         return grid;
// //     };

// //     useEffect(() => {
// //         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

// //         const dimX = selectedScenario?.dimensionX || 18;
// //         const dimZ = selectedScenario?.dimensionZ || 18;
// //         boardSizeRef.current.dimX = dimX;
// //         boardSizeRef.current.dimZ = dimZ;

// //         removeRadialMenu();
// //         highlighterRef.current?.clear?.();
// //         // usuń poprzedni grid i plane
// //         sceneRef.current.children
// //             .filter((o) => o.userData?.isGrid)
// //             .forEach((o) => sceneRef.current.remove(o));

// //         if (planeRef.current) {
// //             sceneRef.current.remove(planeRef.current);
// //             planeRef.current.geometry.dispose();
// //         }

// //         objectsRef.current
// //             .filter((o) => o.userData?.isModel)
// //             .forEach((o) => sceneRef.current.remove(o));

// //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// //         sceneRef.current.add(grid);

// //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// //         planeGeo.rotateX(-Math.PI / 2);
// //         const plane = new THREE.Mesh(
// //             planeGeo,
// //             new THREE.MeshBasicMaterial({ visible: false })
// //         );
// //         plane.position.set(worldOffset.x, 0, worldOffset.z);
// //         sceneRef.current.add(plane);
// //         planeRef.current = plane;
// //         objectsRef.current = [plane];

// //         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// //         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
// //         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
// //         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
// //         controlsRef.current.update();

// //         selectedObjectRef.current = null;
// //         setSelectedObject(null);
// //     }, [selectedScenario?.id]);



// //     useEffect(() => {
// //         if (!sceneRef.current || !selectedScenario?.entities) return;

// //         let cancelled = false;
// //         const loadedInstances = [];

// //         const loadEntities = async () => {
// //             const { dimZ } = boardSizeRef.current;

// //             const worldOffsetZ = -(dimZ) * GRID_SIZE;
// //             const worldOffsetX = 0;

// //             const UNITY_Y_OFFSET = 180;


// //             // grupujemy encje po modelu
// //             const entitiesByModel = selectedScenario.entities.reduce((acc, e) => {
// //                 if (!acc[e.name]) acc[e.name] = [];
// //                 acc[e.name].push(e);
// //                 return acc;
// //             }, {});


// //             const loadModelEntities = async (modelName, entities) => {

// //                 try {
// //                     console.log("Loading model group:", modelName);


// //                     const tpl = await loadModel(modelName);

// //                     if (!tpl || cancelled) return;


// //                     modelTemplates.current[modelName] = tpl;


// //                     // tutaj od razu dodajemy wszystkie instancje tego modelu
// //                     for (const e of entities) {

// //                         if (cancelled) return;


// //                         const model = SkeletonUtils.clone(tpl);

// //                         model.name = e.name;
// //                         model.scale.setScalar(modelName === "whiteboard" ? 7 : 50);
// //                         model.userData.isModel = true;


// //                         const box = new THREE.Box3()
// //                             .setFromObject(model);

// //                         model.userData.heightOffset = -box.min.y;


// //                         const gridX = e.posX ?? 0;
// //                         const gridZ = e.posZ ?? 0;

// //                         model.userData.gridX = gridX;
// //                         model.userData.gridZ = gridZ;


// //                         const worldX = gridX * GRID_SIZE + worldOffsetX;
// //                         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;


// //                         model.position.set(
// //                             worldX,
// //                             model.userData.heightOffset,
// //                             worldZ
// //                         );


// //                         const baseQuat = tpl.quaternion.clone();


// //                         const userQuat = new THREE.Quaternion()
// //                             .setFromEuler(
// //                                 new THREE.Euler(
// //                                     degToRad(e.rotX || 0),
// //                                     degToRad(UNITY_Y_OFFSET - (e.rotY || 0)),
// //                                     degToRad(e.rotZ || 0),
// //                                     "YXZ"
// //                                 )
// //                             );


// //                         model.quaternion
// //                             .copy(baseQuat)
// //                             .multiply(userQuat);


// //                         model.position.y = 0;
// //                         model.userData.heightOffset = -getObjectBox(model).min.y;
// //                         model.position.y = model.userData.heightOffset + (Number(e.posY) || 0) * GRID_SIZE;
// //                         syncPosition(model);
// //                         if (modelName === "whiteboard") initBoard(model, e.boardText || "");
// //                         sceneRef.current.add(model);

// //                         objectsRef.current.push(model);
// //                         loadedInstances.push(model);


// //                         console.log(
// //                             "Added:",
// //                             model.name
// //                         );
// //                     }


// //                 } catch (err) {
// //                     console.error(
// //                         "Failed model group:",
// //                         modelName,
// //                         err
// //                     );
// //                 }
// //             };


// //             Object.entries(entitiesByModel)
// //                 .forEach(([name, entities]) => {
// //                     loadModelEntities(name, entities);
// //                 });

// //         };

// //         loadEntities();

// //         return () => {
// //             cancelled = true;
// //             loadedInstances.forEach(model => model.removeFromParent());
// //             objectsRef.current = objectsRef.current.filter(model => !loadedInstances.includes(model));
// //             if (loadedInstances.includes(selectedObjectRef.current)) {
// //                 removeRadialMenu();
// //                 highlighterRef.current?.clear?.();
// //                 selectedObjectRef.current = null;
// //                 setSelectedObject(null);
// //             }
// //         };

// //     }, [selectedScenario]);

// //     // useEffect(() => {
// //     //     if (!selectedScenario?.entities) return;

// //     //     const uniqueModels = [...new Set(
// //     //         selectedScenario.entities.map(e => e.name)
// //     //     )];

// //     //     uniqueModels.forEach(name => {
// //     //         const config = MODEL_LIBRARY.find(m => m.name === name);

// //     //         if (config) {
// //     //             loadModel(name, config.path).then(tpl => {
// //     //                 modelTemplates.current[name] = tpl;

// //     //                 setModelsReadyVersion(v => v + 1);
// //     //             });
// //     //         }
// //     //     });
// //     // }, [selectedScenario]);

// //     // -------------------- ADD / DELETE --------------------
// //     const addModel = async (name) => {
// //         const config = MODEL_LIBRARY.find(m => m.name === name);
// //         if (!config) return;

// //         // const tpl = modelTemplates.current[name];

// //         console.log("config2121:", config);

// //         const sceneTpl = await loadModel(name);
// //         if (!sceneTpl) return;


// //         const model = SkeletonUtils.clone(sceneTpl);
// //         model.name = name;
// //         model.scale.setScalar(name === "whiteboard" ? 7 : 50);
// //         modelTemplates.current[name] = sceneTpl;
// //         model.userData.isModel = true;

// //         const box = new THREE.Box3().setFromObject(model);
// //         model.userData.heightOffset = -box.min.y;

// //         // model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// //         // model.userData.gridZ = 0;

// //         // Centrowanie
// //         model.userData.gridX = 0
// //         model.userData.gridZ = -Math.floor((selectedScenario?.dimensionZ || 18) / 2);

// //         const worldX = model.userData.gridX * GRID_SIZE;
// //         const worldZ = model.userData.gridZ * GRID_SIZE;
// //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// //         const baseQuat = sceneTpl.quaternion.clone();
// //         const userQuat = new THREE.Quaternion().setFromEuler(
// //             new THREE.Euler(0, degToRad(UNITY_Y_OFFSET), 0, "YXZ")
// //         );

// //         model.quaternion.copy(baseQuat).multiply(userQuat);

// //         model.position.y = 0;
// //         model.userData.heightOffset = -getObjectBox(model).min.y;
// //         model.position.y = model.userData.heightOffset;
// //         syncPosition(model);
// //         if (name === "whiteboard") initBoard(model);
// //         sceneRef.current.add(model);
// //         objectsRef.current.push(model);

// //         if (selectedObjectRef.current) {
// //             removeRadialMenu(selectedObjectRef.current);
// //         }

// //         selectObject(model);
// //         createRadialMenu(model);
// //     };


// //     const deleteSelected = () => {
// //         if (!selectedObjectRef.current) return;
// //         removeRadialMenu();
// //         highlighterRef.current?.clear?.();
// //         sceneRef.current.remove(selectedObjectRef.current);
// //         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
// //         selectedObjectRef.current = null;
// //         setSelectedObject(null);
// //     };

// //     const duplicateSelected = () => {
// //         const selected = selectedObjectRef.current;
// //         if (!selected) return;

// //         highlighterRef.current?.clear?.();
// //         highlighterRef.current?.unhighlight?.(selected);

// //         selected.traverse((child) => {
// //             if (child.isMesh && child.userData?.originalColor) {
// //                 child.material.color.copy(child.userData.originalColor);
// //             }
// //         });

// //         removeRadialMenu();
// //         const copy = SkeletonUtils.clone(selected);

// //         copy.position.x += 10;
// //         copy.position.z += 10;

// //         copy.userData = { ...selected.userData, radialMenu: null };
// //         syncPosition(copy);
// //         if (copy.userData.isWritableBoard) initBoard(copy, selected.userData.boardText || "");

// //         sceneRef.current.add(copy);

// //         objectsRef.current.push(copy);

// //         selectObject(copy);
// //         createRadialMenu(copy);
// //     };


// //     const saveScenario = async () => {
// //         if (!selectedScenario) return;

// //         const entities = objectsRef.current
// //             .filter(o => o.userData.isModel)
// //             .map(o => {
// //                 const tpl = modelTemplates.current[o.name];
// //                 const baseQuat = tpl?.quaternion.clone() || new THREE.Quaternion();
// //                 const baseInv = baseQuat.clone().invert();

// //                 const userQuat = o.quaternion.clone().premultiply(baseInv);

// //                 const euler = new THREE.Euler().setFromQuaternion(userQuat, "YXZ");

// //                 const rotX = normalizeDeg(radToDeg(euler.x));
// //                 const rotY = normalizeDeg(UNITY_Y_OFFSET - normalizeDeg(radToDeg(euler.y))); // flip Y do VR
// //                 const rotZ = normalizeDeg(radToDeg(euler.z));

// //                 return {
// //                     name: o.name,
// //                     GUID: crypto.randomUUID(),
// //                     posX: round3(o.position.x / GRID_SIZE),
// //                     posY: round3((o.position.y - (o.userData.heightOffset || 0)) / GRID_SIZE),
// //                     posZ: round3(-boardSizeRef.current.dimZ - o.position.z / GRID_SIZE),
// //                     ...(o.userData.isWritableBoard ? { boardText: o.userData.boardText || "" } : {}),
// //                     rotX,
// //                     rotY,
// //                     rotZ,
// //                 };
// //             });

// //         // console.log('entities to save:', entities);

// //         try {
// //             const response = await fetch(
// //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// //                 {
// //                     method: "PATCH",
// //                     headers: { "Content-Type": "application/json" },
// //                     body: JSON.stringify({
// //                         stationName: selectedScenario.stationName,
// //                         GUID: crypto.randomUUID(),
// //                         dimensionX: selectedScenario.dimensionX,
// //                         dimensionY: selectedScenario.dimensionY,
// //                         entities,
// //                     }),
// //                 }
// //             );

// //             if (!response.ok) {
// //                 const errorData = await response.json();
// //                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
// //             }

// //             setAlertMessage("Stacja zapisana pomyślnie!");
// //             setAlertSeverity("success");
// //             setAlertOpen(true);
// //         } catch (error) {
// //             console.error(error);
// //             setAlertMessage(`Błąd: ${error.message}`);
// //             setAlertSeverity("error");
// //             setAlertOpen(true);
// //         }
// //     };


// //     const rotateSelected = (angle) => {
// //         const obj = selectedObjectRef.current;
// //         if (!obj || obj.userData.mode === "write") return;
// //         const previous = obj.quaternion.clone();
// //         obj.rotation.y += angle;
// //         if (checkCollision(obj)) obj.quaternion.copy(previous);
// //         highlighterRef.current?.update?.();
// //     };
// //     const rotate90Right = () => rotateSelected(-Math.PI / 2);
// //     const rotate90Left = () => rotateSelected(Math.PI / 2);

// //     const textureLoader = new THREE.TextureLoader();


// //     const createRadialMenu = (object) => {
// //         if (!sceneRef.current) return;

// //         // USUWANIE STAREGO MENU
// //         if (object.userData.radialMenu) {
// //             sceneRef.current.remove(object.userData.radialMenu);
// //             object.userData.radialMenu = null;
// //         }

// //         const radius = 120;
// //         const group = new THREE.Group();
// //         group.userData.isRadialMenu = true;

// //         const actions = [
// //             { fn: rotate90Left, icon: "/icons/rotateleft.png" },
// //             { fn: rotate90Right, icon: "/icons/rotateright.png" },
// //             { fn: deleteSelected, icon: "/icons/delete.png" },
// //             { fn: duplicateSelected, icon: "/icons/copy.png" }
// //         ];

// //         // ======= DODANIE PRZYCISKU PISZ DLA TABLICY =======
// //         if (object.userData.isWritableBoard) {
// //             actions.push({
// //                 fn: () => toggleBoard(object),
// //                 icon: "/icons/pisz.png",
// //                 isWriteButton: true // oznaczenie, żeby łatwo znaleźć
// //             });
// //         }

// //         actions.forEach((action, i) => {
// //             const angle = (i / actions.length) * Math.PI * 2;

// //             const geometry = new THREE.CircleGeometry(20, 32);
// //             const material = new THREE.MeshBasicMaterial({
// //                 color: action.isWriteButton
// //                     ? object.userData.mode === "write"
// //                         ? 0xffff00
// //                         : 0x6cc6d9
// //                     : 0x6cc6d9,
// //                 depthTest: false
// //             });

// //             const mesh = new THREE.Mesh(geometry, material);
// //             mesh.rotation.x = -Math.PI / 2;
// //             mesh.position.set(
// //                 Math.cos(angle) * radius,
// //                 1,
// //                 Math.sin(angle) * radius
// //             );

// //             const texture = textureLoader.load(action.icon);
// //             const iconGeometry = new THREE.PlaneGeometry(30, 30);
// //             const iconMaterial = new THREE.MeshBasicMaterial({
// //                 map: texture,
// //                 transparent: true,
// //                 depthTest: false,
// //                 side: THREE.DoubleSide
// //             });
// //             const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);
// //             iconMesh.rotation.x = Math.PI;
// //             iconMesh.rotation.z = -Math.PI / 2;
// //             iconMesh.position.set(0, 0.1, 0);
// //             mesh.add(iconMesh);

// //             mesh.userData.onClick = action.fn;
// //             mesh.userData.isRadialButton = true;
// //             if (action.isWriteButton) mesh.userData.isWriteButton = true;

// //             group.add(mesh);
// //             object.userData.radialMenu = group;
// //         });

// //         sceneRef.current.add(group);
// //         group.position.copy(object.position);
// //         group.position.y = 1;
// //     };





// //     const removeRadialMenu = () => {
// //         if (!sceneRef.current) return;

// //         const menus = sceneRef.current.children.filter(
// //             o => o.userData?.isRadialMenu
// //         );

// //         menus.forEach(menu => {
// //             sceneRef.current.remove(menu);
// //             menu.traverse(child => {
// //                 child.geometry?.dispose();
// //                 child.material?.map?.dispose();
// //                 child.material?.dispose();
// //             });
// //         });
// //         objectsRef.current.forEach(obj => { obj.userData.radialMenu = null; });
// //     };




// //     // -------------------- KEYBOARD --------------------

// //     useEffect(() => {
// //         // Przesuwanie płynne
// //         const moveGrid = (dx, dz) => {
// //             if (!selectedObjectRef.current) return;

// //             const obj = selectedObjectRef.current;
// //             const { dimX, dimZ } = boardSizeRef.current;

// //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// //             const maxDepth = dimZ * GRID_SIZE;

// //             let worldX = obj.position.x + dx;
// //             let worldZ = obj.position.z + dz;

// //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// //             const prevPosition = obj.position.clone();

// //             obj.position.set(
// //                 worldX,
// //                 obj.position.y,
// //                 worldZ
// //             );

// //             if (checkCollision(obj)) {
// //                 obj.position.copy(prevPosition);
// //                 return;
// //             }

// //             const gridX = Math.round(worldX / GRID_SIZE);
// //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// //             const maxZ = dimZ - 1;
// //             const gridZ = -maxZ - rawGridZ;


// //             obj.userData.gridX = gridX;
// //             obj.userData.gridZ = gridZ;
// //             syncPosition(obj);

// //             removeRadialMenu();
// //         };



// //         const onKeyDown = (e) => {
// //             if (
// //                 e.target.tagName === "INPUT" ||
// //                 e.target.tagName === "TEXTAREA" ||
// //                 e.target.isContentEditable
// //             ) return;

// //             // Dodajemy blokadę ruchu w trybie write (TABLICA)
// //             const obj = selectedObjectRef.current;
// //             if (!obj) return;

// //             if (obj.userData.mode === "write") {
// //                 return;
// //             }

// //             switch (e.key.toLowerCase()) {
// //                 case "q":
// //                 case "e":
// //                     e.preventDefault();
// //                     changeHeight((obj.position.y - (obj.userData.heightOffset || 0)) / GRID_SIZE + (e.key.toLowerCase() === "e" ? 0.1 : -0.1));
// //                     break;

// //                 case "w":
// //                     e.preventDefault();
// //                     moveGrid(0, 5);
// //                     break;
// //                 case "s":
// //                     e.preventDefault();
// //                     moveGrid(0, -5);
// //                     break;
// //                 case "a":
// //                     e.preventDefault();
// //                     moveGrid(5, 0);
// //                     break;
// //                 case "d":
// //                     e.preventDefault();
// //                     moveGrid(-5, 0);
// //                     break;

// //                 case "arrowleft":
// //                     e.preventDefault();
// //                     rotate90Left();
// //                     break;
// //                 case "arrowright":
// //                     e.preventDefault();
// //                     rotate90Right();
// //                     break;

// //                 case "delete":
// //                 case "backspace":
// //                     e.preventDefault();
// //                     deleteSelected();
// //                     break;

// //                 case "escape":
// //                     selectedObjectRef.current = null;
// //                     setSelectedObject(null);
// //                     break;

// //                 default:
// //                     break;
// //             }
// //         };

// //         window.addEventListener("keydown", onKeyDown);
// //         return () => window.removeEventListener("keydown", onKeyDown);
// //     }, []);


// //     // -------------------- RENDER --------------------
// //     return (
// //         <>
// //             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
// //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
// //                     <BackToScenariosButton />
// //                 </Box>
// //                 <Divider sx={{ my: 1 }} />
// //                 {/* <Typography variant="subtitle2">Modele</Typography> */}
// //                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
// //                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// //                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
// //                         <ListItem key={m.name} disablePadding>
// //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// //                                 <ListItemText primary={m.name} />
// //                             </ListItemButton>
// //                         </ListItem>
// //                     ))}
// //                 </List> */}

// //                 {selectedObject && (
// //                     <>
// //                         <Divider sx={{ my: 1 }} />
// //                         <TextField
// //                             label="Wysokość Y (jednostki siatki)" type="number" fullWidth size="small"
// //                             value={height} disabled={boardMode === "write"}
// //                             inputProps={{ min: 0, step: 0.1 }}
// //                             onChange={event => changeHeight(Number(event.target.value))}
// //                         />
// //                         <Stack direction="row" spacing={1} sx={{ my: 1 }}>
// //                             <Button disabled={boardMode === "write"} onClick={() => changeHeight(height - 0.1)}>Y −</Button>
// //                             <Button disabled={boardMode === "write"} onClick={() => changeHeight(height + 0.1)}>Y +</Button>
// //                         </Stack>
// //                         <Button fullWidth disabled={boardMode === "write"} onClick={placeOnSupport}>Opuść na obiekt / podłogę</Button>
// //                         <Typography variant="caption">Q / E: dół / góra. Unieś przedmiot, przesuń nad drugi i opuść.</Typography>
// //                         {selectedObject.userData.isWritableBoard && <>
// //                             <Button fullWidth onClick={() => toggleBoard(selectedObject)}>
// //                                 {boardMode === "write" ? "Zakończ pisanie" : "Pisz na tablicy"}
// //                             </Button>
// //                             {boardMode === "write" && <TextField
// //                                 label="Tekst tablicy" multiline minRows={3} fullWidth value={boardText}
// //                                 onChange={event => {
// //                                     setBoardText(event.target.value);
// //                                     paintBoard(selectedObject, event.target.value);
// //                                 }}
// //                             />}
// //                         </>}
// //                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
// //                             <IconButton
// //                                 onClick={rotate90Left}
// //                             >
// //                                 <Rotate90DegreesCcwIcon />
// //                             </IconButton>
// //                             <IconButton
// //                                 onMouseDown={() => (rotationDirRef.current = 1)}
// //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// //                             >
// //                                 <RotateLeftIcon />
// //                             </IconButton>
// //                             <IconButton
// //                                 onMouseDown={() => (rotationDirRef.current = -1)}
// //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// //                             >
// //                                 <RotateRightIcon />
// //                             </IconButton>
// //                             <IconButton
// //                                 onClick={rotate90Right}
// //                             >
// //                                 <Rotate90DegreesCwIcon />
// //                             </IconButton>
// //                             <IconButton color="error" onClick={deleteSelected}>
// //                                 <DeleteIcon />
// //                             </IconButton>
// //                             {/* <Button onClick={logRotation}>
// //                                 log
// //                             </Button> */}
// //                         </Stack>
// //                     </>
// //                 )}

// //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// //                     Zapisz stację
// //                 </Button>
// //             </Paper>

// //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// //             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
// //             {gridWalls && <GridWalls
// //                 scene={sceneRef.current}
// //                 dimX={boardSizeRef.current.dimX}
// //                 dimZ={boardSizeRef.current.dimZ}
// //                 GRID_SIZE={GRID_SIZE}
// //                 cameraRef={cameraRef}
// //             />}

// //             {sceneRef.current && cameraRef.current && (
// //                 <>
// //                     <FloorLogo
// //                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
// //                         scene={sceneRef.current}
// //                         svgPath="/micree.svg"
// //                         GRID_SIZE={GRID_SIZE}
// //                         boardSizeRef={boardSizeRef.current}
// //                     />
// //                     <UI3DModelButtons
// //                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}-${modelsReadyVersion}`}
// //                         scene={sceneRef.current}
// //                         camera={cameraRef.current}
// //                         modelTemplates={modelTemplates.current}
// //                         objectsRef={objectsRef.current}
// //                         onClickHandlerRef={uiClickHandlerRef}
// //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// //                         changeScenarioRef={changeScenarioRef}
// //                         boardSizeRef={boardSizeRef.current}
// //                         GRID_SIZE={GRID_SIZE}
// //                     />
// //                 </>
// //             )}

// //             <Typography
// //                 variant="h6"
// //                 sx={{
// //                     mt: 1,
// //                     position: "absolute",
// //                     top: 70,
// //                     left: "50%",
// //                     transform: "translateX(-50%)",
// //                     fontSize: "10px",
// //                 }}
// //             >
// //                 Wybrana stacja:
// //             </Typography>
// //             <Typography
// //                 variant="h6"
// //                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
// //             >
// //                 {/* {selectedScenario?.existingSource} */}
// //                 {
// //                     selectedScenario?.stationName
// //                         ? selectedScenario.stationName
// //                         : selectedScenario?.id !== undefined
// //                             ? `Stacja ${selectedScenario.id}`
// //                             : "nie wybrano"
// //                 }
// //             </Typography>

// //             <IconButton
// //                 onClick={() => setGridWalls(prev => !prev)}
// //                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
// //             >
// //                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

// //             </IconButton>

// //             <Snackbar
// //                 open={alertOpen}
// //                 autoHideDuration={4000}
// //                 onClose={() => setAlertOpen(false)}
// //                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
// //                 sx={{ mt: 8 }}
// //             >
// //                 <Alert
// //                     onClose={() => setAlertOpen(false)}
// //                     severity={alertSeverity}
// //                     sx={{ width: "100%" }}
// //                 >
// //                     {alertMessage}
// //                 </Alert>
// //             </Snackbar>


// //             {/* <WallBoards
// //                 scene={sceneRef.current}
// //                 cameraRef={cameraRef}
// //                 rendererRef={rendererRef}
// //             //   wallsRef={}
// //             /> */}





// //         </>
// //     );
// // }










// // // import React, { useEffect, useRef, useState } from "react";
// // // import * as THREE from "three";
// // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// // // import {
// // //     Box,
// // //     Button,
// // //     List,
// // //     ListItem,
// // //     ListItemButton,
// // //     ListItemText,
// // //     Typography,
// // //     Paper,
// // //     IconButton,
// // //     Divider,
// // //     Stack,
// // //     Snackbar,
// // //     Alert,
// // // } from "@mui/material";

// // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// // // import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// // // import DeleteIcon from "@mui/icons-material/Delete";
// // // import GridOffIcon from '@mui/icons-material/GridOff';
// // // import GridOnIcon from '@mui/icons-material/GridOn';

// // // import FloorLogo from "./three/FloorLogo";
// // // import UI3DModelButtons from "./three/UI3DModelButtons";
// // // import ModelList from "./utilities/ModelList";
// // // import GridWalls from "./three/GridWalls";
// // // import { ObjectHighlighter } from "./utilities/ObjectHighlighter";
// // // import WallBoards from "./three/WallBoards";
// // // import WritableBoard, { DebugBoardMeshes } from "./three/WritableBoard";
// // // import WritableBoardItem from "./three/WritableBoard";
// // // import { createWritableBoard } from "./utilities/createWritableBoard";
// // // import { useSelector } from "react-redux";

// // // const GRID_SIZE = 50;

// // // const UNITY_Y_OFFSET = 180;

// // // const MODEL_LIBRARY = [
// // //     { name: "whiteboard", visible: true },
// // //     { name: "CC CC0 1", visible: false },
// // //     { name: "arrow", visible: false },

// // //     { name: "envp_bin_4", visible: true },
// // //     { name: "envp_bin_4_lids", visible: true },
// // //     { name: "envp_bin_lid", visible: true },

// // //     { name: "envp_furnitures_chair", visible: true },
// // //     { name: "envp_furnitures_office_chair", visible: true },

// // //     { name: "envp_furnitures_sofa_corner_3_2", visible: true },
// // //     { name: "envp_furnitures_sofa_corner_3_3", visible: true },
// // //     { name: "envp_furnitures_sofa_line_2", visible: true },
// // //     { name: "envp_furnitures_sofa_line_3", visible: true },
// // //     { name: "envp_furnitures_sofa_single", visible: true },
// // //     { name: "envp_furnitures_sofa_single_corner", visible: true },

// // //     { name: "envp_furnitures_table_140_80", visible: true },
// // //     { name: "envp_furnitures_table_160_80", visible: true },
// // //     { name: "envp_furnitures_table_250_120", visible: true },
// // //     { name: "envp_furnitures_table_400_120", visible: true },

// // //     { name: "envp_furnitures_table_small_100x100x100", visible: true },
// // //     { name: "envp_furnitures_table_small_100x100x75", visible: true },
// // //     { name: "envp_furnitures_table_small_125x100x100", visible: true },
// // //     { name: "envp_furnitures_table_small_125x100x75", visible: true },
// // //     { name: "envp_furnitures_table_small_75x75x100", visible: true },
// // //     { name: "envp_furnitures_table_small_75x75x75", visible: true },

// // //     { name: "envp_modular_door_frame", visible: true },
// // //     { name: "env_staircase_stairs", visible: true },
// // //     { name: "env_wall_column_05", visible: true },
// // //     { name: "env_wall_creator_x2_5", visible: true },

// // //     { name: "exe_cd_essential_items", visible: true },

// // //     { name: "exe_cooking_bins", visible: true },
// // //     { name: "exe_cooking_cookbase", visible: true },
// // //     { name: "exe_cooking_cook_station", visible: true },
// // //     { name: "exe_cooking_countertop_2", visible: true },
// // //     { name: "exe_cooking_countertop_4", visible: true },
// // //     { name: "exe_cooking_countertop_drawer", visible: true },
// // //     { name: "exe_cooking_device_blender", visible: true },
// // //     { name: "exe_cooking_device_mixer", visible: true },
// // //     { name: "exe_cooking_device_mixer_blender", visible: true },
// // //     { name: "exe_cooking_drawers_ingrediens", visible: true },
// // //     { name: "exe_cooking_fridge_dairy", visible: true },
// // //     { name: "exe_cooking_fridge_fruits", visible: true },
// // //     { name: "exe_cooking_fridge_meat", visible: true },
// // //     { name: "exe_cooking_fridge_vegetables", visible: true },
// // //     { name: "exe_cooking_fridges_all", visible: true },
// // //     { name: "exe_cooking_mech_cooker", visible: true },
// // //     { name: "exe_cooking_mech_furnance", visible: true },
// // //     { name: "exe_cooking_sink", visible: true },
// // //     { name: "exe_cooking_sink_hanged", visible: true },
// // //     { name: "exe_cooking_utensils", visible: true },

// // //     { name: "exe_creator_door_office_double", visible: true },
// // //     { name: "exe_creator_door_wooden_1", visible: true },

// // //     { name: "exe_printing_3d_printing_station", visible: true },
// // //     { name: "exe_printing_cabinet_filament_abs_ptg", visible: true },
// // //     { name: "exe_printing_cabinet_filament_pla_tpu", visible: true },
// // //     { name: "exe_printing_cleaning_set", visible: true },
// // //     { name: "exe_printing_examples", visible: true },

// // //     { name: "exe_wd_22bolt_22nut_impactdriver", visible: true },
// // //     { name: "exe_wd_22nut_impactdriver", visible: true },
// // //     { name: "exe_wd_combined_exercises", visible: true },
// // //     { name: "exe_wd_drill_26plug_26screw_impactdriver", visible: true },
// // //     { name: "exe_wd_drill_dowel_hammer", visible: true },
// // //     { name: "exe_wd_foaming", visible: true },
// // //     { name: "exe_wd_garage_gate_installation", visible: true },
// // //     { name: "exe_wd_garage_gate_supply", visible: true },
// // //     { name: "exe_wd_interior_door_installation", visible: true },
// // //     { name: "exe_wd_interior_door_supply", visible: true },
// // //     { name: "exe_wd_leveler", visible: true },
// // //     { name: "exe_wd_outer_door_installation", visible: true },
// // //     { name: "exe_wd_outer_door_supply", visible: true },
// // //     { name: "exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill", visible: true },
// // //     { name: "exe_wd_roof_window_installation", visible: true },
// // //     { name: "exe_wd_roof_window_supply", visible: true },
// // //     { name: "exe_wd_strut", visible: true },
// // //     { name: "exe_wd_technical_door_installation", visible: true },
// // //     { name: "exe_wd_technical_door_supply", visible: true },
// // //     { name: "exe_wd_tool_rack_1", visible: true },
// // //     { name: "exe_wd_tool_rack_boxes", visible: true },
// // //     { name: "exe_wd_vertical_window_installation", visible: true },
// // //     { name: "exe_wd_vertical_window_supply", visible: true },
// // //     { name: "exe_wd_wedge_hammer", visible: true },

// // //     { name: "elevator_1x2.1x", visible: true },
// // // ];

// // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // //     const mountRef = useRef(null);
// // //     const sceneRef = useRef(null);
// // //     const cameraRef = useRef(null);
// // //     const rendererRef = useRef(null);
// // //     const controlsRef = useRef(null);
// // //     const planeRef = useRef(null);
// // //     const objectsRef = useRef([]);
// // //     const modelTemplates = useRef({});
// // //     const selectedObjectRef = useRef(null);
// // //     const uiClickHandlerRef = useRef(null);
// // //     const rotationDirRef = useRef(0);
// // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // //     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
// // //     const [gridVersion, setGridVersion] = useState();

// // //     const highlighterRef = useRef(null);

// // //     const assetsTokenRef = useRef(null);

// // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // //     const [selectedObject, setSelectedObject] = useState(null);
// // //     const [gridWalls, setGridWalls] = useState(true);



// // //     // Powiadomienia
// // //     const [alertOpen, setAlertOpen] = useState(false);
// // //     const [alertMessage, setAlertMessage] = useState("");
// // //     const [alertSeverity, setAlertSeverity] = useState("success");

// // //     const [modelsReadyVersion, setModelsReadyVersion] = useState(0);

// // //     // const [MODEL_LIBRARY, setModelLibrary] = useState([]);


// // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // //     const degToRad = (deg) => (deg * Math.PI) / 180;
// // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


// // //     const { accessToken: token } = useSelector((state) => state.auth);

// // //     async function assetsBootstrap() {
// // //         const res = await fetch(
// // //             // `${process.env.REACT_APP_API_URL_VR_EDITOR}/assets-vr/bootstrap?env=webe`,
// // //             `https://beta.vccsystem.eu:3002/assets-vr/bootstrap?env=webe`,
// // //             {
// // //                 headers: {
// // //                     Authorization: `Bearer ${token}`,
// // //                 },
// // //             }
// // //         );
// // //         console.log("assets bootstrap response:", res);
// // //         if (!res.ok) {
// // //             throw new Error("Bootstrap failed");
// // //         }

// // //         const data = await res.json();

// // //         console.log("assets bootstrap:", data);

// // //         assetsTokenRef.current = data.token;
// // //     }



// // //     useEffect(() => {
// // //         const init = async () => {
// // //             await assetsBootstrap();

// // //             const arrowConfig = MODEL_LIBRARY.find(
// // //                 m => m.name === "arrow"
// // //             );

// // //             if (!arrowConfig) return;
// // //             const tpl = await loadModel(
// // //                 arrowConfig.name
// // //             );

// // //             modelTemplates.current["arrow"] = tpl;
// // //             setModelsReadyVersion(v => v + 1);
// // //         };
// // //         init();
// // //     }, [MODEL_LIBRARY]);


// // //     // -------------------- GRID UPDATE --------------------

// // //     useEffect(() => {
// // //         if (!selectedScenario) return;
// // //         setBoardSize({
// // //             dimX: selectedScenario.dimensionX || 18,
// // //             dimZ: selectedScenario.dimensionZ || 18
// // //         });
// // //         setGridVersion(v => v + 1);
// // //     }, [selectedScenario?.id]);

// // //     const modelCache = useRef({});
// // //     const modelPromises = useRef({});

// // //     const loadModel = (name) => {

// // //         // cache gotowego modelu
// // //         if (modelCache.current[name]) {
// // //             return Promise.resolve(modelCache.current[name]);
// // //         }

// // //         // już trwa pobieranie
// // //         if (modelPromises.current[name]) {
// // //             return modelPromises.current[name];
// // //         }


// // //         const promise = new Promise((resolve, reject) => {

// // //             console.log("Loading model:", name);


// // //             const baseUrl =
// // //                 `https://assets.micree.eu/webe/${encodeURIComponent(name)}`;


// // //             const manager = new THREE.LoadingManager();


// // //             manager.setURLModifier((url) => {

// // //                 if (!url.startsWith("http")) {
// // //                     url = `${baseUrl}/${url}`;
// // //                 }


// // //                 const separator = url.includes("?")
// // //                     ? "&"
// // //                     : "?";


// // //                 return `${url}${separator}${assetsTokenRef.current}`;
// // //             });


// // //             const loader = new GLTFLoader(manager);


// // //             const url =
// // //                 `${baseUrl}/${encodeURIComponent(name)}.gltf`;


// // //             console.log("GLTF URL:", url);


// // //             loader.load(
// // //                 url,

// // //                 (gltf) => {

// // //                     console.log("Loaded model:", name);

// // //                     modelCache.current[name] = gltf.scene;

// // //                     delete modelPromises.current[name];

// // //                     resolve(gltf.scene);
// // //                 },

// // //                 undefined,

// // //                 (err) => {

// // //                     console.error(
// // //                         "GLTF failed:",
// // //                         name,
// // //                         err
// // //                     );

// // //                     delete modelPromises.current[name];

// // //                     reject(err);
// // //                 }
// // //             );

// // //         });


// // //         modelPromises.current[name] = promise;

// // //         return promise;
// // //     };

// // //     const getObjectBox = (obj) => {
// // //         return new THREE.Box3().setFromObject(obj);
// // //     };

// // //     const checkCollision = (obj) => {
// // //         if (!sceneRef.current) return false;

// // //         const objBox = getObjectBox(obj);

// // //         const walls = sceneRef.current.children.filter(o => o.userData?.isWall);

// // //         for (const wall of walls) {
// // //             const wallBox = new THREE.Box3().setFromObject(wall);
// // //             if (objBox.intersectsBox(wallBox)) return true;
// // //         }

// // //         for (const other of objectsRef.current) {
// // //             if (!other.userData?.isModel) continue;
// // //             if (other === obj) continue;

// // //             const otherBox = getObjectBox(other);
// // //             if (objBox.intersectsBox(otherBox)) return true;
// // //         }

// // //         return false;
// // //     };

// // //     // -------------------- INIT SCENE --------------------
// // //     useEffect(() => {
// // //         if (!mountRef.current) return;

// // //         const { dimX, dimZ } = boardSizeRef.current;

// // //         const scene = new THREE.Scene();
// // //         scene.background = new THREE.Color(0xf0f0f0);
// // //         sceneRef.current = scene;

// // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // //         const camera = new THREE.PerspectiveCamera(
// // //             45,
// // //             window.innerWidth / window.innerHeight,
// // //             1,
// // //             10000
// // //         );
// // //         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // //         camera.lookAt(worldOffset.x, 0, worldOffset.z);
// // //         cameraRef.current = camera;

// // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // //         renderer.setPixelRatio(window.devicePixelRatio);
// // //         mountRef.current.appendChild(renderer.domElement);
// // //         rendererRef.current = renderer;

// // //         const controls = new OrbitControls(camera, renderer.domElement);
// // //         controls.enableDamping = true;
// // //         controls.maxPolarAngle = Math.PI / 2.05;
// // //         controls.target.set(worldOffset.x, 0, worldOffset.z);
// // //         controls.update();
// // //         controlsRef.current = controls;

// // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // //         dirLight.position.set(300, 400, 200);
// // //         scene.add(dirLight);

// // //         const raycaster = new THREE.Raycaster();
// // //         const pointer = new THREE.Vector2();
// // //         let isDragging = false;
// // //         // let highlight = null;

// // //         // const highlighter = new ObjectHighlighter(scene, {
// // //         //     color: 0x4da3ff,
// // //         //     tintStrength: 0.35,
// // //         //     auraScale: 1.06,
// // //         //     auraOpacity: 0.25
// // //         // });

// // //         highlighterRef.current = new ObjectHighlighter(scene, {
// // //             color: 0x4da3ff,
// // //             tintStrength: 0.35,
// // //             auraScale: 1.06,
// // //             auraOpacity: 0.25
// // //         });


// // //         const updatePointer = (e) => {
// // //             if (!rendererRef.current) return;
// // //             const rect = rendererRef.current.domElement.getBoundingClientRect();
// // //             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
// // //             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
// // //         };

// // //         const highlightObject = (obj) => {
// // //             // highlighter.highlightObject(obj);
// // //             highlighterRef.current?.highlightObject?.(obj);
// // //         };

// // //         const clearHighlight = () => {
// // //             // highlighter.clear();
// // //             highlighterRef.current?.clear?.();
// // //         };

// // //         const onDown = (e) => {
// // //             if (uiClickHandlerRef.current?.(e)) return;

// // //             updatePointer(e);
// // //             raycaster.setFromCamera(pointer, camera);

// // //             const intersects = raycaster.intersectObjects(
// // //                 sceneRef.current.children,
// // //                 true
// // //             );

// // //             for (const i of intersects) {
// // //                 if (i.object.userData?.isRadialButton) {
// // //                     i.object.userData.onClick();
// // //                     return;
// // //                 }
// // //             }

// // //             let hitModel = null;
// // //             const tmp = new THREE.Vector3();

// // //             objectsRef.current.forEach((o) => {
// // //                 if (!o.userData.isModel) return;

// // //                 const box = new THREE.Box3().setFromObject(o);
// // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) {
// // //                     hitModel = o;
// // //                 }
// // //             });

// // //             if (!hitModel) {
// // //                 selectedObjectRef.current = null;
// // //                 setSelectedObject(null);
// // //                 clearHighlight();
// // //                 removeRadialMenu();
// // //                 return;
// // //             }

// // //             removeRadialMenu();

// // //             selectedObjectRef.current = hitModel;
// // //             setSelectedObject(hitModel);
// // //             highlightObject(hitModel);

// // //             createRadialMenu(hitModel);

// // //             isDragging = true;
// // //             controls.enabled = false;
// // //         };




// // //         // Przesuwanie płynne

// // //         const onMove = (e) => {
// // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // //             const obj = selectedObjectRef.current;

// // //             if (obj.userData.radialMenu) {
// // //                 sceneRef.current.remove(obj.userData.radialMenu);
// // //                 obj.userData.radialMenu = null;
// // //             }

// // //             updatePointer(e);
// // //             raycaster.setFromCamera(pointer, camera);

// // //             const hits = raycaster.intersectObject(planeRef.current);
// // //             if (!hits.length) return;

// // //             const p = hits[0].point;
// // //             const { dimX, dimZ } = boardSizeRef.current;

// // //             const maxZ = dimZ;

// // //             let worldX = p.x;
// // //             let worldZ = p.z;

// // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // //             const maxDepth = maxZ * GRID_SIZE;

// // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));



// // //             const prevPosition = obj.position.clone();

// // //             obj.position.set(
// // //                 worldX,
// // //                 obj.userData.heightOffset || 0,
// // //                 worldZ
// // //             );

// // //             if (checkCollision(obj)) {
// // //                 obj.position.copy(prevPosition);
// // //                 return;
// // //             }

// // //             const gridX = worldX / GRID_SIZE;
// // //             const rawGridZ = worldZ / GRID_SIZE;
// // //             const gridZ = -maxZ - rawGridZ;

// // //             obj.userData.gridX = gridX;
// // //             obj.userData.gridZ = gridZ;


// // //             // highlighter?.update?.();
// // //             highlighterRef.current?.update?.();
// // //         };


// // //         const onUp = () => {
// // //             isDragging = false;
// // //             controls.enabled = true;
// // //         };

// // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // //         renderer.domElement.addEventListener("pointermove", onMove);
// // //         renderer.domElement.addEventListener("pointerup", onUp);

// // //         const animate = () => {
// // //             requestAnimationFrame(animate);


// // //             // highlighter?.update?.();
// // //             highlighterRef.current?.update?.();
// // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // //                 // highlight?.update?.();
// // //             }

// // //             controls.update();
// // //             renderer.render(scene, camera);
// // //         };

// // //         animate();

// // //         return () => renderer.domElement.remove();
// // //     }, []);

// // //     // -------------------- GRID + PLANE --------------------
// // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // //         const material = new THREE.LineBasicMaterial({ color });
// // //         const geometry = new THREE.BufferGeometry();
// // //         const vertices = [];

// // //         const halfWidth = width / 2;

// // //         for (let i = 0; i <= width; i++) {
// // //             const x = (i - halfWidth) * gridSize;
// // //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // //         }

// // //         for (let i = 0; i <= height; i++) {
// // //             const z = -i * gridSize;
// // //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // //         }

// // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // //         const grid = new THREE.LineSegments(geometry, material);
// // //         grid.userData.isGrid = true;
// // //         return grid;
// // //     };

// // //     useEffect(() => {
// // //         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

// // //         const dimX = selectedScenario?.dimensionX || 18;
// // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // //         boardSizeRef.current.dimX = dimX;
// // //         boardSizeRef.current.dimZ = dimZ;

// // //         // usuń poprzedni grid i plane
// // //         sceneRef.current.children
// // //             .filter((o) => o.userData?.isGrid)
// // //             .forEach((o) => sceneRef.current.remove(o));

// // //         if (planeRef.current) {
// // //             sceneRef.current.remove(planeRef.current);
// // //             planeRef.current.geometry.dispose();
// // //         }

// // //         objectsRef.current
// // //             .filter((o) => o.userData?.isModel)
// // //             .forEach((o) => sceneRef.current.remove(o));

// // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // //         sceneRef.current.add(grid);

// // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // //         planeGeo.rotateX(-Math.PI / 2);
// // //         const plane = new THREE.Mesh(
// // //             planeGeo,
// // //             new THREE.MeshBasicMaterial({ visible: false })
// // //         );
// // //         plane.position.set(worldOffset.x, 0, worldOffset.z);
// // //         sceneRef.current.add(plane);
// // //         planeRef.current = plane;
// // //         objectsRef.current = [plane];

// // //         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // //         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
// // //         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
// // //         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
// // //         controlsRef.current.update();

// // //         selectedObjectRef.current = null;
// // //         setSelectedObject(null);
// // //     }, [selectedScenario?.id]);



// // //     useEffect(() => {
// // //         if (!sceneRef.current || !selectedScenario?.entities) return;

// // //         let cancelled = false;

// // //         const loadEntities = async () => {
// // //             const { dimZ } = boardSizeRef.current;

// // //             const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // //             const worldOffsetX = 0;

// // //             const UNITY_Y_OFFSET = 180;


// // //             // grupujemy encje po modelu
// // //             const entitiesByModel = selectedScenario.entities.reduce((acc, e) => {
// // //                 if (!acc[e.name]) acc[e.name] = [];
// // //                 acc[e.name].push(e);
// // //                 return acc;
// // //             }, {});


// // //             const loadModelEntities = async (modelName, entities) => {

// // //                 try {
// // //                     console.log("Loading model group:", modelName);


// // //                     const tpl = await loadModel(modelName);

// // //                     if (!tpl || cancelled) return;


// // //                     modelTemplates.current[modelName] = tpl;


// // //                     // tutaj od razu dodajemy wszystkie instancje tego modelu
// // //                     for (const e of entities) {

// // //                         if (cancelled) return;


// // //                         const model = SkeletonUtils.clone(tpl);

// // //                         model.name = e.name;
// // //                         model.scale.set(50, 50, 50);
// // //                         model.userData.isModel = true;


// // //                         const box = new THREE.Box3()
// // //                             .setFromObject(model);

// // //                         model.userData.heightOffset = -box.min.y;


// // //                         const gridX = e.posX ?? 0;
// // //                         const gridZ = e.posZ ?? 0;

// // //                         model.userData.gridX = gridX;
// // //                         model.userData.gridZ = gridZ;


// // //                         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // //                         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;


// // //                         model.position.set(
// // //                             worldX,
// // //                             model.userData.heightOffset,
// // //                             worldZ
// // //                         );


// // //                         const baseQuat = tpl.quaternion.clone();


// // //                         const userQuat = new THREE.Quaternion()
// // //                             .setFromEuler(
// // //                                 new THREE.Euler(
// // //                                     degToRad(e.rotX || 0),
// // //                                     degToRad(UNITY_Y_OFFSET - (e.rotY || 0)),
// // //                                     degToRad(e.rotZ || 0),
// // //                                     "YXZ"
// // //                                 )
// // //                             );


// // //                         model.quaternion
// // //                             .copy(baseQuat)
// // //                             .multiply(userQuat);


// // //                         sceneRef.current.add(model);

// // //                         objectsRef.current.push(model);


// // //                         console.log(
// // //                             "Added:",
// // //                             model.name
// // //                         );
// // //                     }


// // //                 } catch (err) {
// // //                     console.error(
// // //                         "Failed model group:",
// // //                         modelName,
// // //                         err
// // //                     );
// // //                 }
// // //             };


// // //             Object.entries(entitiesByModel)
// // //                 .forEach(([name, entities]) => {
// // //                     loadModelEntities(name, entities);
// // //                 });

// // //         };

// // //         loadEntities();

// // //         return () => {
// // //             cancelled = true;
// // //         };

// // //     }, [selectedScenario]);

// // //     // useEffect(() => {
// // //     //     if (!selectedScenario?.entities) return;

// // //     //     const uniqueModels = [...new Set(
// // //     //         selectedScenario.entities.map(e => e.name)
// // //     //     )];

// // //     //     uniqueModels.forEach(name => {
// // //     //         const config = MODEL_LIBRARY.find(m => m.name === name);

// // //     //         if (config) {
// // //     //             loadModel(name, config.path).then(tpl => {
// // //     //                 modelTemplates.current[name] = tpl;

// // //     //                 setModelsReadyVersion(v => v + 1);
// // //     //             });
// // //     //         }
// // //     //     });
// // //     // }, [selectedScenario]);

// // //     // -------------------- ADD / DELETE --------------------
// // //     const addModel = async (name) => {
// // //         const config = MODEL_LIBRARY.find(m => m.name === name);
// // //         if (!config) return;

// // //         // const tpl = modelTemplates.current[name];

// // //         console.log("config2121:", config);

// // //         const sceneTpl = await loadModel(name);
// // //         if (!sceneTpl) return;


// // //         // Dodanie tablicy do pisania
// // //         if (name === "whiteboard") {
// // //             createWritableBoard({
// // //                 scene: sceneRef.current,
// // //                 objectsRef,
// // //                 selectedObjectRef,
// // //                 url: "/whiteboard/scene.gltf",
// // //                 position: [0, 0, 0],
// // //                 rotation: [0, 0, 0],
// // //                 scale: [7, 7, 7],
// // //                 heightAboveFloor: 0,
// // //                 writableMeshName: "Object001_0",
// // //                 textureLoader,
// // //             });
// // //             return;
// // //         }

// // //         const model = SkeletonUtils.clone(sceneTpl);
// // //         model.name = name;
// // //         model.scale.set(50, 50, 50);
// // //         model.userData.isModel = true;

// // //         const box = new THREE.Box3().setFromObject(model);
// // //         model.userData.heightOffset = -box.min.y;

// // //         // model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // //         // model.userData.gridZ = 0;

// // //         // Centrowanie
// // //         model.userData.gridX = 0
// // //         model.userData.gridZ = -Math.floor((selectedScenario?.dimensionZ || 18) / 2);

// // //         const worldX = model.userData.gridX * GRID_SIZE;
// // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // //         const baseQuat = sceneTpl.quaternion.clone();
// // //         const userQuat = new THREE.Quaternion().setFromEuler(
// // //             new THREE.Euler(0, degToRad(UNITY_Y_OFFSET), 0, "YXZ")
// // //         );

// // //         model.quaternion.copy(baseQuat).multiply(userQuat);

// // //         sceneRef.current.add(model);
// // //         objectsRef.current.push(model);

// // //         if (selectedObjectRef.current) {
// // //             removeRadialMenu(selectedObjectRef.current);
// // //         }

// // //         selectedObjectRef.current = model;
// // //         setSelectedObject(model);
// // //         createRadialMenu(model);
// // //     };


// // //     const deleteSelected = () => {
// // //         if (!selectedObjectRef.current) return;
// // //         sceneRef.current.remove(selectedObjectRef.current);
// // //         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
// // //         selectedObjectRef.current = null;
// // //         setSelectedObject(null);
// // //     };

// // //     const duplicateSelected = () => {
// // //         const selected = selectedObjectRef.current;
// // //         if (!selected) return;

// // //         highlighterRef.current?.clear?.();
// // //         highlighterRef.current?.unhighlight?.(selected);

// // //         selected.traverse((child) => {
// // //             if (child.isMesh && child.userData?.originalColor) {
// // //                 child.material.color.copy(child.userData.originalColor);
// // //             }
// // //         });

// // //         const copy = selected.clone(true);

// // //         copy.position.x += 10;
// // //         copy.position.z += 10;

// // //         copy.userData = { ...selected.userData };

// // //         sceneRef.current.add(copy);

// // //         objectsRef.current.push(copy);

// // //         selectedObjectRef.current = copy;
// // //         setSelectedObject(copy);
// // //     };


// // //     const saveScenario = async () => {
// // //         if (!selectedScenario) return;

// // //         const entities = objectsRef.current
// // //             .filter(o => o.userData.isModel)
// // //             .map(o => {
// // //                 const tpl = modelTemplates.current[o.name];
// // //                 const baseQuat = tpl?.quaternion.clone() || new THREE.Quaternion();
// // //                 const baseInv = baseQuat.clone().invert();

// // //                 const userQuat = o.quaternion.clone().premultiply(baseInv);

// // //                 const euler = new THREE.Euler().setFromQuaternion(userQuat, "YXZ");

// // //                 const rotX = normalizeDeg(radToDeg(euler.x));
// // //                 const rotY = normalizeDeg(UNITY_Y_OFFSET - normalizeDeg(radToDeg(euler.y))); // flip Y do VR
// // //                 const rotZ = normalizeDeg(radToDeg(euler.z));

// // //                 return {
// // //                     name: o.name,
// // //                     GUID: crypto.randomUUID(),
// // //                     posX: o.userData.gridX ?? 0,
// // //                     posY: 0,
// // //                     posZ: o.userData.gridZ ?? 0,
// // //                     rotX,
// // //                     rotY,
// // //                     rotZ,
// // //                 };
// // //             });

// // //         // console.log('entities to save:', entities);

// // //         try {
// // //             const response = await fetch(
// // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // //                 {
// // //                     method: "PATCH",
// // //                     headers: { "Content-Type": "application/json" },
// // //                     body: JSON.stringify({
// // //                         stationName: selectedScenario.stationName,
// // //                         GUID: crypto.randomUUID(),
// // //                         dimensionX: selectedScenario.dimensionX,
// // //                         dimensionY: selectedScenario.dimensionY,
// // //                         entities,
// // //                     }),
// // //                 }
// // //             );

// // //             if (!response.ok) {
// // //                 const errorData = await response.json();
// // //                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // //             }

// // //             setAlertMessage("Stacja zapisana pomyślnie!");
// // //             setAlertSeverity("success");
// // //             setAlertOpen(true);
// // //         } catch (error) {
// // //             console.error(error);
// // //             setAlertMessage(`Błąd: ${error.message}`);
// // //             setAlertSeverity("error");
// // //             setAlertOpen(true);
// // //         }
// // //     };


// // //     const rotate90Right = () => {
// // //         if (!selectedObjectRef.current) return;

// // //         const step = Math.PI / 2;

// // //         selectedObjectRef.current.rotation.y -= step;

// // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // //     };

// // //     const rotate90Left = () => {
// // //         if (!selectedObjectRef.current) return;

// // //         const step = Math.PI / 2;

// // //         selectedObjectRef.current.rotation.y += step;
// // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // //     };



// // //     const textureLoader = new THREE.TextureLoader();


// // //     const createRadialMenu = (object) => {
// // //         if (!sceneRef.current) return;

// // //         // USUWANIE STAREGO MENU
// // //         if (object.userData.radialMenu) {
// // //             sceneRef.current.remove(object.userData.radialMenu);
// // //             object.userData.radialMenu = null;
// // //         }

// // //         const radius = 120;
// // //         const group = new THREE.Group();
// // //         group.userData.isRadialMenu = true;

// // //         const actions = [
// // //             { fn: rotate90Left, icon: "/icons/rotateleft.png" },
// // //             { fn: rotate90Right, icon: "/icons/rotateright.png" },
// // //             { fn: deleteSelected, icon: "/icons/delete.png" },
// // //             { fn: duplicateSelected, icon: "/icons/copy.png" }
// // //         ];

// // //         // ======= DODANIE PRZYCISKU PISZ DLA TABLICY =======
// // //         if (object.userData.isWritableBoard) {
// // //             actions.push({
// // //                 fn: () => {
// // //                     if (!object) return;

// // //                     // toggle mode
// // //                     if (object.userData.mode === "write") {
// // //                         object.userData.mode = "move";
// // //                         object.userData.isMovable = true;
// // //                         console.log("Tablica: zmiana trybu na MOVE");
// // //                     } else {
// // //                         object.userData.mode = "write";
// // //                         object.userData.isMovable = false;
// // //                         console.log("Tablica: zmiana trybu na WRITE");
// // //                     }

// // //                     // Odśwież podświetlenie przycisku
// // //                     group.children.forEach((mesh) => {
// // //                         if (mesh.userData.isWriteButton) {
// // //                             const mat = mesh.material;
// // //                             if (object.userData.mode === "write") {
// // //                                 mat.color.set(0xffff00); // żółte podświetlenie
// // //                             } else {
// // //                                 mat.color.set(0x6cc6d9); // standardowy kolor
// // //                             }
// // //                         }
// // //                     });
// // //                 },
// // //                 icon: "/icons/pisz.png",
// // //                 isWriteButton: true // oznaczenie, żeby łatwo znaleźć
// // //             });
// // //         }

// // //         actions.forEach((action, i) => {
// // //             const angle = (i / actions.length) * Math.PI * 2;

// // //             const geometry = new THREE.CircleGeometry(20, 32);
// // //             const material = new THREE.MeshBasicMaterial({
// // //                 color: action.isWriteButton
// // //                     ? object.userData.mode === "write"
// // //                         ? 0xffff00
// // //                         : 0x6cc6d9
// // //                     : 0x6cc6d9,
// // //                 depthTest: false
// // //             });

// // //             const mesh = new THREE.Mesh(geometry, material);
// // //             mesh.rotation.x = -Math.PI / 2;
// // //             mesh.position.set(
// // //                 Math.cos(angle) * radius,
// // //                 1,
// // //                 Math.sin(angle) * radius
// // //             );

// // //             const texture = textureLoader.load(action.icon);
// // //             const iconGeometry = new THREE.PlaneGeometry(30, 30);
// // //             const iconMaterial = new THREE.MeshBasicMaterial({
// // //                 map: texture,
// // //                 transparent: true,
// // //                 depthTest: false,
// // //                 side: THREE.DoubleSide
// // //             });
// // //             const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);
// // //             iconMesh.rotation.x = Math.PI;
// // //             iconMesh.rotation.z = -Math.PI / 2;
// // //             iconMesh.position.set(0, 0.1, 0);
// // //             mesh.add(iconMesh);

// // //             mesh.userData.onClick = action.fn;
// // //             mesh.userData.isRadialButton = true;
// // //             if (action.isWriteButton) mesh.userData.isWriteButton = true;

// // //             group.add(mesh);
// // //             object.userData.radialMenu = group;
// // //         });

// // //         sceneRef.current.add(group);
// // //         group.position.copy(object.position);
// // //         group.position.y = 1;
// // //     };





// // //     const removeRadialMenu = () => {
// // //         if (!sceneRef.current) return;

// // //         const menus = sceneRef.current.children.filter(
// // //             o => o.userData?.isRadialMenu
// // //         );

// // //         menus.forEach(menu => sceneRef.current.remove(menu));
// // //     };




// // //     // -------------------- KEYBOARD --------------------

// // //     useEffect(() => {
// // //         // Przesuwanie płynne
// // //         const moveGrid = (dx, dz) => {
// // //             if (!selectedObjectRef.current) return;

// // //             const obj = selectedObjectRef.current;
// // //             const { dimX, dimZ } = boardSizeRef.current;

// // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // //             const maxDepth = (dimZ - 1) * GRID_SIZE;

// // //             let worldX = obj.position.x + dx;
// // //             let worldZ = obj.position.z + dz;

// // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // //             const prevPosition = obj.position.clone();

// // //             obj.position.set(
// // //                 worldX,
// // //                 obj.userData.heightOffset || 0,
// // //                 worldZ
// // //             );

// // //             if (checkCollision(obj)) {
// // //                 obj.position.copy(prevPosition);
// // //                 return;
// // //             }

// // //             const gridX = Math.round(worldX / GRID_SIZE);
// // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // //             const maxZ = dimZ - 1;
// // //             const gridZ = -maxZ - rawGridZ;


// // //             obj.userData.gridX = gridX;
// // //             obj.userData.gridZ = gridZ;

// // //             removeRadialMenu();
// // //         };



// // //         const onKeyDown = (e) => {
// // //             if (
// // //                 e.target.tagName === "INPUT" ||
// // //                 e.target.tagName === "TEXTAREA" ||
// // //                 e.target.isContentEditable
// // //             ) return;

// // //             // Dodajemy blokadę ruchu w trybie write (TABLICA)
// // //             const obj = selectedObjectRef.current;
// // //             if (!obj) return;

// // //             if (obj.userData.mode === "write") {
// // //                 return;
// // //             }

// // //             switch (e.key.toLowerCase()) {

// // //                 case "w":
// // //                     e.preventDefault();
// // //                     moveGrid(0, 5);
// // //                     break;
// // //                 case "s":
// // //                     e.preventDefault();
// // //                     moveGrid(0, -5);
// // //                     break;
// // //                 case "a":
// // //                     e.preventDefault();
// // //                     moveGrid(5, 0);
// // //                     break;
// // //                 case "d":
// // //                     e.preventDefault();
// // //                     moveGrid(-5, 0);
// // //                     break;

// // //                 case "arrowleft":
// // //                     e.preventDefault();
// // //                     rotate90Left();
// // //                     break;
// // //                 case "arrowright":
// // //                     e.preventDefault();
// // //                     rotate90Right();
// // //                     break;

// // //                 case "delete":
// // //                 case "backspace":
// // //                     e.preventDefault();
// // //                     deleteSelected();
// // //                     break;

// // //                 case "escape":
// // //                     selectedObjectRef.current = null;
// // //                     setSelectedObject(null);
// // //                     break;

// // //                 default:
// // //                     break;
// // //             }
// // //         };

// // //         window.addEventListener("keydown", onKeyDown);
// // //         return () => window.removeEventListener("keydown", onKeyDown);
// // //     }, []);


// // //     // LOGIKA PISANIA NA TABLICY - globalny listener, który sprawdza czy aktualnie wybrany obiekt to tablica w trybie pisania, jeśli tak to modyfikuje jej tekst
// // //     if (!window._writableBoardListener) {
// // //         window._writableBoardListener = (e) => {
// // //             const model = selectedObjectRef.current;
// // //             if (!model?.userData?.isWritableBoard) return;
// // //             if (model.userData.mode !== "write") return;

// // //             const mesh = model.getObjectByName(model.userData.writableMeshName);
// // //             if (!mesh) return;

// // //             const { canvas, ctx, texture, clearCanvas } = mesh.userData;
// // //             const lines = model.userData.lines;

// // //             if (e.key === "Backspace") {
// // //                 let last = lines[lines.length - 1];
// // //                 if (last.length > 0) lines[lines.length - 1] = last.slice(0, -1);
// // //                 else if (lines.length > 1) lines.pop();
// // //             } else if (e.key === "Enter") {
// // //                 lines.push("");
// // //             } else if (e.key.length === 1) {
// // //                 lines[lines.length - 1] += e.key;
// // //             } else return;

// // //             clearCanvas();
// // //             ctx.fillStyle = "white";
// // //             ctx.font = "48px Arial";
// // //             ctx.textAlign = "left";
// // //             ctx.textBaseline = "top";

// // //             lines.forEach((line, i) => ctx.fillText(line, 40, 40 + i * 26));
// // //             texture.needsUpdate = true;
// // //         };

// // //         window.addEventListener("keydown", window._writableBoardListener);
// // //     }

// // //     // -------------------- RENDER --------------------
// // //     return (
// // //         <>
// // //             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
// // //                     <BackToScenariosButton />
// // //                 </Box>
// // //                 <Divider sx={{ my: 1 }} />
// // //                 {/* <Typography variant="subtitle2">Modele</Typography> */}
// // //                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
// // //                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // //                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
// // //                         <ListItem key={m.name} disablePadding>
// // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // //                                 <ListItemText primary={m.name} />
// // //                             </ListItemButton>
// // //                         </ListItem>
// // //                     ))}
// // //                 </List> */}

// // //                 {selectedObject && (
// // //                     <>
// // //                         <Divider sx={{ my: 1 }} />
// // //                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
// // //                             <IconButton
// // //                                 onClick={rotate90Left}
// // //                             >
// // //                                 <Rotate90DegreesCcwIcon />
// // //                             </IconButton>
// // //                             <IconButton
// // //                                 onMouseDown={() => (rotationDirRef.current = 1)}
// // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // //                             >
// // //                                 <RotateLeftIcon />
// // //                             </IconButton>
// // //                             <IconButton
// // //                                 onMouseDown={() => (rotationDirRef.current = -1)}
// // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // //                             >
// // //                                 <RotateRightIcon />
// // //                             </IconButton>
// // //                             <IconButton
// // //                                 onClick={rotate90Right}
// // //                             >
// // //                                 <Rotate90DegreesCwIcon />
// // //                             </IconButton>
// // //                             <IconButton color="error" onClick={deleteSelected}>
// // //                                 <DeleteIcon />
// // //                             </IconButton>
// // //                             {/* <Button onClick={logRotation}>
// // //                                 log
// // //                             </Button> */}
// // //                         </Stack>
// // //                     </>
// // //                 )}

// // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // //                     Zapisz stację
// // //                 </Button>
// // //             </Paper>

// // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // //             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
// // //             {gridWalls && <GridWalls
// // //                 scene={sceneRef.current}
// // //                 dimX={boardSizeRef.current.dimX}
// // //                 dimZ={boardSizeRef.current.dimZ}
// // //                 GRID_SIZE={GRID_SIZE}
// // //                 cameraRef={cameraRef}
// // //             />}

// // //             {sceneRef.current && cameraRef.current && (
// // //                 <>
// // //                     <FloorLogo
// // //                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
// // //                         scene={sceneRef.current}
// // //                         svgPath="/micree.svg"
// // //                         GRID_SIZE={GRID_SIZE}
// // //                         boardSizeRef={boardSizeRef.current}
// // //                     />
// // //                     <UI3DModelButtons
// // //                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}-${modelsReadyVersion}`}
// // //                         scene={sceneRef.current}
// // //                         camera={cameraRef.current}
// // //                         modelTemplates={modelTemplates.current}
// // //                         objectsRef={objectsRef.current}
// // //                         onClickHandlerRef={uiClickHandlerRef}
// // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // //                         changeScenarioRef={changeScenarioRef}
// // //                         boardSizeRef={boardSizeRef.current}
// // //                         GRID_SIZE={GRID_SIZE}
// // //                     />
// // //                 </>
// // //             )}

// // //             <Typography
// // //                 variant="h6"
// // //                 sx={{
// // //                     mt: 1,
// // //                     position: "absolute",
// // //                     top: 70,
// // //                     left: "50%",
// // //                     transform: "translateX(-50%)",
// // //                     fontSize: "10px",
// // //                 }}
// // //             >
// // //                 Wybrana stacja:
// // //             </Typography>
// // //             <Typography
// // //                 variant="h6"
// // //                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
// // //             >
// // //                 {/* {selectedScenario?.existingSource} */}
// // //                 {
// // //                     selectedScenario?.stationName
// // //                         ? selectedScenario.stationName
// // //                         : selectedScenario?.id !== undefined
// // //                             ? `Stacja ${selectedScenario.id}`
// // //                             : "nie wybrano"
// // //                 }
// // //             </Typography>

// // //             <IconButton
// // //                 onClick={() => setGridWalls(prev => !prev)}
// // //                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
// // //             >
// // //                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

// // //             </IconButton>

// // //             <Snackbar
// // //                 open={alertOpen}
// // //                 autoHideDuration={4000}
// // //                 onClose={() => setAlertOpen(false)}
// // //                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
// // //                 sx={{ mt: 8 }}
// // //             >
// // //                 <Alert
// // //                     onClose={() => setAlertOpen(false)}
// // //                     severity={alertSeverity}
// // //                     sx={{ width: "100%" }}
// // //                 >
// // //                     {alertMessage}
// // //                 </Alert>
// // //             </Snackbar>


// // //             {/* <WallBoards
// // //                 scene={sceneRef.current}
// // //                 cameraRef={cameraRef}
// // //                 rendererRef={rendererRef}
// // //             //   wallsRef={}
// // //             /> */}

// // //             <WritableBoardItem
// // //                 sceneRef={sceneRef}
// // //                 objectsRef={objectsRef}
// // //                 selectedObjectRef={selectedObjectRef}
// // //                 rendererRef={rendererRef}
// // //                 cameraRef={cameraRef}
// // //                 url="/whiteboard/scene.gltf"
// // //                 position={[0, 0, 0]}
// // //                 scale={[7, 7, 7]}
// // //                 heightAboveFloor={0}
// // //                 writableMeshName="Object001_0"
// // //             />


// // //             <DebugBoardMeshes url="/whiteboard/scene.gltf" />



// // //         </>
// // //     );
// // // }














// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Snackbar,
// // // //     Alert,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// // // // import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// // // // import DeleteIcon from "@mui/icons-material/Delete";
// // // // import GridOffIcon from '@mui/icons-material/GridOff';
// // // // import GridOnIcon from '@mui/icons-material/GridOn';

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";
// // // // import ModelList from "./utilities/ModelList";
// // // // import GridWalls from "./three/GridWalls";
// // // // import { ObjectHighlighter } from "./utilities/ObjectHighlighter";
// // // // import WallBoards from "./three/WallBoards";
// // // // import WritableBoard, { DebugBoardMeshes } from "./three/WritableBoard";
// // // // import WritableBoardItem from "./three/WritableBoard";
// // // // import { createWritableBoard } from "./utilities/createWritableBoard";
// // // // import { useSelector } from "react-redux";

// // // // const GRID_SIZE = 50;

// // // // const UNITY_Y_OFFSET = 180;


// // // // // const MODEL_LIBRARY = [
// // // // //     { name: "whiteboard", path: "/whiteboard/scene.gltf", visible: true },
// // // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // // //     { name: "env_staircase_stairs", path: "/env_staircase_stairs/env_staircase_stairs.gltf", visible: true },
// // // // //     { name: "env_wall_column_05", path: "/env_wall_column_05/env_wall_column_05.gltf", visible: true },
// // // // //     { name: "exe_cd_essential_items", path: "/exe_cd_essential_items/exe_cd_essential_items.gltf", visible: true },
// // // // //     { name: "exe_cooking_bins", path: "/exe_cooking_bins/exe_cooking_bins.gltf", visible: true },
// // // // //     { name: "exe_cooking_cookbase", path: "/exe_cooking_cookbase/exe_cooking_cookbase.gltf", visible: true },
// // // // //     { name: "exe_cooking_cook_station", path: "/exe_cooking_cook_station/exe_cooking_cook_station.gltf", visible: true },
// // // // //     { name: "exe_cooking_device_blender", path: "/exe_cooking_device_blender/exe_cooking_device_blender.gltf", visible: true },
// // // // //     { name: "exe_cooking_device_mixer", path: "/exe_cooking_device_mixer/exe_cooking_device_mixer.gltf", visible: true },
// // // // //     { name: "exe_cooking_device_mixer_blender", path: "/exe_cooking_device_mixer_blender/exe_cooking_device_mixer_blender.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridge_dairy", path: "/exe_cooking_fridge_dairy/exe_cooking_fridge_dairy.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridge_fruits", path: "/exe_cooking_fridge_fruits/exe_cooking_fridge_fruits.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridge_meat", path: "/exe_cooking_fridge_meat/exe_cooking_fridge_meat.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridge_vegetables", path: "/exe_cooking_fridge_vegetables/exe_cooking_fridge_vegetables.gltf", visible: true },
// // // // //     { name: "exe_cooking_mech_cooker", path: "/exe_cooking_mech_cooker/exe_cooking_mech_cooker.gltf", visible: true },
// // // // //     { name: "exe_cooking_mech_furnance", path: "/exe_cooking_mech_furnance/exe_cooking_mech_furnance.gltf", visible: true },
// // // // //     { name: "exe_cooking_sink", path: "/exe_cooking_sink/exe_cooking_sink.gltf", visible: true },
// // // // //     { name: "exe_cooking_sink_hanged", path: "/exe_cooking_sink_hanged/exe_cooking_sink_hanged.gltf", visible: true },
// // // // //     { name: "exe_creator_door_office_double", path: "/exe_creator_door_office_double/exe_creator_door_office_double.gltf", visible: true },
// // // // //     { name: "exe_creator_door_wooden_1", path: "/exe_creator_door_wooden_1/exe_creator_door_wooden_1.gltf", visible: true },
// // // // //     { name: "exe_printing_3d_printing_station", path: "/exe_printing_3d_printing_station/exe_printing_3d_printing_station.gltf", visible: true },
// // // // //     { name: "exe_printing_cabinet_filament_abs_ptg", path: "/exe_printing_cabinet_filament_abs_ptg/exe_printing_cabinet_filament_abs_ptg.gltf", visible: true },
// // // // //     { name: "exe_printing_cabinet_filament_pla_tpu", path: "/exe_printing_cabinet_filament_pla_tpu/exe_printing_cabinet_filament_pla_tpu.gltf", visible: true },
// // // // //     { name: "exe_printing_cleaning_set", path: "/exe_printing_cleaning_set/exe_printing_cleaning_set.gltf", visible: true },
// // // // //     { name: "exe_printing_examples", path: "/exe_printing_examples/exe_printing_examples.gltf", visible: true },
// // // // //     { name: "exe_wd_22nut_impactdriver", path: "/exe_wd_22nut_impactdriver/exe_wd_22nut_impactdriver.gltf", visible: true },
// // // // //     { name: "exe_wd_combined_exercises", path: "/exe_wd_combined_exercises/exe_wd_combined_exercises.gltf", visible: true },
// // // // //     { name: "exe_wd_drill_26plug_26screw_impactdriver", path: "/exe_wd_drill_26plug_26screw_impactdriver/exe_wd_drill_26plug_26screw_impactdriver.gltf", visible: true },
// // // // //     { name: "exe_wd_drill_dowel_hammer", path: "/exe_wd_drill_dowel_hammer/exe_wd_drill_dowel_hammer.gltf", visible: true },
// // // // //     { name: "exe_wd_foaming", path: "/exe_wd_foaming/exe_wd_foaming.gltf", visible: true },
// // // // //     { name: "exe_wd_garage_gate_installation", path: "/exe_wd_garage_gate_installation/exe_wd_garage_gate_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_garage_gate_supply", path: "/exe_wd_garage_gate_supply/exe_wd_garage_gate_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_interior_door_installation", path: "/exe_wd_interior_door_installation/exe_wd_interior_door_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_interior_door_supply", path: "/exe_wd_interior_door_supply/exe_wd_interior_door_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_leveler", path: "/exe_wd_leveler/exe_wd_leveler.gltf", visible: true },
// // // // //     { name: "exe_wd_outer_door_installation", path: "/exe_wd_outer_door_installation/exe_wd_outer_door_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_outer_door_supply", path: "/exe_wd_outer_door_supply/exe_wd_outer_door_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill", path: "/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill.gltf", visible: true },
// // // // //     { name: "exe_wd_roof_window_installation", path: "/exe_wd_roof_window_installation/exe_wd_roof_window_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_roof_window_supply", path: "/exe_wd_roof_window_supply/exe_wd_roof_window_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_strut", path: "/exe_wd_strut/exe_wd_strut.gltf", visible: true },
// // // // //     { name: "exe_wd_tool_rack_1", path: "/exe_wd_tool_rack_1/exe_wd_tool_rack_1.gltf", visible: true },
// // // // //     { name: "exe_wd_tool_rack_boxes", path: "/exe_wd_tool_rack_boxes/exe_wd_tool_rack_boxes.gltf", visible: true },
// // // // //     { name: "exe_wd_wedge_hammer", path: "/exe_wd_wedge_hammer/exe_wd_wedge_hammer.gltf", visible: true },
// // // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_drawer", path: "/exe_cooking_countertop_drawer/exe_cooking_countertop_drawer.gltf", visible: true },
// // // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // // //     { name: "exe_wd_technical_door_installation", path: "/exe_wd_technical_door_installation/exe_wd_technical_door_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // // //     { name: "elevator_1x2.1x", path: "/elevator_1x2.1x/elevator_1x2.1x.gltf", visible: true },
// // // // // ];




// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);
// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
// // // //     const [gridVersion, setGridVersion] = useState();

// // // //     const highlighterRef = useRef(null);


// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);
// // // //     const [gridWalls, setGridWalls] = useState(true);



// // // //     // Powiadomienia
// // // //     const [alertOpen, setAlertOpen] = useState(false);
// // // //     const [alertMessage, setAlertMessage] = useState("");
// // // //     const [alertSeverity, setAlertSeverity] = useState("success");

// // // //     const [modelsReadyVersion, setModelsReadyVersion] = useState(0);

// // // //     const [MODEL_LIBRARY, setModelLibrary] = useState([]);


// // // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //     const degToRad = (deg) => (deg * Math.PI) / 180;
// // // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


// // // //     const { jwt } = useSelector((state) => state.auth.user);


// // // //     async function fetchModelLibrary(token) {
// // // //         const res = await fetch("http://localhost:3001/assets/webgl-manifest", {
// // // //             headers: {
// // // //                 Authorization: `Bearer ${token}`,
// // // //             },
// // // //         });

// // // //         // if (!res.ok) {
// // // //         //     throw new Error(`Failed to fetch manifest: ${res.status}`);
// // // //         // }

// // // //         if (!res.ok) {
// // // //             console.error(`Failed to fetch manifest: ${res.status}`);
// // // //             return;
// // // //         }

// // // //         const data = await res.json();


// // // //         const DEFAULT_VISIBLE = {
// // // //             exe_cooking_cook_station: true,
// // // //             exe_cooking_cookbase: true,
// // // //             exe_cooking_countertop_2: true,
// // // //             exe_cooking_countertop_4: true,
// // // //             exe_cooking_countertop_drawer: true,
// // // //             exe_cooking_device_blender: true,
// // // //             exe_cooking_device_mixer: true,
// // // //             exe_cooking_device_mixer_blender: true,
// // // //             exe_cooking_drawers_ingrediens: true,
// // // //             exe_cooking_fridge_dairy: true,
// // // //             exe_cooking_fridge_fruits: true,
// // // //             exe_cooking_fridge_meat: true,
// // // //             exe_cooking_fridge_vegetables: true,
// // // //             exe_cooking_fridges_all: true,
// // // //             exe_cooking_mech_cooker: true,
// // // //             exe_cooking_mech_furnance: true,
// // // //             exe_cooking_sink: true,
// // // //             exe_cooking_sink_hanged: true,
// // // //             exe_cooking_utensils: true,
// // // //             exe_cooking_bins: true,
// // // //             exe_wd_22bolt_22nut_impactdriver: true,
// // // //             exe_wd_22nut_impactdriver: true,
// // // //             exe_wd_combined_exercises: true,
// // // //             exe_wd_drill_26plug_26screw_impactdriver: true,
// // // //             exe_wd_drill_dowel_hammer: true,
// // // //             exe_wd_foaming: true,
// // // //             exe_wd_garage_gate_installation: true,
// // // //             exe_wd_garage_gate_supply: true,
// // // //             exe_wd_interior_door_installation: true,
// // // //             exe_wd_interior_door_supply: true,
// // // //             exe_wd_leveler: true,
// // // //             exe_wd_outer_door_installation: true,
// // // //             exe_wd_outer_door_supply: true,
// // // //             exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill: true,
// // // //             exe_wd_roof_window_installation: true,
// // // //             exe_wd_roof_window_supply: true,
// // // //             exe_wd_strut: true,
// // // //             exe_wd_technical_door_installation: true,
// // // //             exe_wd_technical_door_supply: true,
// // // //             exe_wd_tool_rack_1: true,
// // // //             exe_wd_tool_rack_boxes: true,
// // // //             exe_wd_vertical_window_installation: true,
// // // //             exe_wd_vertical_window_supply: true,
// // // //             exe_wd_wedge_hammer: true,
// // // //             exe_printing_cabinet_filament_abs_ptg: true,
// // // //             exe_printing_cabinet_filament_pla_tpu: true,
// // // //             exe_printing_cleaning_set: true,
// // // //             exe_printing_examples: true,
// // // //             exe_printing_3d_printing_station: true,
// // // //             envp_furnitures_table_140_80: true,
// // // //             envp_furnitures_table_160_80: true,
// // // //             envp_furnitures_table_250_120: true,
// // // //             envp_furnitures_table_400_120: true,
// // // //             envp_furnitures_table_small_75x75x75: true,
// // // //             envp_furnitures_table_small_75x75x100: true,
// // // //             envp_furnitures_table_small_100x100x75: true,
// // // //             envp_furnitures_table_small_100x100x100: true,
// // // //             envp_furnitures_table_small_125x100x75: true,
// // // //             envp_furnitures_table_small_125x100x100: true,
// // // //             envp_furnitures_sofa_corner_3_2: true,
// // // //             envp_furnitures_sofa_corner_3_3: true,
// // // //             envp_furnitures_sofa_line_2: true,
// // // //             envp_furnitures_sofa_line_3: true,
// // // //             envp_furnitures_sofa_single: true,
// // // //             envp_furnitures_sofa_single_corner: true,
// // // //             envp_furnitures_chair: true,
// // // //             envp_furnitures_office_chair: true,
// // // //             envp_bin_4: true,
// // // //             envp_bin_lid: true,
// // // //             whiteboard: true,
// // // //             env_wall_column_05: true,
// // // //             env_wall_creator_x2_5: true,
// // // //             envp_modular_door_frame: true,
// // // //             exe_creator_door_office_double: true,
// // // //             exe_creator_door_wooden_1: true,
// // // //             "elevator_1x2.1x": true,
// // // //             env_staircase_stairs: true
// // // //         };

// // // //         return data.models.map((m) => ({
// // // //             name: m.name,
// // // //             path: m.url,
// // // //             assets: m.assets ?? [],
// // // //             visible: DEFAULT_VISIBLE[m.name] ?? false,
// // // //         }));
// // // //     }


// // // //     useEffect(() => {
// // // //         fetchModelLibrary(jwt).then((modelLibrary) => {
// // // //             setModelLibrary(modelLibrary);
// // // //         });
// // // //     }, [jwt]);

// // // //     console.log("MODEL_LIBRARY", MODEL_LIBRARY);



// // // //     useEffect(() => {
// // // //         const arrowConfig = MODEL_LIBRARY.find(m => m.name === "arrow");

// // // //         if (!arrowConfig) return;

// // // //         loadModel(arrowConfig).then(tpl => {
// // // //             modelTemplates.current["arrow"] = tpl;
// // // //             setModelsReadyVersion(v => v + 1);
// // // //         });
// // // //     }, [MODEL_LIBRARY]);


// // // //     // -------------------- GRID UPDATE --------------------

// // // //     useEffect(() => {
// // // //         if (!selectedScenario) return;
// // // //         setBoardSize({
// // // //             dimX: selectedScenario.dimensionX || 18,
// // // //             dimZ: selectedScenario.dimensionZ || 18
// // // //         });
// // // //         setGridVersion(v => v + 1);
// // // //     }, [selectedScenario?.id]);

// // // //     // console.log('gridVersion', gridVersion)
// // // //     // console.log('boardSize', boardSize)

// // // //     const modelCache = useRef({});

// // // //     const loadModel = (modelConfig) => {
// // // //         return new Promise((resolve, reject) => {
// // // //             console.log("Loading model:", modelConfig, modelConfig.name, modelConfig.path);
// // // //             if (!modelConfig?.path) {
// // // //                 reject(new Error("Missing model path: " + modelConfig?.name));
// // // //                 return;
// // // //             }

// // // //             if (modelCache.current[modelConfig.name]) {
// // // //                 resolve(modelCache.current[modelConfig.name]);
// // // //                 return;
// // // //             }

// // // //             const manager = new THREE.LoadingManager();

// // // //             const assetMap = {};

// // // //             (modelConfig.assets ?? []).forEach(asset => {
// // // //                 const filename = asset.key.split("/").pop().split("?")[0];
// // // //                 assetMap[filename] = asset.url;
// // // //             });

// // // //             manager.setURLModifier((url) => {
// // // //                 const key = url.split("/").pop().split("?")[0];
// // // //                 return assetMap[key] || url;
// // // //             });

// // // //             const loader = new GLTFLoader(manager);

// // // //             loader.load(
// // // //                 modelConfig.path,
// // // //                 (gltf) => {
// // // //                     modelCache.current[modelConfig.name] = gltf.scene;
// // // //                     resolve(gltf.scene);
// // // //                 },
// // // //                 undefined,
// // // //                 reject
// // // //             );
// // // //         });
// // // //     };

// // // //     const getObjectBox = (obj) => {
// // // //         return new THREE.Box3().setFromObject(obj);
// // // //     };

// // // //     const checkCollision = (obj) => {
// // // //         if (!sceneRef.current) return false;

// // // //         const objBox = getObjectBox(obj);

// // // //         const walls = sceneRef.current.children.filter(o => o.userData?.isWall);

// // // //         for (const wall of walls) {
// // // //             const wallBox = new THREE.Box3().setFromObject(wall);
// // // //             if (objBox.intersectsBox(wallBox)) return true;
// // // //         }

// // // //         for (const other of objectsRef.current) {
// // // //             if (!other.userData?.isModel) continue;
// // // //             if (other === obj) continue;

// // // //             const otherBox = getObjectBox(other);
// // // //             if (objBox.intersectsBox(otherBox)) return true;
// // // //         }

// // // //         return false;
// // // //     };

// // // //     // -------------------- INIT SCENE --------------------
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         camera.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         // let highlight = null;

// // // //         // const highlighter = new ObjectHighlighter(scene, {
// // // //         //     color: 0x4da3ff,
// // // //         //     tintStrength: 0.35,
// // // //         //     auraScale: 1.06,
// // // //         //     auraOpacity: 0.25
// // // //         // });

// // // //         highlighterRef.current = new ObjectHighlighter(scene, {
// // // //             color: 0x4da3ff,
// // // //             tintStrength: 0.35,
// // // //             auraScale: 1.06,
// // // //             auraOpacity: 0.25
// // // //         });


// // // //         // const updatePointer = (e) => {
// // // //         //     pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //         //     pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         // };

// // // //         const updatePointer = (e) => {
// // // //             if (!rendererRef.current) return;
// // // //             const rect = rendererRef.current.domElement.getBoundingClientRect();
// // // //             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
// // // //             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
// // // //         };

// // // //         // const clearHighlight = () => {
// // // //         //     if (highlight) {
// // // //         //         scene.remove(highlight);
// // // //         //         highlight = null;
// // // //         //     }
// // // //         // };

// // // //         // const highlightObject = (obj) => {
// // // //         //     clearHighlight();
// // // //         //     highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //         //     scene.add(highlight);
// // // //         // };

// // // //         const highlightObject = (obj) => {
// // // //             // highlighter.highlightObject(obj);
// // // //             highlighterRef.current?.highlightObject?.(obj);
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             // highlighter.clear();
// // // //             highlighterRef.current?.clear?.();
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const intersects = raycaster.intersectObjects(
// // // //                 sceneRef.current.children,
// // // //                 true
// // // //             );

// // // //             for (const i of intersects) {
// // // //                 if (i.object.userData?.isRadialButton) {
// // // //                     i.object.userData.onClick();
// // // //                     return;
// // // //                 }
// // // //             }

// // // //             let hitModel = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;

// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) {
// // // //                     hitModel = o;
// // // //                 }
// // // //             });

// // // //             if (!hitModel) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 removeRadialMenu();
// // // //                 return;
// // // //             }

// // // //             removeRadialMenu();

// // // //             selectedObjectRef.current = hitModel;
// // // //             setSelectedObject(hitModel);
// // // //             highlightObject(hitModel);

// // // //             createRadialMenu(hitModel);

// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };




// // // //         // Przesuwanie płynne

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             const obj = selectedObjectRef.current;

// // // //             if (obj.userData.radialMenu) {
// // // //                 sceneRef.current.remove(obj.userData.radialMenu);
// // // //                 obj.userData.radialMenu = null;
// // // //             }

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const maxZ = dimZ;

// // // //             let worldX = p.x;
// // // //             let worldZ = p.z;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = maxZ * GRID_SIZE;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));



// // // //             const prevPosition = obj.position.clone();

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             if (checkCollision(obj)) {
// // // //                 obj.position.copy(prevPosition);
// // // //                 return;
// // // //             }

// // // //             const gridX = worldX / GRID_SIZE;
// // // //             const rawGridZ = worldZ / GRID_SIZE;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;


// // // //             // highlighter?.update?.();
// // // //             highlighterRef.current?.update?.();
// // // //         };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let worldX = p.x;
// // // //         //     let worldZ = p.z;

// // // //         //     const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //         //     const maxDepth = maxZ * GRID_SIZE;

// // // //         //     worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //         //     worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX + worldOffsetX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     const gridX = Math.round(worldX / GRID_SIZE);
// // // //         //     const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log(
// // // //         //         `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };

// // // //         // // Przesuwanie Skokowe
// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let rawGridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     rawGridZ = Math.max(-maxZ, Math.min(0, rawGridZ));


// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = rawGridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     // gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridZ = Math.max(-dimZ, Math.min(1, gridZ));


// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = gridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     // highlight?.update?.();
// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     let gridX = Math.round(-p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.round(p.z / GRID_SIZE);
// // // //         //     gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = -gridX * GRID_SIZE;
// // // //         //     const worldZ = gridZ * GRID_SIZE;
// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlight?.update?.();
// // // //         // };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);


// // // //             // highlighter?.update?.();
// // // //             highlighterRef.current?.update?.();
// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 // highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => renderer.domElement.remove();
// // // //     }, []);

// // // //     // -------------------- GRID + PLANE --------------------
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         // usuń poprzedni grid i plane
// // // //         sceneRef.current.children
// // // //             .filter((o) => o.userData?.isGrid)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter((o) => o.userData?.isModel)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(worldOffset.x, 0, worldOffset.z);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
// // // //         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.update();

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);



// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !selectedScenario?.entities) return;

// // // //         let cancelled = false;

// // // //         const loadEntities = async () => {
// // // //             const { dimZ } = boardSizeRef.current;
// // // //             const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //             const worldOffsetX = 0;

// // // //             const UNITY_Y_OFFSET = 180;

// // // //             for (const e of selectedScenario.entities) {
// // // //                 if (cancelled) return;

// // // //                 // const config = MODEL_LIBRARY.find(m => m.name === e.name);
// // // //                 // if (!config) continue;

// // // //                 const config = MODEL_LIBRARY.find(m => m.name === e.name);

// // // //                 if (!config?.path) {
// // // //                     console.warn("[MODEL MISSING PATH]", e.name, config);
// // // //                     continue;
// // // //                 }

// // // //                 try {
// // // //                     // const tpl = await loadModel(e.name, config.path);
// // // //                     const tpl = await loadModel(config);
// // // //                     if (!tpl || cancelled) continue;

// // // //                     modelTemplates.current[e.name] = tpl;

// // // //                     const model = SkeletonUtils.clone(tpl);

// // // //                     model.name = e.name;
// // // //                     model.scale.set(50, 50, 50);
// // // //                     model.userData.isModel = true;

// // // //                     const box = new THREE.Box3().setFromObject(model);
// // // //                     model.userData.heightOffset = -box.min.y;

// // // //                     const gridX = e.posX ?? 0;
// // // //                     const gridZ = e.posZ ?? 0;
// // // //                     model.userData.gridX = gridX;
// // // //                     model.userData.gridZ = gridZ;

// // // //                     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //                     const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;

// // // //                     model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //                     // --- ROTACJA ---
// // // //                     const baseQuat = tpl.quaternion.clone();

// // // //                     const rotX = degToRad(e.rotX || 0);
// // // //                     const rotY = degToRad(UNITY_Y_OFFSET - (e.rotY || 0));
// // // //                     const rotZ = degToRad(e.rotZ || 0);

// // // //                     const userQuat = new THREE.Quaternion().setFromEuler(
// // // //                         new THREE.Euler(rotX, rotY, rotZ, "YXZ")
// // // //                     );

// // // //                     model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //                     sceneRef.current.add(model);
// // // //                     objectsRef.current.push(model);

// // // //                     const testEuler = new THREE.Euler().setFromQuaternion(model.quaternion, "YXZ");

// // // //                     console.log(
// // // //                         `[LOAD] ${model.name} | rotX:${radToDeg(testEuler.x).toFixed(1)} | rotY:${radToDeg(testEuler.y).toFixed(1)} | rotZ:${radToDeg(testEuler.z).toFixed(1)}`
// // // //                     );

// // // //                 } catch (err) {
// // // //                     console.error("Failed to load model:", e.name, err);
// // // //                 }
// // // //             }
// // // //         };

// // // //         loadEntities();

// // // //         return () => {
// // // //             cancelled = true;
// // // //         };

// // // //     }, [selectedScenario]);

// // // //     useEffect(() => {
// // // //         if (!selectedScenario?.entities) return;

// // // //         const uniqueModels = [...new Set(
// // // //             selectedScenario.entities.map(e => e.name)
// // // //         )];

// // // //         uniqueModels.forEach(name => {
// // // //             const config = MODEL_LIBRARY.find(m => m.name === name);

// // // //             if (config) {
// // // //                 loadModel(name, config.path).then(tpl => {
// // // //                     modelTemplates.current[name] = tpl;

// // // //                     setModelsReadyVersion(v => v + 1);
// // // //                 });
// // // //             }
// // // //         });
// // // //     }, [selectedScenario]);

// // // //     // -------------------- ADD / DELETE --------------------
// // // //     const addModel = async (name) => {
// // // //         const config = MODEL_LIBRARY.find(m => m.name === name);
// // // //         if (!config) return;

// // // //         // const tpl = modelTemplates.current[name];

// // // //         console.log("config2121:", config);

// // // //         const sceneTpl = await loadModel(config);
// // // //         if (!sceneTpl) return;


// // // //         // Dodanie tablicy do pisania
// // // //         if (name === "whiteboard") {
// // // //             createWritableBoard({
// // // //                 scene: sceneRef.current,
// // // //                 objectsRef,
// // // //                 selectedObjectRef,
// // // //                 url: "/whiteboard/scene.gltf",
// // // //                 position: [0, 0, 0],
// // // //                 rotation: [0, 0, 0],
// // // //                 scale: [7, 7, 7],
// // // //                 heightAboveFloor: 0,
// // // //                 writableMeshName: "Object001_0",
// // // //                 textureLoader,
// // // //             });
// // // //             return;
// // // //         }

// // // //         const model = SkeletonUtils.clone(sceneTpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         // model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         // model.userData.gridZ = 0;

// // // //         // Centrowanie
// // // //         model.userData.gridX = 0
// // // //         model.userData.gridZ = -Math.floor((selectedScenario?.dimensionZ || 18) / 2);

// // // //         const worldX = model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         const baseQuat = sceneTpl.quaternion.clone();
// // // //         const userQuat = new THREE.Quaternion().setFromEuler(
// // // //             new THREE.Euler(0, degToRad(UNITY_Y_OFFSET), 0, "YXZ")
// // // //         );

// // // //         model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         if (selectedObjectRef.current) {
// // // //             removeRadialMenu(selectedObjectRef.current);
// // // //         }

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //         createRadialMenu(model);
// // // //     };


// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const duplicateSelected = () => {
// // // //         const selected = selectedObjectRef.current;
// // // //         if (!selected) return;

// // // //         highlighterRef.current?.clear?.();
// // // //         highlighterRef.current?.unhighlight?.(selected);

// // // //         selected.traverse((child) => {
// // // //             if (child.isMesh && child.userData?.originalColor) {
// // // //                 child.material.color.copy(child.userData.originalColor);
// // // //             }
// // // //         });

// // // //         const copy = selected.clone(true);

// // // //         copy.position.x += 10;
// // // //         copy.position.z += 10;

// // // //         copy.userData = { ...selected.userData };

// // // //         sceneRef.current.add(copy);

// // // //         objectsRef.current.push(copy);

// // // //         selectedObjectRef.current = copy;
// // // //         setSelectedObject(copy);
// // // //     };


// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => {
// // // //                 const tpl = modelTemplates.current[o.name];
// // // //                 const baseQuat = tpl?.quaternion.clone() || new THREE.Quaternion();
// // // //                 const baseInv = baseQuat.clone().invert();

// // // //                 const userQuat = o.quaternion.clone().premultiply(baseInv);

// // // //                 const euler = new THREE.Euler().setFromQuaternion(userQuat, "YXZ");

// // // //                 const rotX = normalizeDeg(radToDeg(euler.x));
// // // //                 const rotY = normalizeDeg(UNITY_Y_OFFSET - normalizeDeg(radToDeg(euler.y))); // flip Y do VR
// // // //                 const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //                 return {
// // // //                     name: o.name,
// // // //                     GUID: crypto.randomUUID(),
// // // //                     posX: o.userData.gridX ?? 0,
// // // //                     posY: 0,
// // // //                     posZ: o.userData.gridZ ?? 0,
// // // //                     rotX,
// // // //                     rotY,
// // // //                     rotZ,
// // // //                 };
// // // //             });

// // // //         // console.log('entities to save:', entities);

// // // //         try {
// // // //             const response = await fetch(
// // // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //                 {
// // // //                     method: "PATCH",
// // // //                     headers: { "Content-Type": "application/json" },
// // // //                     body: JSON.stringify({
// // // //                         stationName: selectedScenario.stationName,
// // // //                         GUID: crypto.randomUUID(),
// // // //                         dimensionX: selectedScenario.dimensionX,
// // // //                         dimensionY: selectedScenario.dimensionY,
// // // //                         entities,
// // // //                     }),
// // // //                 }
// // // //             );

// // // //             if (!response.ok) {
// // // //                 const errorData = await response.json();
// // // //                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //             }

// // // //             setAlertMessage("Stacja zapisana pomyślnie!");
// // // //             setAlertSeverity("success");
// // // //             setAlertOpen(true);
// // // //         } catch (error) {
// // // //             console.error(error);
// // // //             setAlertMessage(`Błąd: ${error.message}`);
// // // //             setAlertSeverity("error");
// // // //             setAlertOpen(true);
// // // //         }
// // // //     };


// // // //     const rotate90Right = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y -= step;

// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };

// // // //     const rotate90Left = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y += step;
// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };



// // // //     const textureLoader = new THREE.TextureLoader();


// // // //     const createRadialMenu = (object) => {
// // // //         if (!sceneRef.current) return;

// // // //         // USUWANIE STAREGO MENU
// // // //         if (object.userData.radialMenu) {
// // // //             sceneRef.current.remove(object.userData.radialMenu);
// // // //             object.userData.radialMenu = null;
// // // //         }

// // // //         const radius = 120;
// // // //         const group = new THREE.Group();
// // // //         group.userData.isRadialMenu = true;

// // // //         const actions = [
// // // //             { fn: rotate90Left, icon: "/icons/rotateleft.png" },
// // // //             { fn: rotate90Right, icon: "/icons/rotateright.png" },
// // // //             { fn: deleteSelected, icon: "/icons/delete.png" },
// // // //             { fn: duplicateSelected, icon: "/icons/copy.png" }
// // // //         ];

// // // //         // ======= DODANIE PRZYCISKU PISZ DLA TABLICY =======
// // // //         if (object.userData.isWritableBoard) {
// // // //             actions.push({
// // // //                 fn: () => {
// // // //                     if (!object) return;

// // // //                     // toggle mode
// // // //                     if (object.userData.mode === "write") {
// // // //                         object.userData.mode = "move";
// // // //                         object.userData.isMovable = true;
// // // //                         console.log("Tablica: zmiana trybu na MOVE");
// // // //                     } else {
// // // //                         object.userData.mode = "write";
// // // //                         object.userData.isMovable = false;
// // // //                         console.log("Tablica: zmiana trybu na WRITE");
// // // //                     }

// // // //                     // Odśwież podświetlenie przycisku
// // // //                     group.children.forEach((mesh) => {
// // // //                         if (mesh.userData.isWriteButton) {
// // // //                             const mat = mesh.material;
// // // //                             if (object.userData.mode === "write") {
// // // //                                 mat.color.set(0xffff00); // żółte podświetlenie
// // // //                             } else {
// // // //                                 mat.color.set(0x6cc6d9); // standardowy kolor
// // // //                             }
// // // //                         }
// // // //                     });
// // // //                 },
// // // //                 icon: "/icons/pisz.png",
// // // //                 isWriteButton: true // oznaczenie, żeby łatwo znaleźć
// // // //             });
// // // //         }

// // // //         actions.forEach((action, i) => {
// // // //             const angle = (i / actions.length) * Math.PI * 2;

// // // //             const geometry = new THREE.CircleGeometry(20, 32);
// // // //             const material = new THREE.MeshBasicMaterial({
// // // //                 color: action.isWriteButton
// // // //                     ? object.userData.mode === "write"
// // // //                         ? 0xffff00
// // // //                         : 0x6cc6d9
// // // //                     : 0x6cc6d9,
// // // //                 depthTest: false
// // // //             });

// // // //             const mesh = new THREE.Mesh(geometry, material);
// // // //             mesh.rotation.x = -Math.PI / 2;
// // // //             mesh.position.set(
// // // //                 Math.cos(angle) * radius,
// // // //                 1,
// // // //                 Math.sin(angle) * radius
// // // //             );

// // // //             const texture = textureLoader.load(action.icon);
// // // //             const iconGeometry = new THREE.PlaneGeometry(30, 30);
// // // //             const iconMaterial = new THREE.MeshBasicMaterial({
// // // //                 map: texture,
// // // //                 transparent: true,
// // // //                 depthTest: false,
// // // //                 side: THREE.DoubleSide
// // // //             });
// // // //             const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);
// // // //             iconMesh.rotation.x = Math.PI;
// // // //             iconMesh.rotation.z = -Math.PI / 2;
// // // //             iconMesh.position.set(0, 0.1, 0);
// // // //             mesh.add(iconMesh);

// // // //             mesh.userData.onClick = action.fn;
// // // //             mesh.userData.isRadialButton = true;
// // // //             if (action.isWriteButton) mesh.userData.isWriteButton = true;

// // // //             group.add(mesh);
// // // //             object.userData.radialMenu = group;
// // // //         });

// // // //         sceneRef.current.add(group);
// // // //         group.position.copy(object.position);
// // // //         group.position.y = 1;
// // // //     };





// // // //     const removeRadialMenu = () => {
// // // //         if (!sceneRef.current) return;

// // // //         const menus = sceneRef.current.children.filter(
// // // //             o => o.userData?.isRadialMenu
// // // //         );

// // // //         menus.forEach(menu => sceneRef.current.remove(menu));
// // // //     };




// // // //     // -------------------- KEYBOARD --------------------

// // // //     useEffect(() => {
// // // //         // Przesuwanie płynne
// // // //         const moveGrid = (dx, dz) => {
// // // //             if (!selectedObjectRef.current) return;

// // // //             const obj = selectedObjectRef.current;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = (dimZ - 1) * GRID_SIZE;

// // // //             let worldX = obj.position.x + dx;
// // // //             let worldZ = obj.position.z + dz;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             const prevPosition = obj.position.clone();

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             if (checkCollision(obj)) {
// // // //                 obj.position.copy(prevPosition);
// // // //                 return;
// // // //             }

// // // //             const gridX = Math.round(worldX / GRID_SIZE);
// // // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const maxZ = dimZ - 1;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             // console.log(
// // // //             //     `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //             // );

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;

// // // //             removeRadialMenu();
// // // //         };


// // // //         // //  Przesuwanie skokowe

// // // //         //     const moveGrid = (dx, dz) => {
// // // //         //         if (!selectedObjectRef.current) return;

// // // //         //         const { dimX, dimZ } = boardSizeRef.current;
// // // //         //         const GRID = GRID_SIZE;

// // // //         //         let gridX = selectedObjectRef.current.userData.gridX ?? 0;
// // // //         //         let gridZ = selectedObjectRef.current.userData.gridZ ?? 0;

// // // //         //         gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX + dx));
// // // //         //         gridZ = Math.max(-dimZ, Math.min(0, gridZ + dz));

// // // //         //         selectedObjectRef.current.userData.gridX = gridX;
// // // //         //         selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //         const worldX = -gridX * GRID;
// // // //         //         const worldZ = gridZ * GRID;

// // // //         //         selectedObjectRef.current.position.set(
// // // //         //             worldX,
// // // //         //             selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //             worldZ
// // // //         //         );

// // // //         //     };

// // // //         const onKeyDown = (e) => {
// // // //             if (
// // // //                 e.target.tagName === "INPUT" ||
// // // //                 e.target.tagName === "TEXTAREA" ||
// // // //                 e.target.isContentEditable
// // // //             ) return;

// // // //             // Dodajemy blokadę ruchu w trybie write (TABLICA)
// // // //             const obj = selectedObjectRef.current;
// // // //             if (!obj) return;

// // // //             if (obj.userData.mode === "write") {
// // // //                 return;
// // // //             }

// // // //             switch (e.key.toLowerCase()) {

// // // //                 case "w":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, 5);
// // // //                     break;
// // // //                 case "s":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, -5);
// // // //                     break;
// // // //                 case "a":
// // // //                     e.preventDefault();
// // // //                     moveGrid(5, 0);
// // // //                     break;
// // // //                 case "d":
// // // //                     e.preventDefault();
// // // //                     moveGrid(-5, 0);
// // // //                     break;

// // // //                 case "arrowleft":
// // // //                     e.preventDefault();
// // // //                     rotate90Left();
// // // //                     break;
// // // //                 case "arrowright":
// // // //                     e.preventDefault();
// // // //                     rotate90Right();
// // // //                     break;

// // // //                 case "delete":
// // // //                 case "backspace":
// // // //                     e.preventDefault();
// // // //                     deleteSelected();
// // // //                     break;

// // // //                 case "escape":
// // // //                     selectedObjectRef.current = null;
// // // //                     setSelectedObject(null);
// // // //                     break;

// // // //                 default:
// // // //                     break;
// // // //             }
// // // //         };

// // // //         window.addEventListener("keydown", onKeyDown);
// // // //         return () => window.removeEventListener("keydown", onKeyDown);
// // // //     }, []);


// // // //     // LOGIKA PISANIA NA TABLICY - globalny listener, który sprawdza czy aktualnie wybrany obiekt to tablica w trybie pisania, jeśli tak to modyfikuje jej tekst
// // // //     if (!window._writableBoardListener) {
// // // //         window._writableBoardListener = (e) => {
// // // //             const model = selectedObjectRef.current;
// // // //             if (!model?.userData?.isWritableBoard) return;
// // // //             if (model.userData.mode !== "write") return;

// // // //             const mesh = model.getObjectByName(model.userData.writableMeshName);
// // // //             if (!mesh) return;

// // // //             const { canvas, ctx, texture, clearCanvas } = mesh.userData;
// // // //             const lines = model.userData.lines;

// // // //             if (e.key === "Backspace") {
// // // //                 let last = lines[lines.length - 1];
// // // //                 if (last.length > 0) lines[lines.length - 1] = last.slice(0, -1);
// // // //                 else if (lines.length > 1) lines.pop();
// // // //             } else if (e.key === "Enter") {
// // // //                 lines.push("");
// // // //             } else if (e.key.length === 1) {
// // // //                 lines[lines.length - 1] += e.key;
// // // //             } else return;

// // // //             clearCanvas();
// // // //             ctx.fillStyle = "white";
// // // //             ctx.font = "48px Arial";
// // // //             ctx.textAlign = "left";
// // // //             ctx.textBaseline = "top";

// // // //             lines.forEach((line, i) => ctx.fillText(line, 40, 40 + i * 26));
// // // //             texture.needsUpdate = true;
// // // //         };

// // // //         window.addEventListener("keydown", window._writableBoardListener);
// // // //     }

// // // //     // const logRotation = () => {
// // // //     //     console.log("rotation", selectedObjectRef.current.rotation)
// // // //     // }

// // // //     // -------------------- RENDER --------------------
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 {/* <Typography variant="subtitle2">Modele</Typography> */}
// // // //                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
// // // //                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // // //                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List> */}

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Left}
// // // //                             >
// // // //                                 <Rotate90DegreesCcwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = 1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = -1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Right}
// // // //                             >
// // // //                                 <Rotate90DegreesCwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                             {/* <Button onClick={logRotation}>
// // // //                                 log
// // // //                             </Button> */}
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
// // // //             {gridWalls && <GridWalls
// // // //                 scene={sceneRef.current}
// // // //                 dimX={boardSizeRef.current.dimX}
// // // //                 dimZ={boardSizeRef.current.dimZ}
// // // //                 GRID_SIZE={GRID_SIZE}
// // // //                 cameraRef={cameraRef}
// // // //             />}

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}-${modelsReadyVersion}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{
// // // //                     mt: 1,
// // // //                     position: "absolute",
// // // //                     top: 70,
// // // //                     left: "50%",
// // // //                     transform: "translateX(-50%)",
// // // //                     fontSize: "10px",
// // // //                 }}
// // // //             >
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
// // // //             >
// // // //                 {/* {selectedScenario?.existingSource} */}
// // // //                 {
// // // //                     selectedScenario?.stationName
// // // //                         ? selectedScenario.stationName
// // // //                         : selectedScenario?.id !== undefined
// // // //                             ? `Stacja ${selectedScenario.id}`
// // // //                             : "nie wybrano"
// // // //                 }
// // // //             </Typography>

// // // //             <IconButton
// // // //                 onClick={() => setGridWalls(prev => !prev)}
// // // //                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
// // // //             >
// // // //                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

// // // //             </IconButton>

// // // //             <Snackbar
// // // //                 open={alertOpen}
// // // //                 autoHideDuration={4000}
// // // //                 onClose={() => setAlertOpen(false)}
// // // //                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
// // // //                 sx={{ mt: 8 }}
// // // //             >
// // // //                 <Alert
// // // //                     onClose={() => setAlertOpen(false)}
// // // //                     severity={alertSeverity}
// // // //                     sx={{ width: "100%" }}
// // // //                 >
// // // //                     {alertMessage}
// // // //                 </Alert>
// // // //             </Snackbar>


// // // //             {/* <WallBoards
// // // //                 scene={sceneRef.current}
// // // //                 cameraRef={cameraRef}
// // // //                 rendererRef={rendererRef}
// // // //             //   wallsRef={}
// // // //             /> */}

// // // //             <WritableBoardItem
// // // //                 sceneRef={sceneRef}
// // // //                 objectsRef={objectsRef}
// // // //                 selectedObjectRef={selectedObjectRef}
// // // //                 rendererRef={rendererRef}
// // // //                 cameraRef={cameraRef}
// // // //                 url="/whiteboard/scene.gltf"
// // // //                 position={[0, 0, 0]}
// // // //                 scale={[7, 7, 7]}
// // // //                 heightAboveFloor={0}
// // // //                 writableMeshName="Object001_0"
// // // //             />


// // // //             <DebugBoardMeshes url="/whiteboard/scene.gltf" />



// // // //         </>
// // // //     );
// // // // }










// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Snackbar,
// // // //     Alert,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// // // // import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// // // // import DeleteIcon from "@mui/icons-material/Delete";
// // // // import GridOffIcon from '@mui/icons-material/GridOff';
// // // // import GridOnIcon from '@mui/icons-material/GridOn';

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";
// // // // import ModelList from "./utilities/ModelList";
// // // // import GridWalls from "./three/GridWalls";
// // // // import { ObjectHighlighter } from "./utilities/ObjectHighlighter";
// // // // import WallBoards from "./three/WallBoards";
// // // // import WritableBoard, { DebugBoardMeshes } from "./three/WritableBoard";
// // // // import WritableBoardItem from "./three/WritableBoard";
// // // // import { createWritableBoard } from "./utilities/createWritableBoard";

// // // // const GRID_SIZE = 50;

// // // // const UNITY_Y_OFFSET = 180;

// // // // // const MODEL_LIBRARY = [
// // // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // // ];

// // // // const MODEL_LIBRARY = [
// // // //     { name: "whiteboard", path: "/whiteboard/scene.gltf", visible: true },
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // //     { name: "env_staircase_stairs", path: "/env_staircase_stairs/env_staircase_stairs.gltf", visible: true },
// // // //     { name: "env_wall_column_05", path: "/env_wall_column_05/env_wall_column_05.gltf", visible: true },
// // // //     { name: "exe_cd_essential_items", path: "/exe_cd_essential_items/exe_cd_essential_items.gltf", visible: true },
// // // //     { name: "exe_cooking_bins", path: "/exe_cooking_bins/exe_cooking_bins.gltf", visible: true },
// // // //     { name: "exe_cooking_cookbase", path: "/exe_cooking_cookbase/exe_cooking_cookbase.gltf", visible: true },
// // // //     { name: "exe_cooking_cook_station", path: "/exe_cooking_cook_station/exe_cooking_cook_station.gltf", visible: true },
// // // //     { name: "exe_cooking_device_blender", path: "/exe_cooking_device_blender/exe_cooking_device_blender.gltf", visible: true },
// // // //     { name: "exe_cooking_device_mixer", path: "/exe_cooking_device_mixer/exe_cooking_device_mixer.gltf", visible: true },
// // // //     { name: "exe_cooking_device_mixer_blender", path: "/exe_cooking_device_mixer_blender/exe_cooking_device_mixer_blender.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_dairy", path: "/exe_cooking_fridge_dairy/exe_cooking_fridge_dairy.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_fruits", path: "/exe_cooking_fridge_fruits/exe_cooking_fridge_fruits.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_meat", path: "/exe_cooking_fridge_meat/exe_cooking_fridge_meat.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_vegetables", path: "/exe_cooking_fridge_vegetables/exe_cooking_fridge_vegetables.gltf", visible: true },
// // // //     { name: "exe_cooking_mech_cooker", path: "/exe_cooking_mech_cooker/exe_cooking_mech_cooker.gltf", visible: true },
// // // //     { name: "exe_cooking_mech_furnance", path: "/exe_cooking_mech_furnance/exe_cooking_mech_furnance.gltf", visible: true },
// // // //     { name: "exe_cooking_sink", path: "/exe_cooking_sink/exe_cooking_sink.gltf", visible: true },
// // // //     { name: "exe_cooking_sink_hanged", path: "/exe_cooking_sink_hanged/exe_cooking_sink_hanged.gltf", visible: true },
// // // //     { name: "exe_creator_door_office_double", path: "/exe_creator_door_office_double/exe_creator_door_office_double.gltf", visible: true },
// // // //     { name: "exe_creator_door_wooden_1", path: "/exe_creator_door_wooden_1/exe_creator_door_wooden_1.gltf", visible: true },
// // // //     { name: "exe_printing_3d_printing_station", path: "/exe_printing_3d_printing_station/exe_printing_3d_printing_station.gltf", visible: true },
// // // //     { name: "exe_printing_cabinet_filament_abs_ptg", path: "/exe_printing_cabinet_filament_abs_ptg/exe_printing_cabinet_filament_abs_ptg.gltf", visible: true },
// // // //     { name: "exe_printing_cabinet_filament_pla_tpu", path: "/exe_printing_cabinet_filament_pla_tpu/exe_printing_cabinet_filament_pla_tpu.gltf", visible: true },
// // // //     { name: "exe_printing_cleaning_set", path: "/exe_printing_cleaning_set/exe_printing_cleaning_set.gltf", visible: true },
// // // //     { name: "exe_printing_examples", path: "/exe_printing_examples/exe_printing_examples.gltf", visible: true },
// // // //     { name: "exe_wd_22nut_impactdriver", path: "/exe_wd_22nut_impactdriver/exe_wd_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_combined_exercises", path: "/exe_wd_combined_exercises/exe_wd_combined_exercises.gltf", visible: true },
// // // //     { name: "exe_wd_drill_26plug_26screw_impactdriver", path: "/exe_wd_drill_26plug_26screw_impactdriver/exe_wd_drill_26plug_26screw_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_drill_dowel_hammer", path: "/exe_wd_drill_dowel_hammer/exe_wd_drill_dowel_hammer.gltf", visible: true },
// // // //     { name: "exe_wd_foaming", path: "/exe_wd_foaming/exe_wd_foaming.gltf", visible: true },
// // // //     { name: "exe_wd_garage_gate_installation", path: "/exe_wd_garage_gate_installation/exe_wd_garage_gate_installation.gltf", visible: true },
// // // //     { name: "exe_wd_garage_gate_supply", path: "/exe_wd_garage_gate_supply/exe_wd_garage_gate_supply.gltf", visible: true },
// // // //     { name: "exe_wd_interior_door_installation", path: "/exe_wd_interior_door_installation/exe_wd_interior_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_interior_door_supply", path: "/exe_wd_interior_door_supply/exe_wd_interior_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_leveler", path: "/exe_wd_leveler/exe_wd_leveler.gltf", visible: true },
// // // //     { name: "exe_wd_outer_door_installation", path: "/exe_wd_outer_door_installation/exe_wd_outer_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_outer_door_supply", path: "/exe_wd_outer_door_supply/exe_wd_outer_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill", path: "/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill.gltf", visible: true },
// // // //     { name: "exe_wd_roof_window_installation", path: "/exe_wd_roof_window_installation/exe_wd_roof_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_roof_window_supply", path: "/exe_wd_roof_window_supply/exe_wd_roof_window_supply.gltf", visible: true },
// // // //     { name: "exe_wd_strut", path: "/exe_wd_strut/exe_wd_strut.gltf", visible: true },
// // // //     { name: "exe_wd_tool_rack_1", path: "/exe_wd_tool_rack_1/exe_wd_tool_rack_1.gltf", visible: true },
// // // //     { name: "exe_wd_tool_rack_boxes", path: "/exe_wd_tool_rack_boxes/exe_wd_tool_rack_boxes.gltf", visible: true },
// // // //     { name: "exe_wd_wedge_hammer", path: "/exe_wd_wedge_hammer/exe_wd_wedge_hammer.gltf", visible: true },
// // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_drawer", path: "/exe_cooking_countertop_drawer/exe_cooking_countertop_drawer.gltf", visible: true },
// // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_installation", path: "/exe_wd_technical_door_installation/exe_wd_technical_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // //     { name: "elevator_1x2.1x", path: "/elevator_1x2.1x/elevator_1x2.1x.gltf", visible: true },
// // // // ];


// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);
// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
// // // //     const [gridVersion, setGridVersion] = useState();

// // // //     const highlighterRef = useRef(null);


// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);
// // // //     const [gridWalls, setGridWalls] = useState(true);



// // // //     // Powiadomienia
// // // //     const [alertOpen, setAlertOpen] = useState(false);
// // // //     const [alertMessage, setAlertMessage] = useState("");
// // // //     const [alertSeverity, setAlertSeverity] = useState("success");

// // // //     const [modelsReadyVersion, setModelsReadyVersion] = useState(0);


// // // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //     const degToRad = (deg) => (deg * Math.PI) / 180;
// // // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);



// // // //     useEffect(() => {
// // // //         const arrowConfig = MODEL_LIBRARY.find(m => m.name === "arrow");

// // // //         if (arrowConfig) {
// // // //             loadModel("arrow", arrowConfig.path).then(tpl => {
// // // //                 modelTemplates.current["arrow"] = tpl;
// // // //                 setModelsReadyVersion(v => v + 1);
// // // //             });
// // // //         }
// // // //     }, []);


// // // //     // -------------------- GRID UPDATE --------------------

// // // //     useEffect(() => {
// // // //         if (!selectedScenario) return;
// // // //         setBoardSize({
// // // //             dimX: selectedScenario.dimensionX || 18,
// // // //             dimZ: selectedScenario.dimensionZ || 18
// // // //         });
// // // //         setGridVersion(v => v + 1);
// // // //     }, [selectedScenario?.id]);

// // // //     // console.log('gridVersion', gridVersion)
// // // //     // console.log('boardSize', boardSize)

// // // //     const modelCache = {};

// // // //     const loadModel = (name, path) => {
// // // //         return new Promise((resolve, reject) => {
// // // //             if (modelCache[name]) {
// // // //                 resolve(modelCache[name]);
// // // //                 return;
// // // //             }

// // // //             const loader = new GLTFLoader();

// // // //             loader.load(
// // // //                 encodeURI(path),
// // // //                 (gltf) => {
// // // //                     modelCache[name] = gltf.scene;
// // // //                     resolve(gltf.scene);
// // // //                 },
// // // //                 undefined,
// // // //                 reject
// // // //             );
// // // //         });
// // // //     };

// // // //     const getObjectBox = (obj) => {
// // // //         return new THREE.Box3().setFromObject(obj);
// // // //     };

// // // //     const checkCollision = (obj) => {
// // // //         if (!sceneRef.current) return false;

// // // //         const objBox = getObjectBox(obj);

// // // //         const walls = sceneRef.current.children.filter(o => o.userData?.isWall);

// // // //         for (const wall of walls) {
// // // //             const wallBox = new THREE.Box3().setFromObject(wall);
// // // //             if (objBox.intersectsBox(wallBox)) return true;
// // // //         }

// // // //         for (const other of objectsRef.current) {
// // // //             if (!other.userData?.isModel) continue;
// // // //             if (other === obj) continue;

// // // //             const otherBox = getObjectBox(other);
// // // //             if (objBox.intersectsBox(otherBox)) return true;
// // // //         }

// // // //         return false;
// // // //     };

// // // //     // -------------------- INIT SCENE --------------------
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         camera.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         // let highlight = null;

// // // //         // const highlighter = new ObjectHighlighter(scene, {
// // // //         //     color: 0x4da3ff,
// // // //         //     tintStrength: 0.35,
// // // //         //     auraScale: 1.06,
// // // //         //     auraOpacity: 0.25
// // // //         // });

// // // //         highlighterRef.current = new ObjectHighlighter(scene, {
// // // //             color: 0x4da3ff,
// // // //             tintStrength: 0.35,
// // // //             auraScale: 1.06,
// // // //             auraOpacity: 0.25
// // // //         });


// // // //         // const updatePointer = (e) => {
// // // //         //     pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //         //     pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         // };

// // // //         const updatePointer = (e) => {
// // // //             if (!rendererRef.current) return;
// // // //             const rect = rendererRef.current.domElement.getBoundingClientRect();
// // // //             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
// // // //             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
// // // //         };

// // // //         // const clearHighlight = () => {
// // // //         //     if (highlight) {
// // // //         //         scene.remove(highlight);
// // // //         //         highlight = null;
// // // //         //     }
// // // //         // };

// // // //         // const highlightObject = (obj) => {
// // // //         //     clearHighlight();
// // // //         //     highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //         //     scene.add(highlight);
// // // //         // };

// // // //         const highlightObject = (obj) => {
// // // //             // highlighter.highlightObject(obj);
// // // //             highlighterRef.current?.highlightObject?.(obj);
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             // highlighter.clear();
// // // //             highlighterRef.current?.clear?.();
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const intersects = raycaster.intersectObjects(
// // // //                 sceneRef.current.children,
// // // //                 true
// // // //             );

// // // //             for (const i of intersects) {
// // // //                 if (i.object.userData?.isRadialButton) {
// // // //                     i.object.userData.onClick();
// // // //                     return;
// // // //                 }
// // // //             }

// // // //             let hitModel = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;

// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) {
// // // //                     hitModel = o;
// // // //                 }
// // // //             });

// // // //             if (!hitModel) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 removeRadialMenu();
// // // //                 return;
// // // //             }

// // // //             removeRadialMenu();

// // // //             selectedObjectRef.current = hitModel;
// // // //             setSelectedObject(hitModel);
// // // //             highlightObject(hitModel);

// // // //             createRadialMenu(hitModel);

// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };




// // // //         // Przesuwanie płynne

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             const obj = selectedObjectRef.current;

// // // //             if (obj.userData.radialMenu) {
// // // //                 sceneRef.current.remove(obj.userData.radialMenu);
// // // //                 obj.userData.radialMenu = null;
// // // //             }

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const maxZ = dimZ;

// // // //             let worldX = p.x;
// // // //             let worldZ = p.z;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = maxZ * GRID_SIZE;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));



// // // //             const prevPosition = obj.position.clone();

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             if (checkCollision(obj)) {
// // // //                 obj.position.copy(prevPosition);
// // // //                 return;
// // // //             }

// // // //             const gridX = worldX / GRID_SIZE;
// // // //             const rawGridZ = worldZ / GRID_SIZE;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;


// // // //             // highlighter?.update?.();
// // // //             highlighterRef.current?.update?.();
// // // //         };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let worldX = p.x;
// // // //         //     let worldZ = p.z;

// // // //         //     const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //         //     const maxDepth = maxZ * GRID_SIZE;

// // // //         //     worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //         //     worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX + worldOffsetX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     const gridX = Math.round(worldX / GRID_SIZE);
// // // //         //     const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log(
// // // //         //         `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };

// // // //         // // Przesuwanie Skokowe
// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let rawGridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     rawGridZ = Math.max(-maxZ, Math.min(0, rawGridZ));


// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = rawGridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     // gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridZ = Math.max(-dimZ, Math.min(1, gridZ));


// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = gridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     // highlight?.update?.();
// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     let gridX = Math.round(-p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.round(p.z / GRID_SIZE);
// // // //         //     gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = -gridX * GRID_SIZE;
// // // //         //     const worldZ = gridZ * GRID_SIZE;
// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlight?.update?.();
// // // //         // };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);


// // // //             // highlighter?.update?.();
// // // //             highlighterRef.current?.update?.();
// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 // highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => renderer.domElement.remove();
// // // //     }, []);

// // // //     // -------------------- GRID + PLANE --------------------
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         // usuń poprzedni grid i plane
// // // //         sceneRef.current.children
// // // //             .filter((o) => o.userData?.isGrid)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter((o) => o.userData?.isModel)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(worldOffset.x, 0, worldOffset.z);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
// // // //         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.update();

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);


// // // //     // -------------------- LOAD MODELS --------------------
// // // //     // useEffect(() => {
// // // //     //     const loader = new GLTFLoader();
// // // //     //     let loaded = 0;

// // // //     //     MODEL_LIBRARY.forEach((m) => {
// // // //     //         loader.load(
// // // //     //             encodeURI(m.path),
// // // //     //             (gltf) => {
// // // //     //                 modelTemplates.current[m.name] = SkeletonUtils.clone(gltf.scene);
// // // //     //                 loaded++;
// // // //     //                 if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //     //             },
// // // //     //             undefined,
// // // //     //             (error) => {
// // // //     //                 console.error("Failed to load model:", m.name, error);
// // // //     //                 loaded++;
// // // //     //                 if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //     //             }
// // // //     //         );
// // // //     //     });
// // // //     // }, []);

// // // //     // -------------------- LOAD ENTITIES --------------------

// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     // const baseDimZ = selectedScenario.dimensionZ ?? 18;
// // // //     //     // const extraZ = 8;
// // // //     //     // const dimZ = baseDimZ + extraZ;
// // // //     //     // const worldOffsetZ = -(dimZ / 2) * GRID_SIZE;
// // // //     //     // const worldOffsetX = 0;
// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         const rotY = degToRad(e.rotY || 0);
// // // //     //         const frontCorrection = new THREE.Quaternion();
// // // //     //         frontCorrection.setFromEuler(new THREE.Euler(0, -tpl.rotation.y, 0));

// // // //     //         const userRot = new THREE.Quaternion();
// // // //     //         userRot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);

// // // //     //         model.quaternion.copy(userRot).premultiply(frontCorrection);

// // // //     //         // model.rotation.x = degToRad(e.rotX || 0);
// // // //     //         // model.rotation.z = degToRad(e.rotZ || 0);

// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             rotY,
// // // //     //             degToRad(e.rotZ || 0)
// // // //     //         );
// // // //     //         // const pivotHelper = new THREE.AxesHelper(50)
// // // //     //         // model.add(pivotHelper)

// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);

// // // //     //         // console.log(`${e.name}:`, {
// // // //     //         //     rotX: radToDeg(model.rotation.x),
// // // //     //         //     rotY: radToDeg(model.rotation.y),
// // // //     //         //     rotZ: radToDeg(model.rotation.z),
// // // //     //         //     posX: model.position.x,
// // // //     //         //     posZ: model.position.z,
// // // //     //         // });
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         // --- ROTACJA ---
// // // //     //         // const baseQuat = tpl.quaternion.clone();

// // // //     //         // const euler = new THREE.Euler(
// // // //     //         //     degToRad(e.rotX || 0),
// // // //     //         //     degToRad(e.rotY || 0),
// // // //     //         //     degToRad(e.rotZ || 0),
// // // //     //         //     "YXZ"
// // // //     //         // );

// // // //     //         // const userQuat = new THREE.Quaternion().setFromEuler(euler);

// // // //     //         // model.quaternion.copy(baseQuat).multiply(userQuat);


// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             degToRad(e.rotY || 0),
// // // //     //             degToRad(e.rotZ || 0),
// // // //     //             "YXZ"
// // // //     //         );

// // // //     //         const q = model.quaternion;

// // // //     //         const testEulerXYZ = new THREE.Euler().setFromQuaternion(q, "XYZ");
// // // //     //         const testEulerYXZ = new THREE.Euler().setFromQuaternion(q, "YXZ");

// // // //     //         console.log("XYZ:", radToDeg(testEulerXYZ.x), radToDeg(testEulerXYZ.y), radToDeg(testEulerXYZ.z));
// // // //     //         console.log("YXZ:", radToDeg(testEulerYXZ.x), radToDeg(testEulerYXZ.y), radToDeg(testEulerYXZ.z));


// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     const UNITY_Y_OFFSET = 180; // korekta osi Y dla VR

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         // --- ROTACJA ---
// // // //     //         const baseQuat = tpl.quaternion.clone();

// // // //     //         const rotX = degToRad(e.rotX || 0);
// // // //     //         const rotY = degToRad(UNITY_Y_OFFSET - (e.rotY || 0));
// // // //     //         const rotZ = degToRad(e.rotZ || 0);

// // // //     //         const userQuat = new THREE.Quaternion().setFromEuler(
// // // //     //             new THREE.Euler(rotX, rotY, rotZ, "YXZ")
// // // //     //         );

// // // //     //         model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);

// // // //     //         const testEuler = new THREE.Euler().setFromQuaternion(model.quaternion, "YXZ");
// // // //     //         console.log(
// // // //     //             `[LOAD] ${model.name} | rotX:${radToDeg(testEuler.x).toFixed(1)} | rotY:${radToDeg(testEuler.y).toFixed(1)} | rotZ:${radToDeg(testEuler.z).toFixed(1)}`
// // // //     //         );
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !selectedScenario?.entities) return;

// // // //         let cancelled = false;

// // // //         const loadEntities = async () => {
// // // //             const { dimZ } = boardSizeRef.current;
// // // //             const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //             const worldOffsetX = 0;

// // // //             const UNITY_Y_OFFSET = 180;

// // // //             for (const e of selectedScenario.entities) {
// // // //                 if (cancelled) return;

// // // //                 const config = MODEL_LIBRARY.find(m => m.name === e.name);
// // // //                 if (!config) continue;

// // // //                 try {
// // // //                     const tpl = await loadModel(e.name, config.path);
// // // //                     if (!tpl || cancelled) continue;

// // // //                     modelTemplates.current[e.name] = tpl;

// // // //                     const model = SkeletonUtils.clone(tpl);

// // // //                     model.name = e.name;
// // // //                     model.scale.set(50, 50, 50);
// // // //                     model.userData.isModel = true;

// // // //                     const box = new THREE.Box3().setFromObject(model);
// // // //                     model.userData.heightOffset = -box.min.y;

// // // //                     const gridX = e.posX ?? 0;
// // // //                     const gridZ = e.posZ ?? 0;
// // // //                     model.userData.gridX = gridX;
// // // //                     model.userData.gridZ = gridZ;

// // // //                     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //                     const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;

// // // //                     model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //                     // --- ROTACJA ---
// // // //                     const baseQuat = tpl.quaternion.clone();

// // // //                     const rotX = degToRad(e.rotX || 0);
// // // //                     const rotY = degToRad(UNITY_Y_OFFSET - (e.rotY || 0));
// // // //                     const rotZ = degToRad(e.rotZ || 0);

// // // //                     const userQuat = new THREE.Quaternion().setFromEuler(
// // // //                         new THREE.Euler(rotX, rotY, rotZ, "YXZ")
// // // //                     );

// // // //                     model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //                     sceneRef.current.add(model);
// // // //                     objectsRef.current.push(model);

// // // //                     const testEuler = new THREE.Euler().setFromQuaternion(model.quaternion, "YXZ");

// // // //                     console.log(
// // // //                         `[LOAD] ${model.name} | rotX:${radToDeg(testEuler.x).toFixed(1)} | rotY:${radToDeg(testEuler.y).toFixed(1)} | rotZ:${radToDeg(testEuler.z).toFixed(1)}`
// // // //                     );

// // // //                 } catch (err) {
// // // //                     console.error("Failed to load model:", e.name, err);
// // // //                 }
// // // //             }
// // // //         };

// // // //         loadEntities();

// // // //         return () => {
// // // //             cancelled = true;
// // // //         };

// // // //     }, [selectedScenario]);

// // // //     useEffect(() => {
// // // //         if (!selectedScenario?.entities) return;

// // // //         const uniqueModels = [...new Set(
// // // //             selectedScenario.entities.map(e => e.name)
// // // //         )];

// // // //         uniqueModels.forEach(name => {
// // // //             const config = MODEL_LIBRARY.find(m => m.name === name);

// // // //             if (config) {
// // // //                 loadModel(name, config.path).then(tpl => {
// // // //                     modelTemplates.current[name] = tpl;

// // // //                     setModelsReadyVersion(v => v + 1);
// // // //                 });
// // // //             }
// // // //         });
// // // //     }, [selectedScenario]);

// // // //     // -------------------- ADD / DELETE --------------------
// // // //     const addModel = async (name) => {
// // // //         const config = MODEL_LIBRARY.find(m => m.name === name);
// // // //         if (!config) return;

// // // //         // const tpl = modelTemplates.current[name];
// // // //         const sceneTpl = await loadModel(name, config.path);
// // // //         if (!sceneTpl) return;


// // // //         // Dodanie tablicy do pisania
// // // //         if (name === "whiteboard") {
// // // //             createWritableBoard({
// // // //                 scene: sceneRef.current,
// // // //                 objectsRef,
// // // //                 selectedObjectRef,
// // // //                 url: "/whiteboard/scene.gltf",
// // // //                 position: [0, 0, 0],
// // // //                 rotation: [0, 0, 0],
// // // //                 scale: [7, 7, 7],
// // // //                 heightAboveFloor: 0,
// // // //                 writableMeshName: "Object001_0",
// // // //                 textureLoader,
// // // //             });
// // // //             return;
// // // //         }

// // // //         const model = SkeletonUtils.clone(sceneTpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         // model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         // model.userData.gridZ = 0;

// // // //         // Centrowanie
// // // //         model.userData.gridX = 0
// // // //         model.userData.gridZ = -Math.floor((selectedScenario?.dimensionZ || 18) / 2);

// // // //         const worldX = model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         const baseQuat = sceneTpl.quaternion.clone();
// // // //         const userQuat = new THREE.Quaternion().setFromEuler(
// // // //             new THREE.Euler(0, degToRad(UNITY_Y_OFFSET), 0, "YXZ")
// // // //         );

// // // //         model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         if (selectedObjectRef.current) {
// // // //             removeRadialMenu(selectedObjectRef.current);
// // // //         }

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //         createRadialMenu(model);
// // // //     };


// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const duplicateSelected = () => {
// // // //         const selected = selectedObjectRef.current;
// // // //         if (!selected) return;

// // // //         highlighterRef.current?.clear?.();
// // // //         highlighterRef.current?.unhighlight?.(selected);

// // // //         selected.traverse((child) => {
// // // //             if (child.isMesh && child.userData?.originalColor) {
// // // //                 child.material.color.copy(child.userData.originalColor);
// // // //             }
// // // //         });

// // // //         const copy = selected.clone(true);

// // // //         copy.position.x += 10;
// // // //         copy.position.z += 10;

// // // //         copy.userData = { ...selected.userData };

// // // //         sceneRef.current.add(copy);

// // // //         objectsRef.current.push(copy);

// // // //         selectedObjectRef.current = copy;
// // // //         setSelectedObject(copy);
// // // //     };



// // // //     // const saveScenario = async () => {
// // // //     //     if (!selectedScenario) return;

// // // //     //     console.log('objectsRef.current', objectsRef.current)



// // // //     //     // const entities = objectsRef.current
// // // //     //     //     .filter(o => o.userData.isModel)
// // // //     //     //     .map(o => {

// // // //     //     //         const euler = new THREE.Euler().setFromQuaternion(
// // // //     //     //             o.quaternion,
// // // //     //     //             "YXZ"
// // // //     //     //         );

// // // //     //     //         const rotX = normalizeDeg(radToDeg(euler.x));
// // // //     //     //         const rotY = normalizeDeg(radToDeg(euler.y));
// // // //     //     //         const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //     //     //         return {
// // // //     //     //             name: o.name,
// // // //     //     //             posX: o.userData.gridX ?? 0,
// // // //     //     //             posY: 0,
// // // //     //     //             posZ: o.userData.gridZ ?? 0,
// // // //     //     //             rotX,
// // // //     //     //             rotY,
// // // //     //     //             rotZ
// // // //     //     //         };
// // // //     //     //     });


// // // //     //     const entities = objectsRef.current
// // // //     //         .filter(o => o.userData.isModel)
// // // //     //         .map(o => {

// // // //     //             const euler = new THREE.Euler().setFromQuaternion(
// // // //     //                 o.quaternion,
// // // //     //                 "YXZ"
// // // //     //             );

// // // //     //             const rotX = normalizeDeg(radToDeg(euler.x));
// // // //     //             const rotY = normalizeDeg(360 - normalizeDeg(radToDeg(euler.y)));
// // // //     //             const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //     //             return {
// // // //     //                 name: o.name,
// // // //     //                 posX: o.userData.gridX ?? 0,
// // // //     //                 posY: 0,
// // // //     //                 posZ: o.userData.gridZ ?? 0,
// // // //     //                 rotX,
// // // //     //                 rotY,
// // // //     //                 rotZ
// // // //     //             };
// // // //     //         });


// // // //     //     console.log('entities', entities)

// // // //     //     try {
// // // //     //         const response = await fetch(
// // // //     //             `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //     //             {
// // // //     //                 method: "PATCH",
// // // //     //                 headers: { "Content-Type": "application/json" },
// // // //     //                 body: JSON.stringify({
// // // //     //                     stationName: selectedScenario.stationName,
// // // //     //                     dimensionX: selectedScenario.dimensionX,
// // // //     //                     dimensionY: selectedScenario.dimensionY,
// // // //     //                     entities,
// // // //     //                 }),
// // // //     //             }
// // // //     //         );

// // // //     //         if (!response.ok) {
// // // //     //             const errorData = await response.json();
// // // //     //             throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //     //         }

// // // //     //         setAlertMessage("Stacja zapisana pomyślnie!");
// // // //     //         setAlertSeverity("success");
// // // //     //         setAlertOpen(true);
// // // //     //     } catch (error) {
// // // //     //         console.error(error);
// // // //     //         setAlertMessage(`Błąd: ${error.message}`);
// // // //     //         setAlertSeverity("error");
// // // //     //         setAlertOpen(true);
// // // //     //     }
// // // //     // };


// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => {
// // // //                 const tpl = modelTemplates.current[o.name];
// // // //                 const baseQuat = tpl?.quaternion.clone() || new THREE.Quaternion();
// // // //                 const baseInv = baseQuat.clone().invert();

// // // //                 const userQuat = o.quaternion.clone().premultiply(baseInv);

// // // //                 const euler = new THREE.Euler().setFromQuaternion(userQuat, "YXZ");

// // // //                 const rotX = normalizeDeg(radToDeg(euler.x));
// // // //                 const rotY = normalizeDeg(UNITY_Y_OFFSET - normalizeDeg(radToDeg(euler.y))); // flip Y do VR
// // // //                 const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //                 return {
// // // //                     name: o.name,
// // // //                     GUID: crypto.randomUUID(),
// // // //                     posX: o.userData.gridX ?? 0,
// // // //                     posY: 0,
// // // //                     posZ: o.userData.gridZ ?? 0,
// // // //                     rotX,
// // // //                     rotY,
// // // //                     rotZ,
// // // //                 };
// // // //             });

// // // //         // console.log('entities to save:', entities);

// // // //         try {
// // // //             const response = await fetch(
// // // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //                 {
// // // //                     method: "PATCH",
// // // //                     headers: { "Content-Type": "application/json" },
// // // //                     body: JSON.stringify({
// // // //                         stationName: selectedScenario.stationName,
// // // //                         GUID: crypto.randomUUID(),
// // // //                         dimensionX: selectedScenario.dimensionX,
// // // //                         dimensionY: selectedScenario.dimensionY,
// // // //                         entities,
// // // //                     }),
// // // //                 }
// // // //             );

// // // //             if (!response.ok) {
// // // //                 const errorData = await response.json();
// // // //                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //             }

// // // //             setAlertMessage("Stacja zapisana pomyślnie!");
// // // //             setAlertSeverity("success");
// // // //             setAlertOpen(true);
// // // //         } catch (error) {
// // // //             console.error(error);
// // // //             setAlertMessage(`Błąd: ${error.message}`);
// // // //             setAlertSeverity("error");
// // // //             setAlertOpen(true);
// // // //         }
// // // //     };


// // // //     const rotate90Right = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y -= step;

// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };

// // // //     const rotate90Left = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y += step;
// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };


// // // //     // const createRadialMenu = (object) => {
// // // //     //     if (!sceneRef.current) return;

// // // //     //     const radius = 120;

// // // //     //     const group = new THREE.Group();
// // // //     //     group.userData.isRadialMenu = true;

// // // //     //     const actions = [
// // // //     //         { fn: rotate90Left },
// // // //     //         { fn: rotate90Right },
// // // //     //         { fn: deleteSelected }
// // // //     //     ];

// // // //     //     actions.forEach((action, i) => {
// // // //     //         const angle = (i / actions.length) * Math.PI * 2;

// // // //     //         const geometry = new THREE.CircleGeometry(20, 32);
// // // //     //         const material = new THREE.MeshBasicMaterial({
// // // //     //             color: 0xffffff,
// // // //     //             depthTest: false
// // // //     //         });

// // // //     //         const mesh = new THREE.Mesh(geometry, material);

// // // //     //         mesh.rotation.x = -Math.PI / 2;

// // // //     //         mesh.position.set(
// // // //     //             Math.cos(angle) * radius,
// // // //     //             1,
// // // //     //             Math.sin(angle) * radius
// // // //     //         );

// // // //     //         mesh.userData.onClick = action.fn;
// // // //     //         mesh.userData.isRadialButton = true;

// // // //     //         group.add(mesh);
// // // //     //     });

// // // //     //     sceneRef.current.add(group);
// // // //     //     group.position.copy(object.position);
// // // //     //     group.position.y = 1;
// // // //     // };

// // // //     const textureLoader = new THREE.TextureLoader();

// // // //     // const createRadialMenu = (object) => {
// // // //     //     if (!sceneRef.current) return;


// // // //     //     if (object.userData.radialMenu) {
// // // //     //         sceneRef.current.remove(object.userData.radialMenu);
// // // //     //         object.userData.radialMenu = null;
// // // //     //     }

// // // //     //     const radius = 120;

// // // //     //     const group = new THREE.Group();
// // // //     //     group.userData.isRadialMenu = true;


// // // //     //     const actions = [
// // // //     //         // { fn: rotate90Left, icon: "/icons/rotate1.png" },
// // // //     //         // { fn: rotate90Right, icon: "/icons/rotate1.png" },
// // // //     //         { fn: rotate90Left, icon: "/icons/rotateleft.png" },
// // // //     //         { fn: rotate90Right, icon: "/icons/rotateright.png" },
// // // //     //         { fn: deleteSelected, icon: "/icons/delete.png" },
// // // //     //         { fn: duplicateSelected, icon: "/icons/copy.png" }
// // // //     //     ];

// // // //     //     // ======= DODANIE PRZYCISKU WRITE DLA TABLICY =======
// // // //     //     if (object.userData.isWritableBoard) {
// // // //     //         actions.push({
// // // //     //             fn: () => {
// // // //     //                 if (!object) return;
// // // //     //                 if (object.userData.mode === "write") {
// // // //     //                     object.userData.mode = "move";
// // // //     //                     object.userData.isMovable = true;
// // // //     //                     console.log("Tablica: zmiana trybu na MOVE");
// // // //     //                 } else {
// // // //     //                     object.userData.mode = "write";
// // // //     //                     object.userData.isMovable = false;
// // // //     //                     console.log("Tablica: zmiana trybu na WRITE");
// // // //     //                 }
// // // //     //             },
// // // //     //             icon: "/icons/pisz.png"
// // // //     //         });
// // // //     //     }

// // // //     //     actions.forEach((action, i) => {
// // // //     //         const angle = (i / actions.length) * Math.PI * 2;

// // // //     //         const geometry = new THREE.CircleGeometry(20, 32);
// // // //     //         const material = new THREE.MeshBasicMaterial({
// // // //     //             color: 0x6cc6d9,
// // // //     //             depthTest: false
// // // //     //         });
// // // //     //         const mesh = new THREE.Mesh(geometry, material);
// // // //     //         mesh.rotation.x = -Math.PI / 2;
// // // //     //         mesh.position.set(
// // // //     //             Math.cos(angle) * radius,
// // // //     //             1,
// // // //     //             Math.sin(angle) * radius
// // // //     //         );

// // // //     //         const texture = textureLoader.load(action.icon);
// // // //     //         const iconGeometry = new THREE.PlaneGeometry(30, 30);
// // // //     //         const iconMaterial = new THREE.MeshBasicMaterial({
// // // //     //             map: texture,
// // // //     //             transparent: true,
// // // //     //             depthTest: false,
// // // //     //             side: THREE.DoubleSide
// // // //     //         });
// // // //     //         const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);

// // // //     //         iconMesh.rotation.x = Math.PI;
// // // //     //         iconMesh.rotation.z = -Math.PI / 2;

// // // //     //         iconMesh.position.set(0, 0.1, 0);

// // // //     //         mesh.add(iconMesh);

// // // //     //         mesh.userData.onClick = action.fn;
// // // //     //         mesh.userData.isRadialButton = true;

// // // //     //         group.add(mesh);

// // // //     //         object.userData.radialMenu = group;
// // // //     //     });

// // // //     //     sceneRef.current.add(group);
// // // //     //     group.position.copy(object.position);
// // // //     //     group.position.y = 1;
// // // //     // };

// // // //     const createRadialMenu = (object) => {
// // // //         if (!sceneRef.current) return;

// // // //         // USUWANIE STAREGO MENU
// // // //         if (object.userData.radialMenu) {
// // // //             sceneRef.current.remove(object.userData.radialMenu);
// // // //             object.userData.radialMenu = null;
// // // //         }

// // // //         const radius = 120;
// // // //         const group = new THREE.Group();
// // // //         group.userData.isRadialMenu = true;

// // // //         const actions = [
// // // //             { fn: rotate90Left, icon: "/icons/rotateleft.png" },
// // // //             { fn: rotate90Right, icon: "/icons/rotateright.png" },
// // // //             { fn: deleteSelected, icon: "/icons/delete.png" },
// // // //             { fn: duplicateSelected, icon: "/icons/copy.png" }
// // // //         ];

// // // //         // ======= DODANIE PRZYCISKU PISZ DLA TABLICY =======
// // // //         if (object.userData.isWritableBoard) {
// // // //             actions.push({
// // // //                 fn: () => {
// // // //                     if (!object) return;

// // // //                     // toggle mode
// // // //                     if (object.userData.mode === "write") {
// // // //                         object.userData.mode = "move";
// // // //                         object.userData.isMovable = true;
// // // //                         console.log("Tablica: zmiana trybu na MOVE");
// // // //                     } else {
// // // //                         object.userData.mode = "write";
// // // //                         object.userData.isMovable = false;
// // // //                         console.log("Tablica: zmiana trybu na WRITE");
// // // //                     }

// // // //                     // Odśwież podświetlenie przycisku
// // // //                     group.children.forEach((mesh) => {
// // // //                         if (mesh.userData.isWriteButton) {
// // // //                             const mat = mesh.material;
// // // //                             if (object.userData.mode === "write") {
// // // //                                 mat.color.set(0xffff00); // żółte podświetlenie
// // // //                             } else {
// // // //                                 mat.color.set(0x6cc6d9); // standardowy kolor
// // // //                             }
// // // //                         }
// // // //                     });
// // // //                 },
// // // //                 icon: "/icons/pisz.png",
// // // //                 isWriteButton: true // oznaczenie, żeby łatwo znaleźć
// // // //             });
// // // //         }

// // // //         actions.forEach((action, i) => {
// // // //             const angle = (i / actions.length) * Math.PI * 2;

// // // //             const geometry = new THREE.CircleGeometry(20, 32);
// // // //             const material = new THREE.MeshBasicMaterial({
// // // //                 color: action.isWriteButton
// // // //                     ? object.userData.mode === "write"
// // // //                         ? 0xffff00
// // // //                         : 0x6cc6d9
// // // //                     : 0x6cc6d9,
// // // //                 depthTest: false
// // // //             });

// // // //             const mesh = new THREE.Mesh(geometry, material);
// // // //             mesh.rotation.x = -Math.PI / 2;
// // // //             mesh.position.set(
// // // //                 Math.cos(angle) * radius,
// // // //                 1,
// // // //                 Math.sin(angle) * radius
// // // //             );

// // // //             const texture = textureLoader.load(action.icon);
// // // //             const iconGeometry = new THREE.PlaneGeometry(30, 30);
// // // //             const iconMaterial = new THREE.MeshBasicMaterial({
// // // //                 map: texture,
// // // //                 transparent: true,
// // // //                 depthTest: false,
// // // //                 side: THREE.DoubleSide
// // // //             });
// // // //             const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);
// // // //             iconMesh.rotation.x = Math.PI;
// // // //             iconMesh.rotation.z = -Math.PI / 2;
// // // //             iconMesh.position.set(0, 0.1, 0);
// // // //             mesh.add(iconMesh);

// // // //             mesh.userData.onClick = action.fn;
// // // //             mesh.userData.isRadialButton = true;
// // // //             if (action.isWriteButton) mesh.userData.isWriteButton = true;

// // // //             group.add(mesh);
// // // //             object.userData.radialMenu = group;
// // // //         });

// // // //         sceneRef.current.add(group);
// // // //         group.position.copy(object.position);
// // // //         group.position.y = 1;
// // // //     };





// // // //     const removeRadialMenu = () => {
// // // //         if (!sceneRef.current) return;

// // // //         const menus = sceneRef.current.children.filter(
// // // //             o => o.userData?.isRadialMenu
// // // //         );

// // // //         menus.forEach(menu => sceneRef.current.remove(menu));
// // // //     };




// // // //     // -------------------- KEYBOARD --------------------

// // // //     useEffect(() => {
// // // //         // Przesuwanie płynne
// // // //         const moveGrid = (dx, dz) => {
// // // //             if (!selectedObjectRef.current) return;

// // // //             const obj = selectedObjectRef.current;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = (dimZ - 1) * GRID_SIZE;

// // // //             let worldX = obj.position.x + dx;
// // // //             let worldZ = obj.position.z + dz;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             const prevPosition = obj.position.clone();

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             if (checkCollision(obj)) {
// // // //                 obj.position.copy(prevPosition);
// // // //                 return;
// // // //             }

// // // //             const gridX = Math.round(worldX / GRID_SIZE);
// // // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const maxZ = dimZ - 1;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             // console.log(
// // // //             //     `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //             // );

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;

// // // //             removeRadialMenu();
// // // //         };


// // // //         // //  Przesuwanie skokowe

// // // //         //     const moveGrid = (dx, dz) => {
// // // //         //         if (!selectedObjectRef.current) return;

// // // //         //         const { dimX, dimZ } = boardSizeRef.current;
// // // //         //         const GRID = GRID_SIZE;

// // // //         //         let gridX = selectedObjectRef.current.userData.gridX ?? 0;
// // // //         //         let gridZ = selectedObjectRef.current.userData.gridZ ?? 0;

// // // //         //         gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX + dx));
// // // //         //         gridZ = Math.max(-dimZ, Math.min(0, gridZ + dz));

// // // //         //         selectedObjectRef.current.userData.gridX = gridX;
// // // //         //         selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //         const worldX = -gridX * GRID;
// // // //         //         const worldZ = gridZ * GRID;

// // // //         //         selectedObjectRef.current.position.set(
// // // //         //             worldX,
// // // //         //             selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //             worldZ
// // // //         //         );

// // // //         //     };

// // // //         const onKeyDown = (e) => {
// // // //             if (
// // // //                 e.target.tagName === "INPUT" ||
// // // //                 e.target.tagName === "TEXTAREA" ||
// // // //                 e.target.isContentEditable
// // // //             ) return;

// // // //             // Dodajemy blokadę ruchu w trybie write (TABLICA)
// // // //             const obj = selectedObjectRef.current;
// // // //             if (!obj) return;

// // // //             if (obj.userData.mode === "write") {
// // // //                 return;
// // // //             }

// // // //             switch (e.key.toLowerCase()) {

// // // //                 case "w":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, 5);
// // // //                     break;
// // // //                 case "s":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, -5);
// // // //                     break;
// // // //                 case "a":
// // // //                     e.preventDefault();
// // // //                     moveGrid(5, 0);
// // // //                     break;
// // // //                 case "d":
// // // //                     e.preventDefault();
// // // //                     moveGrid(-5, 0);
// // // //                     break;

// // // //                 case "arrowleft":
// // // //                     e.preventDefault();
// // // //                     rotate90Left();
// // // //                     break;
// // // //                 case "arrowright":
// // // //                     e.preventDefault();
// // // //                     rotate90Right();
// // // //                     break;

// // // //                 case "delete":
// // // //                 case "backspace":
// // // //                     e.preventDefault();
// // // //                     deleteSelected();
// // // //                     break;

// // // //                 case "escape":
// // // //                     selectedObjectRef.current = null;
// // // //                     setSelectedObject(null);
// // // //                     break;

// // // //                 default:
// // // //                     break;
// // // //             }
// // // //         };

// // // //         window.addEventListener("keydown", onKeyDown);
// // // //         return () => window.removeEventListener("keydown", onKeyDown);
// // // //     }, []);


// // // //     // LOGIKA PISANIA NA TABLICY - globalny listener, który sprawdza czy aktualnie wybrany obiekt to tablica w trybie pisania, jeśli tak to modyfikuje jej tekst
// // // //     if (!window._writableBoardListener) {
// // // //         window._writableBoardListener = (e) => {
// // // //             const model = selectedObjectRef.current;
// // // //             if (!model?.userData?.isWritableBoard) return;
// // // //             if (model.userData.mode !== "write") return;

// // // //             const mesh = model.getObjectByName(model.userData.writableMeshName);
// // // //             if (!mesh) return;

// // // //             const { canvas, ctx, texture, clearCanvas } = mesh.userData;
// // // //             const lines = model.userData.lines;

// // // //             if (e.key === "Backspace") {
// // // //                 let last = lines[lines.length - 1];
// // // //                 if (last.length > 0) lines[lines.length - 1] = last.slice(0, -1);
// // // //                 else if (lines.length > 1) lines.pop();
// // // //             } else if (e.key === "Enter") {
// // // //                 lines.push("");
// // // //             } else if (e.key.length === 1) {
// // // //                 lines[lines.length - 1] += e.key;
// // // //             } else return;

// // // //             clearCanvas();
// // // //             ctx.fillStyle = "white";
// // // //             ctx.font = "48px Arial";
// // // //             ctx.textAlign = "left";
// // // //             ctx.textBaseline = "top";

// // // //             lines.forEach((line, i) => ctx.fillText(line, 40, 40 + i * 26));
// // // //             texture.needsUpdate = true;
// // // //         };

// // // //         window.addEventListener("keydown", window._writableBoardListener);
// // // //     }

// // // //     // const logRotation = () => {
// // // //     //     console.log("rotation", selectedObjectRef.current.rotation)
// // // //     // }

// // // //     // -------------------- RENDER --------------------
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 {/* <Typography variant="subtitle2">Modele</Typography> */}
// // // //                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
// // // //                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // // //                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List> */}

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Left}
// // // //                             >
// // // //                                 <Rotate90DegreesCcwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = 1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = -1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Right}
// // // //                             >
// // // //                                 <Rotate90DegreesCwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                             {/* <Button onClick={logRotation}>
// // // //                                 log
// // // //                             </Button> */}
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
// // // //             {gridWalls && <GridWalls
// // // //                 scene={sceneRef.current}
// // // //                 dimX={boardSizeRef.current.dimX}
// // // //                 dimZ={boardSizeRef.current.dimZ}
// // // //                 GRID_SIZE={GRID_SIZE}
// // // //                 cameraRef={cameraRef}
// // // //             />}

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}-${modelsReadyVersion}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{
// // // //                     mt: 1,
// // // //                     position: "absolute",
// // // //                     top: 70,
// // // //                     left: "50%",
// // // //                     transform: "translateX(-50%)",
// // // //                     fontSize: "10px",
// // // //                 }}
// // // //             >
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
// // // //             >
// // // //                 {/* {selectedScenario?.existingSource} */}
// // // //                 {
// // // //                     selectedScenario?.stationName
// // // //                         ? selectedScenario.stationName
// // // //                         : selectedScenario?.id !== undefined
// // // //                             ? `Stacja ${selectedScenario.id}`
// // // //                             : "nie wybrano"
// // // //                 }
// // // //             </Typography>

// // // //             <IconButton
// // // //                 onClick={() => setGridWalls(prev => !prev)}
// // // //                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
// // // //             >
// // // //                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

// // // //             </IconButton>

// // // //             <Snackbar
// // // //                 open={alertOpen}
// // // //                 autoHideDuration={4000}
// // // //                 onClose={() => setAlertOpen(false)}
// // // //                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
// // // //                 sx={{ mt: 8 }}
// // // //             >
// // // //                 <Alert
// // // //                     onClose={() => setAlertOpen(false)}
// // // //                     severity={alertSeverity}
// // // //                     sx={{ width: "100%" }}
// // // //                 >
// // // //                     {alertMessage}
// // // //                 </Alert>
// // // //             </Snackbar>


// // // //             {/* <WallBoards
// // // //                 scene={sceneRef.current}
// // // //                 cameraRef={cameraRef}
// // // //                 rendererRef={rendererRef}
// // // //             //   wallsRef={}
// // // //             /> */}

// // // //             <WritableBoardItem
// // // //                 sceneRef={sceneRef}
// // // //                 objectsRef={objectsRef}
// // // //                 selectedObjectRef={selectedObjectRef}
// // // //                 rendererRef={rendererRef}
// // // //                 cameraRef={cameraRef}
// // // //                 url="/whiteboard/scene.gltf"
// // // //                 position={[0, 0, 0]}
// // // //                 scale={[7, 7, 7]}
// // // //                 heightAboveFloor={0}
// // // //                 writableMeshName="Object001_0"
// // // //             />


// // // //             <DebugBoardMeshes url="/whiteboard/scene.gltf" />



// // // //         </>
// // // //     );
// // // // }






// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Snackbar,
// // // //     Alert,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// // // // import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// // // // import DeleteIcon from "@mui/icons-material/Delete";
// // // // import GridOffIcon from '@mui/icons-material/GridOff';
// // // // import GridOnIcon from '@mui/icons-material/GridOn';

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";
// // // // import ModelList from "./utilities/ModelList";
// // // // import GridWalls from "./three/GridWalls";
// // // // import { ObjectHighlighter } from "./utilities/ObjectHighlighter";
// // // // import WallBoards from "./three/WallBoards";
// // // // import WritableBoard, { DebugBoardMeshes } from "./three/WritableBoard";
// // // // import WritableBoardItem from "./three/WritableBoard";
// // // // import { createWritableBoard } from "./utilities/createWritableBoard";

// // // // const GRID_SIZE = 50;

// // // // const UNITY_Y_OFFSET = 180;

// // // // // const MODEL_LIBRARY = [
// // // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // // ];

// // // // const MODEL_LIBRARY = [
// // // //     { name: "whiteboard", path: "/whiteboard/scene.gltf", visible: true },
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // //     { name: "env_staircase_stairs", path: "/env_staircase_stairs/env_staircase_stairs.gltf", visible: true },
// // // //     { name: "env_wall_column_05", path: "/env_wall_column_05/env_wall_column_05.gltf", visible: true },
// // // //     { name: "exe_cd_essential_items", path: "/exe_cd_essential_items/exe_cd_essential_items.gltf", visible: true },
// // // //     { name: "exe_cooking_bins", path: "/exe_cooking_bins/exe_cooking_bins.gltf", visible: true },
// // // //     { name: "exe_cooking_cookbase", path: "/exe_cooking_cookbase/exe_cooking_cookbase.gltf", visible: true },
// // // //     { name: "exe_cooking_cook_station", path: "/exe_cooking_cook_station/exe_cooking_cook_station.gltf", visible: true },
// // // //     { name: "exe_cooking_device_blender", path: "/exe_cooking_device_blender/exe_cooking_device_blender.gltf", visible: true },
// // // //     { name: "exe_cooking_device_mixer", path: "/exe_cooking_device_mixer/exe_cooking_device_mixer.gltf", visible: true },
// // // //     { name: "exe_cooking_device_mixer_blender", path: "/exe_cooking_device_mixer_blender/exe_cooking_device_mixer_blender.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_dairy", path: "/exe_cooking_fridge_dairy/exe_cooking_fridge_dairy.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_fruits", path: "/exe_cooking_fridge_fruits/exe_cooking_fridge_fruits.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_meat", path: "/exe_cooking_fridge_meat/exe_cooking_fridge_meat.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_vegetables", path: "/exe_cooking_fridge_vegetables/exe_cooking_fridge_vegetables.gltf", visible: true },
// // // //     { name: "exe_cooking_mech_cooker", path: "/exe_cooking_mech_cooker/exe_cooking_mech_cooker.gltf", visible: true },
// // // //     { name: "exe_cooking_mech_furnance", path: "/exe_cooking_mech_furnance/exe_cooking_mech_furnance.gltf", visible: true },
// // // //     { name: "exe_cooking_sink", path: "/exe_cooking_sink/exe_cooking_sink.gltf", visible: true },
// // // //     { name: "exe_cooking_sink_hanged", path: "/exe_cooking_sink_hanged/exe_cooking_sink_hanged.gltf", visible: true },
// // // //     { name: "exe_creator_door_office_double", path: "/exe_creator_door_office_double/exe_creator_door_office_double.gltf", visible: true },
// // // //     { name: "exe_creator_door_wooden_1", path: "/exe_creator_door_wooden_1/exe_creator_door_wooden_1.gltf", visible: true },
// // // //     { name: "exe_printing_3d_printing_station", path: "/exe_printing_3d_printing_station/exe_printing_3d_printing_station.gltf", visible: true },
// // // //     { name: "exe_printing_cabinet_filament_abs_ptg", path: "/exe_printing_cabinet_filament_abs_ptg/exe_printing_cabinet_filament_abs_ptg.gltf", visible: true },
// // // //     { name: "exe_printing_cabinet_filament_pla_tpu", path: "/exe_printing_cabinet_filament_pla_tpu/exe_printing_cabinet_filament_pla_tpu.gltf", visible: true },
// // // //     { name: "exe_printing_cleaning_set", path: "/exe_printing_cleaning_set/exe_printing_cleaning_set.gltf", visible: true },
// // // //     { name: "exe_printing_examples", path: "/exe_printing_examples/exe_printing_examples.gltf", visible: true },
// // // //     { name: "exe_wd_22nut_impactdriver", path: "/exe_wd_22nut_impactdriver/exe_wd_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_combined_exercises", path: "/exe_wd_combined_exercises/exe_wd_combined_exercises.gltf", visible: true },
// // // //     { name: "exe_wd_drill_26plug_26screw_impactdriver", path: "/exe_wd_drill_26plug_26screw_impactdriver/exe_wd_drill_26plug_26screw_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_drill_dowel_hammer", path: "/exe_wd_drill_dowel_hammer/exe_wd_drill_dowel_hammer.gltf", visible: true },
// // // //     { name: "exe_wd_foaming", path: "/exe_wd_foaming/exe_wd_foaming.gltf", visible: true },
// // // //     { name: "exe_wd_garage_gate_installation", path: "/exe_wd_garage_gate_installation/exe_wd_garage_gate_installation.gltf", visible: true },
// // // //     { name: "exe_wd_garage_gate_supply", path: "/exe_wd_garage_gate_supply/exe_wd_garage_gate_supply.gltf", visible: true },
// // // //     { name: "exe_wd_interior_door_installation", path: "/exe_wd_interior_door_installation/exe_wd_interior_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_interior_door_supply", path: "/exe_wd_interior_door_supply/exe_wd_interior_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_leveler", path: "/exe_wd_leveler/exe_wd_leveler.gltf", visible: true },
// // // //     { name: "exe_wd_outer_door_installation", path: "/exe_wd_outer_door_installation/exe_wd_outer_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_outer_door_supply", path: "/exe_wd_outer_door_supply/exe_wd_outer_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill", path: "/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill.gltf", visible: true },
// // // //     { name: "exe_wd_roof_window_installation", path: "/exe_wd_roof_window_installation/exe_wd_roof_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_roof_window_supply", path: "/exe_wd_roof_window_supply/exe_wd_roof_window_supply.gltf", visible: true },
// // // //     { name: "exe_wd_strut", path: "/exe_wd_strut/exe_wd_strut.gltf", visible: true },
// // // //     { name: "exe_wd_tool_rack_1", path: "/exe_wd_tool_rack_1/exe_wd_tool_rack_1.gltf", visible: true },
// // // //     { name: "exe_wd_tool_rack_boxes", path: "/exe_wd_tool_rack_boxes/exe_wd_tool_rack_boxes.gltf", visible: true },
// // // //     { name: "exe_wd_wedge_hammer", path: "/exe_wd_wedge_hammer/exe_wd_wedge_hammer.gltf", visible: true },
// // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_drawer", path: "/exe_cooking_countertop_drawer/exe_cooking_countertop_drawer.gltf", visible: true },
// // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_installation", path: "/exe_wd_technical_door_installation/exe_wd_technical_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // //     { name: "elevator_1x2.1x", path: "/elevator_1x2.1x/elevator_1x2.1x.gltf", visible: true },
// // // // ];


// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);
// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
// // // //     const [gridVersion, setGridVersion] = useState();

// // // //     const highlighterRef = useRef(null);


// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);
// // // //     const [gridWalls, setGridWalls] = useState(true);


// // // //     // Powiadomienia
// // // //     const [alertOpen, setAlertOpen] = useState(false);
// // // //     const [alertMessage, setAlertMessage] = useState("");
// // // //     const [alertSeverity, setAlertSeverity] = useState("success");


// // // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //     const degToRad = (deg) => (deg * Math.PI) / 180;
// // // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


// // // //     // -------------------- GRID UPDATE --------------------

// // // //     useEffect(() => {
// // // //         if (!selectedScenario) return;
// // // //         setBoardSize({
// // // //             dimX: selectedScenario.dimensionX || 18,
// // // //             dimZ: selectedScenario.dimensionZ || 18
// // // //         });
// // // //         setGridVersion(v => v + 1);
// // // //     }, [selectedScenario?.id]);

// // // //     // console.log('gridVersion', gridVersion)
// // // //     // console.log('boardSize', boardSize)

// // // //     const getObjectBox = (obj) => {
// // // //         return new THREE.Box3().setFromObject(obj);
// // // //     };

// // // //     const checkCollision = (obj) => {
// // // //         if (!sceneRef.current) return false;

// // // //         const objBox = getObjectBox(obj);

// // // //         const walls = sceneRef.current.children.filter(o => o.userData?.isWall);

// // // //         for (const wall of walls) {
// // // //             const wallBox = new THREE.Box3().setFromObject(wall);
// // // //             if (objBox.intersectsBox(wallBox)) return true;
// // // //         }

// // // //         for (const other of objectsRef.current) {
// // // //             if (!other.userData?.isModel) continue;
// // // //             if (other === obj) continue;

// // // //             const otherBox = getObjectBox(other);
// // // //             if (objBox.intersectsBox(otherBox)) return true;
// // // //         }

// // // //         return false;
// // // //     };

// // // //     // -------------------- INIT SCENE --------------------
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         camera.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         // let highlight = null;

// // // //         // const highlighter = new ObjectHighlighter(scene, {
// // // //         //     color: 0x4da3ff,
// // // //         //     tintStrength: 0.35,
// // // //         //     auraScale: 1.06,
// // // //         //     auraOpacity: 0.25
// // // //         // });

// // // //         highlighterRef.current = new ObjectHighlighter(scene, {
// // // //             color: 0x4da3ff,
// // // //             tintStrength: 0.35,
// // // //             auraScale: 1.06,
// // // //             auraOpacity: 0.25
// // // //         });


// // // //         // const updatePointer = (e) => {
// // // //         //     pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //         //     pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         // };

// // // //         const updatePointer = (e) => {
// // // //             if (!rendererRef.current) return;
// // // //             const rect = rendererRef.current.domElement.getBoundingClientRect();
// // // //             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
// // // //             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
// // // //         };

// // // //         // const clearHighlight = () => {
// // // //         //     if (highlight) {
// // // //         //         scene.remove(highlight);
// // // //         //         highlight = null;
// // // //         //     }
// // // //         // };

// // // //         // const highlightObject = (obj) => {
// // // //         //     clearHighlight();
// // // //         //     highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //         //     scene.add(highlight);
// // // //         // };

// // // //         const highlightObject = (obj) => {
// // // //             // highlighter.highlightObject(obj);
// // // //             highlighterRef.current?.highlightObject?.(obj);
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             // highlighter.clear();
// // // //             highlighterRef.current?.clear?.();
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const intersects = raycaster.intersectObjects(
// // // //                 sceneRef.current.children,
// // // //                 true
// // // //             );

// // // //             for (const i of intersects) {
// // // //                 if (i.object.userData?.isRadialButton) {
// // // //                     i.object.userData.onClick();
// // // //                     return;
// // // //                 }
// // // //             }

// // // //             let hitModel = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;

// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) {
// // // //                     hitModel = o;
// // // //                 }
// // // //             });

// // // //             if (!hitModel) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 removeRadialMenu();
// // // //                 return;
// // // //             }

// // // //             removeRadialMenu();

// // // //             selectedObjectRef.current = hitModel;
// // // //             setSelectedObject(hitModel);
// // // //             highlightObject(hitModel);

// // // //             createRadialMenu(hitModel);

// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };




// // // //         // Przesuwanie płynne

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             const obj = selectedObjectRef.current;

// // // //             if (obj.userData.radialMenu) {
// // // //                 sceneRef.current.remove(obj.userData.radialMenu);
// // // //                 obj.userData.radialMenu = null;
// // // //             }

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const maxZ = dimZ;

// // // //             let worldX = p.x;
// // // //             let worldZ = p.z;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = maxZ * GRID_SIZE;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));



// // // //             const prevPosition = obj.position.clone();

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             if (checkCollision(obj)) {
// // // //                 obj.position.copy(prevPosition);
// // // //                 return;
// // // //             }

// // // //             const gridX = worldX / GRID_SIZE;
// // // //             const rawGridZ = worldZ / GRID_SIZE;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;


// // // //             // highlighter?.update?.();
// // // //             highlighterRef.current?.update?.();
// // // //         };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let worldX = p.x;
// // // //         //     let worldZ = p.z;

// // // //         //     const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //         //     const maxDepth = maxZ * GRID_SIZE;

// // // //         //     worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //         //     worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX + worldOffsetX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     const gridX = Math.round(worldX / GRID_SIZE);
// // // //         //     const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log(
// // // //         //         `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };

// // // //         // // Przesuwanie Skokowe
// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let rawGridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     rawGridZ = Math.max(-maxZ, Math.min(0, rawGridZ));


// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = rawGridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     // gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridZ = Math.max(-dimZ, Math.min(1, gridZ));


// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = gridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     // highlight?.update?.();
// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     let gridX = Math.round(-p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.round(p.z / GRID_SIZE);
// // // //         //     gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = -gridX * GRID_SIZE;
// // // //         //     const worldZ = gridZ * GRID_SIZE;
// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlight?.update?.();
// // // //         // };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);


// // // //             // highlighter?.update?.();
// // // //             highlighterRef.current?.update?.();
// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 // highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => renderer.domElement.remove();
// // // //     }, []);

// // // //     // -------------------- GRID + PLANE --------------------
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         // usuń poprzedni grid i plane
// // // //         sceneRef.current.children
// // // //             .filter((o) => o.userData?.isGrid)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter((o) => o.userData?.isModel)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(worldOffset.x, 0, worldOffset.z);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
// // // //         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.update();

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);


// // // //     // -------------------- LOAD MODELS --------------------
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(
// // // //                 encodeURI(m.path),
// // // //                 (gltf) => {
// // // //                     modelTemplates.current[m.name] = SkeletonUtils.clone(gltf.scene);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 },
// // // //                 undefined,
// // // //                 (error) => {
// // // //                     console.error("Failed to load model:", m.name, error);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 }
// // // //             );
// // // //         });
// // // //     }, []);

// // // //     // -------------------- LOAD ENTITIES --------------------

// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     // const baseDimZ = selectedScenario.dimensionZ ?? 18;
// // // //     //     // const extraZ = 8;
// // // //     //     // const dimZ = baseDimZ + extraZ;
// // // //     //     // const worldOffsetZ = -(dimZ / 2) * GRID_SIZE;
// // // //     //     // const worldOffsetX = 0;
// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         const rotY = degToRad(e.rotY || 0);
// // // //     //         const frontCorrection = new THREE.Quaternion();
// // // //     //         frontCorrection.setFromEuler(new THREE.Euler(0, -tpl.rotation.y, 0));

// // // //     //         const userRot = new THREE.Quaternion();
// // // //     //         userRot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);

// // // //     //         model.quaternion.copy(userRot).premultiply(frontCorrection);

// // // //     //         // model.rotation.x = degToRad(e.rotX || 0);
// // // //     //         // model.rotation.z = degToRad(e.rotZ || 0);

// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             rotY,
// // // //     //             degToRad(e.rotZ || 0)
// // // //     //         );
// // // //     //         // const pivotHelper = new THREE.AxesHelper(50)
// // // //     //         // model.add(pivotHelper)

// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);

// // // //     //         // console.log(`${e.name}:`, {
// // // //     //         //     rotX: radToDeg(model.rotation.x),
// // // //     //         //     rotY: radToDeg(model.rotation.y),
// // // //     //         //     rotZ: radToDeg(model.rotation.z),
// // // //     //         //     posX: model.position.x,
// // // //     //         //     posZ: model.position.z,
// // // //     //         // });
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         // --- ROTACJA ---
// // // //     //         // const baseQuat = tpl.quaternion.clone();

// // // //     //         // const euler = new THREE.Euler(
// // // //     //         //     degToRad(e.rotX || 0),
// // // //     //         //     degToRad(e.rotY || 0),
// // // //     //         //     degToRad(e.rotZ || 0),
// // // //     //         //     "YXZ"
// // // //     //         // );

// // // //     //         // const userQuat = new THREE.Quaternion().setFromEuler(euler);

// // // //     //         // model.quaternion.copy(baseQuat).multiply(userQuat);


// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             degToRad(e.rotY || 0),
// // // //     //             degToRad(e.rotZ || 0),
// // // //     //             "YXZ"
// // // //     //         );

// // // //     //         const q = model.quaternion;

// // // //     //         const testEulerXYZ = new THREE.Euler().setFromQuaternion(q, "XYZ");
// // // //     //         const testEulerYXZ = new THREE.Euler().setFromQuaternion(q, "YXZ");

// // // //     //         console.log("XYZ:", radToDeg(testEulerXYZ.x), radToDeg(testEulerXYZ.y), radToDeg(testEulerXYZ.z));
// // // //     //         console.log("YXZ:", radToDeg(testEulerYXZ.x), radToDeg(testEulerYXZ.y), radToDeg(testEulerYXZ.z));


// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         const { dimZ } = boardSizeRef.current;
// // // //         const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //         const worldOffsetX = 0;

// // // //         const UNITY_Y_OFFSET = 180; // korekta osi Y dla VR

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = SkeletonUtils.clone(tpl);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //             const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //             model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //             // --- ROTACJA ---
// // // //             const baseQuat = tpl.quaternion.clone();

// // // //             const rotX = degToRad(e.rotX || 0);
// // // //             const rotY = degToRad(UNITY_Y_OFFSET - (e.rotY || 0));
// // // //             const rotZ = degToRad(e.rotZ || 0);

// // // //             const userQuat = new THREE.Quaternion().setFromEuler(
// // // //                 new THREE.Euler(rotX, rotY, rotZ, "YXZ")
// // // //             );

// // // //             model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);

// // // //             const testEuler = new THREE.Euler().setFromQuaternion(model.quaternion, "YXZ");
// // // //             console.log(
// // // //                 `[LOAD] ${model.name} | rotX:${radToDeg(testEuler.x).toFixed(1)} | rotY:${radToDeg(testEuler.y).toFixed(1)} | rotZ:${radToDeg(testEuler.z).toFixed(1)}`
// // // //             );
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);


// // // //     // -------------------- ADD / DELETE --------------------
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         // Dodanie tablicy do pisania
// // // //         if (name === "whiteboard") {
// // // //             createWritableBoard({
// // // //                 scene: sceneRef.current,
// // // //                 objectsRef,
// // // //                 selectedObjectRef,
// // // //                 url: "/whiteboard/scene.gltf",
// // // //                 position: [0, 0, 0],
// // // //                 rotation: [0, 0, 0],
// // // //                 scale: [7, 7, 7],
// // // //                 heightAboveFloor: 0,
// // // //                 writableMeshName: "Object001_0",
// // // //                 textureLoader,
// // // //             });
// // // //             return;
// // // //         }

// // // //         const model = SkeletonUtils.clone(tpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         // model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         // model.userData.gridZ = 0;

// // // //         // Centrowanie
// // // //         model.userData.gridX = 0
// // // //         model.userData.gridZ = -Math.floor((selectedScenario?.dimensionZ || 18) / 2);

// // // //         const worldX = model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         const baseQuat = tpl.quaternion.clone();
// // // //         const userQuat = new THREE.Quaternion().setFromEuler(
// // // //             new THREE.Euler(0, degToRad(UNITY_Y_OFFSET), 0, "YXZ")
// // // //         );

// // // //         model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         if (selectedObjectRef.current) {
// // // //             removeRadialMenu(selectedObjectRef.current);
// // // //         }

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //         createRadialMenu(model);
// // // //     };


// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const duplicateSelected = () => {
// // // //         const selected = selectedObjectRef.current;
// // // //         if (!selected) return;

// // // //         highlighterRef.current?.clear?.();
// // // //         highlighterRef.current?.unhighlight?.(selected);

// // // //         selected.traverse((child) => {
// // // //             if (child.isMesh && child.userData?.originalColor) {
// // // //                 child.material.color.copy(child.userData.originalColor);
// // // //             }
// // // //         });

// // // //         const copy = selected.clone(true);

// // // //         copy.position.x += 10;
// // // //         copy.position.z += 10;

// // // //         copy.userData = { ...selected.userData };

// // // //         sceneRef.current.add(copy);

// // // //         objectsRef.current.push(copy);

// // // //         selectedObjectRef.current = copy;
// // // //         setSelectedObject(copy);
// // // //     };



// // // //     // const saveScenario = async () => {
// // // //     //     if (!selectedScenario) return;

// // // //     //     console.log('objectsRef.current', objectsRef.current)



// // // //     //     // const entities = objectsRef.current
// // // //     //     //     .filter(o => o.userData.isModel)
// // // //     //     //     .map(o => {

// // // //     //     //         const euler = new THREE.Euler().setFromQuaternion(
// // // //     //     //             o.quaternion,
// // // //     //     //             "YXZ"
// // // //     //     //         );

// // // //     //     //         const rotX = normalizeDeg(radToDeg(euler.x));
// // // //     //     //         const rotY = normalizeDeg(radToDeg(euler.y));
// // // //     //     //         const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //     //     //         return {
// // // //     //     //             name: o.name,
// // // //     //     //             posX: o.userData.gridX ?? 0,
// // // //     //     //             posY: 0,
// // // //     //     //             posZ: o.userData.gridZ ?? 0,
// // // //     //     //             rotX,
// // // //     //     //             rotY,
// // // //     //     //             rotZ
// // // //     //     //         };
// // // //     //     //     });


// // // //     //     const entities = objectsRef.current
// // // //     //         .filter(o => o.userData.isModel)
// // // //     //         .map(o => {

// // // //     //             const euler = new THREE.Euler().setFromQuaternion(
// // // //     //                 o.quaternion,
// // // //     //                 "YXZ"
// // // //     //             );

// // // //     //             const rotX = normalizeDeg(radToDeg(euler.x));
// // // //     //             const rotY = normalizeDeg(360 - normalizeDeg(radToDeg(euler.y)));
// // // //     //             const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //     //             return {
// // // //     //                 name: o.name,
// // // //     //                 posX: o.userData.gridX ?? 0,
// // // //     //                 posY: 0,
// // // //     //                 posZ: o.userData.gridZ ?? 0,
// // // //     //                 rotX,
// // // //     //                 rotY,
// // // //     //                 rotZ
// // // //     //             };
// // // //     //         });


// // // //     //     console.log('entities', entities)

// // // //     //     try {
// // // //     //         const response = await fetch(
// // // //     //             `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //     //             {
// // // //     //                 method: "PATCH",
// // // //     //                 headers: { "Content-Type": "application/json" },
// // // //     //                 body: JSON.stringify({
// // // //     //                     stationName: selectedScenario.stationName,
// // // //     //                     dimensionX: selectedScenario.dimensionX,
// // // //     //                     dimensionY: selectedScenario.dimensionY,
// // // //     //                     entities,
// // // //     //                 }),
// // // //     //             }
// // // //     //         );

// // // //     //         if (!response.ok) {
// // // //     //             const errorData = await response.json();
// // // //     //             throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //     //         }

// // // //     //         setAlertMessage("Stacja zapisana pomyślnie!");
// // // //     //         setAlertSeverity("success");
// // // //     //         setAlertOpen(true);
// // // //     //     } catch (error) {
// // // //     //         console.error(error);
// // // //     //         setAlertMessage(`Błąd: ${error.message}`);
// // // //     //         setAlertSeverity("error");
// // // //     //         setAlertOpen(true);
// // // //     //     }
// // // //     // };


// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => {
// // // //                 const tpl = modelTemplates.current[o.name];
// // // //                 const baseQuat = tpl?.quaternion.clone() || new THREE.Quaternion();
// // // //                 const baseInv = baseQuat.clone().invert();

// // // //                 const userQuat = o.quaternion.clone().premultiply(baseInv);

// // // //                 const euler = new THREE.Euler().setFromQuaternion(userQuat, "YXZ");

// // // //                 const rotX = normalizeDeg(radToDeg(euler.x));
// // // //                 const rotY = normalizeDeg(UNITY_Y_OFFSET - normalizeDeg(radToDeg(euler.y))); // 👈 flip Y do VR
// // // //                 const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //                 return {
// // // //                     name: o.name,
// // // //                     posX: o.userData.gridX ?? 0,
// // // //                     posY: 0,
// // // //                     posZ: o.userData.gridZ ?? 0,
// // // //                     rotX,
// // // //                     rotY,
// // // //                     rotZ,
// // // //                 };
// // // //             });

// // // //         // console.log('entities to save:', entities);

// // // //         try {
// // // //             const response = await fetch(
// // // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //                 {
// // // //                     method: "PATCH",
// // // //                     headers: { "Content-Type": "application/json" },
// // // //                     body: JSON.stringify({
// // // //                         stationName: selectedScenario.stationName,
// // // //                         dimensionX: selectedScenario.dimensionX,
// // // //                         dimensionY: selectedScenario.dimensionY,
// // // //                         entities,
// // // //                     }),
// // // //                 }
// // // //             );

// // // //             if (!response.ok) {
// // // //                 const errorData = await response.json();
// // // //                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //             }

// // // //             setAlertMessage("Stacja zapisana pomyślnie!");
// // // //             setAlertSeverity("success");
// // // //             setAlertOpen(true);
// // // //         } catch (error) {
// // // //             console.error(error);
// // // //             setAlertMessage(`Błąd: ${error.message}`);
// // // //             setAlertSeverity("error");
// // // //             setAlertOpen(true);
// // // //         }
// // // //     };


// // // //     const rotate90Right = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y -= step;

// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };

// // // //     const rotate90Left = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y += step;
// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };


// // // //     // const createRadialMenu = (object) => {
// // // //     //     if (!sceneRef.current) return;

// // // //     //     const radius = 120;

// // // //     //     const group = new THREE.Group();
// // // //     //     group.userData.isRadialMenu = true;

// // // //     //     const actions = [
// // // //     //         { fn: rotate90Left },
// // // //     //         { fn: rotate90Right },
// // // //     //         { fn: deleteSelected }
// // // //     //     ];

// // // //     //     actions.forEach((action, i) => {
// // // //     //         const angle = (i / actions.length) * Math.PI * 2;

// // // //     //         const geometry = new THREE.CircleGeometry(20, 32);
// // // //     //         const material = new THREE.MeshBasicMaterial({
// // // //     //             color: 0xffffff,
// // // //     //             depthTest: false
// // // //     //         });

// // // //     //         const mesh = new THREE.Mesh(geometry, material);

// // // //     //         mesh.rotation.x = -Math.PI / 2;

// // // //     //         mesh.position.set(
// // // //     //             Math.cos(angle) * radius,
// // // //     //             1,
// // // //     //             Math.sin(angle) * radius
// // // //     //         );

// // // //     //         mesh.userData.onClick = action.fn;
// // // //     //         mesh.userData.isRadialButton = true;

// // // //     //         group.add(mesh);
// // // //     //     });

// // // //     //     sceneRef.current.add(group);
// // // //     //     group.position.copy(object.position);
// // // //     //     group.position.y = 1;
// // // //     // };

// // // //     const textureLoader = new THREE.TextureLoader();

// // // //     // const createRadialMenu = (object) => {
// // // //     //     if (!sceneRef.current) return;


// // // //     //     if (object.userData.radialMenu) {
// // // //     //         sceneRef.current.remove(object.userData.radialMenu);
// // // //     //         object.userData.radialMenu = null;
// // // //     //     }

// // // //     //     const radius = 120;

// // // //     //     const group = new THREE.Group();
// // // //     //     group.userData.isRadialMenu = true;


// // // //     //     const actions = [
// // // //     //         // { fn: rotate90Left, icon: "/icons/rotate1.png" },
// // // //     //         // { fn: rotate90Right, icon: "/icons/rotate1.png" },
// // // //     //         { fn: rotate90Left, icon: "/icons/rotateleft.png" },
// // // //     //         { fn: rotate90Right, icon: "/icons/rotateright.png" },
// // // //     //         { fn: deleteSelected, icon: "/icons/delete.png" },
// // // //     //         { fn: duplicateSelected, icon: "/icons/copy.png" }
// // // //     //     ];

// // // //     //     // ======= DODANIE PRZYCISKU WRITE DLA TABLICY =======
// // // //     //     if (object.userData.isWritableBoard) {
// // // //     //         actions.push({
// // // //     //             fn: () => {
// // // //     //                 if (!object) return;
// // // //     //                 if (object.userData.mode === "write") {
// // // //     //                     object.userData.mode = "move";
// // // //     //                     object.userData.isMovable = true;
// // // //     //                     console.log("Tablica: zmiana trybu na MOVE");
// // // //     //                 } else {
// // // //     //                     object.userData.mode = "write";
// // // //     //                     object.userData.isMovable = false;
// // // //     //                     console.log("Tablica: zmiana trybu na WRITE");
// // // //     //                 }
// // // //     //             },
// // // //     //             icon: "/icons/pisz.png"
// // // //     //         });
// // // //     //     }

// // // //     //     actions.forEach((action, i) => {
// // // //     //         const angle = (i / actions.length) * Math.PI * 2;

// // // //     //         const geometry = new THREE.CircleGeometry(20, 32);
// // // //     //         const material = new THREE.MeshBasicMaterial({
// // // //     //             color: 0x6cc6d9,
// // // //     //             depthTest: false
// // // //     //         });
// // // //     //         const mesh = new THREE.Mesh(geometry, material);
// // // //     //         mesh.rotation.x = -Math.PI / 2;
// // // //     //         mesh.position.set(
// // // //     //             Math.cos(angle) * radius,
// // // //     //             1,
// // // //     //             Math.sin(angle) * radius
// // // //     //         );

// // // //     //         const texture = textureLoader.load(action.icon);
// // // //     //         const iconGeometry = new THREE.PlaneGeometry(30, 30);
// // // //     //         const iconMaterial = new THREE.MeshBasicMaterial({
// // // //     //             map: texture,
// // // //     //             transparent: true,
// // // //     //             depthTest: false,
// // // //     //             side: THREE.DoubleSide
// // // //     //         });
// // // //     //         const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);

// // // //     //         iconMesh.rotation.x = Math.PI;
// // // //     //         iconMesh.rotation.z = -Math.PI / 2;

// // // //     //         iconMesh.position.set(0, 0.1, 0);

// // // //     //         mesh.add(iconMesh);

// // // //     //         mesh.userData.onClick = action.fn;
// // // //     //         mesh.userData.isRadialButton = true;

// // // //     //         group.add(mesh);

// // // //     //         object.userData.radialMenu = group;
// // // //     //     });

// // // //     //     sceneRef.current.add(group);
// // // //     //     group.position.copy(object.position);
// // // //     //     group.position.y = 1;
// // // //     // };

// // // //     const createRadialMenu = (object) => {
// // // //         if (!sceneRef.current) return;

// // // //         // USUWANIE STAREGO MENU
// // // //         if (object.userData.radialMenu) {
// // // //             sceneRef.current.remove(object.userData.radialMenu);
// // // //             object.userData.radialMenu = null;
// // // //         }

// // // //         const radius = 120;
// // // //         const group = new THREE.Group();
// // // //         group.userData.isRadialMenu = true;

// // // //         const actions = [
// // // //             { fn: rotate90Left, icon: "/icons/rotateleft.png" },
// // // //             { fn: rotate90Right, icon: "/icons/rotateright.png" },
// // // //             { fn: deleteSelected, icon: "/icons/delete.png" },
// // // //             { fn: duplicateSelected, icon: "/icons/copy.png" }
// // // //         ];

// // // //         // ======= DODANIE PRZYCISKU PISZ DLA TABLICY =======
// // // //         if (object.userData.isWritableBoard) {
// // // //             actions.push({
// // // //                 fn: () => {
// // // //                     if (!object) return;

// // // //                     // toggle mode
// // // //                     if (object.userData.mode === "write") {
// // // //                         object.userData.mode = "move";
// // // //                         object.userData.isMovable = true;
// // // //                         console.log("Tablica: zmiana trybu na MOVE");
// // // //                     } else {
// // // //                         object.userData.mode = "write";
// // // //                         object.userData.isMovable = false;
// // // //                         console.log("Tablica: zmiana trybu na WRITE");
// // // //                     }

// // // //                     // Odśwież podświetlenie przycisku
// // // //                     group.children.forEach((mesh) => {
// // // //                         if (mesh.userData.isWriteButton) {
// // // //                             const mat = mesh.material;
// // // //                             if (object.userData.mode === "write") {
// // // //                                 mat.color.set(0xffff00); // żółte podświetlenie
// // // //                             } else {
// // // //                                 mat.color.set(0x6cc6d9); // standardowy kolor
// // // //                             }
// // // //                         }
// // // //                     });
// // // //                 },
// // // //                 icon: "/icons/pisz.png",
// // // //                 isWriteButton: true // oznaczenie, żeby łatwo znaleźć
// // // //             });
// // // //         }

// // // //         actions.forEach((action, i) => {
// // // //             const angle = (i / actions.length) * Math.PI * 2;

// // // //             const geometry = new THREE.CircleGeometry(20, 32);
// // // //             const material = new THREE.MeshBasicMaterial({
// // // //                 color: action.isWriteButton
// // // //                     ? object.userData.mode === "write"
// // // //                         ? 0xffff00
// // // //                         : 0x6cc6d9
// // // //                     : 0x6cc6d9,
// // // //                 depthTest: false
// // // //             });

// // // //             const mesh = new THREE.Mesh(geometry, material);
// // // //             mesh.rotation.x = -Math.PI / 2;
// // // //             mesh.position.set(
// // // //                 Math.cos(angle) * radius,
// // // //                 1,
// // // //                 Math.sin(angle) * radius
// // // //             );

// // // //             const texture = textureLoader.load(action.icon);
// // // //             const iconGeometry = new THREE.PlaneGeometry(30, 30);
// // // //             const iconMaterial = new THREE.MeshBasicMaterial({
// // // //                 map: texture,
// // // //                 transparent: true,
// // // //                 depthTest: false,
// // // //                 side: THREE.DoubleSide
// // // //             });
// // // //             const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);
// // // //             iconMesh.rotation.x = Math.PI;
// // // //             iconMesh.rotation.z = -Math.PI / 2;
// // // //             iconMesh.position.set(0, 0.1, 0);
// // // //             mesh.add(iconMesh);

// // // //             mesh.userData.onClick = action.fn;
// // // //             mesh.userData.isRadialButton = true;
// // // //             if (action.isWriteButton) mesh.userData.isWriteButton = true;

// // // //             group.add(mesh);
// // // //             object.userData.radialMenu = group;
// // // //         });

// // // //         sceneRef.current.add(group);
// // // //         group.position.copy(object.position);
// // // //         group.position.y = 1;
// // // //     };





// // // //     const removeRadialMenu = () => {
// // // //         if (!sceneRef.current) return;

// // // //         const menus = sceneRef.current.children.filter(
// // // //             o => o.userData?.isRadialMenu
// // // //         );

// // // //         menus.forEach(menu => sceneRef.current.remove(menu));
// // // //     };




// // // //     // -------------------- KEYBOARD --------------------

// // // //     useEffect(() => {
// // // //         // Przesuwanie płynne
// // // //         const moveGrid = (dx, dz) => {
// // // //             if (!selectedObjectRef.current) return;

// // // //             const obj = selectedObjectRef.current;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = (dimZ - 1) * GRID_SIZE;

// // // //             let worldX = obj.position.x + dx;
// // // //             let worldZ = obj.position.z + dz;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             const prevPosition = obj.position.clone();

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             if (checkCollision(obj)) {
// // // //                 obj.position.copy(prevPosition);
// // // //                 return;
// // // //             }

// // // //             const gridX = Math.round(worldX / GRID_SIZE);
// // // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const maxZ = dimZ - 1;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             // console.log(
// // // //             //     `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //             // );

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;

// // // //             removeRadialMenu();
// // // //         };


// // // //         // //  Przesuwanie skokowe

// // // //         //     const moveGrid = (dx, dz) => {
// // // //         //         if (!selectedObjectRef.current) return;

// // // //         //         const { dimX, dimZ } = boardSizeRef.current;
// // // //         //         const GRID = GRID_SIZE;

// // // //         //         let gridX = selectedObjectRef.current.userData.gridX ?? 0;
// // // //         //         let gridZ = selectedObjectRef.current.userData.gridZ ?? 0;

// // // //         //         gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX + dx));
// // // //         //         gridZ = Math.max(-dimZ, Math.min(0, gridZ + dz));

// // // //         //         selectedObjectRef.current.userData.gridX = gridX;
// // // //         //         selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //         const worldX = -gridX * GRID;
// // // //         //         const worldZ = gridZ * GRID;

// // // //         //         selectedObjectRef.current.position.set(
// // // //         //             worldX,
// // // //         //             selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //             worldZ
// // // //         //         );

// // // //         //     };

// // // //         const onKeyDown = (e) => {
// // // //             if (
// // // //                 e.target.tagName === "INPUT" ||
// // // //                 e.target.tagName === "TEXTAREA" ||
// // // //                 e.target.isContentEditable
// // // //             ) return;

// // // //             // Dodajemy blokadę ruchu w trybie write (TABLICA)
// // // //             const obj = selectedObjectRef.current;
// // // //             if (!obj) return;

// // // //             if (obj.userData.mode === "write") {
// // // //                 return;
// // // //             }

// // // //             switch (e.key.toLowerCase()) {

// // // //                 case "w":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, 5);
// // // //                     break;
// // // //                 case "s":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, -5);
// // // //                     break;
// // // //                 case "a":
// // // //                     e.preventDefault();
// // // //                     moveGrid(5, 0);
// // // //                     break;
// // // //                 case "d":
// // // //                     e.preventDefault();
// // // //                     moveGrid(-5, 0);
// // // //                     break;

// // // //                 case "arrowleft":
// // // //                     e.preventDefault();
// // // //                     rotate90Left();
// // // //                     break;
// // // //                 case "arrowright":
// // // //                     e.preventDefault();
// // // //                     rotate90Right();
// // // //                     break;

// // // //                 case "delete":
// // // //                 case "backspace":
// // // //                     e.preventDefault();
// // // //                     deleteSelected();
// // // //                     break;

// // // //                 case "escape":
// // // //                     selectedObjectRef.current = null;
// // // //                     setSelectedObject(null);
// // // //                     break;

// // // //                 default:
// // // //                     break;
// // // //             }
// // // //         };

// // // //         window.addEventListener("keydown", onKeyDown);
// // // //         return () => window.removeEventListener("keydown", onKeyDown);
// // // //     }, []);


// // // //     // LOGIKA PISANIA NA TABLICY - globalny listener, który sprawdza czy aktualnie wybrany obiekt to tablica w trybie pisania, jeśli tak to modyfikuje jej tekst
// // // //     if (!window._writableBoardListener) {
// // // //         window._writableBoardListener = (e) => {
// // // //             const model = selectedObjectRef.current;
// // // //             if (!model?.userData?.isWritableBoard) return;
// // // //             if (model.userData.mode !== "write") return;

// // // //             const mesh = model.getObjectByName(model.userData.writableMeshName);
// // // //             if (!mesh) return;

// // // //             const { canvas, ctx, texture, clearCanvas } = mesh.userData;
// // // //             const lines = model.userData.lines;

// // // //             if (e.key === "Backspace") {
// // // //                 let last = lines[lines.length - 1];
// // // //                 if (last.length > 0) lines[lines.length - 1] = last.slice(0, -1);
// // // //                 else if (lines.length > 1) lines.pop();
// // // //             } else if (e.key === "Enter") {
// // // //                 lines.push("");
// // // //             } else if (e.key.length === 1) {
// // // //                 lines[lines.length - 1] += e.key;
// // // //             } else return;

// // // //             clearCanvas();
// // // //             ctx.fillStyle = "white";
// // // //             ctx.font = "48px Arial";
// // // //             ctx.textAlign = "left";
// // // //             ctx.textBaseline = "top";

// // // //             lines.forEach((line, i) => ctx.fillText(line, 40, 40 + i * 26));
// // // //             texture.needsUpdate = true;
// // // //         };

// // // //         window.addEventListener("keydown", window._writableBoardListener);
// // // //     }

// // // //     // const logRotation = () => {
// // // //     //     console.log("rotation", selectedObjectRef.current.rotation)
// // // //     // }

// // // //     // -------------------- RENDER --------------------
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 {/* <Typography variant="subtitle2">Modele</Typography> */}
// // // //                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
// // // //                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // // //                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List> */}

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Left}
// // // //                             >
// // // //                                 <Rotate90DegreesCcwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = 1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = -1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Right}
// // // //                             >
// // // //                                 <Rotate90DegreesCwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                             {/* <Button onClick={logRotation}>
// // // //                                 log
// // // //                             </Button> */}
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
// // // //             {gridWalls && <GridWalls
// // // //                 scene={sceneRef.current}
// // // //                 dimX={boardSizeRef.current.dimX}
// // // //                 dimZ={boardSizeRef.current.dimZ}
// // // //                 GRID_SIZE={GRID_SIZE}
// // // //                 cameraRef={cameraRef}
// // // //             />}

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{
// // // //                     mt: 1,
// // // //                     position: "absolute",
// // // //                     top: 70,
// // // //                     left: "50%",
// // // //                     transform: "translateX(-50%)",
// // // //                     fontSize: "10px",
// // // //                 }}
// // // //             >
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
// // // //             >
// // // //                 {/* {selectedScenario?.existingSource} */}
// // // //                 {
// // // //                     selectedScenario?.stationName
// // // //                         ? selectedScenario.stationName
// // // //                         : selectedScenario?.id !== undefined
// // // //                             ? `Stacja ${selectedScenario.id}`
// // // //                             : "nie wybrano"
// // // //                 }
// // // //             </Typography>

// // // //             <IconButton
// // // //                 onClick={() => setGridWalls(prev => !prev)}
// // // //                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
// // // //             >
// // // //                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

// // // //             </IconButton>

// // // //             <Snackbar
// // // //                 open={alertOpen}
// // // //                 autoHideDuration={4000}
// // // //                 onClose={() => setAlertOpen(false)}
// // // //                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
// // // //                 sx={{ mt: 8 }}
// // // //             >
// // // //                 <Alert
// // // //                     onClose={() => setAlertOpen(false)}
// // // //                     severity={alertSeverity}
// // // //                     sx={{ width: "100%" }}
// // // //                 >
// // // //                     {alertMessage}
// // // //                 </Alert>
// // // //             </Snackbar>


// // // //             {/* <WallBoards
// // // //                 scene={sceneRef.current}
// // // //                 cameraRef={cameraRef}
// // // //                 rendererRef={rendererRef}
// // // //             //   wallsRef={}
// // // //             /> */}

// // // //             <WritableBoardItem
// // // //                 sceneRef={sceneRef}
// // // //                 objectsRef={objectsRef}
// // // //                 selectedObjectRef={selectedObjectRef}
// // // //                 rendererRef={rendererRef}
// // // //                 cameraRef={cameraRef}
// // // //                 url="/whiteboard/scene.gltf"
// // // //                 position={[0, 0, 0]}
// // // //                 scale={[7, 7, 7]}
// // // //                 heightAboveFloor={0}
// // // //                 writableMeshName="Object001_0"
// // // //             />


// // // //             <DebugBoardMeshes url="/whiteboard/scene.gltf" />



// // // //         </>
// // // //     );
// // // // }



// // // // //WERSJA Z COLIDEREM ALE BEZ FLOOR MENU

// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Snackbar,
// // // //     Alert,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// // // // import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// // // // import DeleteIcon from "@mui/icons-material/Delete";
// // // // import GridOffIcon from '@mui/icons-material/GridOff';
// // // // import GridOnIcon from '@mui/icons-material/GridOn';

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";
// // // // import ModelList from "./utilities/ModelList";
// // // // import GridWalls from "./three/GridWalls";
// // // // import { ObjectHighlighter } from "./utilities/ObjectHighlighter";

// // // // const GRID_SIZE = 50;

// // // // const UNITY_Y_OFFSET = 180;

// // // // // const MODEL_LIBRARY = [
// // // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // // ];

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // //     { name: "env_staircase_stairs", path: "/env_staircase_stairs/env_staircase_stairs.gltf", visible: true },
// // // //     { name: "env_wall_column_05", path: "/env_wall_column_05/env_wall_column_05.gltf", visible: true },
// // // //     { name: "exe_cd_essential_items", path: "/exe_cd_essential_items/exe_cd_essential_items.gltf", visible: true },
// // // //     { name: "exe_cooking_bins", path: "/exe_cooking_bins/exe_cooking_bins.gltf", visible: true },
// // // //     { name: "exe_cooking_cookbase", path: "/exe_cooking_cookbase/exe_cooking_cookbase.gltf", visible: true },
// // // //     { name: "exe_cooking_cook_station", path: "/exe_cooking_cook_station/exe_cooking_cook_station.gltf", visible: true },
// // // //     { name: "exe_cooking_device_blender", path: "/exe_cooking_device_blender/exe_cooking_device_blender.gltf", visible: true },
// // // //     { name: "exe_cooking_device_mixer", path: "/exe_cooking_device_mixer/exe_cooking_device_mixer.gltf", visible: true },
// // // //     { name: "exe_cooking_device_mixer_blender", path: "/exe_cooking_device_mixer_blender/exe_cooking_device_mixer_blender.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_dairy", path: "/exe_cooking_fridge_dairy/exe_cooking_fridge_dairy.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_fruits", path: "/exe_cooking_fridge_fruits/exe_cooking_fridge_fruits.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_meat", path: "/exe_cooking_fridge_meat/exe_cooking_fridge_meat.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_vegetables", path: "/exe_cooking_fridge_vegetables/exe_cooking_fridge_vegetables.gltf", visible: true },
// // // //     { name: "exe_cooking_mech_cooker", path: "/exe_cooking_mech_cooker/exe_cooking_mech_cooker.gltf", visible: true },
// // // //     { name: "exe_cooking_mech_furnance", path: "/exe_cooking_mech_furnance/exe_cooking_mech_furnance.gltf", visible: true },
// // // //     { name: "exe_cooking_sink", path: "/exe_cooking_sink/exe_cooking_sink.gltf", visible: true },
// // // //     { name: "exe_cooking_sink_hanged", path: "/exe_cooking_sink_hanged/exe_cooking_sink_hanged.gltf", visible: true },
// // // //     { name: "exe_creator_door_office_double", path: "/exe_creator_door_office_double/exe_creator_door_office_double.gltf", visible: true },
// // // //     { name: "exe_creator_door_wooden_1", path: "/exe_creator_door_wooden_1/exe_creator_door_wooden_1.gltf", visible: true },
// // // //     { name: "exe_printing_3d_printing_station", path: "/exe_printing_3d_printing_station/exe_printing_3d_printing_station.gltf", visible: true },
// // // //     { name: "exe_printing_cabinet_filament_abs_ptg", path: "/exe_printing_cabinet_filament_abs_ptg/exe_printing_cabinet_filament_abs_ptg.gltf", visible: true },
// // // //     { name: "exe_printing_cabinet_filament_pla_tpu", path: "/exe_printing_cabinet_filament_pla_tpu/exe_printing_cabinet_filament_pla_tpu.gltf", visible: true },
// // // //     { name: "exe_printing_cleaning_set", path: "/exe_printing_cleaning_set/exe_printing_cleaning_set.gltf", visible: true },
// // // //     { name: "exe_printing_examples", path: "/exe_printing_examples/exe_printing_examples.gltf", visible: true },
// // // //     { name: "exe_wd_22nut_impactdriver", path: "/exe_wd_22nut_impactdriver/exe_wd_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_combined_exercises", path: "/exe_wd_combined_exercises/exe_wd_combined_exercises.gltf", visible: true },
// // // //     { name: "exe_wd_drill_26plug_26screw_impactdriver", path: "/exe_wd_drill_26plug_26screw_impactdriver/exe_wd_drill_26plug_26screw_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_drill_dowel_hammer", path: "/exe_wd_drill_dowel_hammer/exe_wd_drill_dowel_hammer.gltf", visible: true },
// // // //     { name: "exe_wd_foaming", path: "/exe_wd_foaming/exe_wd_foaming.gltf", visible: true },
// // // //     { name: "exe_wd_garage_gate_installation", path: "/exe_wd_garage_gate_installation/exe_wd_garage_gate_installation.gltf", visible: true },
// // // //     { name: "exe_wd_garage_gate_supply", path: "/exe_wd_garage_gate_supply/exe_wd_garage_gate_supply.gltf", visible: true },
// // // //     { name: "exe_wd_interior_door_installation", path: "/exe_wd_interior_door_installation/exe_wd_interior_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_interior_door_supply", path: "/exe_wd_interior_door_supply/exe_wd_interior_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_leveler", path: "/exe_wd_leveler/exe_wd_leveler.gltf", visible: true },
// // // //     { name: "exe_wd_outer_door_installation", path: "/exe_wd_outer_door_installation/exe_wd_outer_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_outer_door_supply", path: "/exe_wd_outer_door_supply/exe_wd_outer_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill", path: "/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill.gltf", visible: true },
// // // //     { name: "exe_wd_roof_window_installation", path: "/exe_wd_roof_window_installation/exe_wd_roof_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_roof_window_supply", path: "/exe_wd_roof_window_supply/exe_wd_roof_window_supply.gltf", visible: true },
// // // //     { name: "exe_wd_strut", path: "/exe_wd_strut/exe_wd_strut.gltf", visible: true },
// // // //     { name: "exe_wd_tool_rack_1", path: "/exe_wd_tool_rack_1/exe_wd_tool_rack_1.gltf", visible: true },
// // // //     { name: "exe_wd_tool_rack_boxes", path: "/exe_wd_tool_rack_boxes/exe_wd_tool_rack_boxes.gltf", visible: true },
// // // //     { name: "exe_wd_wedge_hammer", path: "/exe_wd_wedge_hammer/exe_wd_wedge_hammer.gltf", visible: true },
// // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_drawer", path: "/exe_cooking_countertop_drawer/exe_cooking_countertop_drawer.gltf", visible: true },
// // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_installation", path: "/exe_wd_technical_door_installation/exe_wd_technical_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // //     { name: "elevator_1x2.1x", path: "/elevator_1x2.1x/elevator_1x2.1x.gltf", visible: true },
// // // // ];


// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);
// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
// // // //     const [gridVersion, setGridVersion] = useState();


// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);
// // // //     const [gridWalls, setGridWalls] = useState(true);


// // // //     // Powiadomienia
// // // //     const [alertOpen, setAlertOpen] = useState(false);
// // // //     const [alertMessage, setAlertMessage] = useState("");
// // // //     const [alertSeverity, setAlertSeverity] = useState("success");


// // // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //     const degToRad = (deg) => (deg * Math.PI) / 180;
// // // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


// // // //     // -------------------- GRID UPDATE --------------------

// // // //     useEffect(() => {
// // // //         if (!selectedScenario) return;
// // // //         setBoardSize({
// // // //             dimX: selectedScenario.dimensionX || 18,
// // // //             dimZ: selectedScenario.dimensionZ || 18
// // // //         });
// // // //         setGridVersion(v => v + 1);
// // // //     }, [selectedScenario?.id]);

// // // //     // console.log('gridVersion', gridVersion)
// // // //     // console.log('boardSize', boardSize)

// // // //     // -------------------- INIT SCENE --------------------
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         camera.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         // let highlight = null;

// // // //         const highlighter = new ObjectHighlighter(scene, {
// // // //             color: 0x4da3ff,
// // // //             tintStrength: 0.35,
// // // //             auraScale: 1.06,
// // // //             auraOpacity: 0.25
// // // //         });

// // // //         // const updatePointer = (e) => {
// // // //         //     pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //         //     pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         // };

// // // //         const updatePointer = (e) => {
// // // //             if (!rendererRef.current) return;
// // // //             const rect = rendererRef.current.domElement.getBoundingClientRect();
// // // //             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
// // // //             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
// // // //         };

// // // //         // const clearHighlight = () => {
// // // //         //     if (highlight) {
// // // //         //         scene.remove(highlight);
// // // //         //         highlight = null;
// // // //         //     }
// // // //         // };

// // // //         // const highlightObject = (obj) => {
// // // //         //     clearHighlight();
// // // //         //     highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //         //     scene.add(highlight);
// // // //         // };

// // // //         const highlightObject = (obj) => {
// // // //             highlighter.highlightObject(obj);
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             highlighter.clear();
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;
// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();
// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };



// // // //         const getObjectBox = (obj) => {
// // // //             return new THREE.Box3().setFromObject(obj);
// // // //         };

// // // //         const checkCollision = (obj) => {
// // // //             if (!sceneRef.current) return false;

// // // //             const objBox = getObjectBox(obj);

// // // //             const walls = sceneRef.current.children.filter(o => o.userData?.isWall);

// // // //             for (const wall of walls) {
// // // //                 const wallBox = new THREE.Box3().setFromObject(wall);
// // // //                 if (objBox.intersectsBox(wallBox)) return true;
// // // //             }

// // // //             for (const other of objectsRef.current) {
// // // //                 if (!other.userData?.isModel) continue;
// // // //                 if (other === obj) continue;

// // // //                 const otherBox = getObjectBox(other);
// // // //                 if (objBox.intersectsBox(otherBox)) return true;
// // // //             }

// // // //             return false;
// // // //         };

// // // //         // Przesuwanie płynne

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const maxZ = dimZ;

// // // //             let worldX = p.x;
// // // //             let worldZ = p.z;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = maxZ * GRID_SIZE;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             const obj = selectedObjectRef.current;

// // // //             const prevPosition = obj.position.clone();

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             if (checkCollision(obj)) {
// // // //                 obj.position.copy(prevPosition);
// // // //                 return;
// // // //             }

// // // //             const gridX = worldX / GRID_SIZE;
// // // //             const rawGridZ = worldZ / GRID_SIZE;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;

// // // //             highlighter?.update?.();
// // // //         };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let worldX = p.x;
// // // //         //     let worldZ = p.z;

// // // //         //     const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //         //     const maxDepth = maxZ * GRID_SIZE;

// // // //         //     worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //         //     worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX + worldOffsetX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     const gridX = Math.round(worldX / GRID_SIZE);
// // // //         //     const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log(
// // // //         //         `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };

// // // //         // // Przesuwanie Skokowe
// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let rawGridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     rawGridZ = Math.max(-maxZ, Math.min(0, rawGridZ));


// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = rawGridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     // gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridZ = Math.max(-dimZ, Math.min(1, gridZ));


// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = gridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     // highlight?.update?.();
// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     let gridX = Math.round(-p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.round(p.z / GRID_SIZE);
// // // //         //     gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = -gridX * GRID_SIZE;
// // // //         //     const worldZ = gridZ * GRID_SIZE;
// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlight?.update?.();
// // // //         // };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             highlighter?.update?.();
// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 // highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => renderer.domElement.remove();
// // // //     }, []);

// // // //     // -------------------- GRID + PLANE --------------------
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         // usuń poprzedni grid i plane
// // // //         sceneRef.current.children
// // // //             .filter((o) => o.userData?.isGrid)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter((o) => o.userData?.isModel)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(worldOffset.x, 0, worldOffset.z);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
// // // //         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.update();

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);


// // // //     // -------------------- LOAD MODELS --------------------
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(
// // // //                 encodeURI(m.path),
// // // //                 (gltf) => {
// // // //                     modelTemplates.current[m.name] = SkeletonUtils.clone(gltf.scene);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 },
// // // //                 undefined,
// // // //                 (error) => {
// // // //                     console.error("Failed to load model:", m.name, error);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 }
// // // //             );
// // // //         });
// // // //     }, []);

// // // //     // -------------------- LOAD ENTITIES --------------------

// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     // const baseDimZ = selectedScenario.dimensionZ ?? 18;
// // // //     //     // const extraZ = 8;
// // // //     //     // const dimZ = baseDimZ + extraZ;
// // // //     //     // const worldOffsetZ = -(dimZ / 2) * GRID_SIZE;
// // // //     //     // const worldOffsetX = 0;
// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         const rotY = degToRad(e.rotY || 0);
// // // //     //         const frontCorrection = new THREE.Quaternion();
// // // //     //         frontCorrection.setFromEuler(new THREE.Euler(0, -tpl.rotation.y, 0));

// // // //     //         const userRot = new THREE.Quaternion();
// // // //     //         userRot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);

// // // //     //         model.quaternion.copy(userRot).premultiply(frontCorrection);

// // // //     //         // model.rotation.x = degToRad(e.rotX || 0);
// // // //     //         // model.rotation.z = degToRad(e.rotZ || 0);

// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             rotY,
// // // //     //             degToRad(e.rotZ || 0)
// // // //     //         );
// // // //     //         // const pivotHelper = new THREE.AxesHelper(50)
// // // //     //         // model.add(pivotHelper)

// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);

// // // //     //         // console.log(`${e.name}:`, {
// // // //     //         //     rotX: radToDeg(model.rotation.x),
// // // //     //         //     rotY: radToDeg(model.rotation.y),
// // // //     //         //     rotZ: radToDeg(model.rotation.z),
// // // //     //         //     posX: model.position.x,
// // // //     //         //     posZ: model.position.z,
// // // //     //         // });
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         // --- ROTACJA ---
// // // //     //         // const baseQuat = tpl.quaternion.clone();

// // // //     //         // const euler = new THREE.Euler(
// // // //     //         //     degToRad(e.rotX || 0),
// // // //     //         //     degToRad(e.rotY || 0),
// // // //     //         //     degToRad(e.rotZ || 0),
// // // //     //         //     "YXZ"
// // // //     //         // );

// // // //     //         // const userQuat = new THREE.Quaternion().setFromEuler(euler);

// // // //     //         // model.quaternion.copy(baseQuat).multiply(userQuat);


// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             degToRad(e.rotY || 0),
// // // //     //             degToRad(e.rotZ || 0),
// // // //     //             "YXZ"
// // // //     //         );

// // // //     //         const q = model.quaternion;

// // // //     //         const testEulerXYZ = new THREE.Euler().setFromQuaternion(q, "XYZ");
// // // //     //         const testEulerYXZ = new THREE.Euler().setFromQuaternion(q, "YXZ");

// // // //     //         console.log("XYZ:", radToDeg(testEulerXYZ.x), radToDeg(testEulerXYZ.y), radToDeg(testEulerXYZ.z));
// // // //     //         console.log("YXZ:", radToDeg(testEulerYXZ.x), radToDeg(testEulerYXZ.y), radToDeg(testEulerYXZ.z));


// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         const { dimZ } = boardSizeRef.current;
// // // //         const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //         const worldOffsetX = 0;

// // // //         const UNITY_Y_OFFSET = 180; // korekta osi Y dla VR

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = SkeletonUtils.clone(tpl);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //             const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //             model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //             // --- ROTACJA ---
// // // //             const baseQuat = tpl.quaternion.clone();

// // // //             const rotX = degToRad(e.rotX || 0);
// // // //             const rotY = degToRad(UNITY_Y_OFFSET - (e.rotY || 0));
// // // //             const rotZ = degToRad(e.rotZ || 0);

// // // //             const userQuat = new THREE.Quaternion().setFromEuler(
// // // //                 new THREE.Euler(rotX, rotY, rotZ, "YXZ")
// // // //             );

// // // //             model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);

// // // //             const testEuler = new THREE.Euler().setFromQuaternion(model.quaternion, "YXZ");
// // // //             console.log(
// // // //                 `[LOAD] ${model.name} | rotX:${radToDeg(testEuler.x).toFixed(1)} | rotY:${radToDeg(testEuler.y).toFixed(1)} | rotZ:${radToDeg(testEuler.z).toFixed(1)}`
// // // //             );
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);


// // // //     // -------------------- ADD / DELETE --------------------
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = SkeletonUtils.clone(tpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         const baseQuat = tpl.quaternion.clone();
// // // //         const userQuat = new THREE.Quaternion().setFromEuler(
// // // //             new THREE.Euler(0, degToRad(UNITY_Y_OFFSET), 0, "YXZ")
// // // //         );

// // // //         model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };


// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     // const saveScenario = async () => {
// // // //     //     if (!selectedScenario) return;

// // // //     //     console.log('objectsRef.current', objectsRef.current)



// // // //     //     // const entities = objectsRef.current
// // // //     //     //     .filter(o => o.userData.isModel)
// // // //     //     //     .map(o => {

// // // //     //     //         const euler = new THREE.Euler().setFromQuaternion(
// // // //     //     //             o.quaternion,
// // // //     //     //             "YXZ"
// // // //     //     //         );

// // // //     //     //         const rotX = normalizeDeg(radToDeg(euler.x));
// // // //     //     //         const rotY = normalizeDeg(radToDeg(euler.y));
// // // //     //     //         const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //     //     //         return {
// // // //     //     //             name: o.name,
// // // //     //     //             posX: o.userData.gridX ?? 0,
// // // //     //     //             posY: 0,
// // // //     //     //             posZ: o.userData.gridZ ?? 0,
// // // //     //     //             rotX,
// // // //     //     //             rotY,
// // // //     //     //             rotZ
// // // //     //     //         };
// // // //     //     //     });


// // // //     //     const entities = objectsRef.current
// // // //     //         .filter(o => o.userData.isModel)
// // // //     //         .map(o => {

// // // //     //             const euler = new THREE.Euler().setFromQuaternion(
// // // //     //                 o.quaternion,
// // // //     //                 "YXZ"
// // // //     //             );

// // // //     //             const rotX = normalizeDeg(radToDeg(euler.x));
// // // //     //             const rotY = normalizeDeg(360 - normalizeDeg(radToDeg(euler.y)));
// // // //     //             const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //     //             return {
// // // //     //                 name: o.name,
// // // //     //                 posX: o.userData.gridX ?? 0,
// // // //     //                 posY: 0,
// // // //     //                 posZ: o.userData.gridZ ?? 0,
// // // //     //                 rotX,
// // // //     //                 rotY,
// // // //     //                 rotZ
// // // //     //             };
// // // //     //         });


// // // //     //     console.log('entities', entities)

// // // //     //     try {
// // // //     //         const response = await fetch(
// // // //     //             `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //     //             {
// // // //     //                 method: "PATCH",
// // // //     //                 headers: { "Content-Type": "application/json" },
// // // //     //                 body: JSON.stringify({
// // // //     //                     stationName: selectedScenario.stationName,
// // // //     //                     dimensionX: selectedScenario.dimensionX,
// // // //     //                     dimensionY: selectedScenario.dimensionY,
// // // //     //                     entities,
// // // //     //                 }),
// // // //     //             }
// // // //     //         );

// // // //     //         if (!response.ok) {
// // // //     //             const errorData = await response.json();
// // // //     //             throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //     //         }

// // // //     //         setAlertMessage("Stacja zapisana pomyślnie!");
// // // //     //         setAlertSeverity("success");
// // // //     //         setAlertOpen(true);
// // // //     //     } catch (error) {
// // // //     //         console.error(error);
// // // //     //         setAlertMessage(`Błąd: ${error.message}`);
// // // //     //         setAlertSeverity("error");
// // // //     //         setAlertOpen(true);
// // // //     //     }
// // // //     // };


// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => {
// // // //                 const tpl = modelTemplates.current[o.name];
// // // //                 const baseQuat = tpl?.quaternion.clone() || new THREE.Quaternion();
// // // //                 const baseInv = baseQuat.clone().invert();

// // // //                 const userQuat = o.quaternion.clone().premultiply(baseInv);

// // // //                 const euler = new THREE.Euler().setFromQuaternion(userQuat, "YXZ");

// // // //                 const rotX = normalizeDeg(radToDeg(euler.x));
// // // //                 const rotY = normalizeDeg(UNITY_Y_OFFSET - normalizeDeg(radToDeg(euler.y))); // 👈 flip Y do VR
// // // //                 const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //                 return {
// // // //                     name: o.name,
// // // //                     posX: o.userData.gridX ?? 0,
// // // //                     posY: 0,
// // // //                     posZ: o.userData.gridZ ?? 0,
// // // //                     rotX,
// // // //                     rotY,
// // // //                     rotZ,
// // // //                 };
// // // //             });

// // // //         // console.log('entities to save:', entities);

// // // //         try {
// // // //             const response = await fetch(
// // // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //                 {
// // // //                     method: "PATCH",
// // // //                     headers: { "Content-Type": "application/json" },
// // // //                     body: JSON.stringify({
// // // //                         stationName: selectedScenario.stationName,
// // // //                         dimensionX: selectedScenario.dimensionX,
// // // //                         dimensionY: selectedScenario.dimensionY,
// // // //                         entities,
// // // //                     }),
// // // //                 }
// // // //             );

// // // //             if (!response.ok) {
// // // //                 const errorData = await response.json();
// // // //                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //             }

// // // //             setAlertMessage("Stacja zapisana pomyślnie!");
// // // //             setAlertSeverity("success");
// // // //             setAlertOpen(true);
// // // //         } catch (error) {
// // // //             console.error(error);
// // // //             setAlertMessage(`Błąd: ${error.message}`);
// // // //             setAlertSeverity("error");
// // // //             setAlertOpen(true);
// // // //         }
// // // //     };


// // // //     const rotate90Right = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y -= step;

// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;

// // // //         const euler = new THREE.Euler().setFromQuaternion(selectedObjectRef.current.quaternion, "YXZ");
// // // //         euler.y = degToRad(UNITY_Y_OFFSET - radToDeg(euler.y));
// // // //         selectedObjectRef.current.quaternion.setFromEuler(euler);
// // // //     };

// // // //     const rotate90Left = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y += step;
// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;

// // // //         const euler = new THREE.Euler().setFromQuaternion(selectedObjectRef.current.quaternion, "YXZ");
// // // //         euler.y = degToRad(UNITY_Y_OFFSET - radToDeg(euler.y));
// // // //         selectedObjectRef.current.quaternion.setFromEuler(euler);
// // // //     };



// // // //     // -------------------- KEYBOARD --------------------

// // // //     useEffect(() => {
// // // //         // Przesuwanie płynne
// // // //         const moveGrid = (dx, dz) => {
// // // //             if (!selectedObjectRef.current) return;

// // // //             const obj = selectedObjectRef.current;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = (dimZ - 1) * GRID_SIZE;

// // // //             let worldX = obj.position.x + dx;
// // // //             let worldZ = obj.position.z + dz;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             const gridX = Math.round(worldX / GRID_SIZE);
// // // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const maxZ = dimZ - 1;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             // console.log(
// // // //             //     `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //             // );

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;
// // // //         };


// // // //         // //  Przesuwanie skokowe

// // // //         //     const moveGrid = (dx, dz) => {
// // // //         //         if (!selectedObjectRef.current) return;

// // // //         //         const { dimX, dimZ } = boardSizeRef.current;
// // // //         //         const GRID = GRID_SIZE;

// // // //         //         let gridX = selectedObjectRef.current.userData.gridX ?? 0;
// // // //         //         let gridZ = selectedObjectRef.current.userData.gridZ ?? 0;

// // // //         //         gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX + dx));
// // // //         //         gridZ = Math.max(-dimZ, Math.min(0, gridZ + dz));

// // // //         //         selectedObjectRef.current.userData.gridX = gridX;
// // // //         //         selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //         const worldX = -gridX * GRID;
// // // //         //         const worldZ = gridZ * GRID;

// // // //         //         selectedObjectRef.current.position.set(
// // // //         //             worldX,
// // // //         //             selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //             worldZ
// // // //         //         );

// // // //         //     };

// // // //         const onKeyDown = (e) => {
// // // //             if (
// // // //                 e.target.tagName === "INPUT" ||
// // // //                 e.target.tagName === "TEXTAREA" ||
// // // //                 e.target.isContentEditable
// // // //             ) return;

// // // //             switch (e.key.toLowerCase()) {

// // // //                 case "w":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, 5);
// // // //                     break;
// // // //                 case "s":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, -5);
// // // //                     break;
// // // //                 case "a":
// // // //                     e.preventDefault();
// // // //                     moveGrid(5, 0);
// // // //                     break;
// // // //                 case "d":
// // // //                     e.preventDefault();
// // // //                     moveGrid(-5, 0);
// // // //                     break;

// // // //                 case "arrowleft":
// // // //                     e.preventDefault();
// // // //                     rotate90Left();
// // // //                     break;
// // // //                 case "arrowright":
// // // //                     e.preventDefault();
// // // //                     rotate90Right();
// // // //                     break;

// // // //                 case "delete":
// // // //                 case "backspace":
// // // //                     e.preventDefault();
// // // //                     deleteSelected();
// // // //                     break;

// // // //                 case "escape":
// // // //                     selectedObjectRef.current = null;
// // // //                     setSelectedObject(null);
// // // //                     break;

// // // //                 default:
// // // //                     break;
// // // //             }
// // // //         };

// // // //         window.addEventListener("keydown", onKeyDown);
// // // //         return () => window.removeEventListener("keydown", onKeyDown);
// // // //     }, []);




// // // //     // const logRotation = () => {
// // // //     //     console.log("rotation", selectedObjectRef.current.rotation)
// // // //     // }

// // // //     // -------------------- RENDER --------------------
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 {/* <Typography variant="subtitle2">Modele</Typography> */}
// // // //                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
// // // //                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // // //                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List> */}

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Left}
// // // //                             >
// // // //                                 <Rotate90DegreesCcwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = 1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = -1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Right}
// // // //                             >
// // // //                                 <Rotate90DegreesCwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                             {/* <Button onClick={logRotation}>
// // // //                                 log
// // // //                             </Button> */}
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
// // // //             {gridWalls && <GridWalls
// // // //                 scene={sceneRef.current}
// // // //                 dimX={boardSizeRef.current.dimX}
// // // //                 dimZ={boardSizeRef.current.dimZ}
// // // //                 GRID_SIZE={GRID_SIZE}
// // // //                 cameraRef={cameraRef}
// // // //             />}

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{
// // // //                     mt: 1,
// // // //                     position: "absolute",
// // // //                     top: 70,
// // // //                     left: "50%",
// // // //                     transform: "translateX(-50%)",
// // // //                     fontSize: "10px",
// // // //                 }}
// // // //             >
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
// // // //             >
// // // //                 {/* {selectedScenario?.existingSource} */}
// // // //                 {
// // // //                     selectedScenario?.stationName
// // // //                         ? selectedScenario.stationName
// // // //                         : selectedScenario?.id !== undefined
// // // //                             ? `Stacja ${selectedScenario.id}`
// // // //                             : "nie wybrano"
// // // //                 }
// // // //             </Typography>

// // // //             <IconButton
// // // //                 onClick={() => setGridWalls(prev => !prev)}
// // // //                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
// // // //             >
// // // //                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

// // // //             </IconButton>

// // // //             <Snackbar
// // // //                 open={alertOpen}
// // // //                 autoHideDuration={4000}
// // // //                 onClose={() => setAlertOpen(false)}
// // // //                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
// // // //                 sx={{ mt: 8 }}
// // // //             >
// // // //                 <Alert
// // // //                     onClose={() => setAlertOpen(false)}
// // // //                     severity={alertSeverity}
// // // //                     sx={{ width: "100%" }}
// // // //                 >
// // // //                     {alertMessage}
// // // //                 </Alert>
// // // //             </Snackbar>

// // // //         </>
// // // //     );
// // // // }







// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Snackbar,
// // // //     Alert,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// // // // import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// // // // import DeleteIcon from "@mui/icons-material/Delete";
// // // // import GridOffIcon from '@mui/icons-material/GridOff';
// // // // import GridOnIcon from '@mui/icons-material/GridOn';

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";
// // // // import ModelList from "./utilities/ModelList";
// // // // import GridWalls from "./three/GridWalls";
// // // // import { ObjectHighlighter } from "./utilities/ObjectHighlighter";

// // // // const GRID_SIZE = 50;

// // // // const UNITY_Y_OFFSET = 180;

// // // // // const MODEL_LIBRARY = [
// // // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // // ];

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // //     { name: "env_staircase_stairs", path: "/env_staircase_stairs/env_staircase_stairs.gltf", visible: true },
// // // //     { name: "env_wall_column_05", path: "/env_wall_column_05/env_wall_column_05.gltf", visible: true },
// // // //     { name: "exe_cd_essential_items", path: "/exe_cd_essential_items/exe_cd_essential_items.gltf", visible: true },
// // // //     { name: "exe_cooking_bins", path: "/exe_cooking_bins/exe_cooking_bins.gltf", visible: true },
// // // //     { name: "exe_cooking_cookbase", path: "/exe_cooking_cookbase/exe_cooking_cookbase.gltf", visible: true },
// // // //     { name: "exe_cooking_cook_station", path: "/exe_cooking_cook_station/exe_cooking_cook_station.gltf", visible: true },
// // // //     { name: "exe_cooking_device_blender", path: "/exe_cooking_device_blender/exe_cooking_device_blender.gltf", visible: true },
// // // //     { name: "exe_cooking_device_mixer", path: "/exe_cooking_device_mixer/exe_cooking_device_mixer.gltf", visible: true },
// // // //     { name: "exe_cooking_device_mixer_blender", path: "/exe_cooking_device_mixer_blender/exe_cooking_device_mixer_blender.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_dairy", path: "/exe_cooking_fridge_dairy/exe_cooking_fridge_dairy.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_fruits", path: "/exe_cooking_fridge_fruits/exe_cooking_fridge_fruits.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_meat", path: "/exe_cooking_fridge_meat/exe_cooking_fridge_meat.gltf", visible: true },
// // // //     { name: "exe_cooking_fridge_vegetables", path: "/exe_cooking_fridge_vegetables/exe_cooking_fridge_vegetables.gltf", visible: true },
// // // //     { name: "exe_cooking_mech_cooker", path: "/exe_cooking_mech_cooker/exe_cooking_mech_cooker.gltf", visible: true },
// // // //     { name: "exe_cooking_mech_furnance", path: "/exe_cooking_mech_furnance/exe_cooking_mech_furnance.gltf", visible: true },
// // // //     { name: "exe_cooking_sink", path: "/exe_cooking_sink/exe_cooking_sink.gltf", visible: true },
// // // //     { name: "exe_cooking_sink_hanged", path: "/exe_cooking_sink_hanged/exe_cooking_sink_hanged.gltf", visible: true },
// // // //     { name: "exe_creator_door_office_double", path: "/exe_creator_door_office_double/exe_creator_door_office_double.gltf", visible: true },
// // // //     { name: "exe_creator_door_wooden_1", path: "/exe_creator_door_wooden_1/exe_creator_door_wooden_1.gltf", visible: true },
// // // //     { name: "exe_printing_3d_printing_station", path: "/exe_printing_3d_printing_station/exe_printing_3d_printing_station.gltf", visible: true },
// // // //     { name: "exe_printing_cabinet_filament_abs_ptg", path: "/exe_printing_cabinet_filament_abs_ptg/exe_printing_cabinet_filament_abs_ptg.gltf", visible: true },
// // // //     { name: "exe_printing_cabinet_filament_pla_tpu", path: "/exe_printing_cabinet_filament_pla_tpu/exe_printing_cabinet_filament_pla_tpu.gltf", visible: true },
// // // //     { name: "exe_printing_cleaning_set", path: "/exe_printing_cleaning_set/exe_printing_cleaning_set.gltf", visible: true },
// // // //     { name: "exe_printing_examples", path: "/exe_printing_examples/exe_printing_examples.gltf", visible: true },
// // // //     { name: "exe_wd_22nut_impactdriver", path: "/exe_wd_22nut_impactdriver/exe_wd_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_combined_exercises", path: "/exe_wd_combined_exercises/exe_wd_combined_exercises.gltf", visible: true },
// // // //     { name: "exe_wd_drill_26plug_26screw_impactdriver", path: "/exe_wd_drill_26plug_26screw_impactdriver/exe_wd_drill_26plug_26screw_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_drill_dowel_hammer", path: "/exe_wd_drill_dowel_hammer/exe_wd_drill_dowel_hammer.gltf", visible: true },
// // // //     { name: "exe_wd_foaming", path: "/exe_wd_foaming/exe_wd_foaming.gltf", visible: true },
// // // //     { name: "exe_wd_garage_gate_installation", path: "/exe_wd_garage_gate_installation/exe_wd_garage_gate_installation.gltf", visible: true },
// // // //     { name: "exe_wd_garage_gate_supply", path: "/exe_wd_garage_gate_supply/exe_wd_garage_gate_supply.gltf", visible: true },
// // // //     { name: "exe_wd_interior_door_installation", path: "/exe_wd_interior_door_installation/exe_wd_interior_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_interior_door_supply", path: "/exe_wd_interior_door_supply/exe_wd_interior_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_leveler", path: "/exe_wd_leveler/exe_wd_leveler.gltf", visible: true },
// // // //     { name: "exe_wd_outer_door_installation", path: "/exe_wd_outer_door_installation/exe_wd_outer_door_installation.gltf", visible: true },
// // // //     { name: "exe_wd_outer_door_supply", path: "/exe_wd_outer_door_supply/exe_wd_outer_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill", path: "/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill/exe_wd_powerdrilldrillbit_expansionplug_woodworkscrew_powerdrill.gltf", visible: true },
// // // //     { name: "exe_wd_roof_window_installation", path: "/exe_wd_roof_window_installation/exe_wd_roof_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_roof_window_supply", path: "/exe_wd_roof_window_supply/exe_wd_roof_window_supply.gltf", visible: true },
// // // //     { name: "exe_wd_strut", path: "/exe_wd_strut/exe_wd_strut.gltf", visible: true },
// // // //     { name: "exe_wd_tool_rack_1", path: "/exe_wd_tool_rack_1/exe_wd_tool_rack_1.gltf", visible: true },
// // // //     { name: "exe_wd_tool_rack_boxes", path: "/exe_wd_tool_rack_boxes/exe_wd_tool_rack_boxes.gltf", visible: true },
// // // //     { name: "exe_wd_wedge_hammer", path: "/exe_wd_wedge_hammer/exe_wd_wedge_hammer.gltf", visible: true }
// // // // ];


// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);
// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
// // // //     const [gridVersion, setGridVersion] = useState();


// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);
// // // //     const [gridWalls, setGridWalls] = useState(true);


// // // //     // Powiadomienia
// // // //     const [alertOpen, setAlertOpen] = useState(false);
// // // //     const [alertMessage, setAlertMessage] = useState("");
// // // //     const [alertSeverity, setAlertSeverity] = useState("success");


// // // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //     const degToRad = (deg) => (deg * Math.PI) / 180;
// // // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


// // // //     // -------------------- GRID UPDATE --------------------

// // // //     useEffect(() => {
// // // //         if (!selectedScenario) return;
// // // //         setBoardSize({
// // // //             dimX: selectedScenario.dimensionX || 18,
// // // //             dimZ: selectedScenario.dimensionZ || 18
// // // //         });
// // // //         setGridVersion(v => v + 1);
// // // //     }, [selectedScenario?.id]);

// // // //     // console.log('gridVersion', gridVersion)
// // // //     // console.log('boardSize', boardSize)

// // // //     // -------------------- INIT SCENE --------------------
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         camera.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         // let highlight = null;

// // // //         const highlighter = new ObjectHighlighter(scene, {
// // // //             color: 0x4da3ff,
// // // //             tintStrength: 0.35,
// // // //             auraScale: 1.06,
// // // //             auraOpacity: 0.25
// // // //         });

// // // //         // const updatePointer = (e) => {
// // // //         //     pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //         //     pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         // };

// // // //         const updatePointer = (e) => {
// // // //             if (!rendererRef.current) return;
// // // //             const rect = rendererRef.current.domElement.getBoundingClientRect();
// // // //             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
// // // //             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
// // // //         };

// // // //         // const clearHighlight = () => {
// // // //         //     if (highlight) {
// // // //         //         scene.remove(highlight);
// // // //         //         highlight = null;
// // // //         //     }
// // // //         // };

// // // //         // const highlightObject = (obj) => {
// // // //         //     clearHighlight();
// // // //         //     highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //         //     scene.add(highlight);
// // // //         // };

// // // //         const highlightObject = (obj) => {
// // // //             highlighter.highlightObject(obj);
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             highlighter.clear();
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;
// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();
// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         // Przesuwanie płynne

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const worldOffsetX = 0;
// // // //             const maxZ = dimZ;

// // // //             let worldX = p.x;
// // // //             let worldZ = p.z;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = maxZ * GRID_SIZE;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX + worldOffsetX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             // const gridX = Math.round(worldX / GRID_SIZE);
// // // //             // const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const gridX = worldX / GRID_SIZE;
// // // //             const rawGridZ = worldZ / GRID_SIZE;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             // console.log(
// // // //             //     `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //             // );

// // // //             highlighter?.update?.();
// // // //         };

// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let worldX = p.x;
// // // //         //     let worldZ = p.z;

// // // //         //     const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //         //     const maxDepth = maxZ * GRID_SIZE;

// // // //         //     worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //         //     worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX + worldOffsetX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     const gridX = Math.round(worldX / GRID_SIZE);
// // // //         //     const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log(
// // // //         //         `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };

// // // //         // // Przesuwanie Skokowe
// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let rawGridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     rawGridZ = Math.max(-maxZ, Math.min(0, rawGridZ));


// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = rawGridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     // gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridZ = Math.max(-dimZ, Math.min(1, gridZ));


// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = gridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     // highlight?.update?.();
// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     let gridX = Math.round(-p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.round(p.z / GRID_SIZE);
// // // //         //     gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = -gridX * GRID_SIZE;
// // // //         //     const worldZ = gridZ * GRID_SIZE;
// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlight?.update?.();
// // // //         // };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             highlighter?.update?.();
// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 // highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => renderer.domElement.remove();
// // // //     }, []);

// // // //     // -------------------- GRID + PLANE --------------------
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         // usuń poprzedni grid i plane
// // // //         sceneRef.current.children
// // // //             .filter((o) => o.userData?.isGrid)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter((o) => o.userData?.isModel)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(worldOffset.x, 0, worldOffset.z);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
// // // //         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.update();

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);


// // // //     // -------------------- LOAD MODELS --------------------
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(
// // // //                 encodeURI(m.path),
// // // //                 (gltf) => {
// // // //                     modelTemplates.current[m.name] = SkeletonUtils.clone(gltf.scene);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 },
// // // //                 undefined,
// // // //                 (error) => {
// // // //                     console.error("Failed to load model:", m.name, error);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 }
// // // //             );
// // // //         });
// // // //     }, []);

// // // //     // -------------------- LOAD ENTITIES --------------------

// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     // const baseDimZ = selectedScenario.dimensionZ ?? 18;
// // // //     //     // const extraZ = 8;
// // // //     //     // const dimZ = baseDimZ + extraZ;
// // // //     //     // const worldOffsetZ = -(dimZ / 2) * GRID_SIZE;
// // // //     //     // const worldOffsetX = 0;
// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         const rotY = degToRad(e.rotY || 0);
// // // //     //         const frontCorrection = new THREE.Quaternion();
// // // //     //         frontCorrection.setFromEuler(new THREE.Euler(0, -tpl.rotation.y, 0));

// // // //     //         const userRot = new THREE.Quaternion();
// // // //     //         userRot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);

// // // //     //         model.quaternion.copy(userRot).premultiply(frontCorrection);

// // // //     //         // model.rotation.x = degToRad(e.rotX || 0);
// // // //     //         // model.rotation.z = degToRad(e.rotZ || 0);

// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             rotY,
// // // //     //             degToRad(e.rotZ || 0)
// // // //     //         );
// // // //     //         // const pivotHelper = new THREE.AxesHelper(50)
// // // //     //         // model.add(pivotHelper)

// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);

// // // //     //         // console.log(`${e.name}:`, {
// // // //     //         //     rotX: radToDeg(model.rotation.x),
// // // //     //         //     rotY: radToDeg(model.rotation.y),
// // // //     //         //     rotZ: radToDeg(model.rotation.z),
// // // //     //         //     posX: model.position.x,
// // // //     //         //     posZ: model.position.z,
// // // //     //         // });
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         // --- ROTACJA ---
// // // //     //         // const baseQuat = tpl.quaternion.clone();

// // // //     //         // const euler = new THREE.Euler(
// // // //     //         //     degToRad(e.rotX || 0),
// // // //     //         //     degToRad(e.rotY || 0),
// // // //     //         //     degToRad(e.rotZ || 0),
// // // //     //         //     "YXZ"
// // // //     //         // );

// // // //     //         // const userQuat = new THREE.Quaternion().setFromEuler(euler);

// // // //     //         // model.quaternion.copy(baseQuat).multiply(userQuat);


// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             degToRad(e.rotY || 0),
// // // //     //             degToRad(e.rotZ || 0),
// // // //     //             "YXZ"
// // // //     //         );

// // // //     //         const q = model.quaternion;

// // // //     //         const testEulerXYZ = new THREE.Euler().setFromQuaternion(q, "XYZ");
// // // //     //         const testEulerYXZ = new THREE.Euler().setFromQuaternion(q, "YXZ");

// // // //     //         console.log("XYZ:", radToDeg(testEulerXYZ.x), radToDeg(testEulerXYZ.y), radToDeg(testEulerXYZ.z));
// // // //     //         console.log("YXZ:", radToDeg(testEulerYXZ.x), radToDeg(testEulerYXZ.y), radToDeg(testEulerYXZ.z));


// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         const { dimZ } = boardSizeRef.current;
// // // //         const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //         const worldOffsetX = 0;

// // // //         const UNITY_Y_OFFSET = 180; // korekta osi Y dla VR

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = SkeletonUtils.clone(tpl);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //             const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //             model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //             // --- ROTACJA ---
// // // //             const baseQuat = tpl.quaternion.clone();

// // // //             const rotX = degToRad(e.rotX || 0);
// // // //             const rotY = degToRad(UNITY_Y_OFFSET - (e.rotY || 0));
// // // //             const rotZ = degToRad(e.rotZ || 0);

// // // //             const userQuat = new THREE.Quaternion().setFromEuler(
// // // //                 new THREE.Euler(rotX, rotY, rotZ, "YXZ")
// // // //             );

// // // //             model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);

// // // //             const testEuler = new THREE.Euler().setFromQuaternion(model.quaternion, "YXZ");
// // // //             console.log(
// // // //                 `[LOAD] ${model.name} | rotX:${radToDeg(testEuler.x).toFixed(1)} | rotY:${radToDeg(testEuler.y).toFixed(1)} | rotZ:${radToDeg(testEuler.z).toFixed(1)}`
// // // //             );
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);


// // // //     // -------------------- ADD / DELETE --------------------
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = SkeletonUtils.clone(tpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         const baseQuat = tpl.quaternion.clone();
// // // //         const userQuat = new THREE.Quaternion().setFromEuler(
// // // //             new THREE.Euler(0, degToRad(UNITY_Y_OFFSET), 0, "YXZ")
// // // //         );

// // // //         model.quaternion.copy(baseQuat).multiply(userQuat);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };


// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     // const saveScenario = async () => {
// // // //     //     if (!selectedScenario) return;

// // // //     //     console.log('objectsRef.current', objectsRef.current)



// // // //     //     // const entities = objectsRef.current
// // // //     //     //     .filter(o => o.userData.isModel)
// // // //     //     //     .map(o => {

// // // //     //     //         const euler = new THREE.Euler().setFromQuaternion(
// // // //     //     //             o.quaternion,
// // // //     //     //             "YXZ"
// // // //     //     //         );

// // // //     //     //         const rotX = normalizeDeg(radToDeg(euler.x));
// // // //     //     //         const rotY = normalizeDeg(radToDeg(euler.y));
// // // //     //     //         const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //     //     //         return {
// // // //     //     //             name: o.name,
// // // //     //     //             posX: o.userData.gridX ?? 0,
// // // //     //     //             posY: 0,
// // // //     //     //             posZ: o.userData.gridZ ?? 0,
// // // //     //     //             rotX,
// // // //     //     //             rotY,
// // // //     //     //             rotZ
// // // //     //     //         };
// // // //     //     //     });


// // // //     //     const entities = objectsRef.current
// // // //     //         .filter(o => o.userData.isModel)
// // // //     //         .map(o => {

// // // //     //             const euler = new THREE.Euler().setFromQuaternion(
// // // //     //                 o.quaternion,
// // // //     //                 "YXZ"
// // // //     //             );

// // // //     //             const rotX = normalizeDeg(radToDeg(euler.x));
// // // //     //             const rotY = normalizeDeg(360 - normalizeDeg(radToDeg(euler.y)));
// // // //     //             const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //     //             return {
// // // //     //                 name: o.name,
// // // //     //                 posX: o.userData.gridX ?? 0,
// // // //     //                 posY: 0,
// // // //     //                 posZ: o.userData.gridZ ?? 0,
// // // //     //                 rotX,
// // // //     //                 rotY,
// // // //     //                 rotZ
// // // //     //             };
// // // //     //         });


// // // //     //     console.log('entities', entities)

// // // //     //     try {
// // // //     //         const response = await fetch(
// // // //     //             `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //     //             {
// // // //     //                 method: "PATCH",
// // // //     //                 headers: { "Content-Type": "application/json" },
// // // //     //                 body: JSON.stringify({
// // // //     //                     stationName: selectedScenario.stationName,
// // // //     //                     dimensionX: selectedScenario.dimensionX,
// // // //     //                     dimensionY: selectedScenario.dimensionY,
// // // //     //                     entities,
// // // //     //                 }),
// // // //     //             }
// // // //     //         );

// // // //     //         if (!response.ok) {
// // // //     //             const errorData = await response.json();
// // // //     //             throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //     //         }

// // // //     //         setAlertMessage("Stacja zapisana pomyślnie!");
// // // //     //         setAlertSeverity("success");
// // // //     //         setAlertOpen(true);
// // // //     //     } catch (error) {
// // // //     //         console.error(error);
// // // //     //         setAlertMessage(`Błąd: ${error.message}`);
// // // //     //         setAlertSeverity("error");
// // // //     //         setAlertOpen(true);
// // // //     //     }
// // // //     // };


// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => {
// // // //                 const tpl = modelTemplates.current[o.name];
// // // //                 const baseQuat = tpl?.quaternion.clone() || new THREE.Quaternion();
// // // //                 const baseInv = baseQuat.clone().invert();

// // // //                 const userQuat = o.quaternion.clone().premultiply(baseInv);

// // // //                 const euler = new THREE.Euler().setFromQuaternion(userQuat, "YXZ");

// // // //                 const rotX = normalizeDeg(radToDeg(euler.x));
// // // //                 const rotY = normalizeDeg(UNITY_Y_OFFSET - normalizeDeg(radToDeg(euler.y))); // 👈 flip Y do VR
// // // //                 const rotZ = normalizeDeg(radToDeg(euler.z));

// // // //                 return {
// // // //                     name: o.name,
// // // //                     posX: o.userData.gridX ?? 0,
// // // //                     posY: 0,
// // // //                     posZ: o.userData.gridZ ?? 0,
// // // //                     rotX,
// // // //                     rotY,
// // // //                     rotZ,
// // // //                 };
// // // //             });

// // // //         // console.log('entities to save:', entities);

// // // //         try {
// // // //             const response = await fetch(
// // // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //                 {
// // // //                     method: "PATCH",
// // // //                     headers: { "Content-Type": "application/json" },
// // // //                     body: JSON.stringify({
// // // //                         stationName: selectedScenario.stationName,
// // // //                         dimensionX: selectedScenario.dimensionX,
// // // //                         dimensionY: selectedScenario.dimensionY,
// // // //                         entities,
// // // //                     }),
// // // //                 }
// // // //             );

// // // //             if (!response.ok) {
// // // //                 const errorData = await response.json();
// // // //                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //             }

// // // //             setAlertMessage("Stacja zapisana pomyślnie!");
// // // //             setAlertSeverity("success");
// // // //             setAlertOpen(true);
// // // //         } catch (error) {
// // // //             console.error(error);
// // // //             setAlertMessage(`Błąd: ${error.message}`);
// // // //             setAlertSeverity("error");
// // // //             setAlertOpen(true);
// // // //         }
// // // //     };


// // // //     const rotate90Right = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y -= step;

// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;

// // // //         const euler = new THREE.Euler().setFromQuaternion(selectedObjectRef.current.quaternion, "YXZ");
// // // //         euler.y = degToRad(UNITY_Y_OFFSET - radToDeg(euler.y));
// // // //         selectedObjectRef.current.quaternion.setFromEuler(euler);
// // // //     };

// // // //     const rotate90Left = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         const step = Math.PI / 2;

// // // //         selectedObjectRef.current.rotation.y += step;
// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;

// // // //         const euler = new THREE.Euler().setFromQuaternion(selectedObjectRef.current.quaternion, "YXZ");
// // // //         euler.y = degToRad(UNITY_Y_OFFSET - radToDeg(euler.y));
// // // //         selectedObjectRef.current.quaternion.setFromEuler(euler);
// // // //     };



// // // //     // -------------------- KEYBOARD --------------------

// // // //     useEffect(() => {
// // // //         // Przesuwanie płynne
// // // //         const moveGrid = (dx, dz) => {
// // // //             if (!selectedObjectRef.current) return;

// // // //             const obj = selectedObjectRef.current;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = (dimZ - 1) * GRID_SIZE;

// // // //             let worldX = obj.position.x + dx;
// // // //             let worldZ = obj.position.z + dz;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             const gridX = Math.round(worldX / GRID_SIZE);
// // // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const maxZ = dimZ - 1;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             // console.log(
// // // //             //     `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //             // );

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;
// // // //         };


// // // //         // //  Przesuwanie skokowe

// // // //         //     const moveGrid = (dx, dz) => {
// // // //         //         if (!selectedObjectRef.current) return;

// // // //         //         const { dimX, dimZ } = boardSizeRef.current;
// // // //         //         const GRID = GRID_SIZE;

// // // //         //         let gridX = selectedObjectRef.current.userData.gridX ?? 0;
// // // //         //         let gridZ = selectedObjectRef.current.userData.gridZ ?? 0;

// // // //         //         gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX + dx));
// // // //         //         gridZ = Math.max(-dimZ, Math.min(0, gridZ + dz));

// // // //         //         selectedObjectRef.current.userData.gridX = gridX;
// // // //         //         selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //         const worldX = -gridX * GRID;
// // // //         //         const worldZ = gridZ * GRID;

// // // //         //         selectedObjectRef.current.position.set(
// // // //         //             worldX,
// // // //         //             selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //             worldZ
// // // //         //         );

// // // //         //     };

// // // //         const onKeyDown = (e) => {
// // // //             if (
// // // //                 e.target.tagName === "INPUT" ||
// // // //                 e.target.tagName === "TEXTAREA" ||
// // // //                 e.target.isContentEditable
// // // //             ) return;

// // // //             switch (e.key.toLowerCase()) {

// // // //                 case "w":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, 5);
// // // //                     break;
// // // //                 case "s":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, -5);
// // // //                     break;
// // // //                 case "a":
// // // //                     e.preventDefault();
// // // //                     moveGrid(5, 0);
// // // //                     break;
// // // //                 case "d":
// // // //                     e.preventDefault();
// // // //                     moveGrid(-5, 0);
// // // //                     break;

// // // //                 case "arrowleft":
// // // //                     e.preventDefault();
// // // //                     rotate90Left();
// // // //                     break;
// // // //                 case "arrowright":
// // // //                     e.preventDefault();
// // // //                     rotate90Right();
// // // //                     break;

// // // //                 case "delete":
// // // //                 case "backspace":
// // // //                     e.preventDefault();
// // // //                     deleteSelected();
// // // //                     break;

// // // //                 case "escape":
// // // //                     selectedObjectRef.current = null;
// // // //                     setSelectedObject(null);
// // // //                     break;

// // // //                 default:
// // // //                     break;
// // // //             }
// // // //         };

// // // //         window.addEventListener("keydown", onKeyDown);
// // // //         return () => window.removeEventListener("keydown", onKeyDown);
// // // //     }, []);




// // // //     // const logRotation = () => {
// // // //     //     console.log("rotation", selectedObjectRef.current.rotation)
// // // //     // }

// // // //     // -------------------- RENDER --------------------
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 {/* <Typography variant="subtitle2">Modele</Typography> */}
// // // //                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
// // // //                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // // //                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List> */}

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Left}
// // // //                             >
// // // //                                 <Rotate90DegreesCcwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = 1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = -1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Right}
// // // //                             >
// // // //                                 <Rotate90DegreesCwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                             {/* <Button onClick={logRotation}>
// // // //                                 log
// // // //                             </Button> */}
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
// // // //             {gridWalls && <GridWalls
// // // //                 scene={sceneRef.current}
// // // //                 dimX={boardSizeRef.current.dimX}
// // // //                 dimZ={boardSizeRef.current.dimZ}
// // // //                 GRID_SIZE={GRID_SIZE}
// // // //                 cameraRef={cameraRef}
// // // //             />}

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{
// // // //                     mt: 1,
// // // //                     position: "absolute",
// // // //                     top: 70,
// // // //                     left: "50%",
// // // //                     transform: "translateX(-50%)",
// // // //                     fontSize: "10px",
// // // //                 }}
// // // //             >
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
// // // //             >
// // // //                 {/* {selectedScenario?.existingSource} */}
// // // //                 {
// // // //                     selectedScenario?.stationName
// // // //                         ? selectedScenario.stationName
// // // //                         : selectedScenario?.id !== undefined
// // // //                             ? `Stacja ${selectedScenario.id}`
// // // //                             : "nie wybrano"
// // // //                 }
// // // //             </Typography>

// // // //             <IconButton
// // // //                 onClick={() => setGridWalls(prev => !prev)}
// // // //                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
// // // //             >
// // // //                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

// // // //             </IconButton>

// // // //             <Snackbar
// // // //                 open={alertOpen}
// // // //                 autoHideDuration={4000}
// // // //                 onClose={() => setAlertOpen(false)}
// // // //                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
// // // //                 sx={{ mt: 8 }}
// // // //             >
// // // //                 <Alert
// // // //                     onClose={() => setAlertOpen(false)}
// // // //                     severity={alertSeverity}
// // // //                     sx={{ width: "100%" }}
// // // //                 >
// // // //                     {alertMessage}
// // // //                 </Alert>
// // // //             </Snackbar>

// // // //         </>
// // // //     );
// // // // }








// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Snackbar,
// // // //     Alert,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// // // // import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// // // // import DeleteIcon from "@mui/icons-material/Delete";
// // // // import GridOffIcon from '@mui/icons-material/GridOff';
// // // // import GridOnIcon from '@mui/icons-material/GridOn';

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";
// // // // import ModelList from "./utilities/ModelList";
// // // // import GridWalls from "./three/GridWalls";
// // // // import { ObjectHighlighter } from "./utilities/ObjectHighlighter";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);
// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
// // // //     const [gridVersion, setGridVersion] = useState();


// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);
// // // //     const [gridWalls, setGridWalls] = useState(true);


// // // //     // Powiadomienia
// // // //     const [alertOpen, setAlertOpen] = useState(false);
// // // //     const [alertMessage, setAlertMessage] = useState("");
// // // //     const [alertSeverity, setAlertSeverity] = useState("success");


// // // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //     const degToRad = (deg) => (deg * Math.PI) / 180;
// // // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


// // // //     // -------------------- GRID UPDATE --------------------

// // // //     useEffect(() => {
// // // //         if (!selectedScenario) return;
// // // //         setBoardSize({
// // // //             dimX: selectedScenario.dimensionX || 18,
// // // //             dimZ: selectedScenario.dimensionZ || 18
// // // //         });
// // // //         setGridVersion(v => v + 1);
// // // //     }, [selectedScenario?.id]);

// // // //     console.log('gridVersion', gridVersion)
// // // //     console.log('boardSize', boardSize)

// // // //     // -------------------- INIT SCENE --------------------
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         camera.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         // let highlight = null;

// // // //         const highlighter = new ObjectHighlighter(scene, {
// // // //             color: 0x4da3ff,
// // // //             tintStrength: 0.35,
// // // //             auraScale: 1.06,
// // // //             auraOpacity: 0.25
// // // //         });

// // // //         // const updatePointer = (e) => {
// // // //         //     pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //         //     pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         // };

// // // //         const updatePointer = (e) => {
// // // //             if (!rendererRef.current) return;
// // // //             const rect = rendererRef.current.domElement.getBoundingClientRect();
// // // //             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
// // // //             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
// // // //         };

// // // //         // const clearHighlight = () => {
// // // //         //     if (highlight) {
// // // //         //         scene.remove(highlight);
// // // //         //         highlight = null;
// // // //         //     }
// // // //         // };

// // // //         // const highlightObject = (obj) => {
// // // //         //     clearHighlight();
// // // //         //     highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //         //     scene.add(highlight);
// // // //         // };

// // // //         const highlightObject = (obj) => {
// // // //             highlighter.highlightObject(obj);
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             highlighter.clear();
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;
// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();
// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         // Przesuwanie płynne
// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const worldOffsetX = 0;
// // // //             const maxZ = dimZ - 1;

// // // //             let worldX = p.x;
// // // //             let worldZ = p.z;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = maxZ * GRID_SIZE;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX + worldOffsetX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             const gridX = Math.round(worldX / GRID_SIZE);
// // // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             highlighter?.update?.();
// // // //         };

// // // //         // // Przesuwanie Skokowe
// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let rawGridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     rawGridZ = Math.max(-maxZ, Math.min(0, rawGridZ));


// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = rawGridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     // gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridZ = Math.max(-dimZ, Math.min(1, gridZ));


// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = gridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     // highlight?.update?.();
// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     let gridX = Math.round(-p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.round(p.z / GRID_SIZE);
// // // //         //     gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = -gridX * GRID_SIZE;
// // // //         //     const worldZ = gridZ * GRID_SIZE;
// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlight?.update?.();
// // // //         // };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             highlighter?.update?.();
// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 // highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => renderer.domElement.remove();
// // // //     }, []);

// // // //     // -------------------- GRID + PLANE --------------------
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         // usuń poprzedni grid i plane
// // // //         sceneRef.current.children
// // // //             .filter((o) => o.userData?.isGrid)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter((o) => o.userData?.isModel)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(worldOffset.x, 0, worldOffset.z);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
// // // //         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.update();

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);


// // // //     // -------------------- LOAD MODELS --------------------
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(
// // // //                 encodeURI(m.path),
// // // //                 (gltf) => {
// // // //                     modelTemplates.current[m.name] = SkeletonUtils.clone(gltf.scene);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 },
// // // //                 undefined,
// // // //                 (error) => {
// // // //                     console.error("Failed to load model:", m.name, error);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 }
// // // //             );
// // // //         });
// // // //     }, []);

// // // //     // -------------------- LOAD ENTITIES --------------------

// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     // const baseDimZ = selectedScenario.dimensionZ ?? 18;
// // // //     //     // const extraZ = 8;
// // // //     //     // const dimZ = baseDimZ + extraZ;
// // // //     //     // const worldOffsetZ = -(dimZ / 2) * GRID_SIZE;
// // // //     //     // const worldOffsetX = 0;
// // // //     //     const { dimZ } = boardSizeRef.current;
// // // //     //     const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //     //     const worldOffsetX = 0;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         const rotY = degToRad(e.rotY || 0);
// // // //     //         const frontCorrection = new THREE.Quaternion();
// // // //     //         frontCorrection.setFromEuler(new THREE.Euler(0, -tpl.rotation.y, 0));

// // // //     //         const userRot = new THREE.Quaternion();
// // // //     //         userRot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);

// // // //     //         model.quaternion.copy(userRot).premultiply(frontCorrection);

// // // //     //         // model.rotation.x = degToRad(e.rotX || 0);
// // // //     //         // model.rotation.z = degToRad(e.rotZ || 0);

// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             rotY,
// // // //     //             degToRad(e.rotZ || 0)
// // // //     //         );
// // // //     //         // const pivotHelper = new THREE.AxesHelper(50)
// // // //     //         // model.add(pivotHelper)

// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);

// // // //     //         // console.log(`${e.name}:`, {
// // // //     //         //     rotX: radToDeg(model.rotation.x),
// // // //     //         //     rotY: radToDeg(model.rotation.y),
// // // //     //         //     rotZ: radToDeg(model.rotation.z),
// // // //     //         //     posX: model.position.x,
// // // //     //         //     posZ: model.position.z,
// // // //     //         // });
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);


// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         const { dimZ } = boardSizeRef.current;
// // // //         const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //         const worldOffsetX = 0;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = SkeletonUtils.clone(tpl);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //             const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //             model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //             // --- ROTACJA ---
// // // //             // zapamiętujemy początkową rotację modelu z tpl
// // // //             const baseRot = tpl.rotation.clone(); // Euler

// // // //             // konwersja zapisanych wartości (stopnie) na radiany
// // // //             const rotX = degToRad(e.rotX || 0);
// // // //             const rotY = degToRad(e.rotY || 0);
// // // //             const rotZ = degToRad(e.rotZ || 0);

// // // //             // stosujemy zapisane rotacje względem początkowej orientacji modelu
// // // //             model.rotation.set(
// // // //                 baseRot.x + rotX,
// // // //                 baseRot.y + rotY,
// // // //                 baseRot.z + rotZ
// // // //             );

// // // //             const q = model.quaternion;

// // // //             const testEulerXYZ = new THREE.Euler().setFromQuaternion(q, "XYZ");
// // // //             const testEulerYXZ = new THREE.Euler().setFromQuaternion(q, "YXZ");

// // // //             console.log("XYZ:", radToDeg(testEulerXYZ.x), radToDeg(testEulerXYZ.y), radToDeg(testEulerXYZ.z));
// // // //             console.log("YXZ:", radToDeg(testEulerYXZ.x), radToDeg(testEulerYXZ.y), radToDeg(testEulerYXZ.z));


// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);


// // // //     // -------------------- ADD / DELETE --------------------
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = SkeletonUtils.clone(tpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = -model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         console.log('objectsRef.current', objectsRef.current)

// // // //         const entities = objectsRef.current
// // // //             .filter((o) => o.userData.isModel)
// // // //             .map((o) => ({
// // // //                 name: o.name,
// // // //                 posX: o.userData.gridX ?? 0,
// // // //                 posY: 0,
// // // //                 posZ: o.userData.gridZ ?? 0,
// // // //                 rotX: normalizeDeg(radToDeg(o.rotation.x)),
// // // //                 rotY: normalizeDeg(radToDeg(o.rotation.y)),
// // // //                 rotZ: normalizeDeg(radToDeg(o.rotation.z)),
// // // //             }));


// // // //         console.log('entities', entities)

// // // //         try {
// // // //             const response = await fetch(
// // // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //                 {
// // // //                     method: "PATCH",
// // // //                     headers: { "Content-Type": "application/json" },
// // // //                     body: JSON.stringify({
// // // //                         stationName: selectedScenario.stationName,
// // // //                         dimensionX: selectedScenario.dimensionX,
// // // //                         dimensionY: selectedScenario.dimensionY,
// // // //                         entities,
// // // //                     }),
// // // //                 }
// // // //             );

// // // //             if (!response.ok) {
// // // //                 const errorData = await response.json();
// // // //                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //             }

// // // //             setAlertMessage("Stacja zapisana pomyślnie!");
// // // //             setAlertSeverity("success");
// // // //             setAlertOpen(true);
// // // //         } catch (error) {
// // // //             console.error(error);
// // // //             setAlertMessage(`Błąd: ${error.message}`);
// // // //             setAlertSeverity("error");
// // // //             setAlertOpen(true);
// // // //         }
// // // //     };


// // // //     const rotate90Right = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         selectedObjectRef.current.rotation.y -= Math.PI / 2;
// // // //         const step = Math.PI / 2;
// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };

// // // //     const rotate90Left = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         selectedObjectRef.current.rotation.y += Math.PI / 2;
// // // //         const step = Math.PI / 2;
// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };


// // // //     // -------------------- KEYBOARD --------------------

// // // //     useEffect(() => {
// // // //         // Przesuwanie płynne
// // // //         const moveGrid = (dx, dz) => {
// // // //             if (!selectedObjectRef.current) return;

// // // //             const obj = selectedObjectRef.current;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = (dimZ - 1) * GRID_SIZE;

// // // //             let worldX = obj.position.x + dx;
// // // //             let worldZ = obj.position.z + dz;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             const gridX = Math.round(worldX / GRID_SIZE);
// // // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const maxZ = dimZ - 1;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             console.log(
// // // //                 `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //             );

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;
// // // //         };


// // // //         // //  Przesuwanie skokowe

// // // //         //     const moveGrid = (dx, dz) => {
// // // //         //         if (!selectedObjectRef.current) return;

// // // //         //         const { dimX, dimZ } = boardSizeRef.current;
// // // //         //         const GRID = GRID_SIZE;

// // // //         //         let gridX = selectedObjectRef.current.userData.gridX ?? 0;
// // // //         //         let gridZ = selectedObjectRef.current.userData.gridZ ?? 0;

// // // //         //         gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX + dx));
// // // //         //         gridZ = Math.max(-dimZ, Math.min(0, gridZ + dz));

// // // //         //         selectedObjectRef.current.userData.gridX = gridX;
// // // //         //         selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //         const worldX = -gridX * GRID;
// // // //         //         const worldZ = gridZ * GRID;

// // // //         //         selectedObjectRef.current.position.set(
// // // //         //             worldX,
// // // //         //             selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //             worldZ
// // // //         //         );

// // // //         //     };

// // // //         const onKeyDown = (e) => {
// // // //             if (
// // // //                 e.target.tagName === "INPUT" ||
// // // //                 e.target.tagName === "TEXTAREA" ||
// // // //                 e.target.isContentEditable
// // // //             ) return;

// // // //             switch (e.key.toLowerCase()) {

// // // //                 case "w":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, 5);
// // // //                     break;
// // // //                 case "s":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, -5);
// // // //                     break;
// // // //                 case "a":
// // // //                     e.preventDefault();
// // // //                     moveGrid(5, 0);
// // // //                     break;
// // // //                 case "d":
// // // //                     e.preventDefault();
// // // //                     moveGrid(-5, 0);
// // // //                     break;

// // // //                 case "arrowleft":
// // // //                     e.preventDefault();
// // // //                     rotate90Left();
// // // //                     break;
// // // //                 case "arrowright":
// // // //                     e.preventDefault();
// // // //                     rotate90Right();
// // // //                     break;

// // // //                 case "delete":
// // // //                 case "backspace":
// // // //                     e.preventDefault();
// // // //                     deleteSelected();
// // // //                     break;

// // // //                 case "escape":
// // // //                     selectedObjectRef.current = null;
// // // //                     setSelectedObject(null);
// // // //                     break;

// // // //                 default:
// // // //                     break;
// // // //             }
// // // //         };

// // // //         window.addEventListener("keydown", onKeyDown);
// // // //         return () => window.removeEventListener("keydown", onKeyDown);
// // // //     }, []);




// // // //     // const logRotation = () => {
// // // //     //     console.log("rotation", selectedObjectRef.current.rotation)
// // // //     // }

// // // //     // -------------------- RENDER --------------------
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
// // // //                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // // //                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List> */}

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Left}
// // // //                             >
// // // //                                 <Rotate90DegreesCcwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = 1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = -1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Right}
// // // //                             >
// // // //                                 <Rotate90DegreesCwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                             {/* <Button onClick={logRotation}>
// // // //                                 log
// // // //                             </Button> */}
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
// // // //             {gridWalls && <GridWalls
// // // //                 scene={sceneRef.current}
// // // //                 dimX={boardSizeRef.current.dimX}
// // // //                 dimZ={boardSizeRef.current.dimZ}
// // // //                 GRID_SIZE={GRID_SIZE}
// // // //                 cameraRef={cameraRef}
// // // //             />}

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{
// // // //                     mt: 1,
// // // //                     position: "absolute",
// // // //                     top: 70,
// // // //                     left: "50%",
// // // //                     transform: "translateX(-50%)",
// // // //                     fontSize: "10px",
// // // //                 }}
// // // //             >
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
// // // //             >
// // // //                 {/* {selectedScenario?.existingSource} */}
// // // //                 {
// // // //                     selectedScenario?.stationName
// // // //                         ? selectedScenario.stationName
// // // //                         : selectedScenario?.id !== undefined
// // // //                             ? `Stacja ${selectedScenario.id}`
// // // //                             : "nie wybrano"
// // // //                 }
// // // //             </Typography>

// // // //             <IconButton
// // // //                 onClick={() => setGridWalls(prev => !prev)}
// // // //                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
// // // //             >
// // // //                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

// // // //             </IconButton>

// // // //             <Snackbar
// // // //                 open={alertOpen}
// // // //                 autoHideDuration={4000}
// // // //                 onClose={() => setAlertOpen(false)}
// // // //                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
// // // //                 sx={{ mt: 8 }}
// // // //             >
// // // //                 <Alert
// // // //                     onClose={() => setAlertOpen(false)}
// // // //                     severity={alertSeverity}
// // // //                     sx={{ width: "100%" }}
// // // //                 >
// // // //                     {alertMessage}
// // // //                 </Alert>
// // // //             </Snackbar>

// // // //         </>
// // // //     );
// // // // }







// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Snackbar,
// // // //     Alert,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
// // // // import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
// // // // import DeleteIcon from "@mui/icons-material/Delete";
// // // // import GridOffIcon from '@mui/icons-material/GridOff';
// // // // import GridOnIcon from '@mui/icons-material/GridOn';

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";
// // // // import ModelList from "./utilities/ModelList";
// // // // import GridWalls from "./three/GridWalls";
// // // // import { ObjectHighlighter } from "./utilities/ObjectHighlighter";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);
// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const [boardSize, setBoardSize] = useState({ dimX: 18, dimZ: 18 });
// // // //     const [gridVersion, setGridVersion] = useState();


// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);
// // // //     const [gridWalls, setGridWalls] = useState(true);


// // // //     // Powiadomienia
// // // //     const [alertOpen, setAlertOpen] = useState(false);
// // // //     const [alertMessage, setAlertMessage] = useState("");
// // // //     const [alertSeverity, setAlertSeverity] = useState("success");


// // // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //     const degToRad = (deg) => (deg * Math.PI) / 180;
// // // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);


// // // //     // -------------------- GRID UPDATE --------------------

// // // //     useEffect(() => {
// // // //         if (!selectedScenario) return;
// // // //         setBoardSize({
// // // //             dimX: selectedScenario.dimensionX || 18,
// // // //             dimZ: selectedScenario.dimensionZ || 18
// // // //         });
// // // //         setGridVersion(v => v + 1);
// // // //     }, [selectedScenario?.id]);

// // // //     console.log('gridVersion', gridVersion)
// // // //     console.log('boardSize', boardSize)

// // // //     // -------------------- INIT SCENE --------------------
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         camera.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         // let highlight = null;

// // // //         const highlighter = new ObjectHighlighter(scene, {
// // // //             color: 0x4da3ff,
// // // //             tintStrength: 0.35,
// // // //             auraScale: 1.06,
// // // //             auraOpacity: 0.25
// // // //         });

// // // //         // const updatePointer = (e) => {
// // // //         //     pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //         //     pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         // };

// // // //         const updatePointer = (e) => {
// // // //             if (!rendererRef.current) return;
// // // //             const rect = rendererRef.current.domElement.getBoundingClientRect();
// // // //             pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
// // // //             pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
// // // //         };

// // // //         // const clearHighlight = () => {
// // // //         //     if (highlight) {
// // // //         //         scene.remove(highlight);
// // // //         //         highlight = null;
// // // //         //     }
// // // //         // };

// // // //         // const highlightObject = (obj) => {
// // // //         //     clearHighlight();
// // // //         //     highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //         //     scene.add(highlight);
// // // //         // };

// // // //         const highlightObject = (obj) => {
// // // //             highlighter.highlightObject(obj);
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             highlighter.clear();
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;
// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();
// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         // Przesuwanie płynne
// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const worldOffsetX = 0;
// // // //             const maxZ = dimZ - 1;

// // // //             let worldX = p.x;
// // // //             let worldZ = p.z;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = maxZ * GRID_SIZE;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX + worldOffsetX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             const gridX = Math.round(worldX / GRID_SIZE);
// // // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             highlighter?.update?.();
// // // //         };

// // // //         // // Przesuwanie Skokowe
// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;

// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;
// // // //         //     const maxZ = dimZ - 1;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let rawGridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     rawGridZ = Math.max(-maxZ, Math.min(0, rawGridZ));


// // // //         //     const gridZ = -maxZ - rawGridZ;

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = rawGridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     const worldOffsetX = 0;

// // // //         //     let gridX = Math.round(p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.floor(p.z / GRID_SIZE);

// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //         //     // gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridZ = Math.max(-dimZ, Math.min(1, gridZ));


// // // //         //     console.log('gridX', gridX)
// // // //         //     console.log('gridZ', gridZ)

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //         //     const worldZ = gridZ * GRID_SIZE;

// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     // highlight?.update?.();
// // // //         //     highlighter?.update?.();
// // // //         // };


// // // //         // const onMove = (e) => {
// // // //         //     if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //         //     updatePointer(e);
// // // //         //     raycaster.setFromCamera(pointer, camera);

// // // //         //     const hits = raycaster.intersectObject(planeRef.current);
// // // //         //     if (!hits.length) return;
// // // //         //     const p = hits[0].point;
// // // //         //     const { dimX, dimZ } = boardSizeRef.current;

// // // //         //     let gridX = Math.round(-p.x / GRID_SIZE);
// // // //         //     let gridZ = Math.round(p.z / GRID_SIZE);
// // // //         //     gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //         //     gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //         //     selectedObjectRef.current.userData.gridX = gridX;
// // // //         //     selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //     const worldX = -gridX * GRID_SIZE;
// // // //         //     const worldZ = gridZ * GRID_SIZE;
// // // //         //     selectedObjectRef.current.position.set(
// // // //         //         worldX,
// // // //         //         selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //         worldZ
// // // //         //     );

// // // //         //     highlight?.update?.();
// // // //         // };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             highlighter?.update?.();
// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 // highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => renderer.domElement.remove();
// // // //     }, []);

// // // //     // -------------------- GRID + PLANE --------------------
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         // usuń poprzedni grid i plane
// // // //         sceneRef.current.children
// // // //             .filter((o) => o.userData?.isGrid)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter((o) => o.userData?.isModel)
// // // //             .forEach((o) => sceneRef.current.remove(o));

// // // //         const worldOffset = { x: 0, z: -((dimZ - 1) / 2) * GRID_SIZE };

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(worldOffset.x, 0, worldOffset.z);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         // cameraRef.current.position.set(worldOffset.x + 500, 800, worldOffset.z + 1300);
// // // //         cameraRef.current.position.set(worldOffset.x - 1100, 900, worldOffset.z - 700);
// // // //         cameraRef.current.lookAt(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.target.set(worldOffset.x, 0, worldOffset.z);
// // // //         controlsRef.current.update();

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);


// // // //     // -------------------- LOAD MODELS --------------------
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(
// // // //                 encodeURI(m.path),
// // // //                 (gltf) => {
// // // //                     modelTemplates.current[m.name] = SkeletonUtils.clone(gltf.scene);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 },
// // // //                 undefined,
// // // //                 (error) => {
// // // //                     console.error("Failed to load model:", m.name, error);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 }
// // // //             );
// // // //         });
// // // //     }, []);

// // // //     // -------------------- LOAD ENTITIES --------------------

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         // const baseDimZ = selectedScenario.dimensionZ ?? 18;
// // // //         // const extraZ = 8;
// // // //         // const dimZ = baseDimZ + extraZ;
// // // //         // const worldOffsetZ = -(dimZ / 2) * GRID_SIZE;
// // // //         // const worldOffsetX = 0;
// // // //         const { dimZ } = boardSizeRef.current;
// // // //         const worldOffsetZ = -(dimZ) * GRID_SIZE;
// // // //         const worldOffsetX = 0;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = SkeletonUtils.clone(tpl);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //             const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;
// // // //             model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //             const rotY = degToRad(e.rotY || 0);
// // // //             const frontCorrection = new THREE.Quaternion();
// // // //             frontCorrection.setFromEuler(new THREE.Euler(0, -tpl.rotation.y, 0));

// // // //             const userRot = new THREE.Quaternion();
// // // //             userRot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);

// // // //             model.quaternion.copy(userRot).premultiply(frontCorrection);

// // // //             model.rotation.x = degToRad(e.rotX || 0);
// // // //             model.rotation.z = degToRad(e.rotZ || 0);

// // // //             // const pivotHelper = new THREE.AxesHelper(50)
// // // //             // model.add(pivotHelper)

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);

// // // //             // console.log(`${e.name}:`, {
// // // //             //     rotX: radToDeg(model.rotation.x),
// // // //             //     rotY: radToDeg(model.rotation.y),
// // // //             //     rotZ: radToDeg(model.rotation.z),
// // // //             //     posX: model.position.x,
// // // //             //     posZ: model.position.z,
// // // //             // });
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);


// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         // Klonujemy model
// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         // Korekta wysokości
// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         // Pozycja w świecie (bez dodatkowego offsetu)
// // // //     //         // const worldX = gridX * GRID_SIZE;
// // // //     //         // const worldZ = -gridZ * GRID_SIZE;
// // // //     //         // model.position.set(worldX, model.userData.heightOffset, worldZ);
// // // //     //         console.log('selectedScenario.dimensionZ', selectedScenario.dimensionZ)

// // // //     //         const dimZ = selectedScenario.dimensionZ + selectedScenario.dimensionZ ?? 18;
// // // //     //         const worldOffsetZ = -(dimZ / 2) * GRID_SIZE;

// // // //     //         // const worldOffsetZ = -((selectedScenario.dimensionZ || 18 - 1) / 2) * GRID_SIZE;
// // // //     //         const worldOffsetX = 0; // dla osi X w Twoim przypadku nie trzeba zmieniać

// // // //     //         const worldX = gridX * GRID_SIZE + worldOffsetX;
// // // //     //         const worldZ = -gridZ * GRID_SIZE + worldOffsetZ;

// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         // ----------------------
// // // //     //         // Rotacja Y + automatyczna korekta frontu
// // // //     //         // ----------------------
// // // //     //         const rotY = degToRad(e.rotY || 0);

// // // //     //         const frontCorrection = new THREE.Quaternion();
// // // //     //         frontCorrection.setFromEuler(new THREE.Euler(0, -tpl.rotation.y, 0));

// // // //     //         const userRot = new THREE.Quaternion();
// // // //     //         userRot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);

// // // //     //         model.quaternion.copy(userRot).premultiply(frontCorrection);

// // // //     //         // Rotacja X i Z z danych
// // // //     //         model.rotation.x = degToRad(e.rotX || 0);
// // // //     //         model.rotation.z = degToRad(e.rotZ || 0);

// // // //     //         // Dodajemy do sceny
// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);

// // // //     //         console.log(`${e.name}:`, {
// // // //     //             rotX: radToDeg(model.rotation.x),
// // // //     //             rotY: radToDeg(model.rotation.y),
// // // //     //             rotZ: radToDeg(model.rotation.z),
// // // //     //             posX: model.position.x,
// // // //     //             posZ: model.position.z,
// // // //     //         });
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);





// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;


// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = -gridX * GRID_SIZE;
// // // //     //         const worldZ = gridZ * GRID_SIZE;
// // // //     //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //     //         // let rotXDeg = normalizeDeg(radToDeg(e.rotX || 0));
// // // //     //         // let rotYDeg = normalizeDeg(radToDeg(e.rotY || 0));
// // // //     //         // let rotZDeg = normalizeDeg(radToDeg(e.rotZ || 0));


// // // //     //         // model.rotation.set(degToRad(rotXDeg), degToRad(rotYDeg), degToRad(rotZDeg));

// // // //     //         model.rotation.set(
// // // //     //             degToRad(e.rotX || 0),
// // // //     //             degToRad(e.rotY || 0) + Math.PI,
// // // //     //             degToRad(e.rotZ || 0)
// // // //     //         );

// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);

// // // //     //         console.log(`${e.name}:`, radToDeg(model.rotation.x), radToDeg(model.rotation.y), radToDeg(model.rotation.z));

// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);

// // // //     // -------------------- ADD / DELETE --------------------
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = SkeletonUtils.clone(tpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = -model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter((o) => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         console.log('objectsRef.current', objectsRef.current)

// // // //         const entities = objectsRef.current
// // // //             .filter((o) => o.userData.isModel)
// // // //             .map((o) => ({
// // // //                 name: o.name,
// // // //                 posX: o.userData.gridX ?? 0,
// // // //                 posY: 0,
// // // //                 posZ: o.userData.gridZ ?? 0,
// // // //                 rotX: normalizeDeg(radToDeg(-o.rotation.x)),
// // // //                 rotY: normalizeDeg(radToDeg(o.rotation.y)),
// // // //                 rotZ: normalizeDeg(radToDeg(o.rotation.z)),
// // // //             }));

// // // //         // const entities = objectsRef.current
// // // //         //     .filter((o) => o.userData.isModel)
// // // //         //     .map((o) => {
// // // //         //         const tpl = modelTemplates.current[o.name];
// // // //         //         const templateRotY = tpl ? radToDeg(tpl.rotation.y) : 0;

// // // //         //         return {
// // // //         //             name: o.name,
// // // //         //             posX: o.userData.gridX ?? 0,
// // // //         //             posY: 0,
// // // //         //             posZ: o.userData.gridZ ?? 0,
// // // //         //             rotX: normalizeDeg(radToDeg(o.rotation.x)),
// // // //         //             rotY: normalizeDeg(radToDeg(o.rotation.y) + templateRotY),
// // // //         //             rotZ: normalizeDeg(radToDeg(o.rotation.z)),
// // // //         //         };
// // // //         //     });


// // // //         console.log('entities', entities)

// // // //         try {
// // // //             const response = await fetch(
// // // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //                 {
// // // //                     method: "PATCH",
// // // //                     headers: { "Content-Type": "application/json" },
// // // //                     body: JSON.stringify({
// // // //                         stationName: selectedScenario.stationName,
// // // //                         dimensionX: selectedScenario.dimensionX,
// // // //                         dimensionY: selectedScenario.dimensionY,
// // // //                         entities,
// // // //                     }),
// // // //                 }
// // // //             );

// // // //             if (!response.ok) {
// // // //                 const errorData = await response.json();
// // // //                 throw new Error(errorData.message || "Nie udało się zapisać stacji");
// // // //             }

// // // //             setAlertMessage("Stacja zapisana pomyślnie!");
// // // //             setAlertSeverity("success");
// // // //             setAlertOpen(true);
// // // //         } catch (error) {
// // // //             console.error(error);
// // // //             setAlertMessage(`Błąd: ${error.message}`);
// // // //             setAlertSeverity("error");
// // // //             setAlertOpen(true);
// // // //         }
// // // //     };


// // // //     const rotate90Right = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         selectedObjectRef.current.rotation.y -= Math.PI / 2;
// // // //         const step = Math.PI / 2;
// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };

// // // //     const rotate90Left = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         selectedObjectRef.current.rotation.y += Math.PI / 2;
// // // //         const step = Math.PI / 2;
// // // //         selectedObjectRef.current.rotation.y = Math.round(selectedObjectRef.current.rotation.y / step) * step;
// // // //     };


// // // //     // -------------------- KEYBOARD --------------------

// // // //     useEffect(() => {
// // // //         // Przesuwanie płynne
// // // //         const moveGrid = (dx, dz) => {
// // // //             if (!selectedObjectRef.current) return;

// // // //             const obj = selectedObjectRef.current;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             const halfWidth = Math.floor(dimX / 2) * GRID_SIZE;
// // // //             const maxDepth = (dimZ - 1) * GRID_SIZE;

// // // //             let worldX = obj.position.x + dx;
// // // //             let worldZ = obj.position.z + dz;

// // // //             worldX = Math.max(-halfWidth, Math.min(halfWidth, worldX));
// // // //             worldZ = Math.max(-maxDepth, Math.min(0, worldZ));

// // // //             obj.position.set(
// // // //                 worldX,
// // // //                 obj.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             const gridX = Math.round(worldX / GRID_SIZE);
// // // //             const rawGridZ = Math.round(worldZ / GRID_SIZE);
// // // //             const maxZ = dimZ - 1;
// // // //             const gridZ = -maxZ - rawGridZ;

// // // //             console.log(
// // // //                 `[MOVE] ${selectedObjectRef.current.name} | Grid: X:(${gridX}, Z:${gridZ}) | Position: (${round3(worldX)}, ${round3(selectedObjectRef.current.position.y)}, ${round3(worldZ)}) | Rotation Y: ${normalizeDeg(radToDeg(selectedObjectRef.current.rotation.y))}`
// // // //             );

// // // //             obj.userData.gridX = gridX;
// // // //             obj.userData.gridZ = gridZ;
// // // //         };


// // // //         // //  Przesuwanie skokowe

// // // //         //     const moveGrid = (dx, dz) => {
// // // //         //         if (!selectedObjectRef.current) return;

// // // //         //         const { dimX, dimZ } = boardSizeRef.current;
// // // //         //         const GRID = GRID_SIZE;

// // // //         //         let gridX = selectedObjectRef.current.userData.gridX ?? 0;
// // // //         //         let gridZ = selectedObjectRef.current.userData.gridZ ?? 0;

// // // //         //         gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX + dx));
// // // //         //         gridZ = Math.max(-dimZ, Math.min(0, gridZ + dz));

// // // //         //         selectedObjectRef.current.userData.gridX = gridX;
// // // //         //         selectedObjectRef.current.userData.gridZ = gridZ;

// // // //         //         const worldX = -gridX * GRID;
// // // //         //         const worldZ = gridZ * GRID;

// // // //         //         selectedObjectRef.current.position.set(
// // // //         //             worldX,
// // // //         //             selectedObjectRef.current.userData.heightOffset || 0,
// // // //         //             worldZ
// // // //         //         );

// // // //         //     };

// // // //         const onKeyDown = (e) => {
// // // //             if (
// // // //                 e.target.tagName === "INPUT" ||
// // // //                 e.target.tagName === "TEXTAREA" ||
// // // //                 e.target.isContentEditable
// // // //             ) return;

// // // //             switch (e.key.toLowerCase()) {

// // // //                 case "w":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, 5);
// // // //                     break;
// // // //                 case "s":
// // // //                     e.preventDefault();
// // // //                     moveGrid(0, -5);
// // // //                     break;
// // // //                 case "a":
// // // //                     e.preventDefault();
// // // //                     moveGrid(5, 0);
// // // //                     break;
// // // //                 case "d":
// // // //                     e.preventDefault();
// // // //                     moveGrid(-5, 0);
// // // //                     break;

// // // //                 case "arrowleft":
// // // //                     e.preventDefault();
// // // //                     rotate90Left();
// // // //                     break;
// // // //                 case "arrowright":
// // // //                     e.preventDefault();
// // // //                     rotate90Right();
// // // //                     break;

// // // //                 case "delete":
// // // //                 case "backspace":
// // // //                     e.preventDefault();
// // // //                     deleteSelected();
// // // //                     break;

// // // //                 case "escape":
// // // //                     selectedObjectRef.current = null;
// // // //                     setSelectedObject(null);
// // // //                     break;

// // // //                 default:
// // // //                     break;
// // // //             }
// // // //         };

// // // //         window.addEventListener("keydown", onKeyDown);
// // // //         return () => window.removeEventListener("keydown", onKeyDown);
// // // //     }, []);




// // // //     // const logRotation = () => {
// // // //     //     console.log("rotation", selectedObjectRef.current.rotation)
// // // //     // }

// // // //     // -------------------- RENDER --------------------
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 80, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: "center" }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <ModelList addModel={addModel} MODEL_LIBRARY={MODEL_LIBRARY} />
// // // //                 {/* <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // // //                     {MODEL_LIBRARY.filter((m) => m.visible).map((m) => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List> */}

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row" sx={{ justifyContent: 'center' }}>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Left}
// // // //                             >
// // // //                                 <Rotate90DegreesCcwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = 1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onMouseDown={() => (rotationDirRef.current = -1)}
// // // //                                 onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                             >
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton
// // // //                                 onClick={rotate90Right}
// // // //                             >
// // // //                                 <Rotate90DegreesCwIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                             {/* <Button onClick={logRotation}>
// // // //                                 log
// // // //                             </Button> */}
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {/* {gridWalls && <GridWalls scene={sceneRef.current} boardSizeRef={boardSizeRef.current} GRID_SIZE={GRID_SIZE} />} */}
// // // //             {gridWalls && <GridWalls
// // // //                 scene={sceneRef.current}
// // // //                 dimX={boardSizeRef.current.dimX}
// // // //                 dimZ={boardSizeRef.current.dimZ}
// // // //                 GRID_SIZE={GRID_SIZE}
// // // //                 cameraRef={cameraRef}
// // // //             />}

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${boardSize.dimX}x${boardSize.dimZ}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{
// // // //                     mt: 1,
// // // //                     position: "absolute",
// // // //                     top: 70,
// // // //                     left: "50%",
// // // //                     transform: "translateX(-50%)",
// // // //                     fontSize: "10px",
// // // //                 }}
// // // //             >
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography
// // // //                 variant="h6"
// // // //                 sx={{ mt: 1, color: "#6cc6d9", position: "absolute", top: 90, left: "50%", transform: "translateX(-50%)" }}
// // // //             >
// // // //                 {/* {selectedScenario?.existingSource} */}
// // // //                 {
// // // //                     selectedScenario?.stationName
// // // //                         ? selectedScenario.stationName
// // // //                         : selectedScenario?.id !== undefined
// // // //                             ? `Stacja ${selectedScenario.id}`
// // // //                             : "nie wybrano"
// // // //                 }
// // // //             </Typography>

// // // //             <IconButton
// // // //                 onClick={() => setGridWalls(prev => !prev)}
// // // //                 sx={{ position: 'absolute', bottom: 20, right: 20 }}
// // // //             >
// // // //                 {gridWalls ? <GridOffIcon /> : <GridOnIcon />}

// // // //             </IconButton>

// // // //             <Snackbar
// // // //                 open={alertOpen}
// // // //                 autoHideDuration={4000}
// // // //                 onClose={() => setAlertOpen(false)}
// // // //                 anchorOrigin={{ vertical: "top", horizontal: "center" }}
// // // //                 sx={{ mt: 8 }}
// // // //             >
// // // //                 <Alert
// // // //                     onClose={() => setAlertOpen(false)}
// // // //                     severity={alertSeverity}
// // // //                     sx={{ width: "100%" }}
// // // //                 >
// // // //                     {alertMessage}
// // // //                 </Alert>
// // // //             </Snackbar>

// // // //         </>
// // // //     );
// // // // }




// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import DeleteIcon from "@mui/icons-material/Delete";

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);

// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);

// // // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //     const degToRad = (deg) => deg * (Math.PI / 180);
// // // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE                                                            */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const centerX = 0;
// // // //         const centerZ = -((dimZ - 1) / 2) * GRID_SIZE;

// // // //         const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 10000);
// // // //         camera.position.set(centerX + 500, 800, centerZ + 1300);
// // // //         camera.lookAt(centerX, 0, centerZ);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(centerX, 0, centerZ);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;
// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();
// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;
// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             let gridX = Math.round(-p.x / GRID_SIZE);
// // // //             let gridZ = Math.round(p.z / GRID_SIZE);
// // // //             gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //             gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             highlight?.update?.();
// // // //         };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => renderer.domElement.remove();
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  GRID + PLANE                                                          */
// // // //     /* ===================================================================== */
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];
// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // // //         }
// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // // //         }
// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current) return;

// // // //         sceneRef.current.children
// // // //             .filter(o => o.userData?.isGrid)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter(o => o.userData?.isModel)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(0, 0, -((dimZ - 1) / 2) * GRID_SIZE);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODELS                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(
// // // //                 encodeURI(m.path),
// // // //                 (gltf) => {
// // // //                     modelTemplates.current[m.name] = SkeletonUtils.clone(gltf.scene);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 },
// // // //                 undefined,
// // // //                 (error) => {
// // // //                     console.error("Failed to load model:", m.name, error);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 }
// // // //             );
// // // //         });
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD ENTITIES                                                        */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = SkeletonUtils.clone(tpl);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;
// // // //             model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //             // --- ROTACJE ---
// // // //             let rotXDeg = normalizeDeg(radToDeg(e.rotX || 0));
// // // //             let rotYDeg = normalizeDeg(radToDeg(e.rotY || 0)); // jeśli chcesz możesz dodać przesunięcie np. -180
// // // //             let rotZDeg = normalizeDeg(radToDeg(e.rotZ || 0));

// // // //             model.rotation.set(
// // // //                 degToRad(rotXDeg),
// // // //                 degToRad(rotYDeg),
// // // //                 degToRad(rotZDeg)
// // // //             );

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);

// // // //     /* ===================================================================== */
// // // //     /*  ADD / DELETE / SAVE                                                   */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = SkeletonUtils.clone(tpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = -model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(o => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => ({
// // // //                 name: o.name,
// // // //                 posX: o.userData.gridX ?? 0,
// // // //                 posY: 0,
// // // //                 posZ: o.userData.gridZ ?? 0,
// // // //                 rotX: normalizeDeg(radToDeg(o.rotation.x)),
// // // //                 rotY: normalizeDeg(radToDeg(o.rotation.y)),
// // // //                 rotZ: normalizeDeg(radToDeg(o.rotation.z)),
// // // //             }));

// // // //         await fetch(`${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`, {
// // // //             method: "PATCH",
// // // //             headers: { "Content-Type": "application/json" },
// // // //             body: JSON.stringify({
// // // //                 existingSource: selectedScenario.existingSource,
// // // //                 dimensionX: selectedScenario.dimensionX,
// // // //                 dimensionY: selectedScenario.dimensionY,
// // // //                 entities,
// // // //             }),
// // // //         });
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  RENDER                                                               */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 16, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // // //                     {MODEL_LIBRARY.filter(m => m.visible).map(m => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row">
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = 1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = -1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography variant="h6" sx={{ mt: 1, position: "absolute", top: 10, left: '50%', transform: 'translateX(-50%)', fontSize: '10px' }}>
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography variant="h6" sx={{ mt: 1, color: '#6cc6d9', position: "absolute", top: 30, left: '50%', transform: 'translateX(-50%)' }}>
// // // //                 {selectedScenario?.existingSource}
// // // //             </Typography>
// // // //         </>
// // // //     );
// // // // }







// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import DeleteIcon from "@mui/icons-material/Delete";

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);

// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);

// // // //     const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //     const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //     const degToRad = (deg) => deg * (Math.PI / 180);
// // // //     const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE                                                            */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const centerX = 0;
// // // //         const centerZ = -((dimZ - 1) / 2) * GRID_SIZE;

// // // //         const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 10000);
// // // //         camera.position.set(centerX + 500, 800, centerZ + 1300);
// // // //         camera.lookAt(centerX, 0, centerZ);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(centerX, 0, centerZ);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;
// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();
// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;
// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;
// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             let gridX = Math.round(-p.x / GRID_SIZE);
// // // //             let gridZ = Math.round(p.z / GRID_SIZE);
// // // //             gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //             gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));


// // // //             console.log('gridX', gridX)
// // // //             console.log('gridZ', gridZ)

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             highlight?.update?.();
// // // //         };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => renderer.domElement.remove();
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  GRID + PLANE                                                          */
// // // //     /* ===================================================================== */
// // // //     const createRectGrid = (
// // // //         width,
// // // //         height,
// // // //         gridSize,
// // // //         color = 0x808080,
// // // //         offsetX = 1,
// // // //         offsetZ = 1
// // // //     ) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;
// // // //         const ox = offsetX * gridSize;
// // // //         const oz = offsetZ * gridSize;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize + ox;
// // // //             vertices.push(
// // // //                 x, 0, oz,
// // // //                 x, 0, oz - height * gridSize
// // // //             );
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize + oz;
// // // //             vertices.push(
// // // //                 -halfWidth * gridSize + ox, 0, z,
// // // //                 halfWidth * gridSize + ox, 0, z
// // // //             );
// // // //         }

// // // //         geometry.setAttribute(
// // // //             "position",
// // // //             new THREE.Float32BufferAttribute(vertices, 3)
// // // //         );

// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };
// // // //     // const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //     //     const material = new THREE.LineBasicMaterial({ color });
// // // //     //     const geometry = new THREE.BufferGeometry();
// // // //     //     const vertices = [];
// // // //     //     const halfWidth = width / 2;

// // // //     //     for (let i = 0; i <= width; i++) {
// // // //     //         const x = (i - halfWidth) * gridSize;
// // // //     //         vertices.push(x, 0, 0, x, 0, -height * gridSize);
// // // //     //     }
// // // //     //     for (let i = 0; i <= height; i++) {
// // // //     //         const z = -i * gridSize;
// // // //     //         vertices.push(-halfWidth * gridSize, 0, z, halfWidth * gridSize, 0, z);
// // // //     //     }
// // // //     //     geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
// // // //     //     const grid = new THREE.LineSegments(geometry, material);
// // // //     //     grid.userData.isGrid = true;
// // // //     //     return grid;
// // // //     // };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current) return;

// // // //         sceneRef.current.children
// // // //             .filter(o => o.userData?.isGrid)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter(o => o.userData?.isModel)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(0, 0, -((dimZ - 1) / 2) * GRID_SIZE);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODELS                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(
// // // //                 encodeURI(m.path),
// // // //                 (gltf) => {
// // // //                     modelTemplates.current[m.name] = SkeletonUtils.clone(gltf.scene);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 },
// // // //                 undefined,
// // // //                 (error) => {
// // // //                     console.error("Failed to load model:", m.name, error);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 }
// // // //             );
// // // //         });
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD ENTITIES                                                        */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = SkeletonUtils.clone(tpl);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;
// // // //             model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //             // --- ROTACJE ---
// // // //             let rotXDeg = normalizeDeg(radToDeg(e.rotX || 0));
// // // //             let rotYDeg = normalizeDeg(radToDeg(e.rotY || 0)); // jeśli chcesz możesz dodać przesunięcie np. -180
// // // //             let rotZDeg = normalizeDeg(radToDeg(e.rotZ || 0));

// // // //             model.rotation.set(
// // // //                 degToRad(rotXDeg),
// // // //                 degToRad(rotYDeg),
// // // //                 degToRad(rotZDeg)
// // // //             );

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);

// // // //     /* ===================================================================== */
// // // //     /*  ADD / DELETE / SAVE                                                   */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = SkeletonUtils.clone(tpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = -model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;
// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(o => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => ({
// // // //                 name: o.name,
// // // //                 posX: o.userData.gridX ?? 0,
// // // //                 posY: 0,
// // // //                 posZ: o.userData.gridZ ?? 0,
// // // //                 rotX: normalizeDeg(radToDeg(o.rotation.x)),
// // // //                 rotY: normalizeDeg(radToDeg(o.rotation.y)),
// // // //                 rotZ: normalizeDeg(radToDeg(o.rotation.z)),
// // // //             }));

// // // //         await fetch(`${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`, {
// // // //             method: "PATCH",
// // // //             headers: { "Content-Type": "application/json" },
// // // //             body: JSON.stringify({
// // // //                 existingSource: selectedScenario.existingSource,
// // // //                 dimensionX: selectedScenario.dimensionX,
// // // //                 dimensionY: selectedScenario.dimensionY,
// // // //                 entities,
// // // //             }),
// // // //         });
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  RENDER                                                               */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 16, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <List dense sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
// // // //                     {MODEL_LIBRARY.filter(m => m.visible).map(m => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row">
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = 1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = -1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography variant="h6" sx={{ mt: 1, position: "absolute", top: 10, left: '50%', transform: 'translateX(-50%)', fontSize: '10px' }}>
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography variant="h6" sx={{ mt: 1, color: '#6cc6d9', position: "absolute", top: 30, left: '50%', transform: 'translateX(-50%)' }}>
// // // //                 {selectedScenario?.existingSource}
// // // //             </Typography>
// // // //         </>
// // // //     );
// // // // }





// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import DeleteIcon from "@mui/icons-material/Delete";

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // ];


// // // // // const MODEL_LIBRARY = [
// // // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // // //     { name: "envp_furnitures_table", path: "/envp_furnitures_table/envp_furnitures_table_160_80_graphics.gltf", visible: true },
// // // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);

// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE                                                            */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const centerX = 0;
// // // //         const centerZ = -((dimZ - 1) / 2) * GRID_SIZE;

// // // //         const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 10000);
// // // //         camera.position.set(centerX + 500, 800, centerZ + 1300);
// // // //         camera.lookAt(centerX, 0, centerZ);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(centerX, 0, centerZ);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             let gridX = Math.round(-p.x / GRID_SIZE);
// // // //             let gridZ = Math.round(p.z / GRID_SIZE);
// // // //             gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //             gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             highlight?.update?.();
// // // //         };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => {
// // // //             renderer.domElement.remove();
// // // //         };
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  GRID + PLANE                                                          */
// // // //     /* ===================================================================== */
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0);
// // // //             vertices.push(x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z);
// // // //             vertices.push(halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));

// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;

// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current) return;

// // // //         sceneRef.current.children
// // // //             .filter(o => o.userData?.isGrid)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter(o => o.userData?.isModel)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(0, 0, -((dimZ - 1) / 2) * GRID_SIZE);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODELS                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(
// // // //                 encodeURI(m.path),
// // // //                 (gltf) => {
// // // //                     modelTemplates.current[m.name] = SkeletonUtils.clone(gltf.scene);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 },
// // // //                 undefined,
// // // //                 (error) => {
// // // //                     console.error("Failed to load model:", m.name, error);
// // // //                     loaded++;
// // // //                     if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //                 }
// // // //             );
// // // //         });
// // // //     }, []);


// // // //     /* ===================================================================== */
// // // //     /*  LOAD ENTITIES                                                        */
// // // //     /* ===================================================================== */
// // // //     // useEffect(() => {
// // // //     //     if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //     //     const dimX = selectedScenario?.dimensionX || 18;
// // // //     //     const dimZ = selectedScenario?.dimensionZ || 18;

// // // //     //     selectedScenario.entities.forEach((e) => {
// // // //     //         const tpl = modelTemplates.current[e.name];
// // // //     //         if (!tpl) return;

// // // //     //         const model = SkeletonUtils.clone(tpl);
// // // //     //         model.name = e.name;
// // // //     //         model.scale.set(50, 50, 50);
// // // //     //         model.userData.isModel = true;

// // // //     //         const box = new THREE.Box3().setFromObject(model);
// // // //     //         model.userData.heightOffset = -box.min.y;

// // // //     //         const gridX = e.posX ?? 0;
// // // //     //         const gridZ = e.posZ ?? 0;
// // // //     //         model.userData.gridX = gridX;
// // // //     //         model.userData.gridZ = gridZ;

// // // //     //         const worldX = -gridX * GRID_SIZE;
// // // //     //         const worldZ = gridZ * GRID_SIZE;

// // // //     //         model.position.set(
// // // //     //             worldX,
// // // //     //             model.userData.heightOffset,
// // // //     //             worldZ
// // // //     //         );

// // // //     //         const degToRad = (deg) => (deg * Math.PI) / 180;

// // // //     //         model.rotation.set(
// // // //     //             e.rotX ? degToRad(e.rotX) : 0,
// // // //     //             e.rotY ? degToRad(e.rotY) : 0,
// // // //     //             e.rotZ ? degToRad(e.rotZ) : 0
// // // //     //         );

// // // //     //         model.userData.rotNormalized = (model.rotation.y % (2 * Math.PI)) / (2 * Math.PI);

// // // //     //         sceneRef.current.add(model);
// // // //     //         objectsRef.current.push(model);
// // // //     //     });
// // // //     // }, [modelsLoaded, selectedScenario]);

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = SkeletonUtils.clone(tpl);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;

// // // //             model.position.set(
// // // //                 worldX,
// // // //                 model.userData.heightOffset,
// // // //                 worldZ
// // // //             );

// // // //             // const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //             // const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //             // const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);

// // // //             // const rotYDeg = normalizeDeg(radToDeg(e.rotY || 0) - 180);


// // // //             const radToDeg = (rad) => rad * (180 / Math.PI);
// // // //             const round3 = (num) => Math.round(num * 1000) / 1000;
// // // //             const normalizeDeg = (deg) => round3(((deg % 360) + 360) % 360);

// // // //             const rotYDeg = normalizeDeg(radToDeg(e.rotY || 0) - 90);

// // // //             model.rotation.set(e.rotX || 0, rotYDeg || 0, e.rotZ || 0);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);

// // // //     /* ===================================================================== */
// // // //     /*  ADD / DELETE / SAVE                                                   */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = SkeletonUtils.clone(tpl);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = -model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;

// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(o => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => ({
// // // //                 name: o.name,
// // // //                 posX: o.userData.gridX ?? 0,
// // // //                 posY: 0,
// // // //                 posZ: o.userData.gridZ ?? 0,
// // // //                 rotX: o.rotation.x,
// // // //                 rotY: o.rotation.y,
// // // //                 rotZ: o.rotation.z,
// // // //             }));

// // // //         await fetch(`${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`, {
// // // //             method: "PATCH",
// // // //             headers: { "Content-Type": "application/json" },
// // // //             body: JSON.stringify({
// // // //                 existingSource: selectedScenario.existingSource,
// // // //                 dimensionX: selectedScenario.dimensionX,
// // // //                 dimensionY: selectedScenario.dimensionY,
// // // //                 entities,
// // // //             }),
// // // //         });
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  RENDER                                                               */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 16, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <List dense sx={{
// // // //                     maxHeight: 300,
// // // //                     overflowY: "auto",
// // // //                     overflowX: "hidden",
// // // //                 }}>
// // // //                     {MODEL_LIBRARY.filter(m => m.visible).map(m => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row">
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = 1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = -1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {sceneRef.current && cameraRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}


// // // //             <Typography variant="h6" sx={{ mt: 1, position: "absolute", top: 10, left: '50%', transform: 'translateX(-50%)', fontSize: '10px' }}>
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography variant="h6" sx={{ mt: 1, color: '#6cc6d9', position: "absolute", top: 30, left: '50%', transform: 'translateX(-50%)' }}>
// // // //                 {selectedScenario?.existingSource}
// // // //             </Typography>
// // // //         </>
// // // //     );
// // // // }



// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import DeleteIcon from "@mui/icons-material/Delete";

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_furnitures_table", path: "/envp_furnitures_table/envp_furnitures_table_160_80_graphics.gltf", visible: true },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // ];


// // // // // const MODEL_LIBRARY = [
// // // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // // //     { name: "envp_bin_4", path: "/envp_bin_4/envp_bin_4.gltf", visible: true },
// // // // //     { name: "envp_bin_4_lids", path: "/envp_bin_4_lids/envp_bin_4_lids.gltf", visible: true },
// // // // //     { name: "envp_bin_lid", path: "/envp_bin_lid/envp_bin_lid.gltf", visible: true },
// // // // //     { name: "envp_furnitures_chair", path: "/envp_furnitures_chair/envp_furnitures_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_office_chair", path: "/envp_furnitures_office_chair/envp_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_2", path: "/envp_furnitures_sofa_corner_3_2/envp_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_corner_3_3", path: "/envp_furnitures_sofa_corner_3_3/envp_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_line_3", path: "/envp_furnitures_sofa_line_3/envp_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single", path: "/envp_furnitures_sofa_single/envp_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "envp_furnitures_sofa_single_corner", path: "/envp_furnitures_sofa_single_corner/envp_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_140_80", path: "/envp_furnitures_table_140_80/envp_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_160_80", path: "/envp_furnitures_table_160_80/envp_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_250_120", path: "/envp_furnitures_table_250_120/envp_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_400_120", path: "/envp_furnitures_table_400_120/envp_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x100", path: "/envp_furnitures_table_small_100x100x100/envp_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_100x100x75", path: "/envp_furnitures_table_small_100x100x75/envp_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x100", path: "/envp_furnitures_table_small_125x100x100/envp_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_125x100x75", path: "/envp_furnitures_table_small_125x100x75/envp_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x100", path: "/envp_furnitures_table_small_75x75x100/envp_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "envp_furnitures_table_small_75x75x75", path: "/envp_furnitures_table_small_75x75x75/envp_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "envp_modular_door_frame", path: "/envp_modular_door_frame/envp_modular_door_frame.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized", path: "/env_countertop_flat_optimized/env_countertop_flat_optimized.gltf", visible: true },
// // // // //     { name: "env_countertop_flat_optimized_drawers", path: "/env_countertop_flat_optimized_drawers/env_countertop_flat_optimized_drawers.gltf", visible: true },
// // // // //     { name: "env_kitchen_cook_station", path: "/env_kitchen_cook_station/env_kitchen_cook_station.gltf", visible: true },
// // // // //     { name: "env_sink", path: "/env_sink/env_sink.gltf", visible: true },
// // // // //     { name: "env_sink_hanged_soap", path: "/env_sink_hanged_soap/env_sink_hanged_soap.gltf", visible: true },
// // // // //     { name: "env_wall_creator_x2_5", path: "/env_wall_creator_x2_5/env_wall_creator_x2_5.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_2", path: "/exe_cooking_countertop_2/exe_cooking_countertop_2.gltf", visible: true },
// // // // //     { name: "exe_cooking_countertop_4", path: "/exe_cooking_countertop_4/exe_cooking_countertop_4.gltf", visible: true },
// // // // //     { name: "exe_cooking_drawers_ingrediens", path: "/exe_cooking_drawers_ingrediens/exe_cooking_drawers_ingrediens.gltf", visible: true },
// // // // //     { name: "exe_cooking_fridges_all", path: "/exe_cooking_fridges_all/exe_cooking_fridges_all.gltf", visible: true },
// // // // //     { name: "exe_cooking_utensils", path: "/exe_cooking_utensils/exe_cooking_utensils.gltf", visible: true },
// // // // //     { name: "exe_crm_doc_container", path: "/exe_crm_doc_container/exe_crm_doc_container.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_1", path: "/exe_graphic_cc_logos_1/exe_graphic_cc_logos_1.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_2", path: "/exe_graphic_cc_logos_2/exe_graphic_cc_logos_2.gltf", visible: true },
// // // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // // //     { name: "exe_talentedkids_pyramid", path: "/exe_talentedkids_pyramid/exe_talentedkids_pyramid.gltf", visible: true },
// // // // //     { name: "exe_wd_22bolt_22nut_impactdriver", path: "/exe_wd_22bolt_22nut_impactdriver/exe_wd_22bolt_22nut_impactdriver.gltf", visible: true },
// // // // //     { name: "exe_wd_technical_door_supply", path: "/exe_wd_technical_door_supply/exe_wd_technical_door_supply.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_installation", path: "/exe_wd_vertical_window_installation/exe_wd_vertical_window_installation.gltf", visible: true },
// // // // //     { name: "exe_wd_vertical_window_supply", path: "/exe_wd_vertical_window_supply/exe_wd_vertical_window_supply.gltf", visible: true },
// // // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // //     { name: "mech_cooker", path: "/mech_cooker/mech_cooker.gltf", visible: true },
// // // // //     { name: "mech_furnace_1", path: "/mech_furnace_1/mech_furnace_1.gltf", visible: true },
// // // // //     { name: "model_bin_4_kitchen", path: "/model_bin_4_kitchen/model_bin_4_kitchen.gltf", visible: true },
// // // // //     { name: "model_bin_lid", path: "/model_bin_lid/model_bin_lid.gltf", visible: true },
// // // // //     { name: "model_furnitures_chair", path: "/model_furnitures_chair/model_furnitures_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_office_chair", path: "/model_furnitures_office_chair/model_furnitures_office_chair.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_2", path: "/model_furnitures_sofa_2/model_furnitures_sofa_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_2", path: "/model_furnitures_sofa_corner_3_2/model_furnitures_sofa_corner_3_2.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_corner_3_3", path: "/model_furnitures_sofa_corner_3_3/model_furnitures_sofa_corner_3_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_line_3", path: "/model_furnitures_sofa_line_3/model_furnitures_sofa_line_3.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single", path: "/model_furnitures_sofa_single/model_furnitures_sofa_single.gltf", visible: true },
// // // // //     { name: "model_furnitures_sofa_single_corner", path: "/model_furnitures_sofa_single_corner/model_furnitures_sofa_single_corner.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_140_80", path: "/model_furnitures_table_140_80/model_furnitures_table_140_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_160_80", path: "/model_furnitures_table_160_80/model_furnitures_table_160_80.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_250_120", path: "/model_furnitures_table_250_120/model_furnitures_table_250_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_400_120", path: "/model_furnitures_table_400_120/model_furnitures_table_400_120.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x100", path: "/model_furnitures_table_small_100x100x100/model_furnitures_table_small_100x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_100x100x75", path: "/model_furnitures_table_small_100x100x75/model_furnitures_table_small_100x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x100", path: "/model_furnitures_table_small_125x100x100/model_furnitures_table_small_125x100x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_125x100x75", path: "/model_furnitures_table_small_125x100x75/model_furnitures_table_small_125x100x75.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x100", path: "/model_furnitures_table_small_75x75x100/model_furnitures_table_small_75x75x100.gltf", visible: true },
// // // // //     { name: "model_furnitures_table_small_75x75x75", path: "/model_furnitures_table_small_75x75x75/model_furnitures_table_small_75x75x75.gltf", visible: true },
// // // // //     { name: "slot_exe_wd_technical_door_installation", path: "/slot_exe_wd_technical_door_installation/slot_exe_wd_technical_door_installation.gltf", visible: true }
// // // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);

// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE                                                            */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const centerX = 0;
// // // //         const centerZ = -((dimZ - 1) / 2) * GRID_SIZE;

// // // //         const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 10000);
// // // //         // camera.position.set(500, 800, 1300);
// // // //         camera.position.set(centerX + 500, 800, centerZ + 1300);
// // // //         camera.lookAt(centerX, 0, centerZ);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(centerX, 0, centerZ);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             let gridX = Math.round(-p.x / GRID_SIZE);
// // // //             let gridZ = Math.round(p.z / GRID_SIZE);
// // // //             gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //             gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //             console.log('gridZ', gridZ)
// // // //             console.log('gridX', gridX)

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             highlight?.update?.();
// // // //         };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => {
// // // //             renderer.domElement.remove();
// // // //         };
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  GRID + PLANE                                                          */
// // // //     /* ===================================================================== */
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0);
// // // //             vertices.push(x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z);
// // // //             vertices.push(halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));

// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;

// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current) return;

// // // //         sceneRef.current.children
// // // //             .filter(o => o.userData?.isGrid)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter(o => o.userData?.isModel)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(0, 0, -((dimZ - 1) / 2) * GRID_SIZE);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODELS                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(m.path, (gltf) => {
// // // //                 modelTemplates.current[m.name] = gltf.scene;
// // // //                 loaded++;
// // // //                 if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //             });
// // // //         });
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD ENTITIES                                                        */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = tpl.clone(true);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;

// // // //             model.position.set(
// // // //                 worldX,
// // // //                 model.userData.heightOffset,
// // // //                 worldZ
// // // //             );

// // // //             model.rotation.set(e.rotX || 0, e.rotY || 0, e.rotZ || 0);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);

// // // //     /* ===================================================================== */
// // // //     /*  ADD / DELETE / SAVE                                                   */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = tpl.clone(true);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = -model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;

// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(o => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => ({
// // // //                 name: o.name,
// // // //                 posX: o.userData.gridX ?? 0,
// // // //                 posY: 0,
// // // //                 posZ: o.userData.gridZ ?? 0,
// // // //                 rotX: o.rotation.x,
// // // //                 rotY: o.rotation.y,
// // // //                 rotZ: o.rotation.z,
// // // //             }));

// // // //         await fetch(`${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`, {
// // // //             method: "PATCH",
// // // //             headers: { "Content-Type": "application/json" },
// // // //             body: JSON.stringify({
// // // //                 existingSource: selectedScenario.existingSource,
// // // //                 dimensionX: selectedScenario.dimensionX,
// // // //                 dimensionY: selectedScenario.dimensionY,
// // // //                 entities,
// // // //             }),
// // // //         });
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  RENDER                                                               */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 16, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <List dense sx={{
// // // //                     maxHeight: 300,
// // // //                     overflowY: "auto",
// // // //                     overflowX: "hidden",
// // // //                 }}>
// // // //                     {MODEL_LIBRARY.filter(m => m.visible).map(m => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row">
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = 1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = -1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {sceneRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography variant="h6" sx={{ mt: 1, position: "absolute", top: 10, left: '50%', transform: 'translateX(-50%)', fontSize: '10px' }}>
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography variant="h6" sx={{ mt: 1, color: '#6cc6d9', position: "absolute", top: 30, left: '50%', transform: 'translateX(-50%)' }}>
// // // //                 {selectedScenario?.existingSource}
// // // //             </Typography>
// // // //         </>
// // // //     );
// // // // }





// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import DeleteIcon from "@mui/icons-material/Delete";

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_furnitures_table", path: "/envp_furnitures_table/envp_furnitures_table_160_80_graphics.gltf", visible: true },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });
// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);
// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);

// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE                                                            */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const { dimX, dimZ } = boardSizeRef.current;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const centerX = 0;
// // // //         const centerZ = -((dimZ - 1) / 2) * GRID_SIZE;

// // // //         const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 10000);
// // // //         // camera.position.set(500, 800, 1300);
// // // //         camera.position.set(centerX + 500, 800, centerZ + 1300);
// // // //         camera.lookAt(centerX, 0, centerZ);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controls.target.set(centerX, 0, centerZ);
// // // //         controls.update();
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             let gridX = Math.round(-p.x / GRID_SIZE);
// // // //             let gridZ = Math.round(p.z / GRID_SIZE);
// // // //             gridZ = Math.max(-(dimZ - 1), Math.min(0, gridZ));
// // // //             gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));

// // // //             console.log('gridZ', gridZ)
// // // //             console.log('gridX', gridX)

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             highlight?.update?.();
// // // //         };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => {
// // // //             renderer.domElement.remove();
// // // //         };
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  GRID + PLANE                                                          */
// // // //     /* ===================================================================== */
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0);
// // // //             vertices.push(x, 0, -height * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = -i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z);
// // // //             vertices.push(halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));

// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;

// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current) return;

// // // //         sceneRef.current.children
// // // //             .filter(o => o.userData?.isGrid)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter(o => o.userData?.isModel)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;
// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(0, 0, -((dimZ - 1) / 2) * GRID_SIZE);
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODELS                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(m.path, (gltf) => {
// // // //                 modelTemplates.current[m.name] = gltf.scene;
// // // //                 loaded++;
// // // //                 if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //             });
// // // //         });
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD ENTITIES                                                        */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = tpl.clone(true);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = -gridX * GRID_SIZE;
// // // //             const worldZ = gridZ * GRID_SIZE;

// // // //             model.position.set(
// // // //                 worldX,
// // // //                 model.userData.heightOffset,
// // // //                 worldZ
// // // //             );

// // // //             model.rotation.set(e.rotX || 0, e.rotY || 0, e.rotZ || 0);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);

// // // //     /* ===================================================================== */
// // // //     /*  ADD / DELETE / SAVE                                                   */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = tpl.clone(true);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = -model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;

// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(o => o !== selectedObjectRef.current);
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => ({
// // // //                 name: o.name,
// // // //                 posX: o.userData.gridX ?? 0,
// // // //                 posY: 0,
// // // //                 posZ: o.userData.gridZ ?? 0,
// // // //                 rotX: o.rotation.x,
// // // //                 rotY: o.rotation.y,
// // // //                 rotZ: o.rotation.z,
// // // //             }));

// // // //         await fetch(`${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`, {
// // // //             method: "PATCH",
// // // //             headers: { "Content-Type": "application/json" },
// // // //             body: JSON.stringify({
// // // //                 existingSource: selectedScenario.existingSource,
// // // //                 dimensionX: selectedScenario.dimensionX,
// // // //                 dimensionY: selectedScenario.dimensionY,
// // // //                 entities,
// // // //             }),
// // // //         });
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  RENDER                                                               */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 16, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />
// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <List dense>
// // // //                     {MODEL_LIBRARY.filter(m => m.visible).map(m => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row">
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = 1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = -1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {sceneRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />
// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography variant="h6" sx={{ mt: 1, position: "absolute", top: 10, left: '50%', transform: 'translateX(-50%)', fontSize: '10px' }}>
// // // //                 Wybrana stacja:
// // // //             </Typography>
// // // //             <Typography variant="h6" sx={{ mt: 1, color: '#6cc6d9', position: "absolute", top: 30, left: '50%', transform: 'translateX(-50%)' }}>
// // // //                 {selectedScenario?.existingSource}
// // // //             </Typography>
// // // //         </>
// // // //     );
// // // // }




// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import DeleteIcon from "@mui/icons-material/Delete";

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_furnitures_table", path: "/envp_furnitures_table/envp_furnitures_table_160_80_graphics.gltf", visible: true },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });

// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);

// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);

// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE                                                            */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(500, 800, 1300);
// // // //         camera.lookAt(0, 0, 0);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             // przeliczamy na grid (0..dimZ, -dimX/2..dimX/2)
// // // //             let gridX = Math.round(p.x / GRID_SIZE);
// // // //             let gridZ = Math.round(p.z / GRID_SIZE);

// // // //             // ograniczamy do planszy
// // // //             gridX = Math.max(-Math.floor(dimX / 2), Math.min(Math.floor(dimX / 2), gridX));
// // // //             gridZ = dimZ - 1 - gridZ;
// // // //             gridZ = Math.max(0, Math.min(dimZ - 1, gridZ));


// // // //             console.log('gridX', gridX)
// // // //             console.log('gridZ', gridZ)

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             const worldX = gridX * GRID_SIZE;
// // // //             // const worldZ = gridZ * GRID_SIZE;
// // // //             const worldZ = (dimZ - 1 - gridZ) * GRID_SIZE;

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             highlight?.update?.();
// // // //         };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y +=
// // // //                     0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => {
// // // //             renderer.domElement.remove();
// // // //         };
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  GRID + PLANE                                                          */
// // // //     /* ===================================================================== */
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         const halfWidth = width / 2;

// // // //         // Linie wzdłuż Z (równoległe do Z)
// // // //         for (let i = 0; i <= width; i++) {
// // // //             const x = (i - halfWidth) * gridSize;
// // // //             vertices.push(x, 0, 0);
// // // //             vertices.push(x, 0, height * gridSize);
// // // //         }

// // // //         // Linie wzdłuż X (równoległe do X)
// // // //         for (let i = 0; i <= height; i++) {
// // // //             const z = i * gridSize;
// // // //             vertices.push(-halfWidth * gridSize, 0, z);
// // // //             vertices.push(halfWidth * gridSize, 0, z);
// // // //         }

// // // //         geometry.setAttribute(
// // // //             "position",
// // // //             new THREE.Float32BufferAttribute(vertices, 3)
// // // //         );

// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;

// // // //         return grid;
// // // //     };


// // // //     useEffect(() => {
// // // //         if (!sceneRef.current) return;

// // // //         // cleanup
// // // //         sceneRef.current.children
// // // //             .filter(o => o.userData?.isGrid)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter(o => o.userData?.isModel)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;

// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         // Tworzymy grid
// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         // grid.position.set(0, 0, dimZ * GRID_SIZE / 2);
// // // //         grid.position.set(0, 0, 0);
// // // //         sceneRef.current.add(grid);

// // // //         // Tworzymy płaszczyznę
// // // //         const planeGeo = new THREE.PlaneGeometry(dimX * GRID_SIZE, dimZ * GRID_SIZE);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         plane.position.set(0, 0, dimZ * GRID_SIZE / 2); // przesunięcie w Z
// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);


// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODELS                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(m.path, (gltf) => {
// // // //                 modelTemplates.current[m.name] = gltf.scene;
// // // //                 loaded++;
// // // //                 if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //             });
// // // //         });
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD ENTITIES                                                        */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = tpl.clone(true);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const gridX = e.posX ?? 0;
// // // //             const gridZ = e.posZ ?? 0;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = gridX * GRID_SIZE;
// // // //             const worldZ = (dimZ - 1 - gridZ) * GRID_SIZE;

// // // //             model.position.set(
// // // //                 worldX,
// // // //                 model.userData.heightOffset,
// // // //                 worldZ
// // // //             );

// // // //             model.rotation.set(e.rotX || 0, e.rotY || 0, e.rotZ || 0);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);

// // // //     /* ===================================================================== */
// // // //     /*  UI ACTIONS                                                           */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = tpl.clone(true);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         // domyślnie w lewym górnym rogu
// // // //         model.userData.gridX = -Math.floor((selectedScenario?.dimensionX || 18) / 2);
// // // //         model.userData.gridZ = 0;

// // // //         const worldX = model.userData.gridX * GRID_SIZE;
// // // //         const worldZ = model.userData.gridZ * GRID_SIZE;

// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(
// // // //             o => o !== selectedObjectRef.current
// // // //         );
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  SAVE                                                                 */
// // // //     /* ===================================================================== */
// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => ({
// // // //                 name: o.name,
// // // //                 posX: o.userData.gridX ?? 0,
// // // //                 posY: 0,
// // // //                 posZ: o.userData.gridZ ?? 0,
// // // //                 rotX: o.rotation.x,
// // // //                 rotY: o.rotation.y,
// // // //                 rotZ: o.rotation.z,
// // // //             }));

// // // //         await fetch(
// // // //             `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //             {
// // // //                 method: "PATCH",
// // // //                 headers: { "Content-Type": "application/json" },
// // // //                 body: JSON.stringify({
// // // //                     existingSource: selectedScenario.existingSource,
// // // //                     dimensionX: selectedScenario.dimensionX,
// // // //                     dimensionY: selectedScenario.dimensionY,
// // // //                     entities,
// // // //                 }),
// // // //             }
// // // //         );
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  RENDER                                                               */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 16, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />

// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <List dense>
// // // //                     {MODEL_LIBRARY.filter(m => m.visible).map(m => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row">
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = 1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = -1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {sceneRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         // gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         // gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                     />

// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                         boardSizeRef={boardSizeRef.current}
// // // //                         GRID_SIZE={GRID_SIZE}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography variant="h6" sx={{ mt: 1, position: "absolute", top: 10, left: '50%', transform: 'translateX(-50%)', fontSize: '10px' }}>Wybrana stacja:</Typography>
// // // //             <Typography variant="h6" sx={{ mt: 1, color: '#6cc6d9', position: "absolute", top: 30, left: '50%', transform: 'translateX(-50%)' }}>{selectedScenario?.existingSource}</Typography>
// // // //         </>
// // // //     );
// // // // }




// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import DeleteIcon from "@mui/icons-material/Delete";

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf", visible: false },
// // // //     { name: "puppy", path: "/puppy/scene.gltf", visible: false },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf", visible: false },
// // // //     { name: "arrow", path: "/arrow/scene.gltf", visible: false },
// // // //     { name: "envp_furnitures_table", path: "/envp_furnitures_table/envp_furnitures_table_160_80_graphics.gltf", visible: true },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf", visible: true },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf", visible: true },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf", visible: true },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf", visible: true },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf", visible: true },
// // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);
// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const boardSizeRef = useRef({ dimX: 18, dimZ: 18 });

// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);

// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);

// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE                                                            */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(500, 800, 1300);
// // // //         camera.lookAt(0, 0, 0);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             // const dimX = selectedScenario?.dimensionX || 18;
// // // //             // const dimZ = selectedScenario?.dimensionZ || 18;

// // // //             const { dimX, dimZ } = boardSizeRef.current;

// // // //             console.log('dimX', selectedScenario?.dimensionX)
// // // //             console.log('dimZ', selectedScenario?.dimensionZ)

// // // //             // gridX/gridZ w bazie: 1..N
// // // //             let gridX = Math.round((p.x + (dimX * GRID_SIZE) / 2 - GRID_SIZE / 2) / GRID_SIZE) + 1;
// // // //             let gridZ = Math.round((p.z + (dimZ * GRID_SIZE) / 2 - GRID_SIZE / 2) / GRID_SIZE) + 1;

// // // //             // ograniczamy do planszy
// // // //             gridX = Math.max(1, Math.min(dimX, gridX));
// // // //             gridZ = Math.max(1, Math.min(dimZ, gridZ));

// // // //             console.log('gridX', gridX)
// // // //             console.log('gridZ', gridZ)

// // // //             selectedObjectRef.current.userData.gridX = gridX;
// // // //             selectedObjectRef.current.userData.gridZ = gridZ;

// // // //             // pozycja w świecie: środek komórki
// // // //             const worldX = (gridX - 1) * GRID_SIZE - (dimX * GRID_SIZE) / 2 + GRID_SIZE / 2;
// // // //             const worldZ = (gridZ - 1) * GRID_SIZE - (dimZ * GRID_SIZE) / 2 + GRID_SIZE / 2;

// // // //             selectedObjectRef.current.position.set(
// // // //                 worldX,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 worldZ
// // // //             );

// // // //             highlight?.update?.();
// // // //         };


// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y +=
// // // //                     0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => {
// // // //             renderer.domElement.remove();
// // // //         };
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  GRID + PLANE                                                          */
// // // //     /* ===================================================================== */
// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         for (let i = 0; i <= height; i++) {
// // // //             vertices.push(0, 0, i * gridSize);
// // // //             vertices.push(width * gridSize, 0, i * gridSize);
// // // //         }

// // // //         for (let i = 0; i <= width; i++) {
// // // //             vertices.push(i * gridSize, 0, 0);
// // // //             vertices.push(i * gridSize, 0, height * gridSize);
// // // //         }

// // // //         geometry.setAttribute(
// // // //             "position",
// // // //             new THREE.Float32BufferAttribute(vertices, 3)
// // // //         );

// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current) return;

// // // //         // cleanup
// // // //         sceneRef.current.children
// // // //             .filter(o => o.userData?.isGrid)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter(o => o.userData?.isModel)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;

// // // //         boardSizeRef.current.dimX = dimX;
// // // //         boardSizeRef.current.dimZ = dimZ;

// // // //         const offsetX = (dimX * GRID_SIZE) / -2;
// // // //         const offsetZ = (dimZ * GRID_SIZE) / -2;

// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         grid.position.set(offsetX, 0, offsetZ);
// // // //         grid.userData.isGrid = true;
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(
// // // //             dimX * GRID_SIZE,
// // // //             dimZ * GRID_SIZE
// // // //         );
// // // //         planeGeo.rotateX(-Math.PI / 2);

// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );

// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODELS                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(m.path, (gltf) => {
// // // //                 modelTemplates.current[m.name] = gltf.scene;
// // // //                 loaded++;
// // // //                 if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //             });
// // // //         });
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD ENTITIES                                                        */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = tpl.clone(true);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             // Poprawne ustawienie pozycji
// // // //             const gridX = e.posX ?? 1;
// // // //             const gridZ = e.posZ ?? 1;
// // // //             model.userData.gridX = gridX;
// // // //             model.userData.gridZ = gridZ;

// // // //             const worldX = (gridX - 0.5) * GRID_SIZE - (dimX * GRID_SIZE) / 2;
// // // //             const worldZ = (gridZ - 0.5) * GRID_SIZE - (dimZ * GRID_SIZE) / 2;

// // // //             model.position.set(
// // // //                 worldX,
// // // //                 model.userData.heightOffset,
// // // //                 worldZ
// // // //             );

// // // //             model.rotation.set(e.rotX || 0, e.rotY || 0, e.rotZ || 0);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);

// // // //     /* ===================================================================== */
// // // //     /*  UI ACTIONS                                                           */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = tpl.clone(true);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         // domyślnie w lewym górnym rogu
// // // //         model.userData.gridX = 1;
// // // //         model.userData.gridZ = 1;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;

// // // //         const worldX = (model.userData.gridX - 0.5) * GRID_SIZE - (dimX * GRID_SIZE) / 2;
// // // //         const worldZ = (model.userData.gridZ - 0.5) * GRID_SIZE - (dimZ * GRID_SIZE) / 2;

// // // //         model.position.set(worldX, model.userData.heightOffset, worldZ);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(
// // // //             o => o !== selectedObjectRef.current
// // // //         );
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  SAVE                                                                 */
// // // //     /* ===================================================================== */
// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => ({
// // // //                 name: o.name,
// // // //                 posX: o.userData.gridX ?? 1,
// // // //                 posY: 0,
// // // //                 posZ: o.userData.gridZ ?? 1,
// // // //                 rotX: o.rotation.x,
// // // //                 rotY: o.rotation.y,
// // // //                 rotZ: o.rotation.z,
// // // //             }));

// // // //         await fetch(
// // // //             `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //             {
// // // //                 method: "PATCH",
// // // //                 headers: { "Content-Type": "application/json" },
// // // //                 body: JSON.stringify({
// // // //                     existingSource: selectedScenario.existingSource,
// // // //                     dimensionX: selectedScenario.dimensionX,
// // // //                     dimensionY: selectedScenario.dimensionY,
// // // //                     entities,
// // // //                 }),
// // // //             }
// // // //         );
// // // //     };



// // // //     /* ===================================================================== */
// // // //     /*  RENDER                                                               */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 16, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />

// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <List dense>
// // // //                     {MODEL_LIBRARY.filter(m => m.visible === true).map(m => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row">
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = 1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = -1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {sceneRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                     />

// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                     />
// // // //                 </>
// // // //             )}


// // // //             <Typography variant="h6" sx={{ mt: 1, position: "absolute", top: 10, left: '50%', transform: 'translateX(-50%)', fontSize: '10px' }}>Wybrana stacja:</Typography>
// // // //             <Typography variant="h6" sx={{ mt: 1, color: '#6cc6d9', position: "absolute", top: 30, left: '50%', transform: 'translateX(-50%)' }}>{selectedScenario?.existingSource}</Typography>
// // // //         </>
// // // //     );
// // // // }





// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Tooltip,
// // // // } from "@mui/material";

// // // // import BackToScenariosButton from "./utilities/BackToScenario";
// // // // import RotateLeftIcon from "@mui/icons-material/RotateLeft";
// // // // import RotateRightIcon from "@mui/icons-material/RotateRight";
// // // // import DeleteIcon from "@mui/icons-material/Delete";

// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf" },
// // // //     { name: "puppy", path: "/puppy/scene.gltf" },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf" },
// // // //     { name: "arrow", path: "/arrow/scene.gltf" },
// // // //     { name: "envp_furnitures_table", path: "/envp_furnitures_table/envp_furnitures_table_160_80_graphics.gltf" },
// // // //     { name: "exe_graphic_desk_demo", path: "/exe_graphic_desk_demo/exe_graphic_desk_demo.gltf" },
// // // //     { name: "envp_furnitures_sofa_line_2", path: "/envp_furnitures_sofa_line_2/envp_furnitures_sofa_line_2.gltf" },
// // // //     { name: "exe_graphic_cc_logos_3", path: "/exe_graphic_cc_logos_3/exe_graphic_cc_logos_3.gltf" },
// // // //     { name: "exe_graphic_vector_desk_demo", path: "/exe_graphic_vector_desk_demo/exe_graphic_vector_desk_demo.gltf" },
// // // //     { name: "isp_kitchen_fridge_fruits", path: "/isp_kitchen_fridge_fruits/isp_kitchen_fridge_fruits.gltf" },
// // // //     // { name: "", path: "//.gltf" },
// // // //     // { name: "", path: "//.gltf" },
// // // // ];

// // // // export default function MapEditor({ selectedScenario, changeScenarioRef }) {
// // // //     const mountRef = useRef(null);

// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);

// // // //     const uiClickHandlerRef = useRef(null);
// // // //     const rotationDirRef = useRef(0);

// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);
// // // //     const [selectedObject, setSelectedObject] = useState(null);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE – ZAWSZE 1 RAZ                                             */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!mountRef.current) return;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(500, 800, 1300);
// // // //         camera.lookAt(0, 0, 0);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controlsRef.current = controls;

// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };

// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current || !planeRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const x = Math.round(p.x / GRID_SIZE) * GRID_SIZE;
// // // //             const z = Math.round(p.z / GRID_SIZE) * GRID_SIZE;

// // // //             console.log('x i z', x, z)

// // // //             selectedObjectRef.current.position.set(
// // // //                 x,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 z
// // // //             );

// // // //             highlight?.update?.();
// // // //         };

// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);

// // // //             if (selectedObjectRef.current && rotationDirRef.current) {
// // // //                 selectedObjectRef.current.rotation.y +=
// // // //                     0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }

// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => {
// // // //             renderer.domElement.remove();
// // // //         };
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  GRID + PLANE (fallback + scenariusz)                                  */
// // // //     /* ===================================================================== */

// // // //     const createRectGrid = (width, height, gridSize, color = 0x808080) => {
// // // //         const material = new THREE.LineBasicMaterial({ color });
// // // //         const geometry = new THREE.BufferGeometry();
// // // //         const vertices = [];

// // // //         // linie w osi X (równoległe do X, wzdłuż Z)
// // // //         for (let i = 0; i <= height; i++) {
// // // //             vertices.push(0, 0, i * gridSize);
// // // //             vertices.push(width * gridSize, 0, i * gridSize);
// // // //         }

// // // //         // linie w osi Z (równoległe do Z, wzdłuż X)
// // // //         for (let i = 0; i <= width; i++) {
// // // //             vertices.push(i * gridSize, 0, 0);
// // // //             vertices.push(i * gridSize, 0, height * gridSize);
// // // //         }

// // // //         geometry.setAttribute(
// // // //             "position",
// // // //             new THREE.Float32BufferAttribute(vertices, 3)
// // // //         );

// // // //         const grid = new THREE.LineSegments(geometry, material);
// // // //         grid.userData.isGrid = true;
// // // //         return grid;
// // // //     };



// // // //     useEffect(() => {
// // // //         if (!sceneRef.current) return;

// // // //         // cleanup
// // // //         sceneRef.current.children
// // // //             .filter(o => o.userData?.isGrid)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         if (planeRef.current) {
// // // //             sceneRef.current.remove(planeRef.current);
// // // //             planeRef.current.geometry.dispose();
// // // //         }

// // // //         objectsRef.current
// // // //             .filter(o => o.userData?.isModel)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimZ = selectedScenario?.dimensionZ || 18;

// // // //         const offsetX = (dimX * GRID_SIZE) / -2;
// // // //         const offsetZ = (dimZ * GRID_SIZE) / -2;

// // // //         // const grid = new THREE.GridHelper(
// // // //         //     dimX * GRID_SIZE,
// // // //         //     dimX,
// // // //         //     0x808080,
// // // //         //     0x808080
// // // //         // );
// // // //         const grid = createRectGrid(dimX, dimZ, GRID_SIZE, 0x808080);
// // // //         grid.position.set(offsetX, 0, offsetZ);
// // // //         grid.userData.isGrid = true;
// // // //         sceneRef.current.add(grid);

// // // //         const planeGeo = new THREE.PlaneGeometry(
// // // //             dimX * GRID_SIZE,
// // // //             dimZ * GRID_SIZE
// // // //         );
// // // //         planeGeo.rotateX(-Math.PI / 2);

// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );

// // // //         sceneRef.current.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     }, [selectedScenario?.id]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODELS                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loaded = 0;

// // // //         MODEL_LIBRARY.forEach((m) => {
// // // //             loader.load(m.path, (gltf) => {
// // // //                 modelTemplates.current[m.name] = gltf.scene;
// // // //                 loaded++;
// // // //                 if (loaded === MODEL_LIBRARY.length) setModelsLoaded(true);
// // // //             });
// // // //         });
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD ENTITIES                                                        */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !modelsLoaded || !selectedScenario?.entities) return;

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = tpl.clone(true);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true;

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             model.position.set(
// // // //                 e.posX || 0,
// // // //                 model.userData.heightOffset,
// // // //                 e.posZ || 0
// // // //             );

// // // //             model.rotation.set(e.rotX || 0, e.rotY || 0, e.rotZ || 0);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [modelsLoaded, selectedScenario]);

// // // //     /* ===================================================================== */
// // // //     /*  UI ACTIONS                                                           */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = tpl.clone(true);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;

// // // //         model.position.set(0, model.userData.heightOffset, 0);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);

// // // //         selectedObjectRef.current = model;
// // // //         setSelectedObject(model);
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(
// // // //             o => o !== selectedObjectRef.current
// // // //         );
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  SAVE                                                                 */
// // // //     /* ===================================================================== */
// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) return;

// // // //         const entities = objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .map(o => ({
// // // //                 name: o.name,
// // // //                 posX: o.position.x,
// // // //                 posY: o.position.y,
// // // //                 posZ: o.position.z,
// // // //                 rotX: o.rotation.x,
// // // //                 rotY: o.rotation.y,
// // // //                 rotZ: o.rotation.z,
// // // //             }));

// // // //         await fetch(
// // // //             `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //             {
// // // //                 method: "PATCH",
// // // //                 headers: { "Content-Type": "application/json" },
// // // //                 body: JSON.stringify({
// // // //                     existingSource: selectedScenario.existingSource,
// // // //                     dimensionX: selectedScenario.dimensionX,
// // // //                     dimensionY: selectedScenario.dimensionY,
// // // //                     entities,
// // // //                 }),
// // // //             }
// // // //         );
// // // //     };

// // // //     /* ===================================================================== */
// // // //     /*  RENDER                                                               */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper sx={{ position: "absolute", top: 16, left: 16, width: 280, p: 2, zIndex: 10 }}>
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 <Divider sx={{ my: 1 }} />

// // // //                 <Typography variant="subtitle2">Modele</Typography>
// // // //                 <List dense>
// // // //                     {MODEL_LIBRARY.map(m => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>

// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         <Stack direction="row">
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = 1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateLeftIcon />
// // // //                             </IconButton>
// // // //                             <IconButton onMouseDown={() => rotationDirRef.current = -1} onMouseUp={() => rotationDirRef.current = 0}>
// // // //                                 <RotateRightIcon />
// // // //                             </IconButton>
// // // //                             <IconButton color="error" onClick={deleteSelected}>
// // // //                                 <DeleteIcon />
// // // //                             </IconButton>
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={saveScenario}>
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: "100vw", height: "100vh" }} />

// // // //             {sceneRef.current && (
// // // //                 <>
// // // //                     <FloorLogo
// // // //                         key={`logo-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                     />

// // // //                     <UI3DModelButtons
// // // //                         key={`ui-${selectedScenario?.id}`}
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         changeScenarioRef={changeScenarioRef}
// // // //                     />
// // // //                 </>
// // // //             )}

// // // //             <Typography variant="h6" sx={{ mt: 1, position: "absolute", top: 10, left: '50%', transform: 'translateX(-50%)', fontSize: '10px' }}>Wybrana stacja:</Typography>
// // // //             <Typography variant="h6" sx={{ mt: 1, color: '#6cc6d9', position: "absolute", top: 30, left: '50%', transform: 'translateX(-50%)' }}>{selectedScenario?.existingSource}</Typography>

// // // //         </>
// // // //     );
// // // // }



// // // // import React, { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Tooltip,
// // // // } from "@mui/material";


// // // // import BackToScenariosButton from "./utilities/BackToScenario";

// // // // import RotateLeftIcon from '@mui/icons-material/RotateLeft';
// // // // import RotateRightIcon from '@mui/icons-material/RotateRight';
// // // // import DeleteIcon from '@mui/icons-material/Delete';
// // // // import FloorLogo from "./three/FloorLogo";
// // // // import UI3DModelButtons from "./three/UI3DModelButtons";

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf" },
// // // //     { name: "puppy", path: "/puppy/scene.gltf" },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf" },
// // // //     { name: "arrow", path: "/arrow/scene.gltf" },
// // // // ];

// // // // export default function MapEditor({ selectedScenario }) {
// // // //     const mountRef = useRef(null);

// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);

// // // //     const [modelsLoaded, setModelsLoaded] = useState(false);

// // // //     const [scenarioReady, setScenarioReady] = useState(false);


// // // //     const [modelList, setModelList] = useState(MODEL_LIBRARY);
// // // //     const rotationDirRef = useRef(0)

// // // //     const [selectedObject, setSelectedObject] = useState(null);


// // // //     const uiClickHandlerRef = useRef(null);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {

// // // //         if (!mountRef.current) return;

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimY = selectedScenario?.dimensionY || 18;

// // // //         const gridWidth = dimX * GRID_SIZE;
// // // //         const gridHeight = dimY * GRID_SIZE;

// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(500, 800, 1300);
// // // //         camera.lookAt(0, 0, 0);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controlsRef.current = controls;

// // // //         // Lights
// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);



// // // //         const grid = new THREE.GridHelper(gridWidth, dimX, 0x808080, 0x808080);
// // // //         // GridHelper
// // // //         // const grid = new THREE.GridHelper(1000, 20);
// // // //         scene.add(grid);

// // // //         // Plane for raycasting
// // // //         // const planeGeo = new THREE.PlaneGeometry(1000, 1000);
// // // //         const planeGeo = new THREE.PlaneGeometry(gridWidth, gridHeight);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         scene.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current = [plane];

// // // //         scene.userData.rendererDom = renderer.domElement;
// // // //         scene.userData.camera = camera;

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const onDown = (e) => {
// // // //             if (uiClickHandlerRef.current?.(e)) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 setSelectedObject(null);
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             setSelectedObject(hit);
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };


// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const x = Math.round(p.x / GRID_SIZE) * GRID_SIZE;
// // // //             const z = Math.round(p.z / GRID_SIZE) * GRID_SIZE;

// // // //             selectedObjectRef.current.position.set(
// // // //                 x,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 z
// // // //             );

// // // //             if (highlight && selectedObjectRef.current.userData.isModel) {
// // // //                 try {
// // // //                     highlight.update();
// // // //                 } catch (err) {
// // // //                     console.warn("Highlight update skipped:", err);
// // // //                 }
// // // //             }
// // // //         };


// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);
// // // //             const obj = selectedObjectRef.current;
// // // //             if (obj && rotationDirRef.current && obj.userData.isModel) {
// // // //                 obj.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }
// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => {
// // // //             renderer.domElement.remove();
// // // //             sceneRef.current = null;
// // // //             cameraRef.current = null;
// // // //             rendererRef.current = null;
// // // //         };
// // // //     }, [selectedScenario]);



// // // //     /* ===================================================================== */
// // // //     /*  UPDATE SCENE AFTER CHANGE STATION                                                 */
// // // //     /* ===================================================================== */

// // // //     useEffect(() => {
// // // //         if (!sceneRef.current) return;

// // // //         // usuń stary grid / plane
// // // //         sceneRef.current.children
// // // //             .filter(o => o.userData.isGrid)
// // // //             .forEach(o => sceneRef.current.remove(o));

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimY = selectedScenario?.dimensionY || 18;

// // // //         const grid = new THREE.GridHelper(dimX * GRID_SIZE, dimX);
// // // //         grid.userData.isGrid = true;
// // // //         sceneRef.current.add(grid);

// // // //     }, [selectedScenario?.id]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODEL TEMPLATES                                                 */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         let loadedCount = 0;

// // // //         modelTemplates.current = {};
// // // //         setModelsLoaded(false);

// // // //         modelList.forEach((m) => {
// // // //             loader.load(m.path, (gltf) => {
// // // //                 gltf.scene.traverse((child) => {
// // // //                     if (child.isMesh && child.geometry) {
// // // //                         child.geometry.computeBoundingBox?.();
// // // //                         child.geometry.computeBoundingSphere?.();
// // // //                     }
// // // //                 });
// // // //                 modelTemplates.current[m.name] = gltf.scene;
// // // //                 loadedCount++;
// // // //                 if (loadedCount === modelList.length) setModelsLoaded(true);
// // // //             });
// // // //         });
// // // //     }, [modelList]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD SCENARIO ENTITIES                                               */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !selectedScenario?.entities || !modelsLoaded) return;

// // // //         // Usuń tylko modele (nie siatkę ani światła)
// // // //         // objectsRef.current.forEach((o) => {
// // // //         //     if (o !== planeRef.current) sceneRef.current.remove(o);
// // // //         // });
// // // //         // objectsRef.current = [planeRef.current];

// // // //         objectsRef.current
// // // //             .filter(o => o.userData.isModel)
// // // //             .forEach(o => sceneRef.current.remove(o));
// // // //         objectsRef.current = [planeRef.current];

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = tpl.clone(true);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true; // ⚡ oznaczamy jako prawdziwy model

// // // //             model.traverse((child) => {
// // // //                 if (child.isMesh && child.geometry) {
// // // //                     child.geometry.computeBoundingBox?.();
// // // //                     child.geometry.computeBoundingSphere?.();
// // // //                 }
// // // //             });

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const posX = isFinite(e.posX) ? e.posX : 0;
// // // //             const posZ = isFinite(e.posZ) ? e.posZ : 0;
// // // //             const rotX = isFinite(e.rotX) ? e.rotX : 0;
// // // //             const rotY = isFinite(e.rotY) ? e.rotY : 0;
// // // //             const rotZ = isFinite(e.rotZ) ? e.rotZ : 0;

// // // //             model.position.set(posX, model.userData.heightOffset, posZ);
// // // //             model.rotation.set(rotX, rotY, rotZ);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });

// // // //         setScenarioReady(true);
// // // //     }, [selectedScenario, modelsLoaded]);

// // // //     /* ===================================================================== */
// // // //     /*  UI ACTIONS                                                           */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = tpl.clone(true);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         model.traverse((child) => {
// // // //             if (child.isMesh && child.geometry) {
// // // //                 child.geometry.computeBoundingBox?.();
// // // //                 child.geometry.computeBoundingSphere?.();
// // // //             }
// // // //         });

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;
// // // //         model.position.set(0, model.userData.heightOffset, 0);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);
// // // //         selectedObjectRef.current = model;
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;

// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(
// // // //             (o) => o !== selectedObjectRef.current
// // // //         );
// // // //         selectedObjectRef.current = null;
// // // //         setSelectedObject(null);
// // // //     };


// // // //     useEffect(() => {
// // // //         setScenarioReady(false);
// // // //     }, [selectedScenario?.id]);


// // // //     /* ===================================================================== */
// // // //     /*  ZAPISZ SCENARIUSZ                                                    */
// // // //     /* ===================================================================== */
// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) {
// // // //             console.warn("Nie wybrano scenariusza do zapisania.");
// // // //             return;
// // // //         }

// // // //         try {
// // // //             // Przygotowanie danych do wysłania
// // // //             const entities = objectsRef.current
// // // //                 .filter(o => o !== planeRef.current) // ignorujemy plane
// // // //                 .map(o => ({
// // // //                     name: o.name,
// // // //                     posX: o.position.x,
// // // //                     posY: o.position.y,
// // // //                     posZ: o.position.z,
// // // //                     rotX: o.rotation.x,
// // // //                     rotY: o.rotation.y,
// // // //                     rotZ: o.rotation.z,
// // // //                 }));

// // // //             const body = {
// // // //                 existingSource: selectedScenario.existingSource,
// // // //                 dimensionX: selectedScenario.dimensionX || 8,
// // // //                 dimensionY: selectedScenario.dimensionY || 8,
// // // //                 entities,
// // // //             };

// // // //             const res = await fetch(
// // // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //                 {
// // // //                     method: "PATCH",
// // // //                     headers: { "Content-Type": "application/json" },
// // // //                     body: JSON.stringify(body),
// // // //                 }
// // // //             );

// // // //             if (!res.ok) throw new Error(`Błąd HTTP: ${res.status}`);

// // // //             const data = await res.json();
// // // //             console.log("Scenariusz zapisany:", data);
// // // //             // alert("Scenariusz zapisany pomyślnie!");
// // // //         } catch (err) {
// // // //             console.error("Błąd podczas zapisu scenariusza:", err);
// // // //             alert("Błąd podczas zapisu scenariusza. Sprawdź konsolę.");
// // // //         }
// // // //     };


// // // //     /* ===================================================================== */
// // // //     /*  UI RENDER                                                           */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper
// // // //                 elevation={4}
// // // //                 sx={{
// // // //                     position: 'absolute',
// // // //                     top: 16,
// // // //                     left: 16,
// // // //                     width: 280,
// // // //                     p: 2,
// // // //                     zIndex: 10,
// // // //                     borderRadius: 2,
// // // //                 }}
// // // //             >
// // // //                 {/* Header */}
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 {/* <Box display="flex" alignItems="center" gap={1} mb={1}>
// // // //                     <Typography variant="h6" flexGrow={1} sx={{ textAlign: 'center' }}>
// // // //                         Edytor
// // // //                     </Typography>
// // // //                 </Box> */}

// // // //                 <Divider sx={{ mb: 1 }} />

// // // //                 {/* Models */}
// // // //                 <Typography
// // // //                     variant="subtitle2"
// // // //                     color="text.secondary"
// // // //                     sx={{ mb: 0.5 }}
// // // //                 >
// // // //                     Modele
// // // //                 </Typography>

// // // //                 <List dense sx={{ mb: 1 }}>
// // // //                     {modelList.map((m) => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>





// // // //                 {selectedObject && (
// // // //                     <>
// // // //                         <Divider sx={{ my: 1 }} />
// // // //                         {/* Actions */}
// // // //                         < Typography
// // // //                             variant="subtitle2"
// // // //                             color="text.secondary"
// // // //                             sx={{ mb: 0.5 }}
// // // //                         >
// // // //                             Operacje
// // // //                         </Typography>

// // // //                         <Stack direction="row" spacing={1} mb={1}>
// // // //                             <Tooltip title="Obrót w lewo">
// // // //                                 <IconButton
// // // //                                     onMouseDown={() => (rotationDirRef.current = 0.7)}
// // // //                                     onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                                 >
// // // //                                     <RotateLeftIcon />
// // // //                                 </IconButton>
// // // //                             </Tooltip>

// // // //                             <Tooltip title="Obrót w prawo">
// // // //                                 <IconButton
// // // //                                     onMouseDown={() => (rotationDirRef.current = -0.7)}
// // // //                                     onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                                 >
// // // //                                     <RotateRightIcon />
// // // //                                 </IconButton>
// // // //                             </Tooltip>

// // // //                             <Box flexGrow={1} />

// // // //                             <Tooltip title="Usuń zaznaczony obiekt">
// // // //                                 <IconButton color="error" onClick={deleteSelected}>
// // // //                                     <DeleteIcon />
// // // //                                 </IconButton>
// // // //                             </Tooltip>
// // // //                         </Stack>
// // // //                     </>
// // // //                 )}

// // // //                 <Button
// // // //                     fullWidth
// // // //                     variant="contained"
// // // //                     color="primary"
// // // //                     onClick={saveScenario}
// // // //                 >
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper >

// // // //             <div ref={mountRef} style={{ width: '100vw', height: '100vh' }} />


// // // //             {scenarioReady && modelsLoaded && sceneRef.current && cameraRef.current && (
// // // //                 <React.Fragment key={selectedScenario.id}>
// // // //                     <FloorLogo
// // // //                         scene={sceneRef.current}
// // // //                         svgPath="/micree.svg"
// // // //                         gridWidth={(selectedScenario?.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario?.dimensionY || 18) * GRID_SIZE}
// // // //                         y={5}
// // // //                         maxHeight={10}
// // // //                     />

// // // //                     <UI3DModelButtons
// // // //                         scene={sceneRef.current}
// // // //                         camera={cameraRef.current}
// // // //                         modelTemplates={modelTemplates.current}
// // // //                         objectsRef={objectsRef.current}
// // // //                         gridWidth={(selectedScenario.dimensionX || 18) * GRID_SIZE}
// // // //                         gridHeight={(selectedScenario.dimensionY || 18) * GRID_SIZE}
// // // //                         onClickHandlerRef={uiClickHandlerRef}
// // // //                     />
// // // //                 </React.Fragment>
// // // //             )}







// // // //             <Typography variant="h6" sx={{ mt: 1, position: "absolute", top: 10, left: '50%', transform: 'translateX(-50%)', fontSize: '10px' }}>Wybrany scenariusz:</Typography>
// // // //             <Typography variant="h6" sx={{ mt: 1, color: '#6cc6d9', position: "absolute", top: 30, left: '50%', transform: 'translateX(-50%)' }}>{selectedScenario?.existingSource}</Typography>
// // // //         </>
// // // //     );
// // // // }



// // // // import { useEffect, useRef, useState } from "react";
// // // // import * as THREE from "three";
// // // // import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// // // // import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// // // // import {
// // // //     Box,
// // // //     Button,
// // // //     List,
// // // //     ListItem,
// // // //     ListItemButton,
// // // //     ListItemText,
// // // //     Typography,
// // // //     Paper,
// // // //     IconButton,
// // // //     Divider,
// // // //     Stack,
// // // //     Tooltip,
// // // // } from "@mui/material";


// // // // import BackToScenariosButton from "./utilities/BackToScenario";

// // // // import RotateLeftIcon from '@mui/icons-material/RotateLeft';
// // // // import RotateRightIcon from '@mui/icons-material/RotateRight';
// // // // import DeleteIcon from '@mui/icons-material/Delete';

// // // // const GRID_SIZE = 50;

// // // // const MODEL_LIBRARY = [
// // // //     { name: "award", path: "/award/scene.gltf" },
// // // //     { name: "puppy", path: "/puppy/scene.gltf" },
// // // //     { name: "CC CC0 1", path: "/CC CC0 1/CC CC0 1.gltf" },
// // // // ];

// // // // export default function MapEditor({ selectedScenario }) {
// // // //     const mountRef = useRef(null);

// // // //     const sceneRef = useRef(null);
// // // //     const cameraRef = useRef(null);
// // // //     const rendererRef = useRef(null);
// // // //     const controlsRef = useRef(null);

// // // //     const planeRef = useRef(null);
// // // //     const objectsRef = useRef([]);
// // // //     const modelTemplates = useRef({});
// // // //     const selectedObjectRef = useRef(null);

// // // //     const [modelList, setModelList] = useState(MODEL_LIBRARY);
// // // //     const rotationDirRef = useRef(0);

// // // //     /* ===================================================================== */
// // // //     /*  INIT SCENE                                                          */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const scene = new THREE.Scene();
// // // //         scene.background = new THREE.Color(0xf0f0f0);
// // // //         sceneRef.current = scene;

// // // //         const camera = new THREE.PerspectiveCamera(
// // // //             45,
// // // //             window.innerWidth / window.innerHeight,
// // // //             1,
// // // //             10000
// // // //         );
// // // //         camera.position.set(500, 800, 1300);
// // // //         camera.lookAt(0, 0, 0);
// // // //         cameraRef.current = camera;

// // // //         const renderer = new THREE.WebGLRenderer({ antialias: true });
// // // //         renderer.setSize(window.innerWidth, window.innerHeight);
// // // //         renderer.setPixelRatio(window.devicePixelRatio);
// // // //         mountRef.current.appendChild(renderer.domElement);
// // // //         rendererRef.current = renderer;

// // // //         const controls = new OrbitControls(camera, renderer.domElement);
// // // //         controls.enableDamping = true;
// // // //         controls.maxPolarAngle = Math.PI / 2.05;
// // // //         controlsRef.current = controls;

// // // //         // Lights
// // // //         scene.add(new THREE.AmbientLight(0xffffff, 1.5));
// // // //         const dirLight = new THREE.DirectionalLight(0xffffff, 2);
// // // //         dirLight.position.set(300, 400, 200);
// // // //         scene.add(dirLight);

// // // //         const dimX = selectedScenario?.dimensionX || 18;
// // // //         const dimY = selectedScenario?.dimensionY || 18;

// // // //         const gridWidth = dimX * GRID_SIZE;
// // // //         const gridHeight = dimY * GRID_SIZE;

// // // //         const grid = new THREE.GridHelper(gridWidth, dimX, 0x808080, 0x808080);
// // // //         // GridHelper
// // // //         // const grid = new THREE.GridHelper(1000, 20);
// // // //         scene.add(grid);

// // // //         // Plane for raycasting
// // // //         // const planeGeo = new THREE.PlaneGeometry(1000, 1000);
// // // //         const planeGeo = new THREE.PlaneGeometry(gridWidth, gridHeight);
// // // //         planeGeo.rotateX(-Math.PI / 2);
// // // //         const plane = new THREE.Mesh(
// // // //             planeGeo,
// // // //             new THREE.MeshBasicMaterial({ visible: false })
// // // //         );
// // // //         scene.add(plane);
// // // //         planeRef.current = plane;
// // // //         objectsRef.current.push(plane);

// // // //         const raycaster = new THREE.Raycaster();
// // // //         const pointer = new THREE.Vector2();
// // // //         let isDragging = false;
// // // //         let highlight = null;

// // // //         const clearHighlight = () => {
// // // //             if (highlight) {
// // // //                 scene.remove(highlight);
// // // //                 highlight = null;
// // // //             }
// // // //         };

// // // //         const highlightObject = (obj) => {
// // // //             clearHighlight();
// // // //             highlight = new THREE.BoxHelper(obj, 0xffa500);
// // // //             scene.add(highlight);
// // // //         };

// // // //         const updatePointer = (e) => {
// // // //             pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
// // // //             pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
// // // //         };

// // // //         const onDown = (e) => {
// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             let hit = null;
// // // //             const tmp = new THREE.Vector3();

// // // //             objectsRef.current.forEach((o) => {
// // // //                 if (!o.userData.isModel) return;
// // // //                 const box = new THREE.Box3().setFromObject(o);
// // // //                 if (!box.isEmpty() && raycaster.ray.intersectBox(box, tmp)) hit = o;
// // // //             });

// // // //             if (!hit) {
// // // //                 selectedObjectRef.current = null;
// // // //                 clearHighlight();
// // // //                 return;
// // // //             }

// // // //             selectedObjectRef.current = hit;
// // // //             highlightObject(hit);
// // // //             isDragging = true;
// // // //             controls.enabled = false;
// // // //         };


// // // //         const onMove = (e) => {
// // // //             if (!isDragging || !selectedObjectRef.current) return;

// // // //             updatePointer(e);
// // // //             raycaster.setFromCamera(pointer, camera);

// // // //             const hits = raycaster.intersectObject(planeRef.current);
// // // //             if (!hits.length) return;

// // // //             const p = hits[0].point;
// // // //             const x = Math.round(p.x / GRID_SIZE) * GRID_SIZE;
// // // //             const z = Math.round(p.z / GRID_SIZE) * GRID_SIZE;

// // // //             selectedObjectRef.current.position.set(
// // // //                 x,
// // // //                 selectedObjectRef.current.userData.heightOffset || 0,
// // // //                 z
// // // //             );

// // // //             if (highlight && selectedObjectRef.current.userData.isModel) {
// // // //                 try {
// // // //                     highlight.update();
// // // //                 } catch (err) {
// // // //                     console.warn("Highlight update skipped:", err);
// // // //                 }
// // // //             }
// // // //         };


// // // //         const onUp = () => {
// // // //             isDragging = false;
// // // //             controls.enabled = true;
// // // //         };

// // // //         renderer.domElement.addEventListener("pointerdown", onDown);
// // // //         renderer.domElement.addEventListener("pointermove", onMove);
// // // //         renderer.domElement.addEventListener("pointerup", onUp);

// // // //         const animate = () => {
// // // //             requestAnimationFrame(animate);
// // // //             const obj = selectedObjectRef.current;
// // // //             if (obj && rotationDirRef.current && obj.userData.isModel) {
// // // //                 obj.rotation.y += 0.02 * rotationDirRef.current;
// // // //                 highlight?.update?.();
// // // //             }
// // // //             controls.update();
// // // //             renderer.render(scene, camera);
// // // //         };

// // // //         animate();

// // // //         return () => {
// // // //             renderer.domElement.remove();
// // // //         };
// // // //     }, []);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD MODEL TEMPLATES                                                 */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         const loader = new GLTFLoader();
// // // //         modelTemplates.current = {};

// // // //         modelList.forEach((m) => {
// // // //             loader.load(m.path, (gltf) => {
// // // //                 gltf.scene.traverse((child) => {
// // // //                     if (child.isMesh && child.geometry) {
// // // //                         child.geometry.computeBoundingBox?.();
// // // //                         child.geometry.computeBoundingSphere?.();
// // // //                     }
// // // //                 });
// // // //                 modelTemplates.current[m.name] = gltf.scene;
// // // //             });
// // // //         });
// // // //     }, [modelList]);

// // // //     /* ===================================================================== */
// // // //     /*  LOAD SCENARIO ENTITIES                                               */
// // // //     /* ===================================================================== */
// // // //     useEffect(() => {
// // // //         if (!sceneRef.current || !selectedScenario?.entities) return;

// // // //         // Usuń tylko modele (nie siatkę ani światła)
// // // //         objectsRef.current.forEach((o) => {
// // // //             if (o !== planeRef.current) sceneRef.current.remove(o);
// // // //         });
// // // //         objectsRef.current = [planeRef.current];

// // // //         selectedScenario.entities.forEach((e) => {
// // // //             const tpl = modelTemplates.current[e.name];
// // // //             if (!tpl) return;

// // // //             const model = tpl.clone(true);
// // // //             model.name = e.name;
// // // //             model.scale.set(50, 50, 50);
// // // //             model.userData.isModel = true; // ⚡ oznaczamy jako prawdziwy model

// // // //             model.traverse((child) => {
// // // //                 if (child.isMesh && child.geometry) {
// // // //                     child.geometry.computeBoundingBox?.();
// // // //                     child.geometry.computeBoundingSphere?.();
// // // //                 }
// // // //             });

// // // //             const box = new THREE.Box3().setFromObject(model);
// // // //             model.userData.heightOffset = -box.min.y;

// // // //             const posX = isFinite(e.posX) ? e.posX : 0;
// // // //             const posZ = isFinite(e.posZ) ? e.posZ : 0;
// // // //             const rotX = isFinite(e.rotX) ? e.rotX : 0;
// // // //             const rotY = isFinite(e.rotY) ? e.rotY : 0;
// // // //             const rotZ = isFinite(e.rotZ) ? e.rotZ : 0;

// // // //             model.position.set(posX, model.userData.heightOffset, posZ);
// // // //             model.rotation.set(rotX, rotY, rotZ);

// // // //             sceneRef.current.add(model);
// // // //             objectsRef.current.push(model);
// // // //         });
// // // //     }, [selectedScenario, modelList]);

// // // //     /* ===================================================================== */
// // // //     /*  UI ACTIONS                                                           */
// // // //     /* ===================================================================== */
// // // //     const addModel = (name) => {
// // // //         const tpl = modelTemplates.current[name];
// // // //         if (!tpl) return;

// // // //         const model = tpl.clone(true);
// // // //         model.name = name;
// // // //         model.scale.set(50, 50, 50);
// // // //         model.userData.isModel = true;

// // // //         model.traverse((child) => {
// // // //             if (child.isMesh && child.geometry) {
// // // //                 child.geometry.computeBoundingBox?.();
// // // //                 child.geometry.computeBoundingSphere?.();
// // // //             }
// // // //         });

// // // //         const box = new THREE.Box3().setFromObject(model);
// // // //         model.userData.heightOffset = -box.min.y;
// // // //         model.position.set(0, model.userData.heightOffset, 0);

// // // //         sceneRef.current.add(model);
// // // //         objectsRef.current.push(model);
// // // //         selectedObjectRef.current = model;
// // // //     };

// // // //     const deleteSelected = () => {
// // // //         if (!selectedObjectRef.current) return;
// // // //         sceneRef.current.remove(selectedObjectRef.current);
// // // //         objectsRef.current = objectsRef.current.filter(
// // // //             (o) => o !== selectedObjectRef.current
// // // //         );
// // // //         selectedObjectRef.current = null;
// // // //     };



// // // //     /* ===================================================================== */
// // // //     /*  ZAPISZ SCENARIUSZ                                                    */
// // // //     /* ===================================================================== */
// // // //     const saveScenario = async () => {
// // // //         if (!selectedScenario) {
// // // //             console.warn("Nie wybrano scenariusza do zapisania.");
// // // //             return;
// // // //         }

// // // //         try {
// // // //             // Przygotowanie danych do wysłania
// // // //             const entities = objectsRef.current
// // // //                 .filter(o => o !== planeRef.current) // ignorujemy plane
// // // //                 .map(o => ({
// // // //                     name: o.name,
// // // //                     posX: o.position.x,
// // // //                     posY: o.position.y,
// // // //                     posZ: o.position.z,
// // // //                     rotX: o.rotation.x,
// // // //                     rotY: o.rotation.y,
// // // //                     rotZ: o.rotation.z,
// // // //                 }));

// // // //             const body = {
// // // //                 existingSource: selectedScenario.existingSource,
// // // //                 dimensionX: selectedScenario.dimensionX || 8,
// // // //                 dimensionY: selectedScenario.dimensionY || 8,
// // // //                 entities,
// // // //             };

// // // //             const res = await fetch(
// // // //                 `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/${selectedScenario.id}`,
// // // //                 {
// // // //                     method: "PATCH",
// // // //                     headers: { "Content-Type": "application/json" },
// // // //                     body: JSON.stringify(body),
// // // //                 }
// // // //             );

// // // //             if (!res.ok) throw new Error(`Błąd HTTP: ${res.status}`);

// // // //             const data = await res.json();
// // // //             console.log("Scenariusz zapisany:", data);
// // // //             // alert("Scenariusz zapisany pomyślnie!");
// // // //         } catch (err) {
// // // //             console.error("Błąd podczas zapisu scenariusza:", err);
// // // //             alert("Błąd podczas zapisu scenariusza. Sprawdź konsolę.");
// // // //         }
// // // //     };


// // // //     /* ===================================================================== */
// // // //     /*  UI RENDER                                                           */
// // // //     /* ===================================================================== */
// // // //     return (
// // // //         <>
// // // //             <Paper
// // // //                 elevation={4}
// // // //                 sx={{
// // // //                     position: 'absolute',
// // // //                     top: 16,
// // // //                     left: 16,
// // // //                     width: 280,
// // // //                     p: 2,
// // // //                     zIndex: 10,
// // // //                     borderRadius: 2,
// // // //                 }}
// // // //             >
// // // //                 {/* Header */}
// // // //                 <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ justifyContent: 'center' }}>
// // // //                     <BackToScenariosButton />
// // // //                 </Box>
// // // //                 {/* <Box display="flex" alignItems="center" gap={1} mb={1}>
// // // //                     <Typography variant="h6" flexGrow={1} sx={{ textAlign: 'center' }}>
// // // //                         Edytor
// // // //                     </Typography>
// // // //                 </Box> */}

// // // //                 <Divider sx={{ mb: 1 }} />

// // // //                 {/* Models */}
// // // //                 <Typography
// // // //                     variant="subtitle2"
// // // //                     color="text.secondary"
// // // //                     sx={{ mb: 0.5 }}
// // // //                 >
// // // //                     Modele
// // // //                 </Typography>

// // // //                 <List dense sx={{ mb: 1 }}>
// // // //                     {modelList.map((m) => (
// // // //                         <ListItem key={m.name} disablePadding>
// // // //                             <ListItemButton onMouseDown={() => addModel(m.name)}>
// // // //                                 <ListItemText primary={m.name} />
// // // //                             </ListItemButton>
// // // //                         </ListItem>
// // // //                     ))}
// // // //                 </List>

// // // //                 <Divider sx={{ my: 1 }} />

// // // //                 {/* Actions */}
// // // //                 <Typography
// // // //                     variant="subtitle2"
// // // //                     color="text.secondary"
// // // //                     sx={{ mb: 0.5 }}
// // // //                 >
// // // //                     Operacje
// // // //                 </Typography>

// // // //                 <Stack direction="row" spacing={1} mb={1}>
// // // //                     <Tooltip title="Obrót w lewo">
// // // //                         <IconButton
// // // //                             onMouseDown={() => (rotationDirRef.current = 1)}
// // // //                             onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                         >
// // // //                             <RotateLeftIcon />
// // // //                         </IconButton>
// // // //                     </Tooltip>

// // // //                     <Tooltip title="Obrót w prawo">
// // // //                         <IconButton
// // // //                             onMouseDown={() => (rotationDirRef.current = -1)}
// // // //                             onMouseUp={() => (rotationDirRef.current = 0)}
// // // //                         >
// // // //                             <RotateRightIcon />
// // // //                         </IconButton>
// // // //                     </Tooltip>

// // // //                     <Box flexGrow={1} />

// // // //                     <Tooltip title="Usuń zaznaczony obiekt">
// // // //                         <IconButton color="error" onClick={deleteSelected}>
// // // //                             <DeleteIcon />
// // // //                         </IconButton>
// // // //                     </Tooltip>
// // // //                 </Stack>

// // // //                 <Button
// // // //                     fullWidth
// // // //                     variant="contained"
// // // //                     color="primary"
// // // //                     onClick={saveScenario}
// // // //                 >
// // // //                     Zapisz stację
// // // //                 </Button>
// // // //             </Paper>

// // // //             <div ref={mountRef} style={{ width: '100vw', height: '100vh' }} />
// // // //         </>
// // // //     );
// // // // }
